import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { getCompanies, createCompany, getOnboardingState, updateOnboardingState,
         importHistoricalGLLines, saveGLLineMappings, getHistoricalGLLines, getGLLineMappings,
         reorderGLLineMappings,
         importOnboardingFinancials, getOnboardingFinancialsStatus, getDefrevData, updateBSAssumptionsBulk, getBSAssumptions, deleteBSAssumptions,
         getEmployees, getEmployeesWithActuals, patchEmployee, bulkUpsertEmployeeActuals, importOnboardingEmployees, saveDepartmentMappings, getDepartmentMappings,
         renameBenefitComponent,
         getVendors, importOnboardingVendors, getVendorMonthlyActuals,
         saveVendorCategoryMappings, getVendorCategoryMappings,
         getPrepaidContracts,
         saveRevenueModuleMappings, getRevenueModuleMappings,
         importOnboardingAssets, getOnboardingAssetsStatus,
         getFixedAssets, getIntangibleAssets,
         getStateTaxRates, getCompanyStateSuta, putCompanyStateSuta, mergeIsActualsPerDgPayroll,
         getAEQuotaSettings, getCompanyQuotaSettings, getMiscRevenueLines, createMiscRevenueLine,
         getImplementationLines, getDrLineReservations,
         getCompanySettings, updateCompanySettings, renameGLLineLabel } from '../api';
// 9a-2 (D-B): THE ONE per-module configuration predicate — the same function P8 consumes.
// "Configured" on the Step-9 status table MEANS "passes the same check the pre-flight runs".
import { evaluateModuleConfig, nonzeroSeries } from '../engines/preflightCheck';
import GoLivePanel from '../components/GoLivePanel';
import HealthCheckPanel from '../components/HealthCheckPanel';
import { isSuperAdmin, canManageUsers, canEditModel } from '../utils/audience';
import ViewOnly from '../components/ViewOnly';
import UserManagementPanel from '../components/UserManagementPanel';
import { useUnsavedChangesWarning } from '../utils/useUnsavedChangesWarning';
import GuardedDialog, { useSnapshotDirty } from '../components/GuardedDialog';
import { inferBsMetaAll, rollupMemberKeys, planBsAssumptionSeed, looksNonCash,
    SEPARATE_ANSWERS, isSeparateAnswer, BS_TYPE_OPTIONS, bsTypeSection, bsTypeOf, bsTypeSuggestion,
    bsTypeDriver } from '../utils/inferBsMeta';
import { inferIsMetaLeaf, buildIsSortOrders, matchPayrollRole, planSortOrderReband } from '../utils/inferIsMeta';
import { displayGroupLabel, isVendorBookableRole, vendorLineBlockReason, VENDOR_LINE_BLOCK_MESSAGE, IS_CATEGORY_DISPLAY_TO_SLUG } from '../engines/lineRoles';
import { PAYTYPES, PAYROLL_INDEX_CATS } from '../engines/payrollKeys';   // Batch 1b (L3624): read the back-fill's own keys
/* ★ BUILD A — the ONE BS role vocabulary. bsCategoryVocab imports BS_ROLES from here for
 * exactly the same reason (a re-spelled role literal is a second vocabulary); the Step-4a
 * cash seed derives its role sets from it rather than naming 'cash'/'clearing' inline. */
import { BS_ROLES } from '../engines/lineCategories';
import { matchISCategory, templateISCategory, IS_CATEGORY_LABELS, PAYROLL_IS_CATEGORY_LABELS } from '../utils/isCategoryMatch';
import { US_STATES } from '../utils/usStates';
import { paidThroughWithoutLastDay } from '../utils/leaverLastDay';   // 10-09 (c): the one owner of a leaver's last day
import { FREQ_OPTIONS } from '../utils/compCycle';
import { BS_CATEGORY_OPTIONS, BS_DISPLAY_TO_SNAKE, BS_SNAKE_TO_SUBTYPE, BS_ASSET_ROLES } from '../utils/bsCategoryVocab';
import { slugifyGlName, buildVendorIsLineKey, CAT_SLUG_BY_DISPLAY } from '../utils/isLineKey';
/* ★ The BS / revenue key rule — ONE definition, shared with the Step-4b review bridge
 * (utils/computeHistoricalFinancials), which used to keep its own copy. Deliberately NOT
 * slugifyGlName above: different job, different output, and stored data keyed under this
 * one. See utils/bsKey.js. */
import { normalizeBSKey } from '../utils/bsKey';
import { leafKeyFor, ownsLeaf } from '../utils/leafKey';
import { groupGLLines, reservedLeafKeys, buildRevKeyResolver } from '../utils/revKeys';   // W-082: the revenue-line rules, one owner
import { revenueTargetClaimants } from '../utils/miscTargets';
import { implDrLinkedKey, implSyntheticKey } from '../utils/implKeys';   // W-081: an implementation line's links
import { indexGlByLeafKey, ownedLabelFor, overlayLeafLabels } from '../utils/leafLabels';
import { DR_STREAM_MODULE_TYPES, implDeferredLinks, uncarriedDeferredRevenueAccounts } from '../utils/drCarried';   // W-138: "carried", one rule (Step 7 + P8 + Step 9)
// ★ THE ONE role → paytype map. It used to be an object literal in this file (added by
// B6) and an inline ternary in inferIsMeta; both now read this export, which lives
// beside PAYTYPES and PAYTYPE_SORT_OFFSET so it cannot drift from the vocabulary.
// PAYROLL_ROLE_TO_PAYTYPE is no longer imported (2026-09-28): suggestISLineKey keys every row to its OWN line.
// Build C (C-10): the per-dg historical payroll is_actuals back-fill (client-computes / server-persists).
import { derivePerDgPayrollActuals } from '../utils/perDgPayrollActuals';
import { PAYTYPE_CHOICES, withPayrollPaytype, payrollPaytypeForSave, unansweredPayrollPaytype } from '../utils/payrollPaytype';   // commission ship 2b
import parseFinancialExcel, { parseSingleSheet } from '../utils/parseFinancialExcel';
import parseEmployeeExcel from '../utils/parseEmployeeExcel';
import parseVendorExcel, { parseVendorSheet, assembleVendorResult } from '../utils/parseVendorExcel';
// Principle 0: month arithmetic comes from engines/dateUtils, not a parser's private
// copy. parseVendorExcel used to re-export its own addMonths purely for this import;
// the alias is kept so the 8 call sites below are unchanged.
import { addMonths as addYMMonths, buildAllMonths, formatMonthLabel } from '../engines/dateUtils';
import { isOneMonthStart, openingMonthOf, step1WindowError } from '../utils/companyWindow';   // the one-month start (2026-11-02)
import { TEMPLATE_SPECS, buildHistoricalMonths } from '../utils/templateSpecs';   // the IS / BS window-coverage check (2026-11-02)
import parseAssetExcel from '../utils/parseAssetExcel';
import { downloadISBlank, downloadBSBlank, downloadVendorBlank, downloadEmployeeBlank, downloadAssetBlank, downloadVendorForecastBlank, downloadISStarter, downloadBSStarter } from '../utils/templateGenerators';
import { assetBsLineOptionsFromMeta, deferredRevenueOptionsFromMeta } from '../utils/assetBsLineOptions';
import { computeHistoricalFinancials, isExcludedComputedRow,
    evaluateBSSkeleton, evaluateCFSSkeleton, evaluateISSkeleton, assembleISSkeleton,
    CHECK_ROW_ID } from '../utils/computeHistoricalFinancials';
// W-068 pass 3: the ONE upload check (every template against its spec, at parse) and the ONE "looks like a total" test.
import { checkAgainstSpec, mergeSpecProblems, countBlocking, blockingHeading, BLOCKING } from '../utils/specCheck';
import { DEP_METHODS } from '../utils/depreciationMethods';
import { DA_START_OPTIONS, DA_START_FIELD_LABEL, normalizeDaStart, daStartFromOnboardingState, daStartSavePayload } from '../utils/daStartConvention';
import { looksLikeTotalRow } from '../utils/templateHeaders';
import CompCycleControl from '../components/CompCycleControl';
// The Step-5 employee-flag reader. `399d79d` (the no-Salary-column notice) and `b4d78a8`
// (the severity/scope vocabulary) both shipped INERT — `step5Parsed.flags` had no reader
// at all. Its OWN FILE, deliberately NOT an extraction of the Step-2b/6b/8 renderers: that
// was scoped in diffs/notice-renderer-scope.txt and DECLINED (2 behaviours shared by all
// three vs 6 unique to one, three working screens at risk, and 2b cannot be made
// output-identical without moving sentence composition into parseFinancialExcel).
import EmployeeFlagNotices from '../components/EmployeeFlagNotices';
// Build B (C-2): the ONE salary-series derivation. Onboarding computes the series and sends it in
// the roster payload; the server persists it in-txn (client-computes / server-persists).
import { buildMonthlyData, regenerateSalarySeries } from '../engines/salarySeries';
/* Bi-weekly payroll, step 4 (2026-10-08): the company's pay schedule is ASKED at Step 5a, above the roster upload (Brian Q14),
   and handed to every salary derivation the wizard runs (the Step-5 import, "Edit roster") through the ONE mapper. */
import { payScheduleFromSettings } from '../engines/paySchedule';
import PaySchedulePanel from '../components/PaySchedulePanel';
/* Commission ship 5 (2026-10-10): the Step-5 commission panel (the answer's only writer), the gate's rule, and Step 9's row.
   Each rule has ONE owner: commissionAccrualDisplay (when the answer is required), commissionAccrualCheck (P23, read from the
   real pre-flight). */
import CommissionAccrualPanel from '../components/CommissionAccrualPanel';
import { commissionAnswerMissing } from '../utils/commissionAccrualDisplay';
import { commissionAccrualProblems } from '../utils/commissionAccrualCheck';
import { UPLOAD_REQUIRED_MESSAGE } from '../utils/payScheduleDisplay';
import { buildPrepaidLabelToLine, resolvePrepaidContractLine, resolvePrepaidRowLine, stampPrepaidRowLine, prepaidRowSectionKey, prepaidLineFromVendorLink } from '../engines/prepaidRouting';
import { buildPrepaidVendorIndex, resolvePrepaidVendor, prepaidNameKey } from '../engines/prepaidVendorMatch';
// ★ FIX 0: the ONE vendor-forecast synthesizer, shared with the Vendor Modal and the bulk
// Forecast Wizard. The Step-6 import calls it so onboarding produces the same forecast rows
// the Dashboard would — client-computes / server-persists, no second generator.
import { buildVendorForecastMd, assertVendorRouted, vendorRoute, vendorRoutePreserved } from '../utils/vendorForecast';
import { isForecastMonthFor, inheritedForecastIsStale, foldWizardObjIntoVendor } from '../utils/step6ForecastFold';   // 2026-10-29: the Step-6 fold, one owner
import { firstNonzeroForecast } from '../utils/forecastWizardRows';
import PrepaidTieWarning from '../components/PrepaidTieWarning';   // Batch 1b (N-d2): Step 9 reads the engine's prepaid tie   // Batch 1b (L3613): a stored billing month, read back by the grid's own rule
import parseVendorForecastExcel from '../utils/parseVendorForecastExcel';   // 2026-10-29: fill vendor forecasts from a file
import { planVendorForecastFile } from '../utils/vendorForecastFile';
import { fillForecastPrepaidAmort, deriveAmortLag, prepaidRef } from '../engines/prepaidAmort';
// Prepaid renewals part 2 (2026-10-29): the ONE planner + the shared Renews control (the Vendor Dashboard uses the same).
import { prepaidRenewsView, ensurePrepaidRefs, planPrepaidRenewsAnswer, planPrepaidEdit, planPrepaidFlaggedKeep, planPrepaidFlaggedDelete, applyPrepaidPlanLocal, prepaidCollisionText, prepaidRowLabel, prepaidParentRef, prepaidDisplayOrder } from '../utils/prepaidRenewals';
import { PrepaidRenewsCell, PrepaidRenewalChip, PrepaidRenewsFlagged } from '../components/PrepaidRenewsCell';
// ITEM 2 — the Step-6 import must not silently replace an operator-authored prepaid
// schedule. ONE derivation, TWO readers: the operator-facing gate below and the payload
// patch both call `reconcilePrepaidImport`, so what the screen names and what the save
// writes cannot disagree. Full reasoning: client/src/engines/prepaidReconcile.js.
import { reconcilePrepaidImport, applyPrepaidKeep } from '../engines/prepaidReconcile';
// Build 3 c2: the ONE prepaid editor, shared with the Vendor Dashboard. Prop-pure, so
// the wizard supplies handlers that mutate step6Prepaid instead of calling the API.
import PrepaidModal from '../components/PrepaidModal';
// Build 4b: the ONE bulk forecast editor, shared with the Vendor Dashboard — the same
// component cid=3's vendor forecasts were actually entered through. Prop-pure, so the
// wizard supplies handlers that mutate step6Vendors instead of calling the API.
import ForecastWizard from './ForecastWizard';
// ★ 4b: per-seat department options from the ONE shared resolver over the tenant's own
// department_mappings — never a hardcoded list (item 7).
import { buildSeatDeptResolver } from '../utils/seatDepartments';
import { buildISLineTaxonomy } from '../components/ISLinePicker';
// ★ Followup #3: the model's own derivation of the two CFS cash-account names (the clearing rollup
// and the sweep target), so the Step-4b cash rows read exactly like the live Cash Flow page.
import { cfsCashLabels, buildCFSLinesForCompany } from '../engines/buildCFSLines';
// ★ Build A (D1/D2): the MODEL's BS and CFS skeleton builders, called AS-IS. They take
// bs_actuals_meta — which the Step-4 import itself writes — so they run at Step 4 with rmm=[] and
// an empty moduleContext (measured: cid=3's BS skeleton is identical with and without those, since
// its deferred-revenue lines are historical GL lines, not module synthetics). The review screen no
// longer keeps its own section list; it renders these rows and supplies values by leaf id.
import { buildBSLinesForCompany } from '../engines/buildBSLines';
/* ★ The Chart of Accounts orders its rows by the STATEMENTS' own emission order, so it runs
   the same builders the IS and BS pages run (§6 — reuse the order, never re-derive it: the
   stored sort fields were MEASURED not to reproduce it, on both statements).
   moduleIsWired builds the same moduleContext ModelProvider builds. */
import { buildISLinesForCompany } from '../engines/buildISLines';
import { DA_STREAMS, daTargetOptions } from '../engines/daRouting';
import { moduleIsWired } from '../engines/moduleWiring';
// ★ item 7 (slug unification): the department-name slug is THE shared derivation —
// the same function runFullModel keys payrollHC with and the per-seat resolver builds
// its option ids from. This file previously declared its own copy.
import { deptSlug, groupIdOf } from '../utils/seatDepartments';
/* fmtMoney -- THE ONE MONEY FORMATTER WHERE ZERO IS AN ANSWER (utils/fmtMoney.js).
   THIS FILE USED TO DECLARE ITS OWN, TWICE, BLOCK-SCOPED INSIDE renderStep7 (was :9502)
   AND renderStep9 (was :10625), each as
       const fmtMoney = (n) => Number(n || 0).toLocaleString('en-US', {...});
   There was no collision, because nothing here imported the shared module -- and that was
   the hazard: ** AN IMPORT ADDED LATER WOULD HAVE BEEN SILENTLY SHADOWED ** by whichever
   local const was in scope, so the shared owner would appear to be in use and would not be.
   ** AND THE WHOLE RISK OF FOLDING THEM IN WAS THE DOLLAR SIGN: ** the locals returned a
   BARE NUMBER and each JSX site supplied its own literal '$' text child, while the shared
   owner formats as currency and supplies its own. Measured: a fold that kept the literal
   differs at 100% of call-site values by a DOUBLED '$'. All four literals are deleted in
   the same edit, which is why the rendered output is character-identical.
   NOTE: the two locals also used `Number(n || 0)`, the rule-44 shape -- a null would have
   rendered as 0.00. Unreachable at all four sites (both producers coerce to a finite
   number), so this fold moves nothing; the shared owner renders a null as an em-dash. */
import { fmtMoney } from '../utils/fmtMoney';

const FONT = "'DM Sans',-apple-system,sans-serif";
const MONO = "'Space Mono',monospace";

const r2 = n => Math.round(n * 100) / 100;
const fmt = n => n == null ? '—' : n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmt2 = n => {
    if (n == null) return '—';
    if (Math.abs(n) < 0.005) return '—';
    const s = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return n < 0 ? `(${s})` : s;
};
const BLUE = '#2A6DB0';

// Principle 0: the month window has ONE builder — engines/dateUtils.buildAllMonths.
// The local buildMonthsRange(startYM, endYM) deleted here was byte-identical to it,
// falsy-endpoint -> [] guard included.

/* priorDecOf RETIRED (2026-11-02, the one-month start). It returned the December before the start YEAR, which is the
 * balance sheet's opening month only for a January start. The opening month now has ONE owner,
 * utils/companyWindow.js openingMonthOf -- the month before the historical start, the BS template's own opening
 * column (templateSpecs.buildHistoricalMonths). For a January start the two are the same month. */

const SIDEBAR_TABS = [
    { key: 'onboarding', label: 'Onboarding', superAdminOnly: true },   // W-035: internal use only
    /* ★ C4 (Build 2) — the live-tenant rename surface. It deliberately does NOT live
     * behind "Edit Onboarding": that path re-imports and is destructive, and it is
     * reachable for a live tenant with no gate (measured, diffs/rekey-phase0b.txt §0).
     * Renaming a line is an everyday edit and must not require walking the wizard. */
    { key: 'chart-of-accounts', label: 'Chart of Accounts' },
    { key: 'health-check', label: 'Health Check', superAdminOnly: true },   // W-035: the model's pre-flight, super admin only
    // W-053 step 3a: company admins and the super admin only -- a viewer is not offered the tab at all (Brian D2).
    { key: 'add-user', label: 'Add User', managersOnly: true },
    { key: 'extend-years', label: 'Extend Years' },
    { key: 'monthly-close', label: 'Monthly Close' },
];

const WIZARD_STEPS = [
    { num: 1, label: 'Company Setup' },
    { num: 2, label: 'IS & BS Upload' },
    { num: 3, label: 'GL Mapping' },
    { num: 4, label: 'Review Financials' },
    { num: 5, label: 'Payroll Setup' },
    { num: 6, label: 'Vendor Setup' },
    { num: 7, label: 'Revenue Modules' },
    { num: 8, label: 'Assets' },
    // 9a-2: APPENDED (never inserted — completion is slug-keyed in onboarding_state.steps, so
    // appending shifts no stored meaning; the server's existing assets-import current_step=9
    // write, which used to mean "past the end", now correctly means "advance to Module Setup").
    { num: 9, label: 'Module Setup' },
];

/* Batch 1 (2026-11-03, L3623): Step 4a's "Opening Balance" names its month -- the balance BEFORE the history starts
 * (utils/companyWindow.js openingMonthOf, the same month the import reads; SubBase: Dec 2024). */
const openingMonthText = hs => { const m = openingMonthOf(hs); return m ? ' (' + formatMonthLabel(m, 'shortFull') + ')' : ''; };
const openingBalanceHeader = hs => 'Opening Balance' + openingMonthText(hs);
/* Batch 1b (2026-11-03): a Pre-Launch company (isOneMonthStart) has no history -- its opening balance sheet is the CLOSE month, and
 * the Opening Balance column is the month before it (normally all zero). One row under the header says so (Step 9's prepaid wording,
 * Batch 1 N-d1). Any other company: nothing. */
const openingPreLaunchNoteRow = (ob, cols) => {
    if (!ob || !isOneMonthStart(ob.historical_start, ob.close_month)) return null;
    const om = openingMonthOf(ob.historical_start);
    return <tr data-step4a-prelaunch-note=""><th colSpan={cols} style={{ textAlign: 'left', padding: '0 12px 9px', fontSize: 11, fontWeight: 400, color: '#6B7280' }}>
        {'Pre-Launch: the model starts at your opening balance sheet (' + formatMonthLabel(ob.close_month, 'shortFull') + '); this column is the balance entering it (' + formatMonthLabel(om, 'shortFull') + '), normally all zero.'}
    </th></tr>;
};

const REV_MODULE_OPTIONS = [
    { value: 'skip', label: 'Skip (no forecast)' },
    { value: 'ae_quota', label: 'Account Executive Quota' },
    { value: 'company_quota', label: 'Company Quota' },
    { value: 'misc_revenue', label: 'Misc Revenue' },
    { value: 'implementation', label: 'Implementation Revenue' },
];
/* Batch 2 (2026-11-03, item 16, Brian): on a line WITH history that a Revenue Build implementation line forecasts (SubBase
 * "Implementation Fees"), Skip does not mean "no forecast" -- the forecast is the implementation line's. Other lines unchanged. */
const STEP7_SKIP_IMPL_LABEL = 'Skip here (forecast on Revenue Build)';

const REV_MODULE_LABEL = {
    skip: 'Skip',
    ae_quota: 'Account Executive Quota',
    company_quota: 'Company Quota',
    misc_revenue: 'Misc Revenue',
    implementation: 'Implementation Revenue',
};

// C-1: module types whose deferred-revenue BS line is wired via rmm.bs_line_key.
// MUST match buildBSLines.js MODULE_TYPE_TO_BS_CONFIG (the sole consumer) — {ae_quota,
// company_quota}. misc_revenue/skip have no DR; implementation's DR is wired via
// implementation_lines (dynamic deferred_rev_impl_[id]), NOT rmm.bs_line_key, so it is
// NOT demanded here. Step 7 blocks save if any of these has no bs_line_key selection.
const DR_BS_LINE_MODULE_TYPES = DR_STREAM_MODULE_TYPES;   // W-138 (2026-10-05): the builder's own table, via utils/drCarried.js -- {ae_quota, company_quota}

// 9b (Step-7 cardinality): ae_quota / company_quota are COMPANY-LEVEL SINGLETONS — one
// settings row (DB UNIQUE(company_id)), one engine run, one target line
// (getModuleTargetLine resolves find()-first; buildBSLines maps each type to ONE DR ref).
// ≥2 non-skip rows per singleton type CANNOT be honoured by the engine: the
// alphabetically-first gl_line_key takes 100% of the injection, every other mapped line
// silently forecasts $0, and the same deferred balance books N times (BS check breaks).
// The UI makes that state unrepresentable (a taken type is DISABLED in other rows'
// dropdowns); the server save guard makes it impossible. misc_revenue / implementation
// are PER-LINE engines and stay unconstrained (cid=1 runs 2 of each today).
// See CLAUDE.md §10.5 MODULE CARDINALITY + §12.5 STEP-7 CARDINALITY TRAP.
const SINGLETON_MODULE_TYPES = ['ae_quota', 'company_quota'];
// The WHY, operator-facing — semantic, not a limitation we invented; names the workaround.
const singletonTooltip = (type, takenByLineName) => {
    const label = REV_MODULE_LABEL[type] || type;
    const driver = type === 'ae_quota' ? "your company's sales rep roster — there's one roster"
        : "your company quota settings — there's one set of quota settings";
    return `${label} is already forecasting ${takenByLineName || 'another line'}. ` +
        `A ${label} module is driven by ${driver}, so it forecasts one revenue line. ` +
        `To forecast several historical lines together, map one to ${label} and set the ` +
        `others to Skip; the forecast total will be correct.`;
};

/* W-087 F3 (Brian 2026-10-02): suggestRevenueModule -- Step 7's module-type GUESS from a line's name (Semitek / cid=3 words:
 * stripe, chargebee, mobile, self serve, enterprise, ...) -- is DELETED. An unsaved revenue line's module type starts BLANK and
 * REQUIRED (the W-010 Step-7 pattern) and handleStep7Save names every unanswered line. shortenModuleName (below) only tidies the
 * line's OWN name into the stream nickname -- not a guess -- and stays. */

function shortenModuleName(displayName) {
    const s = String(displayName || '').trim();
    if (!s) return '';
    // Drop common revenue suffix words so "Enterprise Revenue" → "Enterprise"
    return s.replace(/\b(revenue|rev|income|sales)\b/gi, '').replace(/\s+/g, ' ').trim() || s;
}

const MONTHS_LIST = [
    { value: '01', label: 'Jan (01)' }, { value: '02', label: 'Feb (02)' },
    { value: '03', label: 'Mar (03)' }, { value: '04', label: 'Apr (04)' },
    { value: '05', label: 'May (05)' }, { value: '06', label: 'Jun (06)' },
    { value: '07', label: 'Jul (07)' }, { value: '08', label: 'Aug (08)' },
    { value: '09', label: 'Sep (09)' }, { value: '10', label: 'Oct (10)' },
    { value: '11', label: 'Nov (11)' }, { value: '12', label: 'Dec (12)' },
];

// ONE list: the labels live in isCategoryMatch (which direct-maps them) and the dropdown
// derives from them, so the two cannot drift. Order is unchanged: Skip, COGS, S&M, G&A, R&D, Other.
const IS_CATEGORIES = ['Skip', ...IS_CATEGORY_LABELS];
// ★ The BS dropdown list is BS_CATEGORY_OPTIONS, imported from utils/bsCategoryVocab —
// ONE vocabulary that also owns the display->snake and snake->engine maps, so the three
// cannot drift. (This list was spelled out here, the display->snake map again below, and
// the snake->engine map a third time in inferBsMeta.js, with nothing checking they agreed.)
// Renamed on the way out: the engine's own five-value enum in engines/lineCategories.js is
// also called BS_CATEGORIES and means something different — two live constants sharing one
// name across two modules is a hazard in itself.

// US_STATES, DEPT_IS_CATEGORIES and BONUS_FREQ_OPTIONS used to be declared here; they now live with their owners
// (utils/usStates, utils/isCategoryMatch, utils/compCycle) so the onboarding template spec imports the same lists.
const DEPT_IS_CATEGORIES = PAYROLL_IS_CATEGORY_LABELS;
const BONUS_FREQ_OPTIONS = FREQ_OPTIONS;

function suggestDeptCategory(departmentName, fileIsCategory) {
    // Normalize file IS category first if present
    if (fileIsCategory) {
        const norm = String(fileIsCategory).trim().toUpperCase().replace(/\s+/g, '');
        if (norm === 'COGS') return 'COGS';
        if (norm === 'S&M' || norm === 'SM' || norm === 'SALES&MARKETING' || norm === 'SALES') return 'S&M';
        if (norm === 'G&A' || norm === 'GA' || norm === 'GENERAL&ADMIN') return 'G&A';
        if (norm === 'R&D' || norm === 'RD' || norm === 'ENGINEERING') return 'R&D';
    }
    const n = String(departmentName || '').toLowerCase();
    if (/engineering|development|\bdev\b|data science|product|\bqa\b|\br&d\b/.test(n)) return 'R&D';
    if (/customer success|client success|support|implementation|services|\bcogs\b/.test(n)) return 'COGS';
    if (/sales|revenue|business development|quota/.test(n)) return 'S&M';
    if (/marketing|growth|demand/.test(n)) return 'S&M';
    if (/finance|hr|human resources|legal|operations|admin|management|\bg&a\b|general/.test(n)) return 'G&A';
    return 'G&A';
}

/* The tenant's FIRST FORECAST MONTH — the month vendor start_dates are anchored to.
 *
 * ★ NEVER A HARDCODED DEFAULT. This replaces nextMonthOf(), whose `if (!ym) return
 * '2026-01'` was the purest form of the Semitek trap: 2026-01 is EXACTLY Semitek's
 * own answer (its close month is 2025-12), so the guess was silently RIGHT on the
 * tenant it was written against and silently WRONG on every other one — cid=3/cid=8's
 * answer is 2023-04. And it is not cosmetic: this value lands in `vendors.start_date`
 * at the Step-6 import, which is ENGINE-CONSUMED (the 5a start gate). A 2026-01 anchor
 * on a tenant whose forecast window ends 2023-12 puts every vendor past the end of the
 * model, so all 229 forecast $0 — with the balance sheet still balancing.
 *
 * Resolution order:
 *   1. the STORED onboarding_state.forecast_start — Step 1 writes it alongside
 *      close_month, so the tenant's own calendar already holds the answer exactly.
 *   2. else derive it from close_month through the ONE canonical addMonths.
 *   3. else NULL — "unknown", handled by the caller. Callers must not invent a month:
 *      returning null is the point (see the Step-6 gate + the import block).
 */
function firstForecastMonth(ob) {
    if (ob?.forecast_start) return ob.forecast_start;
    return ob?.close_month ? addYMMonths(ob.close_month, 1) : null;
}

// Named, operator-facing copy for the null case. One message, three readers (the
// Step-6 render gate and the import block) — never a second wording to drift.
const NO_FORECAST_START_MSG =
    'No forecast start month is set for this company, so vendor start dates cannot be ' +
    'dated. Set the Last Closed Month and Forecast Through in Step 1 (Company Setup), ' +
    'then return to this step.';

const STEP5_SUB_STEPS = [
    { key: '5a', label: 'Upload' },
    { key: '5b', label: 'Benefits' },
    { key: '5c', label: 'Departments' },
    { key: '5d', label: 'Review' },
    { key: '5e', label: 'SUTA Rates' },
];

/* ★★ BUILD 3 c1 — PREPAID COMES BEFORE FORECAST, and the two moved keys are
 * IDENTIFIERS rather than positional letters.
 *
 * WHY THE ORDER CHANGED: the prepaid contracts DECIDE a vendor's forecast type —
 * `buildStep6Vendors` looks each parsed vendor up in the prepaid set and forces
 * `forecast_type='prepaid'` — and for such a vendor three of the six forecast-grid
 * inputs are inert (its expense comes entirely from its contracts via runFullModel
 * §12b). Asking for a forecast type BEFORE showing the contracts that determined it
 * had the dependency backwards.
 *
 * ★ WHY IDENTIFIERS, NOT A BARE SWAP: `=== '6d'` MEANT "the forecast screen" at five
 * sites. Swapping the array alone would have left those five reading the literal
 * '6d' while '6d' now denoted prepaid — a silent mis-gate, no error anywhere. As
 * consts, a stale or mistyped reference is an `no-undef` ESLint error and CRA fails
 * the build (demonstrated in the c1 verify, V2). The three sub-steps that did NOT
 * move keep their literals — they never changed meaning, so there is nothing to
 * mis-read. */
const S6_PREPAID = 'prepaid';
const S6_FORECAST = 'forecast';

const STEP6_SUB_STEPS = [
    { key: '6a', label: 'Upload' },
    { key: '6b', label: 'Review' },
    { key: '6c', label: 'Categories' },
    { key: S6_PREPAID, label: 'Prepaid' },
    { key: S6_FORECAST, label: 'Forecast' },
];

/* isForecastMonthFor, ENGINE_DERIVED_FORECAST and inheritedForecastIsStale moved, unchanged, to utils/step6ForecastFold.js
 * (2026-10-29, the vendor-forecast file) -- imported above; the wizard-save fold moved with them. */

/* The 6d-editable projection of step6Vendors, used to detect unsaved edits.
 * Exactly the fields the 6d grid can change — matching the set the import
 * persists — so a reload cannot look "dirty" and an edit cannot look clean. */
const step6ForecastFingerprint = (vendors) => JSON.stringify(
    (vendors || []).map(v => [
        v.vendor_name, v.forecast_type, v.billing_frequency,
        Number(v.monthly_amount) || 0, Number(v.pct_rate) || 0,
        Number(v.per_seat_cost) || 0, v.seat_basis || 'fte',
        !!v.is_contractor, v.start_date || '',
    ])
);

/* ★★ C1 s2a — THE PREPAID HALF OF THE UNSAVED-CHANGES GUARD.
 *
 * THE HOLE THIS CLOSES (finding H.3): `step6Dirty` was gated to the S6_FORECAST
 * sub-step AND fingerprinted `step6Vendors` only, so `step6Prepaid` was invisible to
 * it. After a local Apply in the prepaid editor the modal unmounts — taking its OWN
 * beforeunload guard with it — and the folded edits sat in React state with ZERO
 * protection. Same data-loss shape C3 closed for the forecast editor, one screen over.
 *
 * ★ `amort` IS IN THE FINGERPRINT, AND IT IS THE POINT. A Regenerate or a lag change
 * rewrites the schedule while leaving every scalar identical, so a fingerprint over the
 * scalars alone would be blind to exactly the edits Stage 2 is about.
 * ★ ITS KEYS ARE SORTED. Object key order is insertion-dependent, so an unsorted
 * serialization would report a phantom dirty state purely because a reload rebuilt the
 * dict in a different order — the same trap `step6ForecastFingerprint`'s own header
 * warns about for monthly_amounts.
 * ★ A PROJECTION, not the whole row: only the fields `handleSave` writes, so incidental
 * fields a reload happens to rebuild (sort_order, created_at, is_historical) cannot
 * manufacture dirt. */
const step6PrepaidFingerprint = (rows) => JSON.stringify(
    (rows || []).map(p => [
        p.vendor_name || '',
        p.vendor_id ?? null,
        p.account || '',
        p.is_line || '',
        p.start_month || '',
        p.length_months == null ? null : Number(p.length_months),
        Number(p.prepaid_amount) || 0,
        p.event_month || '',
        p.event_identifier || '',
        p.amort_lag_months == null ? null : Number(p.amort_lag_months),
        Object.keys(p.amort || {}).sort().map(m => [m, Number(p.amort[m]) || 0]),
        // prepaid renewals part 2: the Renews answer and the renewal link are saved state too (client_key is not: it is an
        // identity the planner may assign to a parsed row, never an operator change)
        p.renews === true ? true : p.renews === false ? false : null,
        p.renewal_of_key ?? null,
    ])
);

/* ★ THE ONE GET→onboarding SHAPE MAP for prepaid contracts (extracted, ITEM 2).
 *
 * `getPrepaidContracts` returns the LIVE APP's frontend shape ({vendor, start, length,
 * prepaid, amortLagMonths, …}); `step6Prepaid` and the import payload want the
 * onboarding/DB shape. TWO callers need that translation now — `loadStep6ForEdit` (the
 * edit producer) and the import reconcile (which reads the stored contracts back before
 * the payload is sent). A second copy is precisely the §6 duplication that produced the
 * clobber this build fixes, so there is exactly one.
 *
 * ⓘ THIS IS A PURE EXTRACTION — byte-identical to the map that was inline in
 * `loadStep6ForEdit`, field for field, including what it deliberately does NOT carry:
 * `event_identifier` is CARRIED since Batch 2 (2026-11-03, item 1); `is_line` and `event_month` are CARRIED now (see their fields below) (§12.5 P-1 / P-2;
 * the is_line A/B was run 2026-10-29 -- 0 of 850 legacy contracts send another line; diffs/prepaid-line-loss-fix.txt -- and
 * `event_month` belongs to item 4). ⛔ Do not "complete" this map as a tidy-up.
 *
 * ★ THE LAG, WHICH THIS MAP USED TO DROP. The GET emits it as `amortLagMonths`
 * (server.js:988) and every client reader wants `p.amort_lag_months` — nothing joined
 * the two, so the field vanished between a camelCase producer and a snake_case
 * consumer while the five renames around it were done correctly.
 * ★ THE HARM WAS NOT THE COLUMN. PrepaidModal seeds `amortLag` from this (via
 * step6PrepaidToModal), so it opened every contract at 0; its `lagChanged` check then
 * compared 0 to 0 and stayed false — the schedule survived — but `handleSave` writes
 * `amort_lag_months: lag` UNCONDITIONALLY, so opening a lagged contract and saving with
 * NO change ERASED its stored lag. The next Regenerate is
 * buildCycleAmort(addMonths(start, lag), …), which then rebuilds the schedule 1-13
 * months early. cid=8 carries 66 lagged contracts.
 * ★ Dual-read like its neighbours, and ALWAYS A NUMBER on this path — the absent/`null`
 * case is reserved for PARSER rows, which is what the derive-fallback keys on. */
const step6PrepaidFromGetRow = (p) => ({
    vendor_name: p.vendor || p.vendor_name,
    account: p.account,
    start_month: p.start || p.start_month,
    length_months: p.length || p.length_months,
    prepaid_amount: Number(p.prepaid ?? p.prepaid_amount) || 0,
    amort: p.amort || {},
    amort_lag_months: Number(p.amortLagMonths ?? p.amort_lag_months) || 0,
    /* ★ ITEM 4 (§12.5 P-1) — CARRY THE EVENT MONTH ON THE READ-BACK.
     * Without it a re-entered contract opened with the Event toggle OFF and the month
     * blank even when the DB held one, and `PrepaidModal.handleSave` then wrote
     * `event_month: null` over the stored marker on a NO-OP save — the same erasure
     * shape `c2759be` closed for `amort_lag_months`, one field over. Dual-read like its
     * neighbours: the GET emits `eventMonth` (server.js:986), the onboarding row wants
     * `event_month`.
     * ⛔ `is_line` and `event_identifier` WERE DROPPED (is_line is CARRIED since 2026-10-29 -- the field below). `is_line` was §12.5 P-2 — adding
     *   it flips 340 contracts from account-LABEL to SLUG-first resolution in
     *   `resolvePrepaidContractLine`, a money-moving routing change with its own A/B
     *   owed. `event_identifier` is in prepaid_contracts_unique. Neither is item 4. */
    event_month: p.eventMonth ?? p.event_month ?? null,
    /* Batch 2 (2026-11-03, item 1): the EVENT IDENTIFIER travels with the row too (the GET emits eventIdentifier; the Step-6 import
     * stores it since Batch 2). Measured before: 0 stored contracts carry one on any company, so no row changes today. */
    event_identifier: p.eventIdentifier ?? p.event_identifier ?? null,
    /* 2026-10-29 (SubBase, the Step-6 line loss): THE STORED LINE TRAVELS WITH THE ROW. Without it a re-saved contract
     * resolved by its account LABEL alone, and a label the chart repeats across sections ("Software" under COGS, G&A and
     * R&D; "Contractor and Consultant Fees"; "Conferences and Events") named no single line: the import stored is_line NULL
     * (45 of 98 contracts on cid=57) and, for a vendor name on two vendor rows, could not pick the vendor (11 left with no
     * vendor_id) -- the forecast cost then booked to NO income-statement line (P27). The Step-6 import resolves slug-first
     * (resolvePrepaidRowLine), so the stored line is what it sends and stores again. MEASURED before carrying it: on
     * cid=3/8/36/40/56 every stored contract sends the SAME line either way (850 of 850) and the same vendor link -- the
     * routing change the old note feared does not occur; the label resolved to the stored slug wherever both exist. */
    is_line: p.is_line ?? null,
    is_historical: p.isHistorical !== undefined ? p.isHistorical : (p.is_historical !== false),
    /* Prepaid renewals part 1 (2026-10-29): the Renews answer and the renewal link travel with the row, so a Save Progress (a full
     * replace) keeps them: client_key = 'id:<the stored id>', renewal_of_key = 'id:<the predecessor's id>' -- the import maps both
     * to the new ids (server.js planPrepaidRenewalLinks). A PARSED row carries neither. */
    renews: p.renews === true ? true : p.renews === false ? false : null,
    client_key: p.id != null ? 'id:' + p.id : (p.client_key ?? null),
    renewal_of_key: p.renewalOfId != null ? 'id:' + p.renewalOfId : (p.renewal_of_key ?? null),
});

const VENDOR_IS_CATEGORIES = ['COGS', 'S&M', 'G&A', 'R&D', 'Other'];


const VENDOR_FREQ_MONTHS = {
    monthly: 1,
    quarterly: 3,
    semi_annual: 6,
    annual: 12,
};

/* ★★ VENDOR_IS_LINE_KEY_BY_CAT / defaultVendorLineKey — DELETED.
 *
 * The map was { 'COGS':'cogs_vendors', 'S&M':'sm_vendors', 'G&A':'ga_vendors', … } — the
 * SAME pre-Path-C catch-all taxonomy just removed from Step 3 (B3-8). **NO SUCH LEAF EXISTS
 * ON ANY TENANT** (verified live: cid=1/2/3 all have 0 `*_vendors` leaves in
 * gl_line_mappings). So every vendor it defaulted was imported pointing at a leaf that
 * CANNOT EXIST, and computeVendor.js:108 (`if (!isLines[v.isCat]) return;`) then silently
 * dropped it — $0 spend, no error.
 *
 * A fallback to a ghost is strictly worse than an error: it converts a loud, fixable
 * mapping gap into a silent zero. Replaced by vendorLineKeyOptions() below, which offers
 * the TENANT'S REAL leaves, and by a fail-loud guard at import.
 */

/* The IS lines a vendor may be assigned to, for one is_category — from the tenant's OWN
 * gl_line_mappings. Excludes payroll leaves (a vendor is not a salary). Mirrors the
 * isVendorAssignable rule in ISLinePicker's buildISLineTaxonomy. */
/* W-002 (2026-09-24): the VENDOR_UNASSIGNABLE key regex is RETIRED. It caught payroll keys and missed every D&A line
 * the vendor engine also skips; the rule is now engines/lineRoles.js isVendorBookableRole / vendorLineBlockReason. */

/* The key a 6c row and a vendor JOIN on. Per LINE for a canonical upload (the
 * template's gl_line_name), per CATEGORY for a legacy one. ONE derivation used by the
 * 6c row builder, buildStep6Vendors and the import — so the three cannot disagree.
 * (It is stored in `gl_category` because that is the column vendor_category_mappings
 * already keys on, and how cid=3's own 26 rows are keyed — no migration needed.) */
function catMapKey(v) {
    /* ★ THE KEY MUST INCLUDE THE CATEGORY. A line NAME alone is not unique: AE Model's
     * template has "Travel" under BOTH COGS and S&M (33 distinct names, 34 distinct
     * pairs), and keying on the name alone silently merged them — 33 rows instead of
     * 34, cogs_travel got 2 vendors and sm_travel was never produced at all. Caught by
     * the D2 per-line count comparison against cid=3. */
    if (v.gl_line_name == null) return v.gl_category || '(Uncategorized)';   // legacy: no such column
    return `${v.gl_category || ''}|${v.gl_line_name}`;
}

/* ★★ BUILD 2 — ONE WIZARD-SIDE ANSWER TO "WHICH step6Vendors ROWS DOES THIS CONTRACT
 * BELONG TO?", so the three wizard surfaces cannot pick three different vendors for one
 * contract. It returns an ARRAY of row INDICES because the two modes legitimately differ
 * in cardinality, and each site keeps its own historical reading of that array.
 *
 * ★ VOCABULARY — THE WORKBOOK LABEL, on both sides, matching buildStep6Vendors exactly:
 * the vendor sheet's `gl_line_name` against the prepaid sheet's, which parseVendorExcel
 * maps into `account`. Parse-time rows have no DB id and no resolved slug, so the label
 * is the only vocabulary both sides actually carry.
 *
 * ⛔ THE LEGACY MODE IS NOT A PERMISSIVE TIER — it is the absence of a vocabulary. A
 * legacy vendor sheet has no gl_line_name column at all, so a pair key would match
 * NOTHING and every legacy prepaid vendor would silently lose its link. Same predicate
 * renderStep6c uses for `perLine`.
 * ⓘ buildStep6Vendors spells that predicate inline (Build 1, fenced off from this build).
 * Pointing it at WIZ_PER_LINE is a one-line follow-up — see the Build-2 verify. */
const WIZ_LINE_OF_VENDOR = v => v.gl_line_name;
const WIZ_LINE_OF_CONTRACT = c => c.account;
const WIZ_PER_LINE = (parsedVendors) => (parsedVendors || []).some(v => v.gl_line_name != null);

/* 2026-10-13 (Brian, SubBase onboarding) -- A CONTRACT THAT NAMES ITS SECTION IS MATCHED ON SECTION + LINE.
 * The label vocabulary above cannot tell "Software" under G&A from "Software" under R&D: a vendor that sits on both
 * read as "ambiguous -- nothing can tell them apart", and a contract for the G&A line could be paired with the R&D
 * vendor of the same name. So when the prepaid template row carries is_category, BOTH sides are compared as the
 * vendor rule's key (utils/isLineKey.buildVendorIsLineKey): the vendor row's own template section + line, and the
 * contract's line -- its is_line once one is set (stamped at parse, or picked in the prepaid editor), else its
 * section + name through the one owner (prepaidRouting.prepaidRowSectionKey).
 * A contract WITHOUT is_category (every stored contract, the legacy grouped layout, a row whose section cell is
 * blank) is matched on the label exactly as before -- that index and that lineOf are untouched.
 * THE SECTION OF A WIZARD VENDOR ROW: a PARSED row carries it in gl_category ("G&A"); a BUILT row (step6Vendors)
 * carries catMapKey there ("G&A|Software"), so the "|<line>" tail is removed. */
function wizVendorSection(v) {
    const g = v && v.gl_category != null ? String(v.gl_category) : '';
    const tail = v && v.gl_line_name != null ? `|${v.gl_line_name}` : null;
    return tail != null && g.endsWith(tail) ? g.slice(0, g.length - tail.length) : g;
}
const WIZ_SECTION_LINE_OF_VENDOR = v => buildVendorIsLineKey(wizVendorSection(v), v.gl_line_name);
const WIZ_SECTION_LINE_OF_CONTRACT = c => c.is_line || prepaidRowSectionKey(c);
/* Both vendor indexes of one pass. The section index is built only when a contract asks for it. */
function wizPrepaidVendorIndexes(parsedVendors) {
    let section = null;
    return {
        label: buildPrepaidVendorIndex(parsedVendors || [], { lineOf: WIZ_LINE_OF_VENDOR }),
        get section() {
            if (!section) section = buildPrepaidVendorIndex(parsedVendors || [], { lineOf: WIZ_SECTION_LINE_OF_VENDOR });
            return section;
        },
    };
}
/* THE ONE wizard-side "which vendor row is this contract's" -- buildStep6Vendors and wizPrepaidVendorIdxs both call it. */
function wizResolvePrepaidVendor(contract, indexes) {
    return contract && contract.is_category
        ? resolvePrepaidVendor(contract, indexes.section, { lineOf: WIZ_SECTION_LINE_OF_CONTRACT })
        : resolvePrepaidVendor(contract, indexes.label, { lineOf: WIZ_LINE_OF_CONTRACT });
}

function wizPrepaidVendorIdxs(contract, parsedVendors) {
    const indexes = wizPrepaidVendorIndexes(parsedVendors);
    if (WIZ_PER_LINE(parsedVendors)) {
        const r = wizResolvePrepaidVendor(contract, indexes);
        return r.entry ? [r.entry.idx] : [];
    }
    const nk = prepaidNameKey(contract && (contract.vendor_name != null ? contract.vendor_name : contract.vendor));
    return (indexes.label.byName.get(nk) || []).map(e => e.idx);
}

/* ★ §E1 (STAGE 3) — the two operator-facing reasons a vendor row has no usable category,
 * declared ONCE and shared by the message builder and the 6b grouping below. A grouping
 * key that is a re-typed copy of a message is the classic drift: the day someone edits
 * the wording, the grouping silently stops matching and the summary vanishes. */
const MSG_NO_CATEGORY = 'no category on the template — choose one';
const unknownCategoryMsg = value => `unknown category "${value}" — choose one`;

/* ★ THE TYPES Step-2b's dispatch chain COMPOSES A SENTENCE FOR. Anything parseFinancialExcel
 * emits that is NOT here has no clause, and used to render an icon with EMPTY TEXT — the
 * chain is a list of `f.type === X &&` arms and every one of them evaluates false.
 * ⛔ THE FALLBACK BELOW IS NOT A TENTH CLAUSE, DELIBERATELY: a clause fixes the one type we
 *   noticed, a fallback fixes the next one too. `suspicious_header` was the only unclaused
 *   type, and — measured — the ONLY one of the 12 literals that carries a `message`, so the
 *   fallback changes exactly that row and cannot alter any of the other nine.
 * ⚠ KEEP IN STEP with the arms below: a type added here without an arm renders nothing. */
const STEP2B_CLAUSED = new Set(['formula', 'subtotal', 'blank_row', 'merged', 'zero_row',
    'no_dates', 'info', 'no_display_group_column', 'unknown_category']);

/* ★ WHEN IS A DERIVED VENDOR LINE TRUSTWORTHY? Returns null when the row can be
 * pre-filled confidently, else a short reason the operator is shown.
 *
 * The point of (c) is the B3-12 / P5 failure: a key that resolves to NO leaf is an
 * orphan write — computeVendor drops that vendor's spend to $0 and nothing fires. A
 * guess is worse than a question, so an underivable row BLOCKS 6c rather than
 * shipping a plausible key.
 * (e) catches a template row whose section and line disagree, which would otherwise
 * route the spend into the wrong IS section silently. */
function unsureReason(row, isCategory, derivedKey, glMappings) {
    /* ★ §E1 (1c) — THE REASON MUST FIT THE ROW'S SHAPE. A LEGACY sheet has no
     * is_category column and no gl_line_name column, so its rows arrive with NO
     * category at all (the name-guess that used to fill it is gone, 1d). Telling that
     * operator "blank GL line name" or "could not derive a line key" describes a
     * column their workbook does not have and gives them nothing to act on. The only
     * true statement is that the template never carried a category, and the only
     * action is to pick one. Clauses (b) and (c) below are written for the CANONICAL
     * per-line shape and are skipped for a legacy row; (d) and (e) still apply,
     * because they validate the KEY, which both shapes have. */
    const perLine = row.gl_line_name != null;
    /* ★ §E1 (STAGE 3) — TWO FAULTS, TWO MESSAGES. This used to answer "unknown category"
     * for a canonical row whether the cell was EMPTY or held something unrecognised. They
     * are different problems with different fixes: an empty cell is an omission the
     * operator closes by picking here; a value like "Marketing Ops" is a vocabulary
     * mismatch they may prefer to correct in the workbook — and they cannot do that
     * unless we tell them WHICH value we did not recognise. Naming it is the difference
     * between a message and an actionable one.
     * ⓘ The blank message is shape-independent: it is equally true of a canonical sheet
     * whose is_category CELL is empty (Stage 2 now emits the real null) and of a legacy
     * sheet that has no is_category COLUMN at all. */
    if (!isCategory) return MSG_NO_CATEGORY;
    if (!CAT_SLUG_BY_DISPLAY[isCategory]) return unknownCategoryMsg(isCategory);
    if (perLine && (!row.gl_line_name || !String(row.gl_line_name).trim())) return 'blank GL line name';
    if (perLine && !derivedKey) return 'could not derive a line key';
    const leaf = (glMappings || []).find(g =>
        g.statement_type === 'IS' && g.is_line_key === derivedKey && g.role);
    if (!leaf) return `no income-statement line "${derivedKey}" exists on this company`;
    const blocked = vendorLineBlockReason(derivedKey, leaf.role);   // W-002: the ONE rule (payroll, D&A, revenue ...)
    if (blocked) return VENDOR_LINE_BLOCK_MESSAGE[blocked];
    if (leaf.is_category !== isCategory) return `that line sits under ${leaf.is_category}, not ${isCategory}`;
    return null;
}
/* ★ §E1 (1a) — THE ONE DERIVATION of "is this 6c row unfit to save", read by every
 * surface that cares: the row highlight, the per-row message, the Save & Continue gate
 * and the parse-time informational flag. It takes the row's CURRENT is_category and
 * is_line_key, so an operator who picks a category CLEARS the flag.
 *
 * ★ IT USED TO BE FROZEN AT PARSE TIME, and that was the bug: `unsure` was computed
 * once into each row and never recomputed, so the dropdown could not clear it and
 * Save & Continue stayed disabled forever. The screen asked for a choice and then
 * refused to accept it. A derived value must be derived where it is read.
 *
 * ★ IT VALIDATES `is_line_key`, NOT the parse-time `derived_is_line`: is_line_key is
 * what handleStep6SaveCats writes and what the picker mutates. Validating anything
 * else lets the gate pass while the STORED key is wrong — and after a category change
 * `derived_is_line` is stale, which is the frozen-value bug in a new costume. */
function rowUnsure(row, glMappings) {
    return unsureReason(row, row.is_category, row.is_line_key, glMappings);
}
function vendorLineKeyOptions(glMappings, isCategory) {
    if (!isCategory) return [];
    return (glMappings || [])
        .filter(g => g.statement_type === 'IS' && g.is_category === isCategory
            && g.is_line_key && g.role && isVendorBookableRole(g.role))
        .map(g => ({ key: g.is_line_key, label: g.display_name || g.gl_name || g.is_line_key }))
        .sort((a, b) => a.label.localeCompare(b.label));
}
/* The default for a category: its first assignable leaf, or '' — NEVER an invented key.
 * '' is honest ("no line chosen yet") and the import blocks on it. */
function defaultVendorLineKey(glMappings, isCategory) {
    return vendorLineKeyOptions(glMappings, isCategory)[0]?.key || '';
}

function suggestContractor(glCategory, vendorName) {
    const text = `${String(glCategory || '')} ${String(vendorName || '')}`.toLowerCase();
    return /\bcontractors?\b|\bconsultants?\b|\bfreelanc\w*|\bagency\b|\bstaffing\b|\boutsourc\w*|\btemp\w*|\bindependent\b/.test(text);
}

/* ★ §E1 (1d) — `suggestVendorCategory` DELETED. It was the vendor path's only
 * name-guess: a private keyword matcher fed the account label, wrong on 4 of cid=3's
 * own 26 stored operator choices, and its answer was SAVED without anyone confirming
 * it. §E1: no inferred classification becomes stored data. Its one call site (the
 * legacy arm of the 6c assembly) is gone, so the function had zero callers.
 * ⛔ matchISCategory and IS_CATEGORY_LABELS are NOT affected — Step 3, the Step-4b
 * bridge and perDgPayrollActuals all still use them. */

function normalizeBenefitKey(s) {
    return String(s || '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .replace(/^(\d)/, 'k$1');
}

// Formatted number input: live comma formatting while editing, select-on-focus,
// commits a Number on blur. decimals=2 for dollar amounts, 0 for whole numbers.
function NumInput({ value, onChange, decimals = 2, style, placeholder, disabled }) {
    const [ed, setEd] = useState(false);
    const [raw, setRaw] = useState('');
    const n = Number(value) || 0;
    const display = n
        ? n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
        : '';
    const fmtLive = (v) => {
        const stripped = String(v).replace(/[^0-9.]/g, '');
        if (!stripped) return '';
        // Allow only one decimal point
        const firstDot = stripped.indexOf('.');
        const cleaned = firstDot === -1
            ? stripped
            : stripped.slice(0, firstDot + 1) + stripped.slice(firstDot + 1).replace(/\./g, '');
        const parts = cleaned.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        if (parts.length > 1) return parts[0] + '.' + parts[1].slice(0, Math.max(decimals, 2));
        return parts[0];
    };
    return (
        <input type="text" inputMode="decimal" style={style} placeholder={placeholder || ''} disabled={disabled}
            value={ed ? fmtLive(raw) : display}
            onFocus={(e) => { setEd(true); setRaw(n ? String(n) : ''); setTimeout(() => e.target.select(), 0); }}
            onChange={(e) => { setRaw(e.target.value.replace(/,/g, '')); }}
            onBlur={() => {
                setEd(false);
                const cleaned = String(raw).replace(/[^0-9.]/g, '');
                const parsed = parseFloat(cleaned);
                onChange(isNaN(parsed) ? 0 : parsed);
            }} />
    );
}

// Displays a YYYY-MM-DD date as MM-DD-YYYY with an auto-masking input (dashes
// inserted as the user types digits). Converts back to YYYY-MM-DD on blur.
// Invalid text is stored verbatim so import validation can flag it.
// Never uses `new Date()`.
function DateInputMDY({ value, onChange, style, placeholder }) {
    const toMdy = (s) => {
        if (!s || typeof s !== 'string') return '';
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(s).trim());
        return m ? `${m[2]}-${m[3]}-${m[1]}` : String(s);
    };
    // Strips non-digits and re-inserts dashes after positions 2 and 4 so
    // "01012021" → "01-01-2021". Capped at 8 digits.
    const applyMask = (s) => {
        const digits = String(s || '').replace(/\D/g, '').slice(0, 8);
        if (digits.length <= 2) return digits;
        if (digits.length <= 4) return digits.slice(0, 2) + '-' + digits.slice(2);
        return digits.slice(0, 2) + '-' + digits.slice(2, 4) + '-' + digits.slice(4);
    };
    const toYmd = (s) => {
        const t = String(s || '').trim();
        if (!t) return '';
        const m = /^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/.exec(t);
        if (!m) return null;
        return `${m[3]}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
    };
    const [raw, setRaw] = useState(toMdy(value));
    const [focused, setFocused] = useState(false);
    useEffect(() => { if (!focused) setRaw(toMdy(value)); }, [value, focused]);
    return (
        <input type="text" inputMode="numeric" style={style} maxLength={10}
            placeholder={placeholder || 'MM-DD-YYYY'} value={raw}
            onFocus={(e) => { setFocused(true); setTimeout(() => e.target.select(), 0); }}
            onChange={(e) => setRaw(applyMask(e.target.value))}
            onBlur={() => {
                setFocused(false);
                const trimmed = raw.trim();
                if (!trimmed) { onChange(''); return; }
                const ymd = toYmd(trimmed);
                if (ymd) { onChange(ymd); setRaw(toMdy(ymd)); }
                else { onChange(trimmed); }
            }} />
    );
}

/* ★★ THE HARDCODED is_line_key TAXONOMY IS GONE.
 *
 * IS_LINE_KEYS_BY_CAT used to live here: a 6-options-per-category literal
 * (cogs_wages … cogs_VENDORS, sm_… , ga_… , rd_… , plus an 'Other' list keyed
 * income_tax / interest_expense / da_expense). It was the PRE-Path-C catch-all
 * taxonomy, and it had been wrong since Session 4a re-keyed vendors to per-line slugs.
 *
 * WHAT IT COST: 37 of cid=3's 49 IS rows had a key the dropdown could not represent
 * (sm_consultants, ga_office_rent, oi_stock_option_expenses …), so they rendered "—".
 * And because suggestISLineKey's fallback was `${prefix}_vendors`, EDITING SUCH A ROW'S
 * CATEGORY silently rewrote its key — orphaning the 34 leaves that vendors.is_line joins
 * (covering ALL 229 of cid=3's vendors → $0 spend) and the 37 that carry is_actuals
 * (→ $0 for every closed month). The model still computed; the BS still balanced.
 *
 * The options now come from THE TENANT'S OWN gl_line_mappings, via the SAME builder
 * VendorDashboard already uses (buildISLineTaxonomy in components/ISLinePicker.jsx).
 * A third hardcoded list is exactly how we got here — so there is no third list.
 *
 * PAYROLL_KEYS_BY_CAT below is NOT a taxonomy: it is the paytype key SHAPE, which is
 * generated (buildPayrollIsLineKey) rather than mapped, and so cannot be read off
 * existing rows for a tenant that has none yet.
 */
const PAYROLL_KEYS_BY_CAT = {
    'COGS': 'cogs', 'S&M': 'sm', 'G&A': 'ga', 'R&D': 'rd',
};
// PAYTYPE_LABELS DELETED — it existed only to LABEL the paytype options this dropdown
// used to offer. A paytype key is not mappable (the payroll engine owns those leaves), so
// the options are gone and the labels with them. The key SHAPE lives in PAYTYPE_KEY_RE
// beside lineKeyOptionsFor, which now filters them out.

/* slugifyGlName MOVED to utils/isLineKey.js (upload-rework step B) so the Step-3
   line-key suggestion below and the Step-6 per-line vendor mapping share ONE
   derivation. Imported at the top of this file. */

/* The Line Key options for one is_category — derived from the tenant's OWN rows.
 *
 * `rows` is the Step-3 grid's live mappings array (same field names as
 * gl_line_mappings), so a key the operator just assigned is immediately offerable on
 * another row, and every EXISTING key is always representable — which is the whole
 * point: a <select> whose value is not among its options renders blank, and blank is
 * what invited the silent overwrite. */
function lineKeyOptionsFor(rows, category, ownKey, ownLabel) {
    if (!category || category === 'Skip') return [];
    const seen = new Map();
    const add = (key, label) => { if (key && !seen.has(key)) seen.set(key, label || key); };

    // 1. Every key already in use in this category, on this tenant.
    for (const r of (rows || [])) {
        if (r.statement_type !== 'IS' || r.is_category !== category) continue;
        add(r.is_line_key, r.display_name || r.gl_name || r.is_line_key);
    }
    // 2026-09-28 (B1): the ROW'S OWN-NAME key is always offered for that row -- before, a key was offered only while some
    // row held it, so a row whose key was blanked could not get its own line back from this select.
    add(ownKey, ownLabel);
    // 2. (removed) The payroll paytypes are NO LONGER offered — see PAYTYPE_KEY_RE below.

    /* ★ A PAYTYPE KEY IS NOT MAPPABLE — DO NOT OFFER ONE.
     *
     * Salaries / bonuses / commissions / payroll taxes / benefits always come from the
     * payroll engine, which is exactly what the "Handled by Payroll" state exists to
     * enforce — yet this menu used to LIST all five for every category (half of every
     * dropdown), offering the mistake the row above forbids. Mapping a GL line to one
     * writes a duplicate catch-all key and merges that line's history into the payroll
     * leaf.
     *
     * Both sources are filtered, not just the generated list: rule 1 can surface a
     * paytype key too, when an earlier suggestion mis-assigned one to a vendor line
     * (the `ga_benefits` / "Business Insurance" case). Filtering here also means such a
     * key can never be RE-PICKED once corrected. */
    return [...seen.entries()]
        .filter(([key]) => !PAYTYPE_KEY_RE.test(key) || (ownKey && key === ownKey))
        .map(([key, label]) => ({ key, label }))
        .sort((a, b) => a.label.localeCompare(b.label));
}
/* The generated payroll paytype keys, for every category prefix — the shape
 * buildPayrollIsLineKey produces. Kept beside the options builder that excludes them. */
const PAYTYPE_KEY_RE = /^(cogs|sm|ga|rd)_(wages|bonuses|commissions|payroll_taxes|benefits)$/;

function splitYM(val) {
    if (!val) return { year: '', month: '' };
    const [y, m] = val.split('-');
    return { year: y || '', month: m || '' };
}
function joinYM(year, month) {
    if (!year || !month) return '';
    return `${year}-${month}`;
}
/* Step 1 "Forecast Through" default for a NEW company (2026-10-28): the close year's December, filled ONLY while the
   field is blank -- never over a year or month the operator already typed. Waits for a full 4-digit close year, so a
   half-typed year is not copied. Returns { year, month } to fill, or null to leave the field alone. */
function forecastThroughDefault(cmYear, cmMonth, ftYear, ftMonth, step1Done) {
    if (step1Done) return null;
    if (!cmYear || cmYear.length !== 4 || !cmMonth) return null;
    if (ftYear || ftMonth) return null;
    return { year: cmYear, month: '12' };
}

// ── Year + Month picker ─────────────────────────────────────
function YearMonthPicker({ year, month, onChangeYear, onChangeMonth, disabled }) {
    const bg = disabled ? '#F5F6FA' : '#fff';
    return (
        <div style={{ display: 'flex', gap: 8 }}>
            <input type="text" placeholder="YYYY" maxLength={4} value={year}
                onChange={e => onChangeYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
                disabled={disabled}
                style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #D0D5DD',
                    fontSize: 14, fontFamily: MONO, width: 80, color: '#1A1C2E', background: bg }} />
            <select value={month} onChange={e => onChangeMonth(e.target.value)} disabled={disabled}
                style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #D0D5DD',
                    fontSize: 14, fontFamily: MONO, color: '#1A1C2E', background: bg, minWidth: 110 }}>
                <option value="">Month</option>
                {MONTHS_LIST.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
        </div>
    );
}

// ── New Company Modal ────────────────────────────────────────
function NewCompanyModal({ onClose, onCreate }) {
    const [name, setName] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const handleCreate = async () => {
        if (!name.trim()) { setError('Company name is required.'); return; }
        try {
            setSaving(true);
            const res = await createCompany(name.trim());
            onCreate(res.data);
        } catch (err) { setError('Failed to create company.'); console.error(err); }
        finally { setSaving(false); }
    };
    const dirty = useSnapshotDirty({ name }); // W-032
    return (
        <GuardedDialog isDirty={dirty} onClose={onClose}
            overlayStyle={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000,
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            cardStyle={{ background: '#fff', borderRadius: 8, padding: 32, width: 400,
                boxShadow: '0 4px 24px rgba(0,0,0,0.15)' }}>
                <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 18, margin: '0 0 20px' }}>New Company</h3>
                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, fontFamily: FONT, color: '#1A1C2E', marginBottom: 6 }}>Company Name</div>
                    <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Enter company name" autoFocus
                        style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #D0D5DD', fontSize: 14, fontFamily: FONT,
                            width: '100%', boxSizing: 'border-box', color: '#1A1C2E' }}
                        onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }} />
                </div>
                {error && <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginBottom: 12 }}>{error}</div>}
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={btnSecondary}>Cancel</button>
                    <button onClick={handleCreate} disabled={saving} style={btnPrimary}>{saving ? 'Creating...' : 'Create Company'}</button>
                </div>
        </GuardedDialog>
    );
}

// ══════════════════════════════════════════════════════════════
// Auto-suggest helpers (outside component — pure functions)
// ══════════════════════════════════════════════════════════════
/* ★ SPLIT BY STATEMENT. A SUBTOTAL is computed on BOTH statements — importing one
 * double-counts, so those rules apply everywhere. The rest are INCOME-STATEMENT computed
 * rows, and one of them is a real BALANCE-SHEET line: "Net Income" is the YTD equity leaf
 * (role ytd_net_income), whose value the model computes from the IS each forecast month.
 * Auto-skipping it EXCLUDED it from the Step-4 payload entirely (buildFinancialsPayload
 * drops any Skip'd BS row), so the tenant lost an equity leaf that cid=3 has — and the
 * Step-4b balance check then fails by exactly YTD net income. */
const TOTAL_SKIP_EXACT = new Set(['total revenue','total expenses','total cost','total operating','total cogs',
    'total cost of goods sold','total assets','total liabilities','total equity',
    'total current assets','total current liabilities','total long term','total stockholders']);
const IS_COMPUTED_EXACT = new Set(['gross profit','net income','net loss','ebitda','operating income',
    'operating loss','income from operations','loss from operations','net revenue']);

/* THE COMPANY'S STEP-3 LINES AS THE PREPAID RESOLVER READS THEM (label -> leaf ids), from its gl_line_mappings. ONE
 * derivation (W-068): the Step-6 import resolves each contract through it, and the upload check's B8 asks the same map
 * whether a contract's line resolves at all -- the two cannot disagree. (Extracted verbatim from the import.) */
function prepaidLabelToLineFor(glm) {
    const isLines = (glm || [])
        .filter(g => g.is_line_key)
        .map(g => ({ id: g.is_line_key, label: g.display_name || g.gl_name || g.is_line_key }));
    return buildPrepaidLabelToLine(isLines);
}

function isAutoSkip(name, statementType) {
    const l = (name || '').toLowerCase().trim();
    // ── computed on EITHER statement ──
    /* W-068: the template upload now KEEPS a row whose name contains "total" or "subtotal" anywhere (F1), so the ONE
     * total test (templateHeaders.looksLikeTotalRow) pre-sets it to Skip here -- marked "suggested -- looks like a total
     * row", never silent (buildMappings). The patterns below were written for rows that START with "total". */
    if (looksLikeTotalRow(l)) return true;
    if (/^(total|subtotal|grand total)\b/i.test(l)) return true;
    if (TOTAL_SKIP_EXACT.has(l)) return true;
    if (/total revenue|total expense|total cost/i.test(l)) return true;
    // ── IS-only from here: on the BS these are real leaves, not computed rows ──
    if (statementType === 'BS') return false;
    if (IS_COMPUTED_EXACT.has(l)) return true;
    if (/gross profit|net income|net loss|operating income|operating loss/i.test(l)) return true;
    /* ★ MATCH PATTERNS, NOT EXACT STRINGS. The AUTO_SKIP_EXACT set holds 'ebitda', which
     * does NOT match AE Model's actual row label 'EBITDA (Reported)' — so it fell through,
     * got no category suggestion, and sat unmapped. Same for 'GP Margin' and 'EBIT Margin':
     * these are COMPUTED lines (the engine derives them), and importing one would
     * double-count. Any margin line, and any EBITDA line however it is annotated, is a
     * computed row. */
    if (/\bebitda\b/i.test(l)) return true;
    if (/\bmargin\b/i.test(l)) return true;
    return false;
}

// matchISCategory extracted to ../utils/isCategoryMatch (Build C) so the per-dg payroll back-fill
// shares the ONE function for its cat_slug fallback. Imported at the top of this file.
/* ★ THE TEMPLATE'S ANSWER WINS, AND IS NOT SECOND-GUESSED (decision D-2).
 *
 * If the workbook supplied a category, that is the operator's own statement of where the
 * line belongs — so it is the ONLY input consulted. A non-empty label that matchISCategory
 * cannot resolve (a typo like "Markting", or a section name we do not know) comes back
 * BLANK, which the unmapped-IS blocker turns into a visible "pick a category" — NOT a
 * silent fall-back to guessing from the line's name.
 *
 * That fall-back is what produced every wrong category measured in the Phase 0: the label
 * failed to match, the name-guess answered instead, and a confident wrong answer got
 * accepted. A blank makes the operator think; a wrong guess does not.
 *
 * The name-guess survives ONLY for a row whose template category is empty — a
 * non-canonical or legacy workbook, where a guess is strictly better than nothing. */
function suggestISCategory(glCategory, glName) {
    if ((glCategory || '').trim()) return matchISCategory(glCategory);
    return matchISCategory(glName);
}

/* ★ Step-3 pass rider (§12.5 B3-2). This regex used to carry OUR OWN tenants' bank
 * names — brex | stripe | paypal | silicon valley | wells fargo | chase | first republic.
 * 'first republic' was stripped from the Step-4 sweep default-pick regex in 6520b6a (see
 * the note at the sweep suggestion below) but survived here.
 *
 * Widening the list is not the fix: Mercury, Ramp, Novo and whatever bank launches next
 * will always outrun a literal. A tenant banking somewhere unlisted got NO suggestion,
 * and an unmapped current asset never reaches bs_actuals_meta — so it vanishes from the
 * balance sheet AND from the Total Cash rollup, giving the sweep a wrong basis.
 *
 * So: keep only GENERIC account-type tokens, and let renderStep3 fail loud on anything
 * left unmapped (same posture 6520b6a took for the sweep pick — block Continue until the
 * operator decides, rather than guess). */
/* The TEMPLATE's own answer for a BS section column, or '' (2026-09-24): an exact section label.
 * Split out of matchBSCategory (which still consults it first) so Step 3 can show a category
 * that came from here as DECIDED and one that came from the keyword rules as a SUGGESTION. */
function templateBSCategory(text) {
    const raw = (text || '').trim();
    if (!raw) return '';
    return BS_CATEGORY_OPTIONS.find(c => c !== 'Skip' && c.toLowerCase() === raw.toLowerCase()) || '';
}
function matchBSCategory(text) {
    // ★ Direct-map a verbatim canonical category label BEFORE the keyword rules.
    // The canonical BS template carries the exact section names ("Current Assets" /
    // "Current Liabilities" / …) in its is_category column, which parseFinancialExcel
    // writes into historical_gl_lines.gl_category — the first argument here. The keyword
    // rules below recognize ACCOUNT keywords (cash/payable/…), NOT the section labels, so
    // "Current Assets"/"Current Liabilities" fell through to a gl_name guess and mis-landed
    // (e.g. "Accrued Interest" under gl_category "Current Assets" matched /accrued/ →
    // Current Liabilities). An exact label is authoritative — trust it. 'Skip' is
    // intentionally NOT direct-mapped: matchBSCategory's contract is a real section label
    // or '', and 'Skip' never appears as a template gl_category (a skipped line is absent).
    const direct = templateBSCategory(text);
    if (direct) return direct;
    const l = (text || '').toLowerCase();
    /* 2026-09-24 -- THE RULES THAT MISPLACED LINES (diffs/bs-classifier-qbo-phase0.txt). These run only when the section
     * column is NOT an exact template section name, so they are the fallback, never a substitute for one.
     *  - "Customer Deposits" is a liability at one company (money received, owed back as goods or a refund) and deferred
     *    revenue at another -- never a current asset. /deposit/ below made it one, the wrong SIDE of the balance sheet,
     *    caught only by the Step-4b balance gate. It is left BLANK for the operator, as Step 4 leaves its type.
     *  - a short-term loan, a line of credit and the current portion of debt are CURRENT liabilities (Step 4's
     *    short-term debt); the /loan/ rule below used to send "Short-Term Loan" to LT Liabilities.
     *  - a note payable is long-term debt; /payable/ used to claim it for Current Liabilities first.
     *  - "long-term" is matched with a hyphen as well as a space (QuickBooks writes the hyphen). */
    if (/customer\s+deposits?/i.test(l)) return '';
    if (/cash|checking|savings|money market|bank|clearing|deposit account|operating account/i.test(l)) return 'Current Assets';
    if (/receivable|prepaid|deposit|other current asset/i.test(l)) return 'Current Assets';
    if (/intangible|patent|trademark|copyright|goodwill|ip\b|intellectual property|capitalized software|software development/i.test(l)) return 'Intangible Assets';
    if (/fixed asset|equipment|computer|furniture|leasehold|property/i.test(l)) return 'Fixed Assets';
    if (/short[\s-]*term\s+(loans?|debt|borrowings?|notes?)|line\s+of\s+credit|credit\s+line|current\s+portion/i.test(l)) return 'Current Liabilities';
    if (/notes?\s+payable|long[\s-]*term/i.test(l)) return 'LT Liabilities';
    if (/payable|accrued|deferred rev/i.test(l)) return 'Current Liabilities';
    if (/debt|loan|lt /i.test(l)) return 'LT Liabilities';
    if (/equity|stock|retained|capital|paid in/i.test(l)) return 'Equity';
    return '';
}
function suggestBSCategory(glCategory, glName) { return matchBSCategory(glCategory) || matchBSCategory(glName); }

/* ★ B6 — THE SECOND PAYROLL-ROLE MATCHER IS DELETED.
 *
 * `matchISLineKey` used to live here: a PRIVATE 5-branch payroll matcher
 * (wage|salar|compensation / tax|fica|… / benefit|insurance|401k|… / bonus / commission)
 * that composed `${prefix}_<suffix>` itself. It duplicated inferIsMeta's
 * `matchPayrollRole` — the function CLAUDE.md names as "the ONE shared payroll matcher" —
 * and the two had already DISAGREED once, about "Salaries" (/salary/ vs /salar/). They
 * were patched into agreement rather than collapsed, which is exactly the state that
 * produced the bug.
 *
 * ★ THEY ALSO TESTED IN OPPOSITE ORDER: the private copy put WAGES FIRST, while
 * matchPayrollRole puts wages LAST (payroll-tax, benefits, commissions and bonuses all
 * win ahead of it). So a COMPOUND payroll label — "Salary Bonus", "Commission Salaries",
 * "Medical Salaries" — passed the gate below and then received a suffix the canonical
 * matcher disagrees with. No live tenant carries such a label; a future one plausibly
 * could, and the key it would be given is FROZEN once written.
 *
 * NOW: the suffix is DERIVED from matchPayrollRole's role through the explicit map below,
 * so there is ONE payroll matcher, and the gate and the suffix read the SAME input.
 *
 * ★ THE MAP IS EXPLICIT AND IS NOT A SLUG OF THE ROLE. `payroll_tax` (role, SINGULAR)
 * must become `payroll_taxes` (paytype, PLURAL); the other four drop the `payroll_`
 * prefix. A missing or wrong entry ships a silently wrong is_line_key — and is_line_key is
 * FROZEN BY DEPENDENTS (§10.5): vendors.is_line and is_actuals[key] join on it, and both
 * fail SILENTLY when it moves.
 *
 * The VALUES are the paytype vocabulary payrollKeys.js owns (PAYTYPES = wages | bonuses |
 * payroll_taxes | benefits | commissions) and that PAYTYPE_KEY_RE above already spells out.
 * ⓘ FOLLOW-UP, deliberately outside B6's fence: this is a THIRD statement of the same
 * role→paytype knowledge (inferIsMeta.js:234-238 carries it as an inline ternary). The
 * right home is ONE exported map in payrollKeys.js consumed by both. Filed, not built. */
/* PAYROLL_ROLE_TO_PAYTYPE MOVED to engines/payrollKeys.js (2026-09-28: no longer read here -- see suggestISLineKey). It was imported at the top of
 * this file — one map, beside the PAYTYPES vocabulary its values must belong to. */
/* matchOtherLineKey DELETED. It returned income_tax / interest_expense / da_expense /
 * other_income — names the ENGINE NEVER USES. The below-the-line convention is oi_*
 * (oi_taxes, oi_interest_income, oi_depreciation_expense), so not one of its outputs
 * could ever match a real leaf: every 'Other' row was unrepresentable, and suggesting one
 * of these on a category change re-keyed the row to a leaf that does not exist.
 * suggestISLineKey now generates `oi_${slugify(gl_name)}` — the real convention. */
/* ★ SUGGEST A KEY FOR A ROW THAT HAS NONE. Never called for a row that already has one
 * (see updateMapping) — an existing key is an identity that vendors.is_line and
 * is_actuals join on, and reassigning it is a migration, not a suggestion.
 *
 * The old fallback was `${prefix}_vendors` — the pre-Path-C catch-all. That collapsed
 * every vendor line in a category onto ONE leaf. The fallback is now the PER-LINE slug
 * (`${prefix}_${slugify(gl_name)}`), which is the convention Session 4a established and
 * that the historical-template parser and inferIsMeta already produce — so a new tenant's
 * onboarding-generated keys match what an import would generate. */
function suggestISLineKey(glCategory, glName, category) {
    if (!category || category === 'Skip') return '';
    if (category === 'Other') {
        // The engine's below-the-line convention is oi_* (oi_taxes, oi_interest_income,
        // oi_depreciation_expense). The old list offered income_tax / da_expense / … —
        // names the engine never uses, so no 'Other' row could ever be represented.
        return `oi_${slugifyGlName(glName)}`;
    }
    const prefix = PAYROLL_KEYS_BY_CAT[category];
    if (!prefix) return '';
    /* ★ ONLY A REAL PAYROLL LINE MAY BE GIVEN A PAYTYPE KEY.
     *
     * The deleted matchISLineKey was a PAYTYPE matcher whose patterns were broad enough to
     * claim ordinary vendor lines: /benefit|insurance|401k|…/ matched "Business Insurance" and
     * keyed it `ga_benefits` — the payroll benefits leaf. That is not a cosmetic mix-up:
     * is_actuals is bucketed BY is_line_key and SUMS same-key rows, and the payroll
     * engine emits `ga_benefits` too, so the vendor's history and G&A payroll benefits
     * merged onto one line in both history and forecast, while "Business Insurance"
     * vanished as a line of its own.
     *
     * matchPayrollRole is the ONE payroll matcher (the same one inferIsMeta and the
     * "Handled by Payroll" state use). If it rejects the line, the line is spend — so go
     * straight to the per-line slug and never offer it a paytype key. */
    /* 2026-09-28 (Brian 09-25): EVERY key this function suggests is the row's OWN-NAME line. The paytype arm
     * (matchPayrollRole -> PAYROLL_ROLE_TO_PAYTYPE -> <prefix>_wages / _benefits ...) is RETIRED. It was a keyword guess,
     * reached only when an operator force-mapped a payroll-named row ("Map instead"), and it produced a key the Line Key
     * select refuses to offer. Measured: "Insurance:Workers Compensation" -> ga_wages had its WHOLE history erased by the
     * Step-5c per-dg merge, which ASSIGNS over that key (-368,533.21 on cid=36's shape). A genuine payroll line belongs on
     * "Handled by Payroll"; one the operator chooses to map instead becomes its OWN line, beside the payroll leaf.
     * matchPayrollRole is still the load-time "Handled by Payroll" pre-selection (buildMappingsFromLines); only this arm
     * changed. NOTE: A name whose slug IS a paytype ("Bonuses" -> cogs_bonuses) gets that key -- the same key as before. */
    return `${prefix}_${slugifyGlName(glName)}`;
}

/* ★★ B s3 — "HANDLED BY PAYROLL" IS AN OPTION IN THE CATEGORY DROPDOWN.
 *
 * The operator states payroll-handling the same way they state everything else on this
 * grid: by picking from the Category select. A pick IS the decision — there is no
 * separate confirm step and no suggestion state to dismiss.
 *
 * ★ IT IS A SENTINEL, NOT A 6th is_category VALUE. The stored is_category stays "Skip";
 * `handled_by` is the discriminator. A 6th category value was explicitly rejected in the
 * Phase 0: is_category answers "which section is this line in", and making it also answer
 * "who computes this line" merges two orthogonal facts into one column — the same overload
 * this arc exists to remove — and would ripple through every is_category consumer
 * (inferIsMetaLeaf THROWS on an unrecognised category; the unmapped-IS blocker keys on it).
 *
 * The sentinel therefore lives ONLY in the <select>: it is mapped to (is_category,
 * handled_by) on the way in and derived back from them on the way out, by the two
 * functions below. It never reaches state, a payload, or the DB. */
const CAT_OPT_PAYROLL = '__handled_by_payroll__';

/* THE ONE options list for the Step-3 Category select — used by BOTH render sites (the
 * per-row dropdown and the bulk-assign dropdown), so they cannot drift (§6). Derived from
 * IS_CATEGORIES rather than restating it, and the payroll option sits immediately after
 * Skip, where the operator expects the "not an ordinary expense line" choices to live. */
function categoryOptionsFor(statementType) {
    if (statementType !== 'IS') return BS_CATEGORY_OPTIONS.map(c => ({ value: c, label: c }));
    return IS_CATEGORIES.flatMap(c => (
        c === 'Skip'
            ? [{ value: c, label: c }, { value: CAT_OPT_PAYROLL, label: 'Handled by Payroll' }]
            : [{ value: c, label: c }]
    ));
}

/* A chosen option -> the two fields it means. Every write of handled_by from this grid
 * goes through here, so "picking anything else clears it" is a property of ONE function
 * rather than a rule someone has to remember at each call site. */
function categoryChoiceToFields(optionValue) {
    return optionValue === CAT_OPT_PAYROLL
        ? { is_category: 'Skip', handled_by: 'payroll' }
        : { is_category: optionValue, handled_by: null };
}

/* ★★ THE ONE CHIP PREDICATE — now also the one that decides WHICH OPTION IS SELECTED.
 *
 * Evaluated in three places (the read-back on load, updateMapping when the operator picks,
 * and the select's displayed value), which is exactly why it is extracted rather than
 * copied: "two matchers agreeing today" is the state that produced the "Salaries" bug (§6).
 *
 * PRECEDENCE: the STORED answer wins; the guess (`looksPayroll`) is consulted only while
 * the answer is NULL — s2 kept it as the fallback so a NULL renders exactly as it did
 * before any of this existed. */
function payrollChipFor(isCategory, handledBy, looksPayroll) {
    if (isCategory !== 'Skip') return false;
    return handledBy ? handledBy === 'payroll' : !!looksPayroll;
}

/* The option the select must SHOW for a row. Derived from (is_category, handled_by)
 * through the predicate above — never assembled ad hoc at a render site. */
function categoryOptionValueFor(m) {
    if (payrollChipFor(m.is_category, m.handled_by, m.looksPayroll)) return CAT_OPT_PAYROLL;
    return m.is_category || '';
}

/* 2026-09-28 (Brian 09-25) -- B1: A MAPPED IS ROW'S LINE KEY IS REQUIRED. The categories Step 3 keys are exactly the ones
 * suggestISLineKey returns a key for: COGS / S&M / G&A / R&D / Other. NOT Skip (which includes "Handled by Payroll": no
 * line), NOT Revenue (the Step-4 import keys it rev_<slug>), NOT blank (the category gate covers that). A blank key on such
 * a row loses the row's WHOLE history at the Step-4 import -- a non-Skip row with no key falls through to a fallback that
 * drops it (measured -1,491,606.32 on cid=36's "IT Consulting") -- and buildISLines emits a leaf with a null id. */
const KEYED_IS_CATEGORIES = new Set([...Object.keys(PAYROLL_KEYS_BY_CAT), 'Other']);
/* 2026-09-28 (Brian 09-25) -- C: THE ONE PREDICATE for "this row is showing Handled by Payroll as a GUESS": the stored
 * answer (handled_by) is empty and the matcher's pre-selection (looksPayroll) is what the select shows. The Step-3 payroll
 * gate and the row's "suggested" mark both read it, so the rows marked are exactly the rows the gate blocks on. */
function isUnansweredPayrollGuess(m) {
    return m.statement_type === 'IS' && m.is_category === 'Skip' && !m.handled_by && !!m.looksPayroll;
}
/* 2026-10-10 (Brian, Q-B) -- THE PAGE CONFIRM ACCEPTS THE GUESSES. A guessed row already SHOWS "Handled by Payroll", so picking
 * that option fires no change in a browser and cannot confirm it. Ticking Step 3's one page confirm (Step 4a's idiom, the pay-type
 * confirm widened) is the one honest action: every row isUnansweredPayrollGuess says is a guess becomes handled_by 'payroll' IN THE
 * DRAFT, tagged guessAccepted; un-ticking returns exactly those rows (still untouched) to the guess. A manual pick or a bulk assign
 * clears the tag, so an un-tick never undoes the operator's own choice; a successful save clears it (the answer is stored). */
function acceptPayrollGuesses(rows, accept) {
    return rows.map(m => (accept
        ? (isUnansweredPayrollGuess(m) ? { ...m, handled_by: 'payroll', guessAccepted: true } : m)
        : (m.guessAccepted ? { ...m, handled_by: null, guessAccepted: false } : m)));
}
function lineKeyRequired(m) {
    return m.statement_type === 'IS' && KEYED_IS_CATEGORIES.has(m.is_category);
}

/* ★ Step-3 pass (§12.5 B3-1). Derives the four columns onboarding never captured:
 * role, subsection, display_group, sort_order — see inferIsMeta.js for why role is the
 * one that matters (NULL role ⇒ no IS leaf lines ⇒ every vendor's spend silently $0).
 *
 * A saved row's stored values WIN over a fresh inference, so an operator override
 * survives a reload; only unsaved (or newly-categorized) rows get inferred.
 *
 * `deptMappings` may be EMPTY here — Step 3 runs before Step 5 — in which case
 * display_group comes back null and the department-mappings save back-fills it later
 * (server.js). That is expected, not a failure. */
function classifyIsMappings(maps, glLines, deptMappings) {
    const srcSort = new Map();
    for (const row of glLines || []) {
        const k = `${row.statement_type}|${row.gl_name}|${row.gl_category || ''}`;
        if (!srcSort.has(k)) srcSort.set(k, row.sort_order == null ? 999999 : row.sort_order);
    }

    const leaves = [];
    for (const m of maps) {
        if (m.statement_type !== 'IS') continue;
        if (!m.is_category || m.is_category === 'Skip') { m.role = null; m.subsection = null; m.display_group = null; continue; }
        let meta;
        try {
            meta = inferIsMetaLeaf(m);
        } catch (e) {
            // Fail loud — but as a flag the grid can render, not an exception that blanks
            // the wizard. renderStep3 blocks Continue while any row carries inferError.
            m.role = null; m.subsection = null; m.inferError = e.message;
            continue;
        }
        m.inferError = null;
        m.role = m.savedRole || meta.role;
        m.subsection = m.savedSubsection || meta.subsection;
        /* Department grouping (W-022, 2026-09-28) -- THE OPERATOR, THEN THE WORKBOOK, THEN BLANK.
         *
         *   savedDisplayGroup   the operator's own saved Step-3 value -- INCLUDING a saved BLANK:
         *                       `savedGroupAnswered` is true when this line was saved as a
         *                       role-bearing row, so a stored NULL is the operator's answer ("no
         *                       sub-group") and is never refilled. It MUST stay first: the Step-3
         *                       upsert writes `display_group = EXCLUDED.display_group`.
         *   rowDisplayGroup     the canonical template's explicit display_group column -- a
         *                       statement of fact about the row -- for a line not saved yet.
         *   null                a blank stays blank: the line renders under its section heading.
         *
         * The third rank used to be a token-match of gl_category against department names
         * (inferDisplayGroupForGlCategory, now deleted). It was a guess shown as a decision, and
         * it refilled lines the operator had saved blank. A GL line's grouping is display-only
         * (diffs/w022-phase0.txt: 0 leaf / total / NI / BS / CFS cells move). */
        m.display_group = m.savedDisplayGroup
            || (m.savedGroupAnswered ? null : m.rowDisplayGroup)
            || null;
        leaves.push({
            key: `${m.statement_type}|${m.gl_name}|${m.gl_category || ''}`,
            subsection: m.subsection,
            display_group: m.display_group,
            role: m.role,
            src_sort: srcSort.get(`${m.statement_type}|${m.gl_name}|${m.gl_category || ''}`) ?? 999999,
        });
    }

    // sort_order: NOT inferred — the source workbook's own row order (already persisted
    // as historical_gl_lines.sort_order at Step-2 import), banded payroll-first.
    // A saved sort_order WINS: it may carry an operator reorder, and the server's upsert
    // deliberately does not overwrite it either.
    const bands = buildIsSortOrders(leaves, deptMappings);
    for (const m of maps) {
        if (m.statement_type !== 'IS') continue;
        const k = `${m.statement_type}|${m.gl_name}|${m.gl_category || ''}`;
        m.sort_order = m.savedSortOrder != null ? m.savedSortOrder : (bands[k] ?? 0);
    }
    return maps;
}

function buildMappingsFromLines(glLines, existingMappings, deptMappings) {
    console.log('[buildMappings] glLines:', glLines.length, '| existingMappings:', existingMappings.length);
    if (existingMappings.length > 0) {
        console.log('[buildMappings] first saved mapping:', JSON.stringify(existingMappings[0]));
    }

    // Build lookup of existing saved mappings
    const savedLookup = {};
    for (const m of existingMappings) {
        const mKey = `${m.statement_type}|${m.gl_name}|${m.gl_category || ''}`;
        savedLookup[mKey] = m;
    }
    console.log('[buildMappings] savedLookup keys count:', Object.keys(savedLookup).length);

    // Group gl lines by (gl_name, gl_category, statement_type), compute annual totals, track sort_order
    const groups = {};
    const groupOrder = [];
    for (const row of glLines) {
        const key = `${row.statement_type}|${row.gl_name}|${row.gl_category || ''}`;
        if (!groups[key]) {
            /* ★ dgSeen: the DISTINCT non-blank department tags the workbook gives this
             * (gl_name, gl_category). A SET, not a first-wins value, because a group can span
             * SEVERAL departments — cid=8's "Salaries"/"Bonuses"/"Payroll Taxes"/"Benefits" under
             * gl_category "S&M" each appear under three or four. gl_line_mappings has ONE row per
             * (gl_name, statement_type, gl_category), so it cannot carry four answers, and picking
             * the first would be an arbitrary silent choice among them. Resolved below: assign only
             * when the group agrees on exactly one. */
            groups[key] = { gl_name: row.gl_name, gl_category: row.gl_category || '', statement_type: row.statement_type, total: 0, sort_order: row.sort_order || 0, dgSeen: new Set() };
            groupOrder.push(key);
        }
        groups[key].total += parseFloat(row.amount) || 0;
        if (row.display_group) groups[key].dgSeen.add(row.display_group);
    }
    console.log('[buildMappings] unique GL groups:', groupOrder.length);

    let savedCount = 0, autoCount = 0;
    const maps = [];
    for (const gKey of groupOrder) {
        const g = groups[gKey];
        const lookupKey = `${g.statement_type}|${g.gl_name}|${g.gl_category}`;
        const saved = savedLookup[lookupKey];
        if (saved) savedCount++; else autoCount++;
        const autoSkip = isAutoSkip(g.gl_name, g.statement_type);
        /* ★ THE BS "NET INCOME" ROW IS AN EQUITY LEAF THE MODEL COMPUTES — not a skip.
         * inferBsMeta gives it role 'ytd_net_income' + formula 'net_income_ytd', and
         * computeBS writes IS_REF.net_income_ytd into it each forecast month; its
         * historical months come from bs_actuals. So it must be MAPPED (to Equity) for
         * the leaf to exist — but its value is never operator-entered, which is why the
         * grid renders it read-only instead of as an editable dropdown. */
        const autoEquity = g.statement_type === 'BS' && /net\s+income/i.test(g.gl_name || '');
        /* ★ "HANDLED BY PAYROLL" — a payroll paytype must NEVER be GL-mapped.
         *
         * Salaries / bonuses / payroll taxes / benefits / commissions ALWAYS come from the
         * payroll engine (the `employees` table), for every tenant, in every category — never
         * from a gl_line_mappings row. Mapping one writes a catch-all key (`sm_salaries`) that
         * (a) duplicates across departments → breaks the "0 duplicate keys" invariant, and
         * (b) emits a phantom $0 leaf the engine never fills.
         *
         * The detector keys on gl_name ALONE (matchPayrollRole — the SAME matcher inferIsMeta
         * uses, so there is no third matcher to drift). That is deliberate: Step 3 runs BEFORE
         * Step 5, so department_mappings is empty on a first pass, and any department-based
         * detector would find nothing here. gl_name is tenant-independent and order-independent.
         *
         * It resolves to is_category='Skip' — already proven INERT (role NULL → no leaf) — so
         * it satisfies the unmapped-IS blocker, persists, and round-trips. `payrollHandled` is a
         * DISPLAY flag only; the grid renders a read-only "Handled by Payroll" state with a "map
         * instead" escape hatch (a line genuinely named "Bonuses" that is really a customer
         * rebate can be force-mapped). Default, not a lock. */
        let cat = '', key = '', payrollHandled = false;
        /* 2026-09-24 -- true when the category below came from a KEYWORD rule (a guess from the
         * section text or the line's name), false when it is the template's exact section name,
         * the stored answer, or one of the automatic states. Step 3 marks a guess "suggested --
         * confirm or change" so it never looks like a decision; any edit clears it. */
        let categorySuggested = false;
        /* ★ B s2 — the STORED answer to "why is this row Skip'd" (gl_line_mappings
         * .handled_by: 'payroll' | 'computed' | 'revenue_module' | NULL). Carried onto the
         * row so the grid can round-trip it and the save can write it back unchanged. */
        let handledBy = null;
        /* Only an EXPENSE-side line can be payroll (a revenue "Commission Income" must not
         * trip it).
         *
         * ★ THE TEST IS THE TEMPLATE'S OWN CATEGORY, NOT THE SUGGESTER. This used to read
         * `suggestISCategory(...) !== 'Skip'`, which delegated the judgement to the keyword
         * matcher — so ANY payroll line whose NAME contains a revenue-ish word lost the
         * chip. Measured: "Sales Compensation" (template S&M) hit the Skip pattern's
         * `sales` token, came back 'Skip', and rendered as a bare "Skip" — reading as
         * *dropped* rather than *covered by Step 5* — while its sibling "G&A Compensation"
         * kept the chip, for a reason that has nothing to do with payroll.
         *
         * An expense category on the row is the thing the clause was always approximating:
         * the template says which section the line is in, and payroll is possible in any
         * expense section and in none of Revenue.
         *
         * A row whose template category is EMPTY (a legacy / non-canonical workbook) keeps
         * the old suggester-based test — the same rule D-2 applies to the category itself:
         * trust the label when there is one, guess only when there is not.
         *
         * ★★ THE LABEL IS NORMALIZED BEFORE THE MEMBERSHIP TEST, NOT MATCHED EXACTLY.
         *
         * This used to be `IS_CATEGORY_LABELS.some(c => c === tplCat)` — an EXACT match
         * against the five canonical section labels. Nothing on the import path constrains
         * that column to them: parseFinancialExcel writes the cell VERBATIM (canonCellText
         * only trims), the payload and the server pass it through untouched, and there is no
         * validation and no quality flag for an unrecognised value. The still-live legacy
         * parse branch fills it from the running SECTION HEADER, which in a real GL export
         * is a DEPARTMENT NAME. So a workbook can perfectly well say "Sales and Marketing"
         * where the template expects "S&M" — and cid=3 does.
         *
         * The exact match then failed on those rows, and the failure was silent and
         * three-headed: a genuine payroll line lost its "Handled by Payroll" suggestion,
         * was handed a vendor-shaped catch-all key instead (4 duplicate is_line_keys across
         * 23 rows on cid=3's shape — a pre-flight P2 violation and a phantom $0 leaf), and
         * became invisible to Step 3's unanswered-payroll gate.
         *
         * ★ REUSE, NOT A NEW MAP (§6): `matchISCategory` is ALREADY the canonical
         * gl_category→section normalizer, already imported at the top of this file, and
         * already shared by computeHistoricalFinancials and perDgPayrollActuals. It
         * DIRECT-MAPS each canonical label to itself, so this test is a STRICT SUPERSET of
         * the exact match — no row can LOSE payroll detection, which is the whole safety
         * argument. ("Revenue" resolves to Skip and is correctly still not an expense
         * section.) NOT a department token-match (retired by W-022): that resolves a DEPARTMENT, and
         * it needs department_mappings, which is empty here — Step 3 runs before Step 5. */
        const tplCat = (g.gl_category || '').trim();
        const onExpenseSection = tplCat
            ? IS_CATEGORY_LABELS.includes(matchISCategory(tplCat))
            : suggestISCategory(g.gl_category, g.gl_name) !== 'Skip';
        const looksPayroll = g.statement_type === 'IS'
            && !!matchPayrollRole(g.gl_name)
            && onExpenseSection;
        if (saved) {
            cat = saved.is_category || '';
            key = saved.is_line_key || '';
            // A saved Skip on a payroll-named line displays as "Handled by Payroll" so the
            // decision round-trips visually. (An explicit operator Skip of a payroll line lands
            // here too — same inert outcome, nicer label; still overrideable.)
            /* ★★ B s2 — READ THE STORED ANSWER; DO NOT RE-GUESS IT.
             *
             * This line used to be `cat === 'Skip' && looksPayroll`, i.e. the chip was
             * RE-DERIVED from the gl_name guess on every render. Nothing about the
             * operator's decision was stored, so it could not survive a re-entry — and a
             * flag-and-block gate could never be cleared, because there was nothing to
             * clear it WITH. s1 added `handled_by` and backfilled it; this reads it.
             *
             * ★ THE FALLBACK IS STILL `looksPayroll`, DELIBERATELY. s2 is
             * BEHAVIOUR-PRESERVING: a NULL handled_by must render exactly as it does
             * today (the guess), NOT as "unresolved". Surfacing NULL as an unresolved
             * state is stage s4's block gate — doing it here would ship a gate before the
             * control that clears it exists, which is the trap AdminPage.js:3847-3850
             * records for the unmapped-IS blocker.
             *
             * MEASURED at the time of writing: on cid=8/40/36 every stored Skip row
             * carries the value the guess produces, and on cid=3 the 15 rows the s1
             * backfill deliberately left NULL fall through to the guess — so this edit
             * moves ZERO chips on any live tenant. */
            handledBy = saved.handled_by || null;
            payrollHandled = payrollChipFor(cat, handledBy, looksPayroll);
        } else if (autoSkip) {
            /* W-068 F1 (Brian 09-29): a total-looking or computed row is PRE-SET to Skip and MARKED -- "suggested -- looks
             * like a total row -- confirm or change" -- instead of being skipped silently. Any edit clears the mark; a
             * row the operator maps to a real section is booked like any other line (isExcludedComputedRow). A SAVED row
             * never reaches this branch, so no live tenant's Step 3 changes. */
            cat = 'Skip';
            categorySuggested = true;
        } else if (autoEquity) {
            // Model-computed equity leaf — mapped, never skipped. Read-only in the grid.
            cat = 'Equity';
        } else if (looksPayroll) {
            cat = 'Skip';
            payrollHandled = true;
        } else if (g.statement_type === 'IS') {
            cat = suggestISCategory(g.gl_category, g.gl_name);
            key = suggestISLineKey(g.gl_category, g.gl_name, cat);
            categorySuggested = !!cat && !templateISCategory(g.gl_category);
        } else {
            cat = suggestBSCategory(g.gl_category, g.gl_name);
            categorySuggested = !!cat && !templateBSCategory(g.gl_category);
        }
        maps.push({
            gl_name: g.gl_name, gl_category: g.gl_category, statement_type: g.statement_type,
            is_category: cat, is_line_key: key, payrollHandled, autoEquity, categorySuggested,
            handled_by: handledBy,
            /* ★ B s3 — the guess, carried onto the row. `categoryOptionValueFor` needs it to
             * decide which option a row with handled_by=NULL should SHOW, and updateMapping
             * needs it to re-derive the same answer when the operator clears back to NULL.
             * Without it, a clear could not reproduce the load-time state. */
            looksPayroll,
            display_name: saved?.display_name || g.gl_name,
            annual_total: r2(g.total), autoSkip,
            id: saved?.id ?? null,
            // Stored values win over a fresh inference (an operator override — or a manual
            // reorder — must survive a reload). classifyIsMappings reads these.
            savedRole: saved?.role || null,
            savedSubsection: saved?.subsection || null,
            savedDisplayGroup: saved?.display_group || null,
            // W-022: a line SAVED as a role-bearing row has ANSWERED its grouping, even when the
            // answer is blank -- classifyIsMappings then keeps the blank instead of refilling it.
            savedGroupAnswered: !!(saved && saved.role),
            // The template's own tag for this row (historical_gl_lines.display_group). Ranked
            // BELOW savedDisplayGroup and ABOVE the token-match — see classifyIsMappings.
            // UNAMBIGUOUS ONLY: a group whose rows disagree contributes nothing and falls through,
            // exactly as the token-match does when it matches two departments.
            rowDisplayGroup: g.dgSeen.size === 1 ? [...g.dgSeen][0] : null,
            // Batch 1b (L3597): every group this ONE row covers, in workbook order (display only -- not in the save payload)
            groupsCovered: [...g.dgSeen],
            savedSortOrder: saved?.sort_order ?? null,
            // The key AS PERSISTED. A save that changes it is a re-key — and if anything
            // joins the old key, that is a silent orphaning. step3Rekeys compares against this.
            savedLineKey: saved?.is_line_key || null,
        /* ★ C5 — the BS/revenue half of the same fact. `is_line_key` is IS-only and NULL on
         * revenue rows, so without this the re-key block was blind to exactly the two
         * populations Build 1 pinned. Carried beside it rather than merged into it: the two
         * columns are disjoint and must stay legible as separate answers. */
        savedLeafKey: saved?.leaf_key || null,
        /* ★ THE CURRENT VALUE OF THE SAME FACT — and its absence was the whole defect.
         * `savedLeafKey` above is the key AS PERSISTED; this is the key the row CARRIES NOW,
         * which is what `leafKeyFor` (is_line_key ?? leaf_key) reads. C5 (6a362f9) widened the
         * SAVED side of step3Rekeys to see the BS+revenue populations but left the CURRENT side
         * with only `is_line_key` — NULL by design on exactly those rows — so `currentKeyOf`
         * was structurally null, could never equal a real saved key, and the blocker fired
         * UNCONDITIONALLY on every revenue row: 5/6/6/6 on cid=3/8/36/40 since 2026-09-02.
         * ⛔ THE FIX IS THIS FIELD, NOT A LOOSER PREDICATE. A blocker that stops firing is
         *   worse than one that over-fires (§12.5: a check that cannot fire reads as green);
         *   with the field present it goes silent on correct data and STILL names a genuine
         *   re-key — measured, both populations. It pairs with is_line_key/savedLineKey
         *   exactly as those two already pair. */
        leaf_key: saved?.leaf_key || null,
        /* Commission ship 2b: the STORED pay type of a "Handled by Payroll" row (gl_line_mappings.payroll_paytype). Seeded
         * below by withPayrollPaytype: the stored answer, else the name's suggestion (marked), else blank. */
        payroll_paytype: saved?.payroll_paytype || null,
        paytypeSuggested: false,
        });
    }
    for (let i = 0; i < maps.length; i++) maps[i] = withPayrollPaytype(maps[i]);
    classifyIsMappings(maps, glLines, deptMappings);
    console.log('[buildMappings] result: saved applied:', savedCount, '| auto-suggested:', autoCount);
    if (savedCount === 0 && existingMappings.length > 0) {
        // Debug: show first few lookup keys from both sides
        const glKeys = groupOrder.slice(0, 3);
        const savedKeys = Object.keys(savedLookup).slice(0, 3);
        console.log('[buildMappings] MISMATCH DEBUG - first GL keys:', glKeys);
        console.log('[buildMappings] MISMATCH DEBUG - first saved keys:', savedKeys);
    }
    return maps;
}

// ══════════════════════════════════════════════════════════════
// Financials import payload builder
// ══════════════════════════════════════════════════════════════
// ★ Operator display label -> snake is BS_DISPLAY_TO_SNAKE (utils/bsCategoryVocab).

/* ★ STEP-5c SLUG SOURCE — the operator's GROUP NAME is the id, once.
 *
 * The display_group slug composes the payroll IS-line key (buildPayrollIsLineKey →
 * `sm_sales_mktg_wages`) and those keys are PERSISTED in is_actuals, so the slug is an
 * IDENTITY, not a caption. It used to be `deptSlug(<anchor department name>)` with no way to
 * express a group name — which meant a merged "Sales + Marketing" block was called
 * `marketing`, and a block named after its own IS category (giving the collapsed
 * `rd_wages` form via buildPayrollIsLineKey's middle-drop rule) was unreachable entirely.
 *
 * PRECEDENCE, in order:
 *   (a) the row's SAVED slug, IF the row is still in the group it was saved in — THE FREEZE.
 *       A later Group Name edit then changes the caption only. Gated on the saved value and
 *       NOT on the has-history check, deliberately: that check
 *       (/^(cogs|sm|ga|rd)_(.+)_(wages|…)$/) needs a middle segment, so it is BLIND to the
 *       collapsed two-part keys — measured on cid=3, its cogs/ga/rd groups have history and
 *       are not detected. The server also cannot backstop this: its precedence is
 *       `m.display_group || existing || slug`, i.e. an explicit client value WINS, and its
 *       own comment says it trusts the UI. So the guard has to be right here.
 *       ★ The "still in the same group" half matters: without it, moving a department OUT of
 *       a saved group would keep the old slug and silently merge it back.
 *   (b) deptSlug(Group Name) when the operator gave one. Because setDeptGroupLabel writes the
 *       label to EVERY member of a group, every member derives the same slug — the members
 *       agree by construction, with no anchor lookup.
 *   (c) the current group slug (which for an un-merged department is deptSlug(its own name)).
 *       This is today's expression, so a tenant who types nothing gets today's behaviour.
 */
function slugForDeptRow(d) {
    const currentGroup = d.display_group || deptSlug(d.department_name);
    if (d.savedDisplayGroup && currentGroup === d.savedDisplayGroup) return d.savedDisplayGroup;
    const fromLabel = d.display_group_label ? deptSlug(d.display_group_label) : '';
    return fromLabel || currentGroup;
}

/* A row's CURRENT group identity — the same expression the 5c grid's dgOf() uses. */
function deptGroupKey(d) {
    return d.display_group || deptSlug(d.department_name);
}

/* ★ THE GROUP'S ID IS RESOLVED ONCE, FROM ITS ANCHOR — not per row.
 *
 * A per-ROW derivation can split one visual group across two ids, and it is reachable:
 * merge a third department INTO an already-SAVED group and give the block a Group Name, and
 * the saved members stay frozen on the old id while the joiner derives the new one. (Measured
 * — diffs/step5c-preview-build.txt scenario B. It is reachable because dgFrozen() misses the
 * collapsed two-part keys, so "Group with…" is not disabled for such a group.)
 *
 * Resolving from the ANCHOR (lowest sort_order, name tiebreak — the same rule dgAnchor uses)
 * makes every member agree BY CONSTRUCTION, and picks the safe outcome: a joiner ADOPTS the
 * saved group's existing id rather than re-keying it.
 *
 * This is also what lets the grid PREVIEW the id honestly — caption and save read one map.
 */
function resolveDeptGroupIds(rows) {
    const byGroup = new Map();
    for (const d of (rows || [])) {
        if (!d.department_name || !d.is_category) continue;
        const k = deptGroupKey(d);
        if (!byGroup.has(k)) byGroup.set(k, []);
        byGroup.get(k).push(d);
    }
    const out = new Map();
    for (const [k, members] of byGroup.entries()) {
        const anchor = [...members].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
            || a.department_name.localeCompare(b.department_name))[0];
        out.set(k, slugForDeptRow(anchor));
    }
    return out;
}

/* The Step-5c save payload. ONE builder, called by BOTH save handlers (the first save and
   the re-entry edit) — they were character-identical copies, and a slug rule applied to one
   only would be a silent divergence between first-save and re-entry. */
/* ★★ ZERO DEPARTMENT MAPPINGS IS ALWAYS AN ERROR — §12 item-7 residue (iii), closed here.
 *
 * THE HOLE, measured to its exact mechanism: the 5c Continue button gates on
 *     const allMapped = step5DeptMap.every(d => d.is_category);
 * and `Array.prototype.every()` on an EMPTY array is VACUOUSLY TRUE. So a step5DeptMap of
 * length 0 enabled the button, both save handlers then took `if (mappings.length > 0)` and
 * skipped the write, and the wizard advanced to 5d having stored NOTHING. The tenant lands
 * live with no department_mappings — where PayrollPage's Modal falls back to
 * LEGACY_SEMITEK_DEPTS and offers Semitek's department names to an unrelated company.
 *
 * AND IT IS REACHABLE WITH A REAL ROSTER: parseEmployeeExcel only records a department when
 * the cell is non-empty (`if (deptVal)` at :327), so a sheet whose department column is
 * blank throughout yields employees but ZERO departments. The 5a gate rejects an EMPTY
 * roster ("No employees found in the file"), so there is no legitimate zero-department
 * case to protect: employees exist, and every employee has a department.
 *
 * Returns a named operator message, or null when the step is safe to save. */
function deptMappingsBlocker(rows, employeeCount) {
  const pairs = buildDeptMappingsPayload(rows);
  if (pairs.length > 0) return null;
  if (!employeeCount) return null;   // no roster at all — 5a already refuses this
  const named = (rows || []).filter(d => d.department_name).length;
  return named === 0
    ? 'No departments were found in the roster. Every employee needs a department — it is what '
      + 'routes their pay to an income-statement category. Add a Department column to the file '
      + '(or fill in the blanks) and re-upload at Step 5a.'
    : 'No department has an income-statement category. Pick a category for each department '
      + 'below — a department with none routes its payroll nowhere and its cost disappears '
      + 'from the P&L.';
}

function buildDeptMappingsPayload(rows) {
    const ids = resolveDeptGroupIds(rows);
    return (rows || [])
        .filter(d => d.department_name && d.is_category)
        .map(d => ({
            department_name: d.department_name, is_category: d.is_category,
            slug: deptSlug(d.department_name),
            display_group: ids.get(deptGroupKey(d)),
            sort_order: d.sort_order ?? 0,
            // Written to EVERY member of the group, so the DB rows agree on the label.
            display_group_label: d.display_group_label || null,
        }));
}

/* W-022 (B): the payroll workbook rows the Step-5c back-fill will REFUSE, for the departments on
   screen. The SAME derivation the save runs (derivePerDgPayrollActuals, over the mapping payload
   buildDeptMappingsPayload writes and the window backfillPerDgPayroll uses), so the notice and the
   save cannot disagree. A refusal is a payroll row with no resolvable department group -- its
   history is NOT written. Returns [] until the inputs exist. Each item: { gl_name, gl_category,
   reason, amount }. */
function step5PayrollRefusedOf(src, deptRows, obState) {
    if (!src || !(src.glLines || []).length) return [];
    const months = buildAllMonths(obState?.historical_start, obState?.close_month);
    if (!months.length) return [];
    const depts = buildDeptMappingsPayload(deptRows);
    if (!depts.length) return [];
    return derivePerDgPayrollActuals(src.glLines, src.mappings || [], depts, months).failures;
}

/* Batch 1b (2026-11-03, L3624) -- THE STEP-5c SAFETY NET. A payroll-history row whose group TEXT (the template's display_group) names
   no department group of its section is written by the back-fill onto a key no statement line carries: its history reaches no
   line (2026-10-28, SubBase's "Cost of Revenue" before its Group Name was typed -- P3 + P4 at go-live; diffs/step5-group-slug-
   fix-build.txt V6). READ FROM THE BACK-FILL'S OWN OUTPUT (derivePerDgPayrollActuals' keys, over the payload the save writes), so
   the refusal and the save cannot disagree; the group's text is named through groupIdOf (the one helper). Payroll history only --
   a vendor-only template group (SubBase's "Marketing") is never checked. Returns [{ section, group, text, keys }] ([] until the
   inputs exist). */
const SECTION_LABEL_OF_SLUG = Object.fromEntries(Object.entries(IS_CATEGORY_DISPLAY_TO_SLUG).filter(([, s]) => s).map(([d, s]) => [s, d]));
function step5PayrollGroupMismatchesOf(src, deptRows, obState) {
    if (!src || !(src.glLines || []).length) return [];
    const months = buildAllMonths(obState?.historical_start, obState?.close_month);
    if (!months.length) return [];
    const depts = buildDeptMappingsPayload(deptRows);
    if (!depts.length) return [];
    const known = new Set();   // section slug | department group id, as the save writes them
    for (const d of depts) { const cat = IS_CATEGORY_DISPLAY_TO_SLUG[d.is_category]; if (cat) known.add(cat + '|' + (d.display_group || deptSlug(d.department_name))); }
    const { actuals } = derivePerDgPayrollActuals(src.glLines, src.mappings || [], depts, months);
    const textOf = new Map();
    for (const r of src.glLines) { if (r.statement_type !== 'IS' || !r.display_group) continue; const id = groupIdOf(r.display_group); if (id && !textOf.has(id)) textOf.set(id, String(r.display_group).trim()); }
    const out = new Map();
    for (const key of Object.keys(actuals)) {
        const cat = PAYROLL_INDEX_CATS.find(c => key.startsWith(c + '_'));
        const pt = cat && PAYTYPES.find(p => key.endsWith('_' + p));
        if (!pt) continue;
        const group = key.slice(cat.length + 1, key.length - pt.length - 1);
        if (!group || known.has(cat + '|' + group)) continue;   // no group segment (a collapsed key), or a real department group
        const k = cat + '|' + group;
        if (!out.has(k)) out.set(k, { section: SECTION_LABEL_OF_SLUG[cat] || cat, group, text: textOf.get(group) || group, keys: [] });
        out.get(k).keys.push(key);
    }
    return [...out.values()];
}
/* the refusal text, one owner (both Step-5c save handlers and the notice) */
function step5GroupMismatchMessage(list) {
    const named = list.map(x => '"' + x.text + '" (' + x.section + ')').join(', ');
    return 'Not saved: the payroll history in your workbook is grouped under ' + named + ', which ' + (list.length === 1 ? 'is' : 'are') +
        ' not the Group Name of any department in ' + (list.length === 1 ? 'that section' : 'those sections') + ', so that history would reach no income-statement line. ' +
        'Type the workbook\u2019s group text as the Group Name of the department it belongs to (or correct the display_group column and re-upload it at Step 2), then save.';
}

/* ★ FAIL LOUD ON A SLUG COLLISION. Deriving the slug from operator text reintroduces a
   hazard the department-name design had removed: two groups named "Ops" both resolve to
   `ops` and their payroll would SILENTLY MERGE into one set of IS leaves. Merging is meant
   to be an explicit "Group with…" action, never a consequence of typing. Returns
   { slug, groups } for the first clash, or null. */
function findDeptSlugCollision(rows) {
    const ids = resolveDeptGroupIds(rows);
    const nameFor = new Map();
    for (const d of (rows || [])) {
        if (!d.department_name || !d.is_category) continue;
        const k = deptGroupKey(d);
        if (!nameFor.has(k)) nameFor.set(k, d.display_group_label || d.department_name);
    }
    const bySlug = new Map();
    for (const [groupKey, slug] of ids.entries()) {
        if (!bySlug.has(slug)) bySlug.set(slug, []);
        bySlug.get(slug).push(nameFor.get(groupKey) || groupKey);
    }
    for (const [slug, groups] of bySlug.entries()) {
        if (groups.length > 1) return { slug, groups };
    }
    return null;
}

/* Slug for a department name — the default display_group (Step 5c).
   ★ item 7 (slug unification): the local copy that stood here is DELETED; `deptSlug`
   is now imported from utils/seatDepartments.js. It differed from the shared one by
   omitting the `|| "dept"` empty-result fallback, so a department whose name contains
   no alphanumerics ("---") slugged to "" here and to "dept" everywhere else. NO REAL
   DEPARTMENT IS AFFECTED: all 23 distinct department identifiers across cid=1/2/3
   slug identically under both (measured — see diffs/item7-slug-verify.txt), so this
   is a proven no-op on live data; it only removes the drift surface. */

/* ★ normalizeBSKey MOVED to utils/bsKey.js (the B7 §6 dedup) so the Step-4b review bridge
 * calls the SAME function instead of its own copy. The body moved VERBATIM — the keys in
 * the database were produced by exactly that regex sequence. All four call sites in this
 * file are unchanged and now resolve to the import above. */

/* groupGLLines MOVED to utils/revKeys.js (W-082, 2026-09-30) -- the Step-4b bridge reads the same grouping. */

/* ★ Build A (D1) — THE ONE bs_actuals key derivation, extracted so it has a single home.
 *
 * A balance-sheet leaf's key is `normalizeBSKey(display_name || gl_name)` plus a dedupe counter in
 * group order. That key IS the model's BS leaf id: buildFinancialsPayload writes bs_actuals and
 * bs_actuals_meta under it, and buildBSLinesForCompany then emits rows with exactly those ids. The
 * Step-4b screen now has to key its OWN values by the same id to hand them to the model's skeleton
 * — and the one thing it must not do is derive the key a second time. This returns a resolver both
 * the import path and the review path call.
 *
 * Returns (glName, glCategory) => key|null. Null means the line has no place on the BS (unmapped or
 * Skip) — the caller decides what to do, and the skeleton evaluator reports it rather than dropping
 * it silently.
 */
function buildBsKeyResolver(glLines, mappings) {
    const mlookup = new Map();
    for (const m of mappings || []) mlookup.set(`${m.statement_type}|${m.gl_name}|${m.gl_category || ''}`, m);
    const counts = new Map();
    const byLine = new Map();
    const used = reservedLeafKeys(mappings);   // W-078: a derived key never takes a key another line holds
    for (const g of groupGLLines(glLines)) {
        if (g.statement_type !== 'BS') continue;
        const m = mlookup.get(`BS|${g.gl_name}|${g.gl_category || ''}`);
        if (!m || !m.is_category || m.is_category === 'Skip') continue;
        if (!BS_DISPLAY_TO_SNAKE[m.is_category]) continue;
        /* ★ THE PINNED KEY WINS. gl_line_mappings.leaf_key is this leaf's stored identity
         * (Build 1, b710c10), so the key no longer follows the LABEL and a rename cannot
         * re-key it. Every row that exists today carries one — the backfill was proven
         * 0-cell against the live bs_actuals_meta keys on all four tenants — so in practice
         * this branch always fires and the derive below is dead for current data. */
        const pinned = leafKeyFor(m);
        if (pinned) {
            /* register it so a later DERIVED key cannot collide with a pinned one */
            counts.set(pinned, Math.max(counts.get(pinned) || 0, 1));
            byLine.set(`${g.gl_name}|${g.gl_category || ''}`, pinned);
            continue;
        }
        /* ⚠ THE DERIVE FALLBACK IS LOAD-BEARING — DO NOT DELETE IT. A line added to the
         * workbook AFTER the Build-1 backfill has no leaf_key yet, and it must still get a
         * key. This is the CREATE path, still label-derived until the add-a-line build gives
         * a new line a pinned key at birth. It fires for 0 rows on all four tenants today. */
        const base = normalizeBSKey(m.display_name || g.gl_name);
        if (!base) continue;
        let n = (counts.get(base) || 0) + 1;
        let key = n === 1 ? base : `${base}_${n}`;
        while (used.has(key)) { n++; key = `${base}_${n}`; }   // W-078: only in the mixed case (see reservedLeafKeys)
        counts.set(base, n);
        used.add(key);
        byLine.set(`${g.gl_name}|${g.gl_category || ''}`, key);
    }
    return (glName, glCategory) => byLine.get(`${glName}|${glCategory || ''}`) || null;
}

/* W-078 -- the rows as the Step-3 save will LEAVE them: every stored row the save does not send, plus every row
 * it sends, carrying the STORED leaf_key when the row already exists (the server writes leaf_key only on INSERT).
 * That is exactly what the Step-4 import will read, so a key resolved over it is the key Step 4 would book. */
function step3StoredImage(storedRows, payloadRows) {
    const id = r => `${r.statement_type}|${r.gl_name}|${r.gl_category || ''}`;
    const stored = new Map((storedRows || []).map(r => [id(r), r]));
    const sent = new Set((payloadRows || []).map(id));
    const image = (storedRows || []).filter(r => !sent.has(id(r)));
    for (const p of payloadRows || []) {
        const st = stored.get(id(p));
        image.push({ ...p, leaf_key: st ? (st.leaf_key || null) : null });
    }
    return image;
}

/* W-078 -- the key a Step-3 row is born with: a BS row's from buildBsKeyResolver, a revenue row's from
 * buildRevKeyResolver -- the SAME two resolvers the Step-4 import calls, never a second derivation. An IS row
 * with an is_line_key (and anything else) gets none: its identity is the is_line_key, unchanged. */
function buildStep3LeafKeyResolver(glLines, image) {
    const bs = buildBsKeyResolver(glLines, image);
    const rev = buildRevKeyResolver(glLines, image);
    return row => {
        if (!row) return null;
        if (row.statement_type === 'BS') return bs(row.gl_name, row.gl_category);
        if (row.statement_type === 'IS' && !row.is_line_key) return rev(row.gl_name, row.gl_category);
        return null;
    };
}

/* ★ Build 1 (PART 3a) — resolve the Step-4b sweep account, PREFERRING the durable copy.
 *
 * The sweep pick has TWO homes. onboarding_state.steps.review_financials.sweep_account_key is
 * written by the import (server.js:2462-2468) and is FRAGILE — steps is a whole-blob JSONB column
 * and several wizard writers replace rather than merge it, so the key is `null` on cid=3 AND cid=8
 * today. bs_actuals_meta's role='sweep_target' leaf is written by the same import and is DURABLE —
 * present and correct on both tenants. Prefer the durable one.
 *
 * Falling through to the label heuristic is what produced the bug this fixes: no tenant's sweep is
 * literally named "money market", so it resolved to '' and the money market was silently counted as
 * ordinary cash. The heuristic is kept LAST (it is the only signal a pre-import screen has), and
 * failing to resolve is now reported by computeCFS rather than absorbed (PART 3b).
 *
 * Returns { key, glName, source } — source is for diagnosis, not display.
 */
function resolveStep4Sweep(bsOpts, bsActualsMeta, savedKey) {
    const opts = bsOpts || [];
    const metaSweepKey = Object.keys(bsActualsMeta || {})
        .find(k => bsActualsMeta[k] && bsActualsMeta[k].role === 'sweep_target') || null;
    const byKey = k => (k ? opts.find(o => o.key === k) : null);

    const fromMeta = byKey(metaSweepKey);
    if (fromMeta) return { key: fromMeta.key, glName: fromMeta.gl_name, source: 'bs_actuals_meta' };

    const fromSaved = byKey(savedKey);
    if (fromSaved) return { key: fromSaved.key, glName: fromSaved.gl_name, source: 'onboarding_state' };

    const heuristic = opts.find(o => /money market|\bmm\b|sweep/i.test(o.label));
    if (heuristic) return { key: heuristic.key, glName: heuristic.gl_name, source: 'label_heuristic' };

    return { key: '', glName: null, source: 'unresolved' };
}

/* ★ Build 1 (PART 2) — the operator's "Total Cash" rollup, for the Step-4b Balance Sheet.
 *
 * Members come from the operator's own answers, never from a keyword guess:
 *   · IMPORT path   — the `classification` map just submitted ('rollup' answers), plus the label
 *                     they typed on Step 4a.
 *   · RE-ENTRY path — bs_actuals_meta leaves with subcategory 'clearing', which is what that same
 *                     classification was persisted as, plus the stored total_clearing label.
 * Both are translated to gl_names here because the bridge matches BS lines by name; bsOpts is the
 * one place holding both the normalized key and the gl_name for a leaf.
 */
function buildStep4CashRollup(bsOpts, { classification, bsActualsMeta, label }) {
    const opts = bsOpts || [];
    let keys = [];
    if (classification) {
        keys = Object.keys(classification).filter(k => classification[k] === 'rollup');
    } else if (bsActualsMeta) {
        keys = Object.keys(bsActualsMeta).filter(k => bsActualsMeta[k] && bsActualsMeta[k].subcategory === 'clearing');
    }
    if (!keys.length) return null;
    const memberGlNames = keys.map(k => opts.find(o => o.key === k)).filter(Boolean).map(o => o.gl_name);
    if (!memberGlNames.length) return null;
    // Label precedence: what the operator typed / what was stored — never a literal here.
    const stored = bsActualsMeta && bsActualsMeta.total_clearing;
    const resolved = label || (stored && (stored.display_label || stored.label)) || null;
    return resolved ? { label: resolved, memberGlNames } : { memberGlNames };
}

// ── THE ONE BS-LEAF OPTION BUILDER ──────────────────────────────────────────────────
//
// Serves BOTH Step-4a panels: the current-asset cash classification, and (B5 s5) the
// non-current-asset gross-vs-contra classification. The ONLY difference between them is
// which is_category values are admitted — so that is the parameter, and the body is
// shared (§6: extract, never copy; a second body is where the two would drift).
//
// ★ `key` now comes from buildBsKeyResolver — THE ONE bs_actuals key derivation — not
// from a private counter. The old private counter only counted the leaves this builder
// KEPT, while buildFinancialsPayload's counter runs over EVERY BS leaf; the two would
// hand the same leaf different keys the moment two BS lines in different categories
// normalized to the same base, and the panel's row identity would stop matching
// bs_actuals_meta. MEASURED across cid 3/8/36/40 before the switch: 0 base collisions,
// 0 key disagreements — so this is a no-op on live data AND the latent divergence is gone.
//
// `opening` is the leaf's balance at priorDec, used for the live "Total Cash" preview
// (the same sum written to cfs_dec23.total_clearing).
/* ★★ BUILD A — ROLE -> STEP-4a ANSWER, for the RE-ENTRY SEED.
 *
 * Mirrors how the s5 asset panel builds CONTRA_ROLES / GROSS_ROLES out of BS_ASSET_ROLES:
 * the sets are DERIVED from the vocabulary, never spelled as inline literals, so a role
 * rename cannot leave this seed silently reading a string nothing produces any more.
 *
 * ⓘ 'sweep' is deliberately ABSENT from these sets. The sweep answer has exactly one owner
 * on this screen — `step4SweepKey`, restored on re-entry by resolveStep4Sweep, which
 * already PREFERS the stored role='sweep_target' leaf. `step4AnswerOf` short-circuits on it
 * before it ever consults step4Cls, so seeding it here would be a second source of truth
 * for one answer — the two-way sync the cash-panel comment explicitly refuses.
 *
 * Everything that is not cash/clearing on a CURRENT-ASSET leaf was answered 'separate':
 * accounts_receivable (all three subs) and prepaid. Expressed as "not rollup" rather than
 * as a positive list so a NEW separate-row role cannot silently fall through to 'rollup'
 * and be folded into Total Cash — the failure that would actually move money. */
/* ★★ BUILD B — DOES THIS ROW NEED THE SUB-QUESTION? Exactly one condition: the operator
 * pulled it out of the rollup AND its name matches none of the three known patterns. The
 * name test comes from the ONE exported helper in inferBsMeta — the panel does not own a
 * copy of those regexes, so what the screen asks and what the import decides cannot drift.
 * ⓘ A Step-4a option carries `label` and `gl_name`, which is all the helper reads. */
/* 2026-09-24 -- THE STEP-4a TYPE PICKER (it replaces Build B's sub-question, which was asked
 * only when no name test matched). EVERY Separate current asset, current liability and equity
 * line now shows its TYPE:
 *   - the operator's answer (step4Cls, keyed by bsKey like every other Step-4a answer) wins;
 *   - else the classifier's own name test SUGGESTS (bsTypeSuggestion -- no copy of the patterns
 *     here), and the row is marked "suggested -- confirm or change";
 *   - else the row is BLANK, required, and blocks import by name.
 * Returns { section, answer, suggested } (answer null when blank), or null for a row with no
 * type question: a current asset in Total Cash or the sweep, or a long-term liability (debt). */
const step4LeafOf = o => ({ label: o.label, gl_name: o.gl_name, category: BS_DISPLAY_TO_SNAKE[o.is_category] });
const step4SectionOf = o => bsTypeSection(BS_DISPLAY_TO_SNAKE[o.is_category]);
const step4TypeOf = (o, parentAnswer, cls) => {
    const section = step4SectionOf(o);
    if (!section) return null;
    let own;
    if (section === 'separate') {
        if (!isSeparateAnswer(parentAnswer)) return null;
        own = parentAnswer === SEPARATE_ANSWERS.base ? null : parentAnswer;
    } else {
        own = (cls || {})[o.key] || null;
    }
    if (own) return { section, answer: own, suggested: false };
    const s = bsTypeSuggestion(step4LeafOf(o));
    return { section, answer: s, suggested: !!s };
};
/* EVERY row still blank: current assets pulled out as Separate, then liability / equity rows. */
const step4TypeUnansweredOf = (cashOpts, typeOpts, answerOf, cls) => [
    ...(cashOpts || []).filter(o => { const t = step4TypeOf(o, answerOf(o), cls); return !!t && !t.answer; }),
    ...(typeOpts || []).filter(o => { const t = step4TypeOf(o, undefined, cls); return !!t && !t.answer; }),
];
/* A formula type (retained earnings, current-year net income) may be held by ONE line: the
 * engine finds each by role. Returns [{ type, labels }] for every type held by more than one. */
const step4FormulaClashOf = (typeOpts, cls) => {
    const by = new Map();
    for (const o of typeOpts || []) {
        const t = step4TypeOf(o, undefined, cls);
        const d = t && t.answer ? bsTypeDriver(t.section, t.answer) : null;
        if (!d || d.driver !== 'formula') continue;
        if (!by.has(t.answer)) by.set(t.answer, []);
        by.get(t.answer).push(o.label);
    }
    return [...by.entries()].filter(([, labels]) => labels.length > 1)
        .map(([answer, labels]) => ({ type: BS_TYPE_OPTIONS.equity.find(x => x.answer === answer).label, labels }));
};
const step4FormulaClashText = clashes => clashes.map(c =>
    'Only one line can be "' + c.type + '" -- ' + c.labels.join(', ') + ' are all set to it.').join(' ');
/* The forecast a type selects, in words -- read from the classifier (bsTypeDriver), never restated. */
const step4DriverNote = (section, answer) => {
    const d = bsTypeDriver(section, answer);
    const opt = (BS_TYPE_OPTIONS[section] || []).find(x => x.answer === answer);
    if (!d || !opt) return '';
    if (d.driver === 'dso') return 'Forecast from DSO (' + d.days + ' days of revenue).';
    if (d.driver === 'dpo') return 'Forecast from DPO (' + d.days + ' days of vendor spend).';
    if (d.driver === 'formula') return 'A formula line, rolled from net income.';
    if (d.driver === 'module_link') return opt.role === 'prepaid' ? 'Forecast from the prepaid schedule.'
        : 'Forecast from a revenue module -- link it to the module on Step 7.';
    if (opt.role === 'short_term_debt') return 'Held constant; its changes are financing on the cash flow.';
    return 'Held constant.';
};

const STEP4_ROLLUP_ROLES = new Set([BS_ROLES.CASH, BS_ROLES.CLEARING]);
const step4AnswerFromRole = (role) =>
    (!role || role === BS_ROLES.SWEEP_TARGET) ? null
        : STEP4_ROLLUP_ROLES.has(role) ? 'rollup' : 'separate';

const STEP4_CURRENT_ASSET_CATS = ['Current Assets'];
const STEP4_ASSET_SUBTYPE_CATS = ['Fixed Assets', 'Intangible Assets'];

function buildBSLeafOptions(glLines, mappings, priorDec, isCategories) {
    const admit = new Set(isCategories);
    const mlookup = new Map();
    for (const m of mappings || []) {
        mlookup.set(`${m.statement_type}|${m.gl_name}|${m.gl_category || ''}`, m);
    }
    const resolveKey = buildBsKeyResolver(glLines, mappings);
    const options = [];
    for (const g of groupGLLines(glLines)) {
        if (g.statement_type !== 'BS') continue;
        const m = mlookup.get(`${g.statement_type}|${g.gl_name}|${g.gl_category || ''}`);
        if (!m || !admit.has(m.is_category)) continue;
        const key = resolveKey(g.gl_name, g.gl_category);
        if (!key) continue;
        options.push({
            key, label: m.display_name || g.gl_name, gl_name: g.gl_name, gl_category: g.gl_category,
            is_category: m.is_category,
            // The fixed/intangible sub-type, READ from the one vocabulary (B5 s1) rather
            // than re-derived. Null for a current asset; the asset panel words its contra
            // option from it so an intangible row says "amortization", not "depreciation".
            subType: BS_SNAKE_TO_SUBTYPE[BS_DISPLAY_TO_SNAKE[m.is_category]] || null,
            opening: priorDec ? r2(g.amounts[priorDec] || 0) : 0,
        });
    }
    return options;
}

// The sweep-account dropdown AND the Step-4 cash classification panel.
function buildBSCurrentAssetOptions(glLines, mappings, priorDec) {
    return buildBSLeafOptions(glLines, mappings, priorDec, STEP4_CURRENT_ASSET_CATS);
}

// ★ B5 s5 — the Fixed / Intangible asset leaves. These have never had an operator row:
// gross-vs-contra was inferred from the account NAME. See renderStep4AssetPanel.
function buildBSAssetOptions(glLines, mappings, priorDec) {
    return buildBSLeafOptions(glLines, mappings, priorDec, STEP4_ASSET_SUBTYPE_CATS);
}

// 2026-09-24 -- the liability, long-term liability and equity leaves of the type picker.
const STEP4_TYPE_CATS = ['Current Liabilities', 'LT Liabilities', 'Equity'];
function buildBSTypeOptions(glLines, mappings, priorDec) {
    return buildBSLeafOptions(glLines, mappings, priorDec, STEP4_TYPE_CATS);
}

// Build the full financials import payload from historical_gl_lines + mappings.
//
// `classification` = { [bsKey]: 'sweep' | 'rollup' | 'separate' } — the operator's
// Step-4 cash answers. Current-asset leaves with no answer default to 'rollup'.
// `totalCashLabel` overrides the Total Cash rollup's label (blank ⇒ no override
// entry ⇒ buildBSLines falls back to the static 'Total Clearing').
//
// Before this existed, onboarding wrote only {label, category, gl_name, gl_category}
// and no cfs_dec23.total_clearing — so a new tenant got an EMPTY total_clearing rollup
// and a $0 opening: a silently broken sweep. inferBsMeta now classifies every leaf.
function buildFinancialsPayload(glLines, mappings, months, priorDec, sweepKey, classification, totalCashLabel, deptMappings) {
    const mlookup = new Map();
    for (const m of mappings || []) {
        mlookup.set(`${m.statement_type}|${m.gl_name}|${m.gl_category || ''}`, m);
    }
    const groups = groupGLLines(glLines);
    const bsMonths = priorDec ? [priorDec, ...months] : [...months];

    // IS → is_actuals keyed by is_line_key, summed over same key.
    // Revenue lines (unmapped or Skip-category) fall back to `rev_<normalized_gl_name>`
    // so they still flow into totalRevenue when the runtime engine consumes this.
    const is_actuals = {};
    const resolveRevKey = buildRevKeyResolver(glLines, mappings);   // W-078: THE ONE owner (Step 3 pins the same key)
    const addToBucket = (key, g) => {
        if (!is_actuals[key]) is_actuals[key] = {};
        for (const month of months) {
            is_actuals[key][month] = r2((is_actuals[key][month] || 0) + (g.amounts[month] || 0));
        }
    };
    for (const g of groups) {
        if (g.statement_type !== 'IS') continue;
        const m = mlookup.get(`${g.statement_type}|${g.gl_name}|${g.gl_category || ''}`);
        // W-068: a computed-looking row stays out -- unless it is a total-looking row the operator MAPPED at Step 3 (the
        // ONE predicate the Step-4b bridge applies too). This read `if (isComputedRow(g)) continue;` before the lookup.
        if (isExcludedComputedRow(g, m?.is_category)) continue;
        if (m && m.is_line_key) {
            addToBucket(m.is_line_key, g);
            continue;
        }
        /* Fallback: an unmapped or Skip'd line that looks like revenue. W-078: which lines, and under which key
         * (payroll first; the pinned key wins; else rev_<name>), is decided by buildRevKeyResolver -- the ONE
         * owner, which the Step-3 save calls too, so the key booked here is the key Step 3 pinned. */
        const revKey = resolveRevKey(g.gl_name, g.gl_category);
        if (revKey) addToBucket(revKey, g);
    }

    // ★ C-10 (Build C) — SELF-HEALING per-dg payroll. buildFinancialsPayload DROPPED Skip'd payroll
    // above (no is_line_key → no key). When department_mappings exist, derive the per-dg keys
    // (sm_<dg>_<paytype>, + collapsed cogs_wages/… for single-dg categories) and MERGE them into
    // is_actuals so the WHOLE-BLOB write (server data=EXCLUDED.data) always contains them → a
    // Step-4 re-import can never drop them. First pass (Step 4 before Step 5) has no departments →
    // nothing merged; the Step-5c dept-save back-fills the already-written blob. FAIL-LOUD offenders
    // are surfaced (never guessed onto a wrong leaf). Disjoint from the mapped keys above (the
    // derivation skips mapped catch-alls), so a plain merge cannot clobber.
    if (deptMappings && deptMappings.length) {
        const { actuals: perDg, failures, collisions } = derivePerDgPayrollActuals(glLines, mappings, deptMappings, months);
        for (const [k, series] of Object.entries(perDg)) is_actuals[k] = series;
        if (failures.length) console.warn('[perDgPayroll] unresolved payroll rows (NOT written):', failures);
        if (collisions.length) console.warn('[perDgPayroll] key collisions (two gl_categories → one key):', collisions);
    }

    // BS → bs_actuals keyed by normalized display_name/gl_name
    const bs_actuals = {};
    const bs_actuals_meta = {};
    const cfs_dec23 = {};
    const resolveBsKey = buildBsKeyResolver(glLines, mappings);   // W-078: THE ONE owner (Step 3 pins the same key)
    const bsLeaves = [];
    for (const g of groups) {
        if (g.statement_type !== 'BS') continue;
        const m = mlookup.get(`${g.statement_type}|${g.gl_name}|${g.gl_category || ''}`);
        if (!m || !m.is_category || m.is_category === 'Skip') continue;
        const snakeCat = BS_DISPLAY_TO_SNAKE[m.is_category];
        if (!snakeCat) continue;
        const label = m.display_name || g.gl_name;
        /* ★ THE PINNED KEY WINS — see buildBsKeyResolver. `label` above is still the
         * operator's LABEL and still what bs_actuals_meta records; it just no longer
         * decides the KEY. That separation is the entire fix. */
        /* W-078: the key comes from buildBsKeyResolver -- the pinned key wins, else the label-derived key with its
         * `_${n}` dedupe. This loop carried an INLINE copy of that rule (a second derivation); it now calls the owner. */
        const bsKey = resolveBsKey(g.gl_name, g.gl_category);
        if (!bsKey) continue;

        bs_actuals[bsKey] = {};
        for (const month of bsMonths) {
            bs_actuals[bsKey][month] = r2(g.amounts[month] || 0);
        }
        bs_actuals_meta[bsKey] = {
            label,
            category: snakeCat,
            gl_name: g.gl_name,
            gl_category: g.gl_category,
        };
        cfs_dec23[bsKey] = priorDec ? r2(g.amounts[priorDec] || 0) : 0;
        bsLeaves.push({ bsKey, label, category: snakeCat, gl_name: g.gl_name, gl_category: g.gl_category });
    }

    // ── Classify every BS leaf (roles/subcategories/display_order/cfs_id) ───────
    // Throws on an unclassifiable leaf — never guesses. inferBsMetaAll also
    // NORMALIZES `category` onto the engine's five buckets (Step 3's 'fixed_assets' /
    // 'intangible_assets' / 'lt_liabilities' are NOT engine categories — leaves
    // carrying them are silently dropped from the BS).
    const inferred = inferBsMetaAll(bsLeaves, classification);
    for (const [bsKey, cls] of Object.entries(inferred)) {
        bs_actuals_meta[bsKey] = { ...bs_actuals_meta[bsKey], ...cls };
    }

    // Total Cash rollup label — a display_label override on the synthetic
    // `total_clearing` line (Session A's pattern, buildBSLines.js). Blank ⇒ omit the
    // entry entirely ⇒ the builder falls back to the static 'Total Clearing'.
    const tcLabel = (totalCashLabel || '').trim();
    if (tcLabel && tcLabel !== 'Total Clearing') {
        bs_actuals_meta.total_clearing = { display_label: tcLabel };
    }

    // ★ The opening the sweep seeds from. `total_clearing` is a SYNTHETIC rollup, never
    // a GL leaf, so the per-leaf loop above never writes it — and `dec23.total_clearing
    // || 0` then makes computeCFS start the sweep from $0. That is exactly what silently
    // zeroed AE Model's sweep. Sum the rollup members the operator just confirmed.
    cfs_dec23.total_clearing = r2(
        rollupMemberKeys(bsLeaves, classification).reduce((s, k) => s + (cfs_dec23[k] || 0), 0)
    );

    // cfs_sweep = per-month delta of selected sweep account balance (first month uses priorDec as prior)
    const cfs_sweep = {};
    const sweepBal = sweepKey ? bs_actuals[sweepKey] : null;
    for (let i = 0; i < months.length; i++) {
        const mo = months[i];
        if (!sweepBal) { cfs_sweep[mo] = 0; continue; }
        const prev = i === 0 ? priorDec : months[i - 1];
        const cur = sweepBal[mo] || 0;
        const prv = (prev != null && sweepBal[prev] != null) ? sweepBal[prev] : 0;
        cfs_sweep[mo] = r2(cur - prv);
    }

    return { is_actuals, bs_actuals, bs_actuals_meta, cfs_sweep, cfs_dec23 };
}

// ══════════════════════════════════════════════════════════════
// Main AdminPage
// ══════════════════════════════════════════════════════════════
// onConfigureCompany(companyId, path) — provided by AppShell: selects the wizard's tenant in
// the NAV (the switcher's own predicate guards it) and navigates to the owning page. Step 9's
// Configure buttons use it so ONE CLICK lands on the RIGHT company's page — the wizard's
// selectedCompanyId is independent of the nav's selectedCompany, and without this wire the
// link landed on whatever tenant the nav held (the 9a-2 live bug).
// onCompaniesChanged() -- provided by AppShell (W-027): re-reads the shell's company list. Called after a
// company is created, after the Step-8 import (it sets is_onboarded) and after go-live, so the switcher and
// onConfigureCompany see the change without a browser reload. A failed read is ignored here (the shell
// also re-reads on a Step-9 miss).
export default function AdminPage({ onConfigureCompany, onCompaniesChanged, user, navCompany, onUserChanged, onWizardCompany }) {
    const companiesChanged = () => { if (onCompaniesChanged) Promise.resolve(onCompaniesChanged()).catch(() => {}); };
    // W-035: ONE audience predicate (utils/audience.js). A tenant user keeps the Admin page but never sees the
    // Onboarding tab (the wizard, "+ New Company", "Open" / "Edit Onboarding") or the Health Check tab.
    const superAdmin = isSuperAdmin(user);
    const visibleTabs = SIDEBAR_TABS.filter(t => (superAdmin || !t.superAdminOnly) && (!t.managersOnly || canManageUsers(user)));
    const [activeTab, setActiveTab] = useState(superAdmin ? 'onboarding' : 'chart-of-accounts');
    const [companies, setCompanies] = useState([]);
    const [onboardingStates, setOnboardingStates] = useState({});
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [showNewCompanyModal, setShowNewCompanyModal] = useState(false);
    const [companiesLoading, setCompaniesLoading] = useState(true);
    const [obState, setObState] = useState(null);
    /* ★★ H1 — THE FORECAST HORIZON, READ FROM ITS OWNER.
       `companies.forecast_through` is a SERVER-owned column. The wizard used to read it off
       the client's in-memory `companies` array, and NOTHING refreshes that array after Step 1
       writes the column: `loadCompanies` has exactly three call sites (the mount effect and
       the two "← Back to Companies" buttons), the Step-1 save is not one of them, and
       `loadWizardState` refreshes neither the array nor the horizon. So in an unbroken
       create → Step 1 → Step 5/6 session every reader saw the row as it was at CREATE time,
       where `forecast_through` is NULL (the INSERT sets only name + fiscal_year_end).
       ⇒ THIS IS THE ONE PLACE THE WIZARD HOLDS IT, refreshed from the server on every step
         entry and re-read at the moment of use by each import. ⛔ Do not reintroduce a
         `companies.find(...)?.forecast_through` read — see `readForecastThrough` below. */
    const [horizonFt, setHorizonFt] = useState(null);
    const [wizardLoading, setWizardLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [viewingStep, setViewingStep] = useState(1);

    // Step 1 form
    const [hsYear, setHsYear] = useState('');
    const [hsMonth, setHsMonth] = useState('');
    const [cmYear, setCmYear] = useState('');
    const [cmMonth, setCmMonth] = useState('');
    const [ftYear, setFtYear] = useState('');
    const [ftMonth, setFtMonth] = useState('');
    const [editing, setEditing] = useState(false);

    // Step 2 state
    const [subStep, setSubStep] = useState('2a');
    const [uploadMode, setUploadMode] = useState('single');
    const [singleFile, setSingleFile] = useState(null);
    const [isFile, setIsFile] = useState(null);
    const [bsFile, setBsFile] = useState(null);
    const [parsing, setParsing] = useState(false);
    const [parseError, setParseError] = useState('');
    const [parsedData, setParsedData] = useState(null);
    const [step2bZeroOpen, setStep2bZeroOpen] = useState(false);   // Batch 1 (2026-11-03, L3596): the Pre-Launch all-zero line, expanded
    const singleFileRef = useRef(null);
    const isFileRef = useRef(null);
    const bsFileRef = useRef(null);

    // Step 3 (GL Mapping) state
    const [mappings, setMappings] = useState([]);
    const [mappingSaved, setMappingSaved] = useState(false);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [bulkCat, setBulkCat] = useState('');
    // bulkKey DELETED — bulk-assign sets the CATEGORY only. Stamping one is_line_key across
    // N selected rows collapsed N leaves into one, detaching their vendors and history.
    const [step3Loading, setStep3Loading] = useState(false);
    // Tenant's departments — drives the Step-3 display_group dropdown. Empty on a first
    // pass (Step 3 precedes Step 5); populated on re-entry.
    const [step3Depts, setStep3Depts] = useState([]);
    // is_line_keys that vendors join / that carry historical actuals — re-keying either is
    // a migration, not an edit. Blocks Save (see step3Rekeys).
    const [step3Deps, setStep3Deps] = useState({ vendorKeys: new Set(), histKeys: new Set() });

    // Step 4 (Review Financials) state
    const [step4Loading, setStep4Loading] = useState(false);
    const [step4Data, setStep4Data] = useState(null);
    const [step4Tab, setStep4Tab] = useState('is');
    const [step4Collapsed, setStep4Collapsed] = useState(new Set());
    const [step4SubStep, setStep4SubStep] = useState(null); // '4a' | '4b' | null (loading)
    const [step4GlData, setStep4GlData] = useState(null); // { glLines, mappings }
    const [step4BsOptions, setStep4BsOptions] = useState([]);
    const [step4SweepKey, setStep4SweepKey] = useState('');
    // Cash classification (Step 4a). { [bsKey]: 'rollup' | 'separate' } for every
    // current-asset leaf. The SWEEP answer is NOT stored here — `step4SweepKey` remains
    // the single source of truth for it (one existing control, no two-way sync to drift);
    // the classification map handed to the payload is derived from both at submit time.
    // ALL leaves default to 'rollup' — name detection is unreliable in BOTH directions
    // (it misses real bank accounts AND false-positives on the de-facto-AR account), so
    // nothing is auto-excluded and nothing is hidden. The operator pulls out exceptions.
    const [step4Cls, setStep4Cls] = useState({});
    // 2026-09-24 -- the liability / long-term / equity rows of the type picker (their answers
    // ride step4Cls, keyed by bsKey), and the page confirm that accepts a suggested type.
    const [step4TypeOptions, setStep4TypeOptions] = useState([]);
    const [step4TypesConfirmed, setStep4TypesConfirmed] = useState(false);
    // Commission ship 2b: Step 4a's page confirm, reused on Step 3 -- it accepts the SUGGESTED pay types of the payroll lines.
    const [step3PaytypesConfirmed, setStep3PaytypesConfirmed] = useState(false);
    // ★ B5 s5 — the Fixed / Intangible leaves, and the operator's gross-vs-contra answer
    // for each: { [bsKey]: 'gross' | 'contra' }.
    // ⛔ THE DEFAULT IS UNANSWERED, DELIBERATELY. Pre-selecting what the old /accum/i test
    // would have said would ship the guess wearing an operator's clothes — §E1's exact
    // failure mode, and worse than leaving the old code in place, because a displayed
    // guess is indistinguishable from a real decision. An unanswered leaf BLOCKS import.
    // On RE-ENTRY these are seeded from the STORED ROLE (the persisted answer), never
    // from the name — see the seeding block in the Step-4 loader.
    const [step4AssetOptions, setStep4AssetOptions] = useState([]);
    const [step4AssetCls, setStep4AssetCls] = useState({});
    const [step4TotalCashLabel, setStep4TotalCashLabel] = useState('Total Cash');
    const [step4Importing, setStep4Importing] = useState(false);
    const [step4ImportError, setStep4ImportError] = useState('');

    // Step 5 (Payroll Setup) state
    const [step5Loading, setStep5Loading] = useState(false);
    const [step5SubStep, setStep5SubStep] = useState(null); // 're-entry' | '5a' | '5b' | '5c' | '5d' | null (loading)
    const [step5ExistingCount, setStep5ExistingCount] = useState(0);
    const [step5ExistingSalesReps, setStep5ExistingSalesReps] = useState(0);
    const [step5ExistingDepts, setStep5ExistingDepts] = useState([]);
    const [step5File, setStep5File] = useState(null);
    const [step5Parsing, setStep5Parsing] = useState(false);
    const [step5ParseError, setStep5ParseError] = useState('');
    const [step5Parsed, setStep5Parsed] = useState(null); // { employees, benefitColumns, departments, flags }
    const [step5DefaultState, setStep5DefaultState] = useState('CA');
    const [step5BenefitCols, setStep5BenefitCols] = useState([]); // [{ headerName, normalizedKey, sampleValues, include, benefitName, isManual?, defaultAmount? }]
    const [step5DeptMap, setStep5DeptMap] = useState([]); // [{ department_name, is_category, display_group, display_group_label, sort_order, employee_count }]
    // display_groups that already have historical actuals → membership is FROZEN (D2).
    const [step5DgsWithHistory, setStep5DgsWithHistory] = useState(new Set());
    const [step5Page, setStep5Page] = useState(0);
    const [step5Importing, setStep5Importing] = useState(false);
    const [step5ImportError, setStep5ImportError] = useState('');
    const [step5Employees, setStep5Employees] = useState([]); // mutable per-employee state for 5d edits
    const [step5Expanded, setStep5Expanded] = useState(new Set()); // indexes of rows with expanded benefit breakdown
    // Re-entry edit mode — entering 5c/5b/5d from the re-entry summary to edit
    // existing DB data instead of parsing a fresh file. Changes button labels + save destination.
    const [step5EditMode, setStep5EditMode] = useState(false);
    // Edit-mode benefit rename state: [{ originalKey, currentName, _originalName }]
    const [step5BenefitRenames, setStep5BenefitRenames] = useState([]);
    // Edit-mode employee roster: snapshot of originals (by id) to detect diffs on save.
    const [step5EditOriginals, setStep5EditOriginals] = useState({});
    const step5FileRef = useRef(null);
    // Step 5e (SUTA Rates) state — build 2b-2b. Onboarding is NOT a ModelProvider
    // consumer, so we fetch state_tax_rates + company_state_suta ourselves and build a
    // latest-year lookup. Nothing here touches existing-tenant paths.
    const [step5SutaLoaded, setStep5SutaLoaded] = useState(false);   // fetch done (success or fail)
    const [step5SutaLoading, setStep5SutaLoading] = useState(false);
    const [step5SutaFetchError, setStep5SutaFetchError] = useState(''); // non-blocking fetch-fail notice
    const [step5StateTax, setStep5StateTax] = useState({}); // { ST → { globalRate, globalCap, rateRangeLow, rateRangeHigh } } (latest year)
    const [step5SutaMode, setStep5SutaMode] = useState({}); // { ST → 'default' | 'override' } (default states only)
    const [step5SutaInput, setStep5SutaInput] = useState({}); // { ST → raw rate string } (override / null-rate)
    const [step5SutaRowErr, setStep5SutaRowErr] = useState({}); // { ST → validation msg } (display-only, non-blocking)
    const [step5SutaFlushErrors, setStep5SutaFlushErrors] = useState([]); // [{ state, error }] from post-import flush
    /* Bi-weekly payroll, step 4: what is STORED as this company's pay schedule, as the Step-5a panel last reported it
       (undefined = not reported yet; null = it could not be read). THE ROSTER UPLOAD IS DISABLED UNTIL A PAY FREQUENCY IS
       STORED (Brian B2 / Q14): no silent default -- every salary series the import stores is derived from that answer. */
    const [step5PayStored, setStep5PayStored] = useState(undefined);
    const step5PayFrequencyStored = !!(step5PayStored && step5PayStored.pay_frequency);
    /* Commission ship 5: what the commission panel last reported -- undefined = not read yet, null = the read failed,
       { stored, missing } otherwise. THE GATE: a company with a commissioned rep and no stored answer cannot continue past
       Step 5 (Brian's standing rule: a blank required answer blocks, naming it). The sentence is the rule owner's. */
    const [step5CommState, setStep5CommState] = useState(undefined);
    const step5CommBlock = step5CommState === undefined ? 'Checking the commission answer...'
        : step5CommState === null ? 'The commission answer could not be read \u2014 Retry it above before continuing.'
        : step5CommState.missing;

    // Step 6 (Vendor Setup) state
    const [step6Loading, setStep6Loading] = useState(false);
    const [step6SubStep, setStep6SubStep] = useState(null); // 're-entry' | 'resume' | '6a' | '6b' | '6c' | S6_PREPAID | S6_FORECAST | null
    const [step6ExistingCount, setStep6ExistingCount] = useState(0);
    const [step6ExistingPrepaidCount, setStep6ExistingPrepaidCount] = useState(0);
    const [step6ExistingActiveCount, setStep6ExistingActiveCount] = useState(0);
    const [step6ExistingContractorCount, setStep6ExistingContractorCount] = useState(0);
    const [step6File, setStep6File] = useState(null);
    // Upload rework step 2 — mirrors Step 2's uploadMode / isFile / bsFile trio.
    const [step6UploadMode, setStep6UploadMode] = useState('single'); // 'single' | 'separate'
    const [step6VendorFile, setStep6VendorFile] = useState(null);
    const [step6PrepaidFile, setStep6PrepaidFile] = useState(null);   // OPTIONAL
    /* Upload rework step 3 — the operator's explicit assertion that this tenant has NO
     * prepaid contracts. WIZARD-LOCAL ON PURPOSE, not persisted: the vendor import writes
     * `steps.vendor_setup = { completed: true }` as a FULL-SLUG REPLACE (server.js), so a
     * nested flag would be wiped by the very import it describes unless the server were
     * taught to carry it — a paired UI+server change, deliberately not bundled here.
     * Nothing downstream reads "has prepaid" (the engine iterates the array, P11 is silent
     * on empty, and the only hasPrepaid in the tree is a per-vendor row tint), so this is a
     * UX gate that distinguishes "asserted none" from "forgot the file" at the one moment
     * that distinction matters — before the import — and claims no persistence it lacks. */
    const [step6NoPrepaidConfirmed, setStep6NoPrepaidConfirmed] = useState(false);
    const [step8NoAssetsConfirmed, setStep8NoAssetsConfirmed] = useState(false);   // S2 (2026-11-02): "no fixed or intangible assets yet"
    const [step6Parsing, setStep6Parsing] = useState(false);
    const [step6ParseError, setStep6ParseError] = useState('');
    const [step6Parsed, setStep6Parsed] = useState(null); // output of parseVendorExcel
    const [step6CatMap, setStep6CatMap] = useState([]);
    /* Upload rework step D — the whole-screen "these are correct" tick. The derived
     * defaults are good enough to pre-fill but not to accept unread: they decide every
     * vendor's is_line, and a wrong one books real spend to the wrong IS line silently
     * (computeVendor finds any existing leaf perfectly valid). Cleared on every parse. */
    const [step6CatConfirmed, setStep6CatConfirmed] = useState(false); // [{ gl_category, count, is_category, is_line_key }]
    const [step6Vendors, setStep6Vendors] = useState([]); // per-vendor forecast config
    const [step6Prepaid, setStep6Prepaid] = useState([]); // list of prepaid contracts (editable review)
    const [step6Importing, setStep6Importing] = useState(false);
    const [step6ImportError, setStep6ImportError] = useState('');
    // Build 4c: non-blocking notices from the prepaid forecast-amort generator —
    // { irregular: [{vendor_name, account, start_month}], conferences: [{name, amount, paid}] }.
    const [step6PrepaidNotice, setStep6PrepaidNotice] = useState(null);
    /* Prepaid renewals part 2: the edited renewals kept when Renews was turned off (the operator chooses for each), a
     * renewal-chain notice (a collision stops a chain -- D5), and the import's contracts left with NO vendor (U4). */
    const [step6RenewFlagged, setStep6RenewFlagged] = useState([]);   // [{ ref, label }]
    const [step6RenewNotice, setStep6RenewNotice] = useState(null);     // string
    const [step6UnlinkedNotice, setStep6UnlinkedNotice] = useState(null);   // { unlinked: [...], unresolvedLinks: [...] }
    /* ★ THE PRESERVED-ROUTE NOTICE. Informational, set AFTER a successful import — the
     * sibling of step6PrepaidNotice, not of setStep6ImportError (which blocks and returns).
     * No Keep/Replace action by design: the operator changes it on the Vendor Dashboard if
     * they disagree, which is the same screen the preserved value came from. */
    const [step6RouteNotice, setStep6RouteNotice] = useState(null);   // { names: string[] }
    /* ═══ ITEM 2 — THE PREPAID CLOBBER GATE (R3 flag-and-choose, DEFAULT = KEEP) ═══
     *
     * `step6Clobber`       null | { affected, fingerprint } — the NAMED list of stored
     *                      contracts whose forecast schedule this import would replace.
     *                      Computed from the REAL outgoing payload, inside
     *                      handleStep6Import, immediately before the POST.
     * `step6ClobberChoice` 'keep' | 'replace' — the operator's answer.
     *                      ★ DEFAULT 'keep' (Brian's ruling): it is the non-destructive
     *                      arm, and the loss it prevents is $64.6k-$76.9k of hand entry.
     *                      A pre-selected default is NOT a silent one — the gate below
     *                      still BLOCKS until the operator acknowledges (§E1: the answer
     *                      is the operator's, and a blank blocks).
     * `step6ClobberAck`    null | fingerprint — WHICH SET was acknowledged, not a bare
     *                      boolean. A boolean would either nag on all four "Save
     *                      Progress" buttons (§12.5 shape 10 — a check nobody reads) or,
     *                      worse, let a LATER upload put NEW rows at risk under an
     *                      acknowledgement given for an earlier one.
     * ⓘ WIZARD-LOCAL BY DESIGN, and deliberately not persisted: the choice is about ONE
     *   upload, not durable tenant state, and the import writes `steps.vendor_setup` as
     *   a FULL-SLUG REPLACE (server.js:3017) so a nested flag would be wiped by the very
     *   import it describes (§12 post-walk item (c), still filed). */
    const [step6Clobber, setStep6Clobber] = useState(null);
    const [step6ClobberChoice, setStep6ClobberChoice] = useState('keep');
    const [step6ClobberAck, setStep6ClobberAck] = useState(null);
    /* Build 3 c2: the embedded PrepaidModal's target. null = closed, 'new' = add,
     * otherwise the INDEX of the step6Prepaid row being edited. An index, because a
     * wizard contract has no DB id until the import inserts it. */
    const [step6PrepaidModal, setStep6PrepaidModal] = useState(null);
    // Build 4b: the embedded ForecastWizard's open/closed state.
    const [step6ForecastWizardOpen, setStep6ForecastWizardOpen] = useState(false);
    // 2026-10-29: the vendor-forecast file -- the upload's parse + plan (shown before anything is applied), and the note after Apply
    const [step6FcFile, setStep6FcFile] = useState(null);
    const [step6FcNote, setStep6FcNote] = useState('');
    /* ★ 4b: department_mappings for the per-seat department picker. Step 5 writes them,
     * but nothing in the Step-6 context loaded them, so the wizard would have offered a
     * company-wide-only choice — the item-7 "per-seat resolves 0 headcount, silent $0"
     * class. Loaded with the rest of the Step-6 data. */
    const [step6DeptMappings, setStep6DeptMappings] = useState([]);
    // "Save Progress" acknowledgement (6d). Cleared on every import attempt.
    const [step6DraftSaved, setStep6DraftSaved] = useState('');
    /* ★ Flicker fix: the self-heal effect below used `step6Vendors.length > 0` as its
     * "already loaded?" exit. That is UNSATISFIABLE for a tenant with genuinely zero
     * vendors — the load resolves, the array is still empty, `step6Loading` flips, the
     * effect re-fires on its own dependency and loops forever (4 GETs per cycle, and the
     * pane oscillates between "Loading..." and a half-rendered screen). Empty is a VALID
     * fully-loaded answer; what the guard actually needs is "have I ATTEMPTED a load?",
     * which is a ref, not a row count. Keyed by sub-step so each of 6c/6d/6e still gets
     * its own single attempt. */
    const step6EditLoadAttempted = useRef({});
    /* ★ 6d unsaved-changes tracking. Brian lost hours of 6d hand-entry when a failed
     * completing import rolled back and the React state went with it — the edits had
     * never been persisted and nothing warned him. `step6SavedSnapshot` is the
     * projection of step6Vendors as of the last time it was LOADED or SAVED; anything
     * different from it is unsaved work.
     * ★ Projection, not the whole array: only the fields 6d actually edits, so a
     * re-load that rebuilds incidental fields (or reorders monthly_amounts keys)
     * cannot produce a phantom dirty state — and stringifying 229 rows stays cheap. */
    const step6SavedSnapshot = useRef(null);
    /* ★ C1 s2a — the PREPAID baseline, deliberately a SEPARATE ref rather than a second
     * field on a composed string. Keeping them apart is what lets the forecast
     * comparison stay textually and behaviourally byte-identical, which is what makes
     * the before/after proof total instead of argumentative. Written at exactly the same
     * lifecycle sites as its sibling so the two cannot drift. */
    const step6PrepaidSnapshot = useRef(null);
    // Keyed by gl_category → { is_category, is_line_key }. Loaded once on Step 6 mount.
    const [step6SavedCatMap, setStep6SavedCatMap] = useState({});
    // The tenant's own gl_line_mappings — the ONLY source of valid vendor is_line keys.
    const [step6Glm, setStep6Glm] = useState([]);
    /* c2: the 6e inline rename-box state is GONE — the embedded PrepaidModal replaced
     * the rename box, and its own form state lives inside the component. */
    // Re-entry edit mode — entering 6c/6d/6e from the re-entry summary instead
    // of from a fresh file parse. Changes button labels and save destination.
    const [step6EditMode, setStep6EditMode] = useState(false);
    const step6FileRef = useRef(null);
    const step6VendorFileRef = useRef(null);
    const step6PrepaidFileRef = useRef(null);

    // ── Step 7 state (Revenue Module Selection) ──────────────
    const [step7Loading, setStep7Loading] = useState(false);
    const [step7Lines, setStep7Lines] = useState([]); // [{ gl_line_key, display_name, annual_total, module_type, module_name, bs_line_key }] (9b: module_instance retired from the UI; save pins 1)
    const [step7DrOptions, setStep7DrOptions] = useState([]); // C-1: deferred-revenue BS leaves from bs_actuals_meta [{id,label}]
    const [step7Saving, setStep7Saving] = useState(false);
    const [step7Error, setStep7Error] = useState('');
    // W-016: revenue line key -> 'a misc revenue line' | 'an implementation revenue line' already targeting it (Step 7's AE / CQ guard)
    const [step7LineClaims, setStep7LineClaims] = useState({});
    const [step7ImplTargets, setStep7ImplTargets] = useState(() => new Set());   // Batch 2 (item 16): lines an enabled implementation line forecasts
    /* W-081: the deferred-revenue ACCOUNTS enabled deferred implementation lines are linked to ({ id, name, key }) -- counted by the
       "a deferred-revenue line no stream links" refusal and by the "two streams on one line" refusal below. */
    const [step7ImplDrLinks, setStep7ImplDrLinks] = useState([]);
    /* W-081 part 2: the deferred-revenue accounts RESERVED for implementation revenue (companies.dr_line_reservations) -- a Set of
       leaf keys. step7ResvLoaded false = the read failed: the choice is disabled and the save does NOT send the set (so a failed read
       can never erase a stored reservation). */
    const [step7DrReservations, setStep7DrReservations] = useState(new Set());
    const [step7ResvLoaded, setStep7ResvLoaded] = useState(true);

    // ── Step 9 state (Module Setup — 9a-2 status table; WRITES NOTHING for settings) ──
    const [step9Loading, setStep9Loading] = useState(false);
    const [step9Rows, setStep9Rows] = useState(null);   // evaluateModuleConfig rows + suggestions
    const [step9Meta, setStep9Meta] = useState(null);   // { cutoff, autoCreated: [gl_line_key] }
    const [step9Error, setStep9Error] = useState('');
    const [step9Saving, setStep9Saving] = useState(false);
    const [step9Comm, setStep9Comm] = useState(null);   // commission ship 5: { missing, p23 } | { error } (utils/commissionAccrualCheck)
    const [step9Confirmed, setStep9Confirmed] = useState({}); // per-row "intentional — proceed" (session-local, never persisted)

    // ── Step 9 "Company Financials" sub-section (Build 1 / item 4) ───────────
    // Company-level financial inputs with no other onboarding path. cfData holds the
    // live companies-settings row + the DERIVED prepaid opening balance (from
    // bs_actuals[prepaid_expenses][openingMonth], where openingMonth =
    // subtractOneMonth(historical_start)). min_cash/sweep_rate are the empty-required
    // gate (Option B honest-null); the rest are non-blocking. Saved via the settings PUT.
    const [cfData, setCfData] = useState(null);
    const [cfLoading, setCfLoading] = useState(false);
    const [cfError, setCfError] = useState('');
    /* ★ THE SWEEP-INTEREST PICK, HELD LOCAL UNTIL THE OPERATOR CONFIRMS (§E1).
     * The Step-9 control PRE-FILLS this from role inference when the tenant has exactly one
     * candidate — but a pre-fill is a SUGGESTION, not an answer. Nothing persists until the
     * operator clicks Confirm, which is the difference between a stored decision and a guess
     * wearing a decision's clothes (the payroll-handling lesson, §E1). `undefined` means the
     * seeding effect has not run for this tenant yet; '' means "no pre-selection" (>=2
     * candidates — the operator must choose). */
    const [cfSweepIntPick, setCfSweepIntPick] = useState(undefined);
    /* D&A routing, Part 2: the pending pick per stream, keyed by column. A key absent from the
     * object means the seeding effect has not run for this tenant yet; '' means no pre-selection. */
    const [cfDaPick, setCfDaPick] = useState({});

    // ── Step 8 state (Fixed & Intangible Assets) ─────────────
    const [step8Loading, setStep8Loading] = useState(false);
    const [step8SubStep, setStep8SubStep] = useState(null); // 're-entry' | 'home' | 'review' | 'complete' | null
    const [step8File, setStep8File] = useState(null);
    const [step8Parsing, setStep8Parsing] = useState(false);
    const [step8ParseError, setStep8ParseError] = useState('');
    const [step8Flags, setStep8Flags] = useState([]);
    const [step8Fixed, setStep8Fixed] = useState([]);
    const [step8Intang, setStep8Intang] = useState([]);
    const [step8Importing, setStep8Importing] = useState(false);
    const [step8ImportError, setStep8ImportError] = useState('');
    const [step8ExistingFixed, setStep8ExistingFixed] = useState(0);
    const [step8ExistingIntang, setStep8ExistingIntang] = useState(0);
    // BS-line options for each asset row, from defrev_data.bs_actuals_meta by role (fixed_asset /
    // intangible_asset). Shape: [{ key: bsLeafId, label }]. Empty = BS chart has no asset lines yet.
    const [step8FaOptions, setStep8FaOptions] = useState([]);
    const [step8IaOptions, setStep8IaOptions] = useState([]);
    // Edit-mode flag. 'fixed' or 'intangible' when user is editing a single
    // section from the re-entry summary; null during fresh import flow.
    const [step8EditMode, setStep8EditMode] = useState(null);
    const step8FileRef = useRef(null);
    // The D&A start convention on the Step-8 screens (2026-10-13): the value this session saved, per company ({ cid, value });
    // until a save, the screens read the stored value off obState.
    const [step8DaStart, setStep8DaStart] = useState(null);
    const [step8DaStartSaving, setStep8DaStartSaving] = useState(false);
    const [step8DaStartError, setStep8DaStartError] = useState('');

    // ── Load companies ───────────────────────────────────────
    const loadCompanies = useCallback(async () => {
        try {
            setCompaniesLoading(true);
            const res = await getCompanies();
            setCompanies(res.data);
            const states = {};
            // Load onboarding state for ALL companies (live + in-progress) so
            // the company list can display historical_start / close_month and
            // "Edit Onboarding" re-entry works for live companies too.
            await Promise.all(res.data.map(async (co) => {
                try { const obRes = await getOnboardingState(co.id); states[co.id] = obRes.data; }
                catch { /* ignore */ }
            }));
            setOnboardingStates(states);
        } catch (err) { console.error('Failed to load companies:', err); }
        finally { setCompaniesLoading(false); }
    }, []);
    useEffect(() => { loadCompanies(); }, [loadCompanies]);

    /* ★★ H1 — THE ONE READER OF THE FORECAST HORIZON.
       Returns the server's CURRENT value and stores it for the render path, so the screen the
       operator fills in and the payload the import writes can never disagree about the window.
       ⛔ THE RETURN VALUE, NOT THE STATE, IS WHAT A WRITE PATH USES — a setState is async, so
         an import that read `horizonFt` immediately after calling this would still be reading
         the previous render's value. Every import therefore awaits and uses what comes back.
       ⛔ REFRESHING `companies` AFTER STEP 1 WAS REJECTED AS THE FIX: it closes the one path
         we measured and leaves every other open (a later Step-1 edit, any arrival that does
         not remount). That fixes an instance; reading the owner fixes the class.
       ⓘ `GET /companies/:id/settings` (server.js:1311) returns the column; the onboarding-state
         GET does NOT (it returns current_step, close_month, forecast_start, historical_start,
         steps and nothing else), which is why `obState` cannot carry this and a second endpoint
         is genuinely required rather than merely convenient. */
    const readForecastThrough = useCallback(async (cid) => {
        if (!cid) { setHorizonFt(null); return null; }
        try {
            const res = await getCompanySettings(cid);
            const ft = (res && res.data && res.data.forecast_through) || null;
            setHorizonFt(ft);
            return ft;
        } catch (err) {
            /* A FAILED READ IS NOT A BLANK HORIZON. Leave the stored value alone and return
               null: every caller treats null as "no window", which REFUSES rather than writes
               an empty grid, so a transient network failure cannot be mistaken for an answer. */
            console.error('Failed to read the forecast horizon:', err);
            return null;
        }
    }, []);
    /* Refresh on every step entry AND on every tenant change, so the render path is current
       wherever the operator arrives from — including the Step-1 save, which advances the step
       and therefore re-fires this without needing to know that it should. */
    useEffect(() => { readForecastThrough(selectedCompanyId); },
        [selectedCompanyId, viewingStep, readForecastThrough]);
    /* THE PAY SCHEDULE IS READ FROM ITS OWNER AT THE MOMENT OF USE -- the rule readForecastThrough follows, for the same
       reason: the screen's copy (step5PayStored) says what the panel last saw, and a salary series must be derived from what
       is STORED. Returns the settings row, or null when the read failed. A FAILED READ IS NOT "SEMI-MONTHLY": every caller
       refuses on null rather than deriving under a schedule it did not read. */
    const readPaySettings = useCallback(async (cid) => {
        if (!cid) return null;
        try {
            const res = await getCompanySettings(cid);
            return (res && res.data) || null;
        } catch (err) {
            console.error('Failed to read the pay schedule:', err);
            return null;
        }
    }, []);

    // ── Load wizard state ────────────────────────────────────
    const loadWizardState = useCallback(async (companyId) => {
        try {
            setWizardLoading(true);
            /* Forecast Through is read from its OWNER, companies.forecast_through, in the same await as the
               onboarding state so every field below is set in one batch. readForecastThrough never throws: a
               failed read returns null and the field stays blank. */
            const [res, storedFt] = await Promise.all([getOnboardingState(companyId), readForecastThrough(companyId)]);
            setObState(res.data);
            setViewingStep(res.data.current_step || 1);
            const hs = splitYM(res.data.historical_start);
            setHsYear(hs.year); setHsMonth(hs.month);
            const cm = splitYM(res.data.close_month);
            setCmYear(cm.year); setCmMonth(cm.month);
            /* It used to be GUESSED as forecast_start's year + December (2026-10-28), which showed 2026 for a
               forecast stored through 2027-12. Never saved -> blank year and month, no guess. */
            const ft = splitYM(storedFt);
            setFtYear(ft.year); setFtMonth(ft.month);
            setEditing(false); setError('');
            // Reset step 2 + 3 state
            setSubStep('2a'); setParsedData(null); setMappings([]); setMappingSaved(false);
            setSingleFile(null); setIsFile(null); setBsFile(null); setParseError('');
            setSelectedRows(new Set());
            setStep4Data(null); setStep4Tab('is'); setStep4Collapsed(new Set());
            setStep4SubStep(null); setStep4GlData(null); setStep4BsOptions([]);
            setStep4AssetOptions([]); setStep4AssetCls({});
            setStep4TypeOptions([]); setStep4TypesConfirmed(false);
            setStep4SweepKey(''); setStep4Importing(false); setStep4ImportError('');
            setStep5SubStep(null); setStep5File(null); setStep5Parsed(null); setStep5ParseError('');
            setStep5PayStored(undefined); setStep5CommState(undefined);
            setStep5DefaultState('CA'); setStep5BenefitCols([]); setStep5DeptMap([]); setStep5Page(0);
            setStep5Importing(false); setStep5ImportError(''); setStep5ExistingCount(0); setStep5ExistingSalesReps(0); setStep5ExistingDepts([]);
            setStep5Employees([]); setStep5Expanded(new Set());
            setStep5EditMode(false); setStep5BenefitRenames([]); setStep5EditOriginals({});
            setStep6SubStep(null); setStep6File(null); setStep6Parsed(null); setStep6ParseError('');
            setStep6CatMap([]); setStep6Vendors([]); setStep6Prepaid([]);
            setStep6Importing(false); setStep6ImportError(''); setStep6ExistingCount(0); setStep6ExistingPrepaidCount(0);
            // ITEM 2 — a tenant switch is a new subject entirely; an acknowledgement given
            // for one company's rows must never carry into another's.
            setStep6Clobber(null); setStep6ClobberChoice('keep'); setStep6ClobberAck(null);
            setStep6ExistingActiveCount(0); setStep6ExistingContractorCount(0);
            setStep6SavedCatMap({});
            setStep6EditMode(false);
            setStep7Lines([]); setStep7Saving(false); setStep7Error('');
            setStep8SubStep(null); setStep8File(null); setStep8Parsing(false); setStep8ParseError('');
            setStep8Flags([]); setStep8Fixed([]); setStep8Intang([]);
            setStep8Importing(false); setStep8ImportError('');
            setStep8ExistingFixed(0); setStep8ExistingIntang(0);
            setStep8FaOptions([]); setStep8IaOptions([]);
            setStep8EditMode(null);
            /* ★ 4b (stale-screen fix, part 1 of 3): RETURN the freshly-fetched state as
             * well as setting it. `setObState` schedules a re-render; it does not update
             * any closure already captured, so a caller that needs the new value in the
             * SAME tick has to be handed it. Placed at the END of the try on purpose —
             * an earlier `return` skipped every reset below it, which is precisely what
             * ESLint's no-unreachable caught on the first attempt. Purely additive: the
             * other eleven callers ignore the return. */
            return res.data;
        } catch (err) { console.error('Failed to load onboarding state:', err); return null; }
        finally { setWizardLoading(false); }
    }, [readForecastThrough]);

    useEffect(() => {
        const d = forecastThroughDefault(cmYear, cmMonth, ftYear, ftMonth, obState?.steps?.company_setup?.completed);
        if (d) { setFtYear(d.year); setFtMonth(d.month); }
    }, [cmYear, cmMonth, ftYear, ftMonth, obState?.steps?.company_setup?.completed]);

    const openCompany = (companyId) => { setSelectedCompanyId(companyId); loadWizardState(companyId); };

    const handleNewCompanyCreated = (company) => {
        setShowNewCompanyModal(false);
        setCompanies(prev => [...prev, company]);
        companiesChanged();   // W-027: the shell's list too
        setOnboardingStates(prev => ({ ...prev, [company.id]: { current_step: 1, close_month: null, forecast_start: null, historical_start: null, steps: {} } }));
        openCompany(company.id);
    };

    // ── Step 1 ───────────────────────────────────────────────
    const step1Complete = obState?.steps?.company_setup?.completed;
    const isReadOnly = step1Complete && !editing;
    const historicalStart = joinYM(hsYear, hsMonth);
    const closeMonth = joinYM(cmYear, cmMonth);
    const forecastThrough = joinYM(ftYear, ftMonth);

    const handleSaveStep1 = async () => {
        setError('');
        /* ONE owner of the window rule (utils/companyWindow.js): every field required; Historical Start a January -- OR equal to
         * the Last Closed Month for a brand-new company with no earlier history (the one-month start, 2026-11-02); Last Closed
         * after Historical Start otherwise; Forecast Through after the close and a December (Build 1b). Same order, same texts. */
        { const windowError = step1WindowError({ historicalStart, closeMonth, forecastThrough }); if (windowError) { setError(windowError); return; } }
        const cmMNum = parseInt(cmMonth, 10);
        const cmYNum = parseInt(cmYear, 10);
        const fsY = cmMNum === 12 ? cmYNum + 1 : cmYNum;
        const fsM = cmMNum === 12 ? 1 : cmMNum + 1;
        const forecastStart = `${fsY}-${String(fsM).padStart(2, '0')}`;
        try {
            setSaving(true);
            const newSteps = { ...(obState?.steps || {}), company_setup: { completed: true } };
            // Build 1b: forecast_through is persisted to companies (not onboarding_state) IN THE
            // SAME TXN as this upsert by the onboarding-state PUT — Step 1 can never save with no
            // horizon (the create INSERT leaves it NULL until this write).
            const payload = { current_step: editing ? obState.current_step : 2, close_month: closeMonth, forecast_start: forecastStart,
                historical_start: historicalStart, forecast_through: forecastThrough,
                /* ★ Part 3c — the reset is now DECLARED, and the payload stays NARROW *on purpose*.
                 *
                 * Edit mode still sends only the slug it is saving — but that is no longer how the
                 * reset happens. The server merges, so the other slugs (and everything stored in
                 * them) survive; `steps_reset` is what clears their `completed` flags, which is the
                 * promise the confirm dialog actually makes.
                 *
                 * ★ IT MUST BE NARROW: a full spread would carry every slug's `completed: true` and
                 * merge it back OVER the reset, silently un-doing it. The first cut of this fix did
                 * exactly that and the gate caught it — the reset ran and then the payload undid it.
                 * Safety now comes from the FLAG plus the server-side merge, not from the shape of
                 * this object. */
                steps: editing ? { company_setup: { completed: true } } : newSteps,
                ...(editing ? { steps_reset: true } : {}) };
            await updateOnboardingState(selectedCompanyId, payload);
            setEditing(false);
            await loadWizardState(selectedCompanyId);
            setViewingStep(2);
            setOnboardingStates(prev => ({ ...prev, [selectedCompanyId]: { ...prev[selectedCompanyId], ...payload } }));
        } catch (err) { setError('Failed to save. Please try again.'); console.error(err); }
        finally { setSaving(false); }
    };

    const handleEditStep1 = () => {
        if (window.confirm('Editing Company Setup will require re-completing all subsequent steps. Continue?')) setEditing(true);
    };

    const handleBack = (stepNum) => { setViewingStep(stepNum - 1); };

    // ── Step 2: Parse files ──────────────────────────────────
    const handleParseFiles = async () => {
        setParseError(''); setParsing(true);
        try {
            let result;
            if (uploadMode === 'single' && singleFile) {
                const buf = await singleFile.arrayBuffer();
                result = parseFinancialExcel(buf);
            } else if (uploadMode === 'separate') {
                if (!isFile || !bsFile) { setParseError('Both IS and BS files are required.'); setParsing(false); return; }
                const isBuf = await isFile.arrayBuffer();
                const bsBuf = await bsFile.arrayBuffer();
                result = { is: parseSingleSheet(isBuf, 'IS'), bs: parseSingleSheet(bsBuf, 'BS') };
            } else { setParseError('Please select a file.'); setParsing(false); return; }

            if (!result.is && !result.bs) { setParseError('Could not detect IS or BS sheets.'); setParsing(false); return; }
            if (result.is && result.is.months.length === 0 && result.bs && result.bs.months.length === 0) {
                setParseError('Could not detect date columns in either sheet.'); setParsing(false); return;
            }
            /* W-068 pass 3 -- THE UPLOAD CHECK: each sheet against its template spec, EVERY problem named by row and
             * column, merged into the sheet's own flag list (the 2b review renders it; a BLOCKING problem disables
             * "continue" until the file is fixed and re-uploaded). Runs on this fresh parse only. */
            for (const key of ['is', 'bs']) {
                if (result[key]) result[key] = { ...result[key], flags: mergeSpecProblems(result[key].flags, checkAgainstSpec(key, result[key], { window: buildHistoricalMonths(obState?.historical_start, obState?.close_month, TEMPLATE_SPECS[key].months) })) };   // 2026-11-02: B10 checks the file covers the company's months
            }
            setParsedData(result);
            const allFlags = [...(result.is?.flags || []), ...(result.bs?.flags || [])];

            /* ★★ THE RE-IMPORT TIER — THE ONE PLACE THE ERASED DISTINCTION IS RECOVERABLE.
             *
             * The parser cannot tell a DELETED display_group column from an empty one (see
             * parseCanonicalSheet's note: canonCellText collapses both to null and `cols` is
             * discarded), so its flag has to hedge — "if you have no sub-groups that is fine".
             * But the DATABASE remembers. If this tenant's own previous import stored rows
             * WITH a sub-group and the workbook now has no such column, that is no longer a
             * maybe: the operator's own data says the column used to be there.
             *
             * ⇒ UPGRADE the notice to a WARNING and NAME THE COUNT, so the sentence stops
             *   hedging and starts stating a fact the operator can check.
             * ⛔ STILL NOT A BLOCK: a tenant legitimately restructuring to no sub-groups
             *   exists, and refusing would be the same fire-on-correct-data error one level
             *   up. It changes what the flag SAYS, never whether the import may proceed.
             * ⓘ CLIENT-ONLY and best-effort — reuses getHistoricalGLLines (what Step 3's own
             *   loader calls); a failed lookup leaves the plain notice exactly as parsed. */
            const dgFlag = allFlags.find(f => f.type === 'no_display_group_column');
            if (dgFlag && selectedCompanyId) {
                try {
                    const prior = await getHistoricalGLLines(selectedCompanyId);
                    const tagged = (prior.data || []).filter(
                        r => r.statement_type === 'IS' && r.display_group && String(r.display_group).trim() !== ''
                    ).length;
                    if (tagged > 0) { dgFlag.severity = 'warning'; dgFlag.priorTagged = tagged; }
                } catch { /* no prior import, or the lookup failed — keep the plain notice */ }
            }

            setSubStep(allFlags.length > 0 ? '2b' : '2c');
        } catch (err) {
            setParseError('Failed to parse file: ' + (err.message || 'Unknown error'));
            console.error(err);
        } finally { setParsing(false); }
    };

    // ── Step 2 completion: import GL lines to DB ─────────────
    const handleStep2Complete = async () => {
        setError('');
        if (!parsedData) { setError('No parsed data available.'); return; }
        // W-068: a file with blocking problems is never imported (2b already disables its continue; this is the backstop).
        {
            const nBlock = countBlocking([...(parsedData.is?.flags || []), ...(parsedData.bs?.flags || [])]);
            if (nBlock) { setError(blockingHeading(nBlock)); return; }
        }
        try {
            setSaving(true);
            // Import ALL parsed GL lines to historical_gl_lines (with sort_order preserving Excel row order)
            const rows = [];
            const addRows = (lines, stType) => {
                for (let lineIdx = 0; lineIdx < lines.length; lineIdx++) {
                    const line = lines[lineIdx];
                    for (const [month, amount] of Object.entries(line.amounts)) {
                        /* ★ display_group IS PART OF THE ROW'S IDENTITY, and used to be dropped
                         * here. The canonical template distinguishes S&M "Salaries" ×3 and
                         * "Bonuses"/"Payroll Taxes"/"Benefits" ×4 ONLY by this column, so
                         * without it those 15 rows collide on (gl_name, gl_category) and the
                         * server kept the last — 28 payroll rows stored as 17, $5,224,059.21
                         * lost on AE Model 2 and the Step-4 balance out in 27 of 27 months.
                         * The parser has always read it (parseFinancialExcel.js:241); only
                         * this payload discarded it. */
                        rows.push({ month, gl_name: line.gl_name, gl_category: line.gl_category || '',
                            display_group: line.display_group || '',
                            amount: amount || 0, statement_type: stType, sort_order: lineIdx });
                    }
                }
            };
            if (parsedData.is) addRows(parsedData.is.lines, 'IS');
            if (parsedData.bs) addRows(parsedData.bs.lines, 'BS');
            if (rows.length > 0) await importHistoricalGLLines(selectedCompanyId, rows);

            // Mark step 2 complete, advance to step 3
            // ★ CURSOR-REWIND GUARD: current_step is a HIGH-WATER MARK, not a position.
            // Writing it unconditionally meant re-entering an earlier step RE-LOCKED every
            // later step the operator had already reached (getStepStatus unlocks on
            // `stepNum <= current_step`). Math.max keeps the cursor from ever moving
            // BACKWARD — the shape the Step-1 "Continue" button already uses. Completion
            // state (steps.*.completed) is untouched: a completed step stays clickable
            // regardless of the cursor, so this changes navigation only.
            const newSteps = { ...(obState?.steps || {}), is_bs_upload: { completed: true } };
            await updateOnboardingState(selectedCompanyId, {
                current_step: Math.max(obState?.current_step || 3, 3), steps: newSteps,
                close_month: obState.close_month, forecast_start: obState.forecast_start, historical_start: obState.historical_start,
            });
            setParsedData(null);
            await loadWizardState(selectedCompanyId);
            setViewingStep(3);
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Unknown error';
            setError(`Failed to import data: ${msg}`);
            console.error(err);
        } finally { setSaving(false); }
    };

    // ── Step 3: GL Mapping (loads from DB) ───────────────────
    const loadStep3Data = useCallback(async (companyId) => {
        try {
            setStep3Loading(true);
            // department_mappings is EMPTY on a first pass (Step 3 precedes Step 5) — the
            // display_group column then shows "—" and the dept-mappings save back-fills it.
            // On re-entry it is populated, and the operator can override per row.
            const [glRes, mapRes, deptRes, vendRes, drRes] = await Promise.all([
                getHistoricalGLLines(companyId),
                getGLLineMappings(companyId),
                getDepartmentMappings(companyId).catch(() => ({ data: [] })),
                getVendors(companyId).catch(() => ({ data: [] })),
                getDefrevData(companyId).catch(() => ({ data: {} })),
            ]);
            const maps = buildMappingsFromLines(glRes.data, mapRes.data, deptRes.data || []);
            setStep3Depts(deptRes.data || []);

            /* ★ WHICH KEYS HAVE DEPENDENTS? is_line_key is not a label — it is the leaf's id,
             * and two things JOIN on it:
             *   vendors.is_line   → computeVendor.js: `if (!isLines[v.isCat]) return;`
             *                       re-key the leaf and the vendor's spend silently becomes $0
             *   is_actuals[key]   → computeIS reads pool[line.id][month]
             *                       re-key the leaf and every closed month reads $0
             * Changing such a key is a DATA MIGRATION, not a dropdown edit. handleSaveMappings
             * blocks it. (Same fetch-the-dependents pattern the Step-5c loader uses for the
             * display_group freeze.) */
            const vendorKeys = new Set((vendRes.data || []).map(v => v.is_line).filter(Boolean));
            const histKeys = new Set(Object.keys((drRes.data || {}).is_actuals || {}));
            setStep3Deps({ vendorKeys, histKeys });
            setMappings(maps);
            setMappingSaved(false);
            setSelectedRows(new Set());
            setStep3PaytypesConfirmed(false);
        } catch (err) { console.error('Failed to load Step 3 data:', err); }
        finally { setStep3Loading(false); }
    }, []);

    // Load step 3 data when viewing step 3
    useEffect(() => {
        if (viewingStep === 3 && selectedCompanyId && mappings.length === 0) {
            loadStep3Data(selectedCompanyId);
        }
    }, [viewingStep, selectedCompanyId, mappings.length, loadStep3Data]);

    // ── Step 4: Review Financials loader ────────────────────
    // Fetches status + raw GL data; routes to 4a (import) or 4b (review).
    const loadStep4Data = useCallback(async (companyId, obStateArg) => {
        try {
            setStep4Loading(true);
            setStep4ImportError('');
            const [statusRes, glRes, mapRes] = await Promise.all([
                getOnboardingFinancialsStatus(companyId),
                getHistoricalGLLines(companyId),
                getGLLineMappings(companyId),
            ]);
            const hs = obStateArg?.historical_start;
            const cm = obStateArg?.close_month;
            const months = buildAllMonths(hs, cm);
            const priorDec = openingMonthOf(hs);   // the opening month (utils/companyWindow.js); for a January start the prior December, as before
            setStep4GlData({ glLines: glRes.data, mappings: mapRes.data, months, priorDec });

            const bsOpts = buildBSCurrentAssetOptions(glRes.data, mapRes.data, priorDec);
            setStep4BsOptions(bsOpts);
            // Every current-asset leaf starts in the Total Cash rollup; the operator pulls
            // out the exceptions (see step4Cls).
            setStep4Cls({});

            // ★ B5 s5 — the Fixed / Intangible leaves. Cleared to UNANSWERED here; the
            // re-entry block below seeds them from the stored roles when there are any.
            const assetOpts = buildBSAssetOptions(glRes.data, mapRes.data, priorDec);
            setStep4AssetOptions(assetOpts);
            setStep4AssetCls({});
            // 2026-09-24 -- the liability / equity rows; unanswered until seeded below.
            const typeOpts = buildBSTypeOptions(glRes.data, mapRes.data, priorDec);
            setStep4TypeOptions(typeOpts);
            setStep4TypesConfirmed(false);

            const status = statusRes.data || {};
            const alreadyImported = !!(status.has_is_actuals && status.has_bs_actuals);

            // ★ Build 1 (PART 3a): the defrev_data fetch MOVED UP — bs_actuals_meta is now an INPUT
            // to the sweep resolution and the cash rollup, not just something attached afterwards.
            // Non-fatal: a failure leaves meta null and resolveStep4Sweep falls back exactly as before.
            let defrevData = null;
            if (alreadyImported) {
                try {
                    const dRes = await getDefrevData(companyId);
                    defrevData = dRes.data || null;
                } catch (e) { console.warn('[Step4] failed to fetch defrev_data:', e); }
            }

            // ★ B5 s5 — RE-ENTRY SEEDS EACH ASSET CONTROL FROM THE STORED ROLE, NEVER FROM
            // THE NAME. The role IS the operator's persisted answer, so the §E1 discipline
            // ("the read-back TRUSTS the stored value and consults a guess only while it is
            // absent") is reached WITHOUT a new column. Precedent on this very screen:
            // resolveStep4Sweep already prefers bs_actuals_meta's role='sweep_target' over
            // the fragile onboarding_state key. A leaf with no stored role stays UNANSWERED
            // and blocks, exactly as it does on a first pass.
            if (defrevData && defrevData.bs_actuals_meta) {
                const CONTRA_ROLES = new Set(Object.values(BS_ASSET_ROLES).map(r => r.contra));
                const GROSS_ROLES = new Set(Object.values(BS_ASSET_ROLES).map(r => r.gross));
                const seededAssets = {};
                for (const o of assetOpts) {
                    const stored = defrevData.bs_actuals_meta[o.key];
                    const role = stored && stored.role;
                    if (CONTRA_ROLES.has(role)) seededAssets[o.key] = 'contra';
                    else if (GROSS_ROLES.has(role)) seededAssets[o.key] = 'gross';
                }
                setStep4AssetCls(seededAssets);

                /* ★★ BUILD A — THE CASH PANEL NOW SEEDS FROM THE STORED ANSWER TOO.
                 *
                 * It did not, and that was the §E1 asymmetry: the asset panel two lines up
                 * reads the operator's persisted answer back, while this panel cleared
                 * step4Cls unconditionally and let step4AnswerOf fall through to
                 * `looksNonCash` — a NAME GUESS — on every re-entry. So an operator who had
                 * already answered "Separate" for a row the heuristic reads as cash came
                 * back to find their decision replaced by a guess, with nothing saying so.
                 * A guess displayed where a decision was made is indistinguishable from the
                 * decision (§E1), which is the whole reason s5 exists.
                 *
                 * ★ THE ROLE IS THE PERSISTED ANSWER — no new column, exactly as s5. The
                 * mapping is DERIVED from BS_ROLES (step4AnswerFromRole), not spelled here.
                 *
                 * ★ SEED ONLY WHERE THERE IS A STORED ANSWER. A leaf with no stored meta —
                 * a first-time onboarding, or a GL line added since the last import — gets
                 * NO entry, so step4AnswerOf falls through to its existing default and a
                 * fresh onboarding is byte-identical to before. This CORRECTS a re-entry;
                 * it does not change a first pass.
                 *
                 * ⓘ MEASURED BEFORE BUILDING (diffs/bs-seed-verify.txt): on cid 3/8/36/40
                 * today's re-entry already reproduces the stored answer on 56/56 rows, so on
                 * THIS data the seed moves nothing. It is robustness, not a live fix — the
                 * raw guess DOES disagree on the sweep row of every tenant and is saved only
                 * by step4AnswerOf's unrelated sweep short-circuit. This removes the
                 * dependence on that coincidence. */
                const seededCash = {};
                for (const o of bsOpts) {
                    const stored = defrevData.bs_actuals_meta[o.key];
                    const a = step4AnswerFromRole(stored && stored.role);
                    if (!a) continue;
                    /* ★ BUILD B extends Build A's seed: a re-entered row that NEEDS the
                     * sub-question is seeded to its WIDENED value from the stored
                     * SUBCATEGORY — which is that operator's persisted sub-answer, exactly
                     * as the role is their persisted parent answer. A name-test row keeps
                     * the bare 'separate' (it never had a sub-question).
                     * ⓘ The stored subcategory is safe to read back as the answer: MEASURED
                     * (diffs/bs-catchall-verify.txt STEP 0b) that the Step-4 import is the
                     * ONLY writer of bs_actuals_meta — server.js:2678 is the single
                     * upsert, buildFinancialsPayload the single producer; everything else
                     * reads. */
                    // 2026-09-24 (supersedes the note above): EVERY Separate row is seeded to its
                    // specific type from the stored role + subcategory -- the persisted answer,
                    // CONFIRMED, never re-guessed from the name. An unrecognised pair stays the
                    // bare 'separate', so the row shows the suggestion or blocks.
                    seededCash[o.key] = a === SEPARATE_ANSWERS.base
                        ? (bsTypeOf('separate', stored.role, stored.subcategory) || a)
                        : a;
                }
                // The liability and equity rows, the same way: the stored role + subcategory
                // read back as the operator's confirmed type. Long-term liabilities have none.
                for (const o of typeOpts) {
                    const section = step4SectionOf(o);
                    const stored = defrevData.bs_actuals_meta[o.key];
                    const t = section && stored ? bsTypeOf(section, stored.role, stored.subcategory) : null;
                    if (t) seededCash[o.key] = t;
                }
                setStep4Cls(seededCash);
            }

            // Default sweep SUGGESTION.
            //
            // ★ NO arbitrary fallback. The previous `|| bsOpts[0].key` silently pre-selected
            // whichever current asset happened to sort first as the sweep PLUG — the single
            // most consequential choice on this screen, and the one thing that is genuinely
            // un-inferable. If nothing matches, leave it EMPTY: Import is then blocked with an
            // explicit "select exactly one Sweep account" error until the operator decides.
            //
            // ★ Build 1 (PART 3a): resolution now PREFERS bs_actuals_meta role='sweep_target' over
            // the fragile onboarding_state key, then the label heuristic (see resolveStep4Sweep).
            const sweepPick = resolveStep4Sweep(bsOpts, defrevData?.bs_actuals_meta, status.sweep_account_key);
            const defaultSweep = sweepPick.key;
            setStep4SweepKey(defaultSweep);

            if (alreadyImported) {
                const sweepGlName = sweepPick.glName;
                if (sweepPick.source !== 'bs_actuals_meta') {
                    console.warn(`[Step4] sweep account resolved via ${sweepPick.source}`, sweepPick);
                }
                // ★ Build 1 (PART 2): re-entry sources the Total Cash rollup from the persisted
                // classification — the 'clearing' leaves of bs_actuals_meta — and its stored label.
                const cashRollup = buildStep4CashRollup(bsOpts, { bsActualsMeta: defrevData?.bs_actuals_meta });
                // ★ Build A (D1): the value<->layout contract — the bridge keys its BS values by the
                // model's leaf id via the ONE shared resolver, so the model's own skeleton can be
                // rendered directly instead of a parallel section list.
                const bsKeyMap = buildBsKeyResolver(glRes.data, mapRes.data);
                // ★ Build C: department_mappings are an INPUT to the review screen now — they give a
                // recovered payroll row its department (and the operator's own block names). Absent
                // before Step 5c, which simply yields a flat section, exactly as the model does.
                const deptForIS = await getDepartmentMappings(companyId).then(r => r.data || []).catch(() => []);
                const result = computeHistoricalFinancials(glRes.data, mapRes.data, months, priorDec, sweepGlName, cashRollup,
                    l => bsKeyMap(l.gl_name, l.gl_category), deptForIS);
                setStep4Data({ ...result, months, priorDec, defrevData });
                setStep4SubStep('4b');
            } else {
                setStep4Data(null);
                setStep4SubStep('4a');
            }
        } catch (err) {
            console.error('Failed to load Step 4 data:', err);
            setError('Failed to load financial data: ' + (err?.message || 'Unknown error'));
        } finally { setStep4Loading(false); }
    }, []);

    useEffect(() => {
        if (viewingStep === 4 && selectedCompanyId && obState && step4SubStep === null && !step4Loading) {
            loadStep4Data(selectedCompanyId, obState);
        }
    }, [viewingStep, selectedCompanyId, obState, step4SubStep, step4Loading, loadStep4Data]);

    // The panel's per-row answer: an explicit operator choice if one was made, else the
    // DEFAULT starting position. Shared by the panel render and the import handler so the
    // radio you see is always the answer that gets submitted.
    //
    // ★ SAFE-BY-DEFAULT: a leaf whose label reads like a receivable / prepaid / deposit /
    // accrual defaults to SEPARATE; everything else defaults to TOTAL CASH. This means an
    // operator who clicks straight through does NOT fold A/R and Prepaid Expenses into the
    // cash rollup. It is only a starting position — every row stays freely editable to any
    // of the three options, the full list still renders, nothing is hidden or locked. The
    // classification MAP the operator submits (not this default) is what drives inference.
    //
    // Detection is unreliable in BOTH directions, which is why it may only set a DEFAULT and
    // may never filter: it misses real bank accounts (Wells Fargo #4165, Emburse Prepaid
    // Debit Card carry no cash/bank token) — those land in Total Cash, which is correct —
    // and it flags Undeposited Funds, which IS the de-facto-A/R case, so Separate is correct
    // there too. Where it guesses wrong, the operator sees the row and fixes it.
    const step4AnswerOf = (o) => {
        if (o.key === step4SweepKey) return 'sweep';
        if (step4Cls[o.key]) return step4Cls[o.key];
        return looksNonCash(o) ? 'separate' : 'rollup';
    };

    // ── Step 4a: Import financial data to defrev_data ───────
    const handleStep4Import = async () => {
        if (!step4GlData || !obState) return;
        if (!step4SweepKey) { setStep4ImportError('Please select a sweep account before importing.'); return; }
        // ★ B5 s5 — an unanswered asset leaf BLOCKS, and names itself. Same treatment Step 3
        // gives an unanswered payroll row and an unmapped IS line: incomplete, not defaulted.
        const unansweredAssets = step4AssetOptions.filter(o => !step4AssetCls[o.key]);
        if (unansweredAssets.length) {
            setStep4ImportError(
                'Classify every fixed / intangible asset line before importing — still unanswered: ' +
                unansweredAssets.map(o => o.label).join(', ') + '.');
            return;
        }
        /* ★ BUILD B — the same treatment for a Separate row whose kind cannot be inferred.
         * Blocked here as well as by the disabled button, because a write path must never
         * rely on a render guard having run (the C1b precedent). Without this the row would
         * submit a bare 'separate' and inferBsMetaLeaf would throw mid-import instead. */
        const unansweredSep = step4TypeUnansweredOf(step4BsOptions, step4TypeOptions, step4AnswerOf, step4Cls);
        if (unansweredSep.length) {
            setStep4ImportError(
                'Choose the type of every balance-sheet line before importing \u2014 still unanswered: ' +
                unansweredSep.map(o => o.label).join(', ') + '.');
            return;
        }
        const formulaClash = step4FormulaClashOf(step4TypeOptions, step4Cls);
        if (formulaClash.length) { setStep4ImportError(step4FormulaClashText(formulaClash)); return; }
        if (!step4TypesConfirmed) {
            setStep4ImportError('Confirm the balance-sheet line types (the checkbox above Import) before importing.');
            return;
        }
        try {
            setStep4Importing(true);
            setStep4ImportError('');
            const { glLines, mappings, months, priorDec } = step4GlData;

            // The operator's cash answers. Uses the SAME default rule the panel renders
            // (step4AnswerOf) — otherwise an untouched row could display 'Separate' but
            // submit 'rollup'.
            const classification = {};
            for (const o of step4BsOptions) {
                // A Separate row submits its TYPE -- the operator's, or the suggestion the page
                // confirm accepted; every other current asset its Sweep / Total Cash answer.
                const a = step4AnswerOf(o);
                const t = step4TypeOf(o, a, step4Cls);
                classification[o.key] = t ? t.answer : a;
            }
            // The liability and equity rows join the SAME map (one channel). A long-term
            // liability sends no answer: its type is always debt.
            for (const o of step4TypeOptions) {
                const t = step4TypeOf(o, undefined, step4Cls);
                if (t) classification[o.key] = t.answer;
            }
            // ★ B5 s5 — the asset answers join the SAME map and the SAME key space. ONE
            // classification channel, not two (§6): inferBsMetaAll already looks every leaf
            // up by bsKey, so nothing downstream changes shape. Guarded above, so an
            // undefined answer cannot reach here — and inferBsMetaLeaf throws if one does.
            for (const o of step4AssetOptions) classification[o.key] = step4AssetCls[o.key];

            // C-10: pass current department_mappings so a RE-import (after Step 5 exists) self-heals
            // the per-dg payroll keys into the whole-blob write. First pass returns [] → no per-dg keys.
            const deptForPayroll = await getDepartmentMappings(selectedCompanyId).then(r => r.data || []).catch(() => []);
            const payload = buildFinancialsPayload(
                glLines, mappings, months, priorDec, step4SweepKey, classification, step4TotalCashLabel, deptForPayroll
            );
            // W-013 (2026-09-25): A RE-IMPORT KEEPS EVERY OPERATOR EDIT to the balance-sheet
            // assumptions (Brian). The rule has ONE owner, planBsAssumptionSeed (utils/inferBsMeta.js):
            // a NEW or UNTOUCHED row is seeded; an EDITED row is kept and not sent; an edited row whose
            // line changed type is re-seeded with its map cleared; a row whose line left the spreadsheet
            // is deleted. Edit vs seed is decided by comparing each stored row with what the seed would
            // have written from the PRIOR meta -- which is still stored only until the import below
            // writes, so the plan is read HERE. A read that fails stops the import before any write.
            const [priorDefrev, storedAssum] = await Promise.all([
                getDefrevData(selectedCompanyId), getBSAssumptions(selectedCompanyId)]);
            const seedPlan = planBsAssumptionSeed(
                (priorDefrev.data || {}).bs_actuals_meta || {}, payload.bs_actuals_meta, storedAssum.data || {});
            // The notice: a KEPT row changes nothing, so only a reset or a removal is named -- in the
            // page's confirm idiom (step6RemoveVendor), before anything is written. Cancel writes nothing.
            if (seedPlan.reseeded.length || seedPlan.deleted.length) {
                const parts = [];
                if (seedPlan.reseeded.length) parts.push("These lines' assumptions will be reset to the new type's defaults: "
                    + seedPlan.reseeded.map(r => r.label).join(', ') + '.');
                if (seedPlan.deleted.length) parts.push('These lines are no longer in the spreadsheet and their assumptions will be removed: '
                    + seedPlan.deleted.map(r => r.label).join(', ') + '.');
                if (!window.confirm(parts.join('\n\n') + '\n\nImport the financial data?')) return;
            }
            await importOnboardingFinancials(selectedCompanyId, {
                ...payload,
                sweep_account_key: step4SweepKey,
                close_month: obState.close_month,
            });

            // Seed bs_assumptions from the roles Step 4 just wrote. The inference keys purely
            // on role + subcategory, so it "falls out" now that those exist. Uses the existing
            // bulk endpoint — no new route.
            //
            // ★ F0 — THE WRAPPER. PUT /bs-assumptions/bulk takes `{ line_id, updates }` per row
            // (its own 400 message says so) and does `const { line_id, updates: u } = item;
            // if (!line_id || !u) continue;`. buildBsAssumptions emits the FLAT
            // `{ line_id, ...cfg }`, so EVERY row hit that `continue`: the empty transaction
            // COMMITted and the endpoint answered HTTP 200 `{updated: 0}`. A successful-looking
            // 200 — which is why the catch below (a throw-only channel) never spoke and this
            // survived. MEASURED: cid=8, the only wizard-onboarded tenant, had ZERO
            // bs_assumptions rows while the three script-seeded tenants had theirs.
            // ★ THE WRAPPER LIVES HERE, AT THE BOUNDARY — NOT INSIDE buildBsAssumptions, which
            // has a SECOND consumer (verify-step4-cash-classification.js GATE 5) reading the
            // flat shape directly. One producer, one shape; the transport adapts at its edge.
            //
            // ★ AND THE READER. `{updated: N}` was returned and discarded. Without a reader the
            // only symptom is a $0 forecast balance sheet three steps later (computeBS:
            // `if (!assum) { result[line.id][m] = 0; return; }`), so a short write now BLOCKS
            // the advance to 4b with a named banner instead of a console line. Exact equality is
            // the right assertion: the handler is one transaction, so a real DB error throws and
            // 500s — `updated < target` can ONLY mean a malformed payload.
            // The financials are ALREADY imported and are NOT rolled back by a seed failure;
            // both sides are idempotent (financials DELETE+INSERT, seed ON CONFLICT DO UPDATE),
            // so re-clicking Import is a genuine recovery — the message says so.
            let seedCount = 0, seedTarget = 0, seedErr = null;
            try {
                // W-013: only the plan's rows are sent -- a KEPT row is not, so the exact-count reader
                // below compares against the plan. On a first import the plan's rows ARE
                // buildBsAssumptions(meta) wrapped as before, so that request is unchanged.
                seedTarget = seedPlan.writes.length;
                const seedRes = await updateBSAssumptionsBulk(selectedCompanyId, seedPlan.writes);
                seedCount = seedRes?.data?.updated;
                if (seedPlan.deleted.length) {
                    const ids = seedPlan.deleted.map(d => d.line_id);
                    const delRes = await deleteBSAssumptions(selectedCompanyId, ids);
                    if (delRes?.data?.deleted !== ids.length) {
                        throw new Error(`removed ${delRes?.data?.deleted} of ${ids.length} rows for lines no longer in the spreadsheet`);
                    }
                }
            } catch (aerr) {
                seedErr = aerr;
                console.error('bs_assumptions auto-seed failed (financials imported OK):', aerr);
            }
            if (seedErr || seedCount !== seedTarget) {
                setStep4ImportError(
                    'Financial data imported — but the balance-sheet assumptions were NOT seeded'
                    + (seedErr
                        ? ` (${seedErr?.response?.data?.error || seedErr?.message || 'unknown error'})`
                        : ` (wrote ${seedCount} of ${seedTarget} rows)`)
                    + '. The forecast balance sheet computes as $0 without them. Click Import again — it is safe to re-run.'
                );
                return;
            }
            // Compute review and transition to 4b.
            //
            // ★ Build 1 (PART 3a): resolved the same way as re-entry, from the meta this import just
            // built (payload.bs_actuals_meta) — one resolver, so the screen cannot show one thing
            // straight after an import and another after a reload. step4SweepKey is the saved-key
            // input; it is non-empty here (Import is blocked without it), so this normally resolves
            // identically — the shared path is for consistency, not to change this case.
            const sweepPick = resolveStep4Sweep(step4BsOptions, payload.bs_actuals_meta, step4SweepKey);
            const sweepGlName = sweepPick.glName;
            // ★ Build 1 (PART 2): on THIS path the members come from the operator's answers directly
            // (the `classification` submitted above) and the label from the field they typed.
            const cashRollup = buildStep4CashRollup(step4BsOptions, { classification, label: step4TotalCashLabel });
            const bsKeyMap = buildBsKeyResolver(glLines, mappings);
            const result = computeHistoricalFinancials(glLines, mappings, months, priorDec, sweepGlName, cashRollup,
                l => bsKeyMap(l.gl_name, l.gl_category), deptForPayroll);
            // ★ Build 1: attach the meta this import produced. Previously the import path set
            // step4Data WITHOUT defrevData while the re-entry path set it WITH — so anything reading
            // step4Data.defrevData rendered only after a reload, never straight after the import,
            // which is the one moment the operator is looking at the screen.
            setStep4Data({ ...result, months, priorDec, defrevData: { ...payload } });
            setStep4SubStep('4b');
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Unknown error';
            setStep4ImportError('Failed to import financial data: ' + msg);
            console.error(err);
        } finally { setStep4Importing(false); }
    };

    // Re-import button on 4b: go back to 4a without deleting anything in DB
    const handleStep4ReImport = () => {
        setStep4Data(null);
        setStep4SubStep('4a');
        setStep4ImportError('');
    };

    // Navigate back to Step 2 or 3 from Step 4 (does NOT clear data)
    const handleStep4BackToStep = async (stepNum) => {
        try {
            await updateOnboardingState(selectedCompanyId, {
                current_step: stepNum,
                close_month: obState.close_month,
                forecast_start: obState.forecast_start,
                historical_start: obState.historical_start,
                steps: obState.steps || {},
            });
            await loadWizardState(selectedCompanyId);
            setViewingStep(stepNum);
        } catch (err) {
            console.error('Failed to navigate back:', err);
            setError('Failed to navigate: ' + (err?.message || 'Unknown error'));
        }
    };

    const handleStep4Complete = async () => {
        setError('');
        if (!step4Data) return;
        const checkRow = step4Data.bs?.checkRow || {};
        const anyOutOfBalance = Object.values(checkRow).some(v => Math.abs(v || 0) >= 0.01);
        if (anyOutOfBalance) { setError('Balance Sheet does not balance. Fix before continuing.'); return; }
        try {
            setSaving(true);
            /* ★ Part 3c — SPREAD THE SLUG, do not rebuild it. The Step-4 import writes
             * review_financials = { imported, sweep_account_key } (server.js:2462-2468); this used to
             * overwrite that whole object with one field, so finishing Step 4 destroyed the sweep pick
             * the import had just stored (cid=3 holds exactly {"completed": true} because of it). The
             * server-side merge is one level deep and cannot save a slug from its own writer. */
            const newSteps = {
                ...(obState?.steps || {}),
                review_financials: { ...(obState?.steps?.review_financials || {}), completed: true },
            };
            await updateOnboardingState(selectedCompanyId, {
                // cursor is a high-water mark — never moves backward (see the Step-2 note)
                current_step: Math.max(obState?.current_step || 5, 5), steps: newSteps,
                close_month: obState.close_month, forecast_start: obState.forecast_start, historical_start: obState.historical_start,
            });
            await loadWizardState(selectedCompanyId);
            setViewingStep(5);
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Unknown error';
            setError(`Failed to complete step: ${msg}`);
            console.error(err);
        } finally { setSaving(false); }
    };

    const toggleStep4Section = (key) => {
        setStep4Collapsed(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
    };

    // ── Step 5: Payroll Setup ────────────────────────────────
    const loadStep5Data = useCallback(async (companyId, obStateArg) => {
        try {
            setStep5Loading(true);
            setStep5ImportError('');
            const [empRes, deptRes] = await Promise.all([
                getEmployees(companyId).catch(() => ({ data: [] })),
                getDepartmentMappings(companyId).catch(() => ({ data: [] })),
            ]);
            const employees = empRes.data || [];
            if (employees.length > 0) {
                const deptSet = new Map();
                for (const e of employees) {
                    if (!e.department) continue;
                    const cur = deptSet.get(e.department) || { name: e.department, count: 0, is_category: e.is_category };
                    cur.count += 1;
                    deptSet.set(e.department, cur);
                }
                const salesReps = employees.filter(e => e.is_sales_rep || (Number(e.annual_quota) || 0) > 0).length;
                setStep5ExistingCount(employees.length);
                setStep5ExistingSalesReps(salesReps);
                setStep5ExistingDepts([...deptSet.values()]);
                setStep5SubStep('re-entry');
            } else {
                setStep5ExistingCount(0);
                setStep5ExistingSalesReps(0);
                setStep5ExistingDepts([]);
                // Preserve saved dept mappings for auto-suggest defaults in 5c
                if (deptRes.data && deptRes.data.length > 0) {
                    // Will be merged into 5c when 5c mounts
                }
                setStep5SubStep('5a');
            }
        } catch (err) {
            console.error('Failed to load Step 5 data:', err);
            setError('Failed to load payroll data: ' + (err?.message || 'Unknown error'));
        } finally { setStep5Loading(false); }
    }, []);

    useEffect(() => {
        if (viewingStep === 5 && selectedCompanyId && obState && step5SubStep === null && !step5Loading) {
            loadStep5Data(selectedCompanyId, obState);
        }
    }, [viewingStep, selectedCompanyId, obState, step5SubStep, step5Loading, loadStep5Data]);

    /* W-022 (B): the payroll workbook rows + mappings the Step-5c back-fill reads, loaded when 5c
       opens, so the screen can name a payroll row the back-fill will refuse BEFORE the save moves on
       (the save leaves 5c at once). A failed read shows nothing -- the back-fill still runs as before. */
    const [step5PayrollSrc, setStep5PayrollSrc] = useState(null);
    /* Batch 1b (2026-11-03, L3643 / W-127): the inputs used to load ONCE, on opening 5c (diffs/w022-build.txt: "does not re-check after
       a re-upload until 5c is reopened"). They are now re-read whenever Step 5 is viewed again with 5c open, and when the window regains
       focus or becomes visible while 5c is open -- a workbook re-uploaded at Step 2 in another tab (the notice's own remedy) is re-checked
       on return, without reopening 5c. */
    const [step5PayrollReload, setStep5PayrollReload] = useState(0);
    useEffect(() => {
        if (step5SubStep !== '5c' || viewingStep !== 5) return;
        const again = () => { if (document.visibilityState !== 'hidden') setStep5PayrollReload(n => n + 1); };
        window.addEventListener('focus', again);
        document.addEventListener('visibilitychange', again);
        return () => { window.removeEventListener('focus', again); document.removeEventListener('visibilitychange', again); };
    }, [step5SubStep, viewingStep]);
    useEffect(() => {
        if (step5SubStep !== '5c' || !selectedCompanyId || viewingStep !== 5) return;
        let live = true;
        Promise.all([getHistoricalGLLines(selectedCompanyId), getGLLineMappings(selectedCompanyId)])
            .then(([g, m]) => { if (live) setStep5PayrollSrc({ glLines: g.data || [], mappings: m.data || [] }); })
            .catch(() => { if (live) setStep5PayrollSrc(null); });
        return () => { live = false; };
    }, [step5SubStep, selectedCompanyId, viewingStep, step5PayrollReload]);

    const handleStep5ReImport = () => {
        // Reset state and go to 5a. Actual employee deletion happens on import.
        setStep5File(null); setStep5Parsed(null); setStep5ParseError('');
        setStep5BenefitCols([]); setStep5DeptMap([]); setStep5Page(0);
        setStep5ImportError('');
        setStep5Employees([]); setStep5Expanded(new Set());
        setStep5EditMode(false); setStep5BenefitRenames([]); setStep5EditOriginals({});
        setStep5SubStep('5a');
    };

    // ── Re-entry edit mode: load employees + dept mappings from DB, rebuild
    // state so 5b/5c/5d render from DB, flip edit-mode flag on.
    const loadStep5ForEdit = async () => {
        const [empRes, deptRes, drRes] = await Promise.all([
            getEmployees(selectedCompanyId).catch(() => ({ data: [] })),
            getDepartmentMappings(selectedCompanyId).catch(() => ({ data: [] })),
            getDefrevData(selectedCompanyId).catch(() => ({ data: {} })),
        ]);
        const emps = empRes.data || [];
        const deptRows = deptRes.data || [];

        /* ★ D2 — which display_groups already have HISTORICAL actuals?
         * display_group composes the payroll IS-line key (buildPayrollIsLineKey →
         * `sm_sales_mktg_wages`), and those keys are PERSISTED in is_actuals. Changing a
         * group's MEMBERSHIP re-keys its leaves, so every closed month would read $0 —
         * silently. Membership is therefore frozen for any group with history; reordering
         * and re-labelling stay free (neither touches the slug). */
        const isActuals = (drRes.data || {}).is_actuals || {};
        const withHistory = new Set();
        for (const k of Object.keys(isActuals)) {
            const m = k.match(/^(?:cogs|sm|ga|rd)_(.+)_(?:wages|bonuses|payroll_taxes|benefits)$/);
            if (m) withHistory.add(m[1]);
        }
        setStep5DgsWithHistory(withHistory);

        // Build step5DeptMap from saved mappings + employee counts. Walk employees
        // to get live counts; fall back to row without a count if no employees match.
        const deptCounts = new Map();
        for (const e of emps) {
            if (!e.department) continue;
            deptCounts.set(e.department, (deptCounts.get(e.department) || 0) + 1);
        }
        const savedByName = new Map();
        for (const d of deptRows) savedByName.set(d.department_name, d);
        const allDeptNames = new Set([...deptCounts.keys(), ...savedByName.keys()]);
        const deptMap = [...allDeptNames].map(name => {
            const s = savedByName.get(name);
            return {
                department_name: name,
                is_category: s?.is_category || '',
                // Every department is its OWN group by default. Merging is an explicit
                // operator action ("group with…"), never a slug the operator types.
                display_group: s?.display_group || deptSlug(name),
                // ★ THE FREEZE'S INPUT: the slug already PERSISTED for this row, kept apart from
                // the line above (which collapses saved-and-defaulted into one value). Null means
                // "never saved", which is the only state in which the Group Name may set the id.
                savedDisplayGroup: s?.display_group || null,
                display_group_label: s?.display_group_label || '',
                sort_order: s?.sort_order ?? 0,
                employee_count: deptCounts.get(name) || 0,
            };
        }).sort((a, b) => (a.sort_order - b.sort_order)
            || a.department_name.localeCompare(b.department_name));
        setStep5DeptMap(deptMap);

        // Build step5BenefitRenames from unique keys across all employees' benefit_components.
        const allKeys = new Set();
        for (const e of emps) {
            const bc = e.benefit_components || {};
            for (const k of Object.keys(bc)) allKeys.add(k);
        }
        const renames = [...allKeys].map(k => ({ originalKey: k, currentName: k }));
        setStep5BenefitRenames(renames);

        // Build step5Employees from DB rows for 5d edit. Keep _dbId + originals snapshot
        // so save diff-detects changed fields and only PATCHes dirty rows.
        const mapped = emps.map(e => ({
            _dbId: e.id,
            name: e.name || '',
            title: e.title || '',
            department: e.department || '',
            is_category: e.is_category || '',
            annual_salary: Number(e.annual_salary) || 0,
            state: e.state || step5DefaultState,
            start_date: e.start_date || null,
            benefitAmounts: { ...(e.benefit_components || {}) },
            bonus_amount: Number(e.bonus_amount) || 0,
            bonus_frequency: e.bonus_frequency || null,
            annual_quota: Number(e.annual_quota) || 0,
            is_sales_rep: !!e.is_sales_rep,
        }));
        setStep5Employees(mapped);
        const origSnap = {};
        for (const m of mapped) {
            origSnap[m._dbId] = {
                name: m.name, state: m.state,
                annual_salary: m.annual_salary, annual_quota: m.annual_quota,
                is_sales_rep: m.is_sales_rep,
                bonus_amount: m.bonus_amount,
                bonus_frequency: m.bonus_frequency,
            };
        }
        setStep5EditOriginals(origSnap);
        // Stub step5Parsed so renderStep5d's null-guard passes.
        setStep5Parsed({ employees: mapped, benefitColumns: [], departments: [], flags: [] });
        // Stub step5BenefitCols so 5d's included-benefits math works (renamed key = benefit name).
        setStep5BenefitCols(renames.map(r => ({
            headerName: r.originalKey, normalizedKey: r.originalKey,
            sampleValues: [], include: true, benefitName: r.currentName,
        })));
        setStep5Page(0);
        setStep5Expanded(new Set());
        setStep5EditMode(true);
    };

    const handleStep5Edit = async (targetSubStep) => {
        try {
            setStep5Loading(true);
            setStep5ImportError('');
            await loadStep5ForEdit();
            setStep5SubStep(targetSubStep);
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Unknown error';
            setStep5ImportError('Failed to load data for edit: ' + msg);
            console.error(err);
        } finally { setStep5Loading(false); }
    };

    const handleStep5BackToSummary = () => {
        setStep5EditMode(false);
        setStep5BenefitRenames([]);
        setStep5EditOriginals({});
        setStep5SubStep('re-entry');
        loadStep5Data(selectedCompanyId, obState);
    };

    // ★ C-10 (Build C): after department_mappings are saved, back-fill the per-dg payroll is_actuals
    // into the ALREADY-written blob (Step 4 ran before Step 5, so buildFinancialsPayload could not
    // emit them then). Client-computes / server-persists — the derivation is client-side (needs
    // matchPayrollRole etc.), the server just merges. Non-fatal: a failure warns and NEVER blocks the
    // wizard (the pre-flight's P4 flags any missing payroll history at model build). Idempotent: a
    // re-run reproduces the same keys. (A subsequent Step-4 re-import self-heals via buildFinancials
    // Payload, so this only owns the first-pass case.)
    /* Part B — departments now exist, so any IS leaf still carrying Step-3's
     * FALLBACK band gets re-based onto its display_group's real band.
     *
     * Step 3 runs before Step 5, so with no department_mappings dgSortBase returns
     * DG_BAND_WIDTH and every leaf is banded at 1000. That is correct while the
     * subsection has one group and WRONG once this save creates a second one —
     * and sort_order is INSERT-only, so nothing else would ever revise it.
     * See planSortOrderReband (inferIsMeta.js) for what it refuses to touch.
     *
     * Re-fetches rather than reusing local state: the plan must be computed from
     * the departments this save just wrote, exactly as backfillPerDgPayroll does.
     * Display-only, so a failure must never block the department save — warn and
     * move on, the same posture as the C-10 back-fill beneath it. */
    const rebandIsSortOrders = async () => {
        try {
            const [mapRes, deptRes] = await Promise.all([
                getGLLineMappings(selectedCompanyId),
                getDepartmentMappings(selectedCompanyId),
            ]);
            const { lines, skipped } = planSortOrderReband(mapRes.data, deptRes.data);
            if (skipped.length) {
                console.warn('[IS sort_order re-band] leaves NOT moved (guard):', skipped);
            }
            if (!lines.length) return;   // already banded correctly — idempotent no-op
            await reorderGLLineMappings(
                selectedCompanyId, lines.map(l => ({ id: l.id, sort_order: l.sort_order })));
            console.info(
                `[IS sort_order re-band] re-based ${lines.length} leaf(s) onto their display_group's band:`,
                lines.map(l => `${l.is_line_key} ${l.from}→${l.to}`).join(', '));
        } catch (err) {
            console.warn('[IS sort_order re-band] skipped:', err?.response?.data?.error || err?.message || err);
        }
    };

    const backfillPerDgPayroll = async () => {
        try {
            const [glRes, mapRes, deptRes, drRes] = await Promise.all([
                getHistoricalGLLines(selectedCompanyId),
                getGLLineMappings(selectedCompanyId),
                getDepartmentMappings(selectedCompanyId),
                getDefrevData(selectedCompanyId),
            ]);
            const isa = (drRes.data || {}).is_actuals || {};
            if (!Object.keys(isa).length) return;   // financials not imported yet — nothing to merge into
            const months = buildAllMonths(obState?.historical_start, obState?.close_month);
            if (!months.length) return;
            const { actuals, failures, collisions } = derivePerDgPayrollActuals(glRes.data, mapRes.data, deptRes.data, months);
            if (failures.length) console.warn('[perDgPayroll back-fill] unresolved payroll rows (NOT written):', failures);
            if (collisions.length) console.warn('[perDgPayroll back-fill] key collisions:', collisions);
            if (Object.keys(actuals).length) await mergeIsActualsPerDgPayroll(selectedCompanyId, actuals);
        } catch (e) {
            console.warn('[perDgPayroll back-fill] failed (non-fatal):', e?.message || e);
        }
    };

    // Edit-mode save: dept mappings only (5c).
    const handleStep5SaveEditDepts = async () => {
        try {
            setStep5Importing(true);
            setStep5ImportError('');
            // ★ Refuse before any write: two groups resolving to one id would merge silently.
            const clash = findDeptSlugCollision(step5DeptMap);
            if (clash) {
                setStep5ImportError(
                    `Two groups resolve to the same id '${clash.slug}' (${clash.groups.join(' and ')}) — rename one. `
                    + `A group's name becomes its internal id, so two groups cannot share one.`);
                setStep5Importing(false);
                return;
            }
            /* ★ FAIL LOUD ON ZERO MAPPINGS — see deptMappingsBlocker. Refused BEFORE any write and
               before any advance: the old `if (mappings.length > 0)` silently skipped the save and
               let the step complete, which is how a tenant reached the live model with none. */
            const deptBlock = deptMappingsBlocker(step5DeptMap, (step5Employees || []).length);
            if (deptBlock) {
                setStep5ImportError(deptBlock);
                setStep5Importing(false);
                return;
            }
            // Batch 1b (L3624): refuse before any write when the payroll history names a group no department has
            { const mis = step5PayrollGroupMismatchesOf(step5PayrollSrc, step5DeptMap, obState); if (mis.length) { setStep5ImportError(step5GroupMismatchMessage(mis)); setStep5Importing(false); return; } }
            const mappings = buildDeptMappingsPayload(step5DeptMap);
            await saveDepartmentMappings(selectedCompanyId, mappings);
            await backfillPerDgPayroll();   // C-10: departments now exist → write per-dg payroll history
            await rebandIsSortOrders();     // Part B: … and re-base any leaf still on Step-3’s fallback band
            handleStep5BackToSummary();
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Unknown error';
            setStep5ImportError('Failed to save department mappings: ' + msg);
        } finally { setStep5Importing(false); }
    };

    // Edit-mode save: benefit renames — fires rename endpoint once per changed key.
    const handleStep5SaveEditBenefits = async () => {
        try {
            setStep5Importing(true);
            setStep5ImportError('');
            const changed = step5BenefitRenames.filter(r => r.currentName && r.currentName !== r.originalKey);
            for (const r of changed) {
                await renameBenefitComponent(selectedCompanyId, r.originalKey, r.currentName);
            }
            handleStep5BackToSummary();
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Unknown error';
            setStep5ImportError('Failed to rename benefit components: ' + msg);
        } finally { setStep5Importing(false); }
    };

    // Edit-mode save: per-employee PATCH — only fields that actually changed.
    const handleStep5SaveEditRoster = async () => {
        try {
            setStep5Importing(true);
            setStep5ImportError('');
            /* W-001 -- A SALARY CORRECTION HERE REGENERATES THE WHOLE SERIES (mode 'full').
               The engine books forecast wages from the STORED employee_monthly_actuals
               (computePayroll reads monthlyData), so a PATCH of annual_salary alone was saved,
               displayed, and moved nothing. Brian (2026-09-23): a correction inside the
               onboarding wizard goes back through the WHOLE history -- the number was wrong all
               along. (On a live tenant the Payroll Modal applies a typo fix FORWARD only; that
               is regenerateSalarySeries' 'forward' mode and is not touched here.) Closed IS
               months come from is_actuals, so this cannot move a closed IS figure.
               Only annual_salary triggers it: it is the only series field this editor edits
               (name / state / quota / bonus are not inputs to buildMonthlyData).
               THE TRAP: this screen's state carries annual_salary and start_date but NOT
               end_date and NOT the raises. Regenerating from it would pay a terminated employee
               as active and drop every raise. So the record is read from its OWNERS first
               (GET /employees-with-actuals: the employees row + employee_raises + the stored
               series), and everything is checked BEFORE any write. */
            const origOf = e => step5EditOriginals[e._dbId] || {};
            const salaryEdited = step5Employees.filter(e => e._dbId && Number(e.annual_salary) !== Number(origOf(e).annual_salary));
            const seriesSource = {};
            let seriesWindow = [];
            let seriesSchedule = null;   // bi-weekly step 4: the company's STORED pay schedule
            if (salaryEdited.length) {
                const historicalStart = obState?.historical_start;
                const forecastThrough = await readForecastThrough(selectedCompanyId);
                seriesWindow = (historicalStart && forecastThrough) ? buildAllMonths(historicalStart, forecastThrough) : [];
                if (!seriesWindow.length) {
                    setStep5ImportError('Cannot save a salary change: this company has no model window yet (historical start or forecast end is missing), so the monthly salary series cannot be rebuilt. Complete Step 1, then save again. Nothing was saved.');
                    return;
                }
                const paySettings = await readPaySettings(selectedCompanyId);
                if (!paySettings) {
                    setStep5ImportError('Cannot save a salary change: the pay schedule of this company could not be read, so the monthly salary series cannot be rebuilt. Nothing was saved. Try again.');
                    return;
                }
                seriesSchedule = payScheduleFromSettings(paySettings);
                const owners = (await getEmployeesWithActuals(selectedCompanyId)).data || [];
                const refused = [];
                for (const e of salaryEdited) {
                    const row = owners.find(r => r.id === e._dbId);
                    if (!row) { refused.push((e.name || 'employee ' + e._dbId) + ' (not returned by the server)'); continue; }
                    /* A terminated employee with no stored end_date: the series stops paying but
                       the row says nothing about when. buildMonthlyData would pay them to the end
                       of the window. Refuse rather than guess the date (section E1). The rule has ONE
                       owner since 10-09 (c), utils/leaverLastDay.js -- the Payroll page asks it too. */
                    const paidThrough = paidThroughWithoutLastDay(row.end_date, row.monthlyData, seriesWindow);
                    if (paidThrough) {
                        refused.push((e.name || 'employee ' + e._dbId) + ' (paid through ' + paidThrough + ' but has no end date)');
                        continue;
                    }
                    seriesSource[e._dbId] = row;
                }
                if (refused.length) {
                    setStep5ImportError('Cannot save: the monthly salary series cannot be rebuilt safely for ' + refused.join('; ') + '. Set their termination date on the Payroll page first. Nothing was saved.');
                    return;
                }
            }
            for (const e of step5Employees) {
                if (!e._dbId) continue;
                const orig = step5EditOriginals[e._dbId] || {};
                const patch = {};
                if (e.name !== orig.name) patch.name = e.name;
                if (e.state !== orig.state) patch.state = e.state;
                if (Number(e.annual_salary) !== Number(orig.annual_salary)) patch.annual_salary = Number(e.annual_salary) || 0;
                const quotaVal = Number(e.annual_quota) || 0;
                if (quotaVal !== Number(orig.annual_quota)) {
                    patch.annual_quota = quotaVal;
                    patch.is_sales_rep = quotaVal > 0;
                }
                const bonusVal = Number(e.bonus_amount) || 0;
                if (bonusVal !== Number(orig.bonus_amount)) patch.bonus_amount = bonusVal;
                const freqVal = e.bonus_frequency || null;
                if (freqVal !== (orig.bonus_frequency || null)) patch.bonus_frequency = freqVal;
                if (Object.keys(patch).length > 0) {
                    await patchEmployee(selectedCompanyId, e._dbId, patch);
                    const row = seriesSource[e._dbId];
                    if (patch.annual_salary !== undefined && row) {
                        const d10 = v => (v ? String(v).slice(0, 10) : null);
                        const series = regenerateSalarySeries({
                            mode: 'full',
                            salary: patch.annual_salary,
                            startDate: d10(row.start_date),
                            termDate: d10(row.end_date),
                            raises: (row.raises || []).map(r => ({ salary: r.salary, effectiveDate: d10(r.effectiveDate) })),
                            allMonths: seriesWindow,
                            schedule: seriesSchedule,
                        });
                        // Every month of the window, zeros included: /actuals/bulk never deletes.
                        await bulkUpsertEmployeeActuals(selectedCompanyId, e._dbId,
                            Object.entries(series).map(([month, amount]) => ({ month, amount })));
                    }
                }
            }
            handleStep5BackToSummary();
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Unknown error';
            setStep5ImportError('Failed to save employee changes: ' + msg);
        } finally { setStep5Importing(false); }
    };

    const handleStep5Parse = async () => {
        setStep5ParseError('');
        if (!step5File) { setStep5ParseError('Please select a file.'); return; }
        try {
            setStep5Parsing(true);
            /* THE UPLOAD GATE, IN THE HANDLER TOO (the control is disabled on what the panel reported; this asks the OWNER):
               no roster is parsed until a pay frequency is stored. The same answer picks the raise-day rule the parser applies. */
            const paySettings = await readPaySettings(selectedCompanyId);
            if (!paySettings) { setStep5ParseError('The pay schedule of this company could not be read, so the roster was not parsed. Try again.'); return; }
            if (!paySettings.pay_frequency) { setStep5PayStored(null); setStep5ParseError(UPLOAD_REQUIRED_MESSAGE + '. Save the Pay Schedule above, then upload the roster.'); return; }
            const buf = await step5File.arrayBuffer();
            const result = parseEmployeeExcel(buf, { payFrequency: paySettings.pay_frequency });
            if (!result.employees || result.employees.length === 0) {
                setStep5ParseError('No employees found in the file. Check that the first row is a header and subsequent rows have employee names.');
                setStep5Parsing(false);
                return;
            }
            // W-068 pass 3 -- the upload check, merged into the roster's own flags (EmployeeFlagNotices renders them on
            // 5b; a BLOCKING problem disables "Confirm Benefits" and the import).
            result.flags = mergeSpecProblems(result.flags, checkAgainstSpec('employee', result));
            setStep5Parsed(result);

            // Initialize benefit columns (all included by default)
            const benefitCols = (result.benefitColumns || []).map(c => ({
                headerName: c.headerName,
                normalizedKey: c.normalizedKey,
                sampleValues: c.sampleValues || [],
                include: true,
                benefitName: c.normalizedKey,
            }));
            setStep5BenefitCols(benefitCols);

            // Initialize dept mappings — try to load saved first, then auto-suggest
            let savedMap = {};
            try {
                const savedRes = await getDepartmentMappings(selectedCompanyId);
                for (const row of (savedRes.data || [])) savedMap[row.department_name] = row.is_category;
            } catch { /* ignore */ }
            const depts = (result.departments || []).map(d => {
                const suggested = savedMap[d.name] || suggestDeptCategory(d.name, d.is_category);
                return { department_name: d.name, is_category: suggested, employee_count: d.count };
            });
            setStep5DeptMap(depts);

            // Seed step5Employees as mutable working copy (used in 5d editing).
            const seeded = result.employees.map(e => ({
                ...e,
                state: e.state || step5DefaultState,
                is_sales_rep: (e.annual_quota || 0) > 0,
                benefitAmounts: { ...(e.benefitAmounts || {}) },
            }));
            setStep5Employees(seeded);
            setStep5Expanded(new Set());

            // Advance: 5b always (so user can add manual benefits even if none detected)
            setStep5SubStep('5b');
        } catch (err) {
            console.error(err);
            setStep5ParseError('Failed to parse file: ' + (err?.message || 'Unknown error'));
        } finally { setStep5Parsing(false); }
    };

    const handleStep5SaveDepts = async () => {
        // Batch 1b (L3624): the safety net (the payroll history names a group no department has) -- refused before any write
        { const mis = step5PayrollGroupMismatchesOf(step5PayrollSrc, step5DeptMap, obState); if (mis.length) { setStep5ImportError(step5GroupMismatchMessage(mis)); return; } }
        try {
            setStep5Importing(true);
            setStep5ImportError('');
            // ★ Refuse before any write: two groups resolving to one id would merge silently.
            const clash = findDeptSlugCollision(step5DeptMap);
            if (clash) {
                setStep5ImportError(
                    `Two groups resolve to the same id '${clash.slug}' (${clash.groups.join(' and ')}) — rename one. `
                    + `A group's name becomes its internal id, so two groups cannot share one.`);
                setStep5Importing(false);
                return;
            }
            /* ★ FAIL LOUD ON ZERO MAPPINGS — see deptMappingsBlocker. Refused BEFORE any write and
               before any advance: the old `if (mappings.length > 0)` silently skipped the save and
               let the step complete, which is how a tenant reached the live model with none. */
            const deptBlock = deptMappingsBlocker(step5DeptMap, (step5Employees || []).length);
            if (deptBlock) {
                setStep5ImportError(deptBlock);
                setStep5Importing(false);
                return;
            }
            const mappings = buildDeptMappingsPayload(step5DeptMap);
            await saveDepartmentMappings(selectedCompanyId, mappings);
            await backfillPerDgPayroll();   // C-10: departments now exist → write per-dg payroll history
            await rebandIsSortOrders();     // Part B: … and re-base any leaf still on Step-3’s fallback band

            // Inject manual benefit defaults into step5Employees before entering 5d.
            // Only add keys not already present so prior edits are preserved.
            const includedManuals = step5BenefitCols
                .filter(c => c.include && c.isManual && c.normalizedKey)
                .map(c => ({ key: c.normalizedKey, amount: Number(c.defaultAmount) || 0 }));
            if (includedManuals.length > 0) {
                setStep5Employees(prev => prev.map(e => {
                    const b = { ...(e.benefitAmounts || {}) };
                    for (const m of includedManuals) {
                        if (!(m.key in b)) b[m.key] = m.amount;
                    }
                    return { ...e, benefitAmounts: b };
                }));
            }

            setStep5SubStep('5d');
            setStep5Page(0);
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Unknown error';
            setStep5ImportError('Failed to save department mappings: ' + msg);
        } finally { setStep5Importing(false); }
    };

    const handleStep5Import = async () => {
        if (!step5Parsed) return;
        // W-068: a roster with blocking upload problems is never imported (5b already stops it; this is the backstop).
        { const nBlock = countBlocking(step5Parsed.flags); if (nBlock) { setStep5ImportError(blockingHeading(nBlock)); return; } }
        try {
            setStep5Importing(true);
            setStep5ImportError('');

            // Build dept → is_category lookup
            const deptToCat = {};
            for (const d of step5DeptMap) deptToCat[d.department_name] = d.is_category;

            // Build set of included benefit keys — skip entries without a usable key/name
            const validBenefits = step5BenefitCols.filter(c => c.include && c.normalizedKey);
            const includedBenefits = new Set(validBenefits.map(c => c.normalizedKey));
            const benefitNameByKey = {};
            for (const c of validBenefits) benefitNameByKey[c.normalizedKey] = c.benefitName || c.normalizedKey;

            // (A `firstForecast` was derived here and never read — comp-cycle #1 removed
            //  its only consumer, see the DO-NOT-stamp note below. Deleted with C1b.)

            // Use edited step5Employees when available; fall back to parsed employees.
            const sourceEmps = step5Employees.length > 0 ? step5Employees : step5Parsed.employees;

            // ★ C-2 fix (Build B): derive each employee's salary series over the FULL model window
            // (historical_start … forecast_through), NOT forecast-only — computePayroll consumes the
            // historical months for calendar-YTD tax-cap accumulation (they are never displayed;
            // is_actuals wins for m ≤ close_month). ONE shared derivation (../engines/salarySeries).
            // Onboarding captures no raises/end_date (a fresh hire has none — raises are a separate
            // C-item), so raises=[] / termDate=null here. A missing window → [] → the pre-flight's P7
            // fires loud rather than a silent $0 (never silently ship an empty series).
            /* ★★ H1 — THE HORIZON IS READ FROM ITS OWNER, NOT FROM A CACHED COPY.
               This used to be `companies.find(c => c.id === selectedCompanyId)?.forecast_through`
               — the CLIENT'S IN-MEMORY array — and NOTHING REFRESHES THAT ARRAY AFTER STEP 1
               WRITES THE COLUMN. Measured: `loadCompanies` has exactly three call sites (the
               mount effect and the two "← Back to Companies" buttons); the Step-1 save is not
               one of them, and `loadWizardState` refreshes neither `companies` nor the horizon.
               The "+ New Company" create returns `forecast_through: NULL` (the INSERT sets only
               name + fiscal_year_end), so in an unbroken create → Step 1 → Step 5 session the
               cached row still said NULL while the SERVER held the real horizon.
               ⇒ THE SYMPTOM WAS SILENT: a null horizon made `emaWindow` EMPTY, every employee
                 shipped `monthly_actuals: []`, and nothing said so until pre-flight P7 failed
                 two steps later. Step 6 already blocked loudly on the same state; Step 5 did not.
               ⇒ ★ READ THE OWNER AT THE MOMENT OF USE. `companies.forecast_through` is owned by
                 the server and `GET /companies/:id/settings` returns it (server.js:1311).
                 `getCompanySettings` was ALREADY imported and already used twice in this file, so
                 this is one awaited call on a path that is already async — not new plumbing.
               ⛔ REFRESHING `companies` AFTER STEP 1 WAS REJECTED: it closes the one path we
                 measured and leaves every other open (a later Step-1 edit, any arrival that does
                 not remount). That fixes an instance; this fixes the class. (PRINCIPLE 0, and the
                 same move that closed the year-end close: read the owner, never a sibling cache.) */
            const historicalStart = obState?.historical_start;
            const forecastThrough = await readForecastThrough(selectedCompanyId);
            /* ★ §E1 — A BLANK IS FLAGGED, NEVER SILENTLY DEFAULTED, and Step 5 now refuses the
               way Step 6 already did. An empty `monthly_actuals` grid IS a blank being accepted:
               it produced a tenant whose forecast payroll was $0 for its whole horizon, caught
               only downstream. ⛔ The message MIRRORS Step 6's existing block — same shape, same
               instruction, one vocabulary — it does not invent a second.
               ⛔ P7 IS UNTOUCHED and remains the backstop: this stops the empty grid being
                 written, it does not replace the check that catches one that already exists. */
            const emaWindow = (historicalStart && forecastThrough) ? buildAllMonths(historicalStart, forecastThrough) : [];
            if (!emaWindow.length) {
                setStep5ImportError(
                    'Import blocked — this company has no forecast horizon, so employee salary ' +
                    'series cannot be generated. Set Forecast Through in Step 1 (Company Setup), ' +
                    'then return to this step.'
                );
                setStep5Importing(false);
                return;
            }
            /* Bi-weekly step 4: every series below is derived under the company's STORED pay schedule, read now. No stored
               frequency = no import (Brian B2: no silent default; the upload is already disabled without one). */
            const paySettings = await readPaySettings(selectedCompanyId);
            if (!paySettings || !paySettings.pay_frequency) {
                setStep5ImportError(paySettings
                    ? 'Import blocked \u2014 this company has no pay schedule yet, so employee salary series cannot be generated. Save the Pay Schedule in Step 5 (above the roster upload), then import again.'
                    : 'Import blocked \u2014 the pay schedule of this company could not be read, so employee salary series cannot be generated. Try again.');
                setStep5Importing(false);
                return;
            }
            const importSchedule = payScheduleFromSettings(paySettings);

            const employees = sourceEmps.map(e => {
                const isCat = deptToCat[e.department] || e.is_category || 'G&A';
                const benefitComponents = {};
                for (const [k, v] of Object.entries(e.benefitAmounts || {})) {
                    if (!includedBenefits.has(k)) continue;
                    const displayKey = benefitNameByKey[k] || k;
                    benefitComponents[displayKey] = Number(v) || 0;
                }
                const quotaVal = Number(e.annual_quota) || 0;
                const isSalesRep = quotaVal > 0;
                return {
                    name: e.name,
                    title: e.title || null,
                    department: e.department || null,
                    is_category: isCat,
                    annual_salary: Number(e.annual_salary) || 0,
                    state: e.state || step5DefaultState,
                    default_state: step5DefaultState,
                    start_date: e.start_date || null,
                    benefit_components: benefitComponents,
                    bonus_amount: e.bonus_amount || 0,
                    bonus_frequency: e.bonus_frequency || null,
                    // ★ COMP-CYCLE (item #1): DO NOT stamp firstForecast here. Leaving the
                    // anchor null lets computePayroll DERIVE it from the company comp cycle
                    // (calendar period-END for bonus). Stamping firstForecast was the walk gap —
                    // it made a product-onboarded tenant's bonuses land a month late vs cid=3's
                    // scripted period-end anchors.
                    bonus_start_month: null,
                    is_sales_rep: isSalesRep,
                    annual_quota: quotaVal,
                    // ★ EMP-1: these were hardcoded 0 / 'monthly', which DISCARDED whatever the
                    // parser read — cid=3's 8 reps are quarterly @ 0.08, so a product-onboarded
                    // tenant imported them monthly @ 0%, losing both the commission dollars and
                    // the quarterly cycle-close lumping. Carry the parsed values through;
                    // server.js:2539-2540 persists them (`e.commission_rate || 0`,
                    // `e.commission_frequency || 'monthly'`), so null/absent still defaults safely.
                    commission_rate: e.commission_rate ?? 0,
                    commission_frequency: e.commission_frequency || 'monthly',
                    // ★ COMP-CYCLE: null → derived period-START (first forecast month) at read.
                    commission_start_month: null,
                    /* ★★ ITEM 4b — THE RAISE, IN THE MODAL'S OWN SHAPE.
                     * ⛔ `raises` USED TO BE A HARDCODED `[]` HERE, and that literal was half the bug:
                     *   even with a parser that reads the columns, this call would still derive a FLAT
                     *   series, and the import PERSISTS what the client derives (the C-2 pattern), so a
                     *   captured raise would have been silently discarded one line before it was sent.
                     * ⛔ THE STORED SHAPE IS THE PAYROLL MODAL'S, NOT AN IMPORT-ONLY ONE — same three
                     *   columns, so a row written here is indistinguishable from one the COMPENSATION
                     *   HISTORY editor writes, and is editable there afterwards.
                     * ★ raise_pct MATCHES THE MODAL'S **SAVE** FORMULA (PayrollPage `handleSave`):
                     *   against the STARTING salary, not the previous raise. The modal's DISPLAY uses
                     *   the previous raise as its base; the two agree for a first raise and diverge
                     *   after, and we import at most ONE raise per employee, so they always agree here.
                     *   ⓘ The column is measured READ BY NOTHING (the engine ignores it; the screen
                     *   recomputes at render) — it is written to match the modal, not because a number
                     *   depends on it. */
                    raises: e.raise
                        ? [{
                            effective_date: e.raise.effective_date,
                            new_salary: e.raise.new_salary,
                            raise_pct: Number(e.annual_salary) > 0
                                ? r2(((e.raise.new_salary - Number(e.annual_salary)) / Number(e.annual_salary)) * 100)
                                : 0,
                        }]
                        : [],
                    monthly_actuals: emaWindow.length
                        ? Object.entries(buildMonthlyData(
                            Number(e.annual_salary) || 0, e.start_date || null, e.end_date || null,
                            // ⛔ buildMonthlyData reads `salary` + `effectiveDate` (NOT the stored column
                            // names) and does STRING ops on the date — so it is handed the engine's shape.
                            e.raise ? [{ salary: e.raise.new_salary, effectiveDate: e.raise.effective_date }] : [],
                            emaWindow, importSchedule))
                            .map(([month, amount]) => ({ month, amount }))
                        : [],
                };
            });

            await importOnboardingEmployees(selectedCompanyId, employees);
            // Build 2b-2b: flush staged SUTA overrides AFTER a successful roster import.
            // Roster-first: if the import above threw, we never reach here (operator retries
            // clean, nothing staged written). Each override PUT is best-effort — a failure is
            // collected and surfaced non-blockingly, NEVER thrown, NEVER rolls back the roster,
            // NEVER blocks advancing to step 6. (walked states == imported states: derived from
            // the same payload the import used.)
            const sutaStates = [...new Set(employees.map(e => e.state || step5DefaultState))].sort();
            const staging = buildStep5SutaStaging(sutaStates);
            const flushErrors = [];
            for (const [st, body] of Object.entries(staging)) {
                try { await putCompanyStateSuta(selectedCompanyId, st, body); }
                catch (e) {
                    flushErrors.push({ state: st, error: e?.response?.data?.error || e?.message || 'save failed' });
                    console.warn(`[Step 5e] SUTA override PUT failed for ${st}:`, e?.message);
                }
            }
            setStep5SutaFlushErrors(flushErrors);
            await loadWizardState(selectedCompanyId);
            /* Commission ship 5: the commission answer depends on the STORED roster (the basis is asked only when a rep is on a
               non-monthly cycle, and the route counts the reps from the employees table), so it is asked HERE, after the import.
               A company whose roster has a commissioned rep and no stored answer stays on Step 5 -- loadWizardState reset the
               sub-step, so the summary loads with the panel and its continue closed. Every other company: Step 6, as before. */
            if (commissionAnswerMissing(employees, paySettings)) setViewingStep(5);
            else setViewingStep(6);
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Unknown error';
            setStep5ImportError('Failed to import employees: ' + msg);
            console.error(err);
        } finally { setStep5Importing(false); }
    };

    // ── Step 5e: SUTA Rates (build 2b-2b) ────────────────────
    // The distinct roster states to walk — SAME sourceEmps expression the import uses
    // (step5Employees preferred, step5Parsed fallback) so walked states == imported states.
    const distinctRosterStates = () => {
        const sourceEmps = step5Employees.length > 0 ? step5Employees : (step5Parsed?.employees || []);
        return [...new Set(sourceEmps.map(e => e.state || step5DefaultState))].sort();
    };

    // Fetch global rates + this company's existing overrides; build a latest-year lookup.
    // Never traps the operator: on failure, set a non-blocking notice and proceed (staging nothing).
    const loadStep5SutaData = useCallback(async (companyId) => {
        setStep5SutaLoading(true);
        setStep5SutaFetchError('');
        try {
            const [stRes, ovRes] = await Promise.all([
                getStateTaxRates(),
                getCompanyStateSuta(companyId).catch(() => ({ data: [] })),
            ]);
            const rows = stRes.data || [];
            const latest = rows.reduce((mx, r) => Math.max(mx, Number(r.tax_year) || 0), 0);
            const lookup = {};
            rows.filter(r => Number(r.tax_year) === latest).forEach(r => {
                lookup[r.state_code] = {
                    globalRate: r.suta_rate == null ? null : parseFloat(r.suta_rate),
                    globalCap: r.suta_cap == null ? null : parseFloat(r.suta_cap),
                    rateRangeLow: r.rate_range_low == null ? null : parseFloat(r.rate_range_low),
                    rateRangeHigh: r.rate_range_high == null ? null : parseFloat(r.rate_range_high),
                };
            });
            setStep5StateTax(lookup);
            // Re-entry: pre-populate mode/input from existing override rows (rate present →
            // override w/ that rate; inherit {null} row → 'default' accepted).
            const mode = {}, input = {};
            (ovRes.data || []).forEach(o => {
                if (o.suta_rate != null) { mode[o.state_code] = 'override'; input[o.state_code] = String(Math.round(parseFloat(o.suta_rate) * 100 * 1e4) / 1e4); }
                else { mode[o.state_code] = 'default'; }
            });
            setStep5SutaMode(mode);
            setStep5SutaInput(input);
        } catch (err) {
            console.warn('[Step 5e] tax fetch failed:', err?.message);
            setStep5SutaFetchError("Couldn't load state rate tables — you can set SUTA rates in Tax Config after onboarding.");
            setStep5StateTax({});
        } finally {
            setStep5SutaLoaded(true);
            setStep5SutaLoading(false);
        }
    }, []);

    useEffect(() => {
        if (viewingStep === 5 && step5SubStep === '5e' && selectedCompanyId && !step5SutaLoaded && !step5SutaLoading) {
            loadStep5SutaData(selectedCompanyId);
        }
    }, [viewingStep, step5SubStep, selectedCompanyId, step5SutaLoaded, step5SutaLoading, loadStep5SutaData]);

    // Rate input handler: sanitize + client-side validate (0 < r ≤ 15%). Non-blocking.
    const setStep5SutaRate = (st, val) => {
        const clean = val.replace(/[^0-9.]/g, '');
        setStep5SutaInput(prev => ({ ...prev, [st]: clean }));
        setStep5SutaRowErr(prev => {
            const n = parseFloat(clean);
            const msg = clean.trim() === '' ? '' : (isNaN(n) || n <= 0 || n > 15 ? 'Rate must be between 0 and 15%' : '');
            return { ...prev, [st]: msg };
        });
    };

    // Derive the staging map { ST → { suta_rate, suta_cap:null } } from mode+input.
    //   default accepted        → { null, null }   (inherit + acknowledged on flush)
    //   default overridden      → { <dec>, null }
    //   null-rate with a value  → { <dec>, null }
    //   null-rate left blank    → ABSENT (never {null,null} — the PUT 400s on it)
    const buildStep5SutaStaging = (states) => {
        const out = {};
        for (const st of states) {
            const tax = step5StateTax[st];
            const isNullRate = !tax || tax.globalRate == null;
            const mode = step5SutaMode[st] || (isNullRate ? 'override' : 'default');
            const raw = String(step5SutaInput[st] ?? '').trim();
            if (!isNullRate && mode === 'default') { out[st] = { suta_rate: null, suta_cap: null }; continue; }
            if (raw === '') {
                if (!isNullRate) out[st] = { suta_rate: null, suta_cap: null }; // blank override on a default state → inherit
                continue; // null-rate blank → stage NOTHING
            }
            const n = parseFloat(raw);
            if (isNaN(n) || n <= 0 || n > 15) continue; // invalid → stage nothing (row error already shown)
            out[st] = { suta_rate: Math.round(n / 100 * 1e6) / 1e6, suta_cap: null };
        }
        return out;
    };

    const updateMapping = (idx, field, value) => {
        /* ★★ B s3 — THE CATEGORY SELECT CAN CARRY THE PAYROLL SENTINEL.
         * Translate it here, ONCE, so everything below still sees an ordinary is_category
         * value and the existing clear/re-infer logic is untouched. `handledByPick` is
         * undefined for every other field, so nothing else changes shape. */
        let handledByPick;
        if (field === 'is_category') {
            const f = categoryChoiceToFields(value);
            value = f.is_category;
            handledByPick = f.handled_by;
        }
        setMappings(prev => {
            const next = [...prev];
            next[idx] = { ...next[idx], [field]: value };
            // ★ Any explicit category choice is an OVERRIDE of the "Handled by Payroll"
            // default — the operator has decided this line IS mappable (or should stay Skip
            // on purpose). Clear the flag so the grid stops showing the payroll chip.
            if (field === 'is_category') next[idx].payrollHandled = false;
            if (field === 'is_category') next[idx].categorySuggested = false;   // the operator's choice now, not a guess
            if (field === 'is_category') next[idx].guessAccepted = false;       // Step-3 confirm: an un-tick never undoes a pick
            /* ★ B s2 — and clear the STORED answer with it. "Map instead" moves the row off
             * Skip, where handled_by is meaningless (the s1 migration forbids a non-Skip row
             * from carrying it). Clearing it returns the row to the guess-fallback, which is
             * exactly how it behaves today: the chip disappears and stays gone until the
             * category is Skip again. */
            if (field === 'is_category') next[idx].handled_by = null;
            /* ★ …and then apply what the pick actually meant. The s2 clear above still runs
             * for EVERY category pick — Skip and real categories alike — so "anything other
             * than Handled by Payroll clears it" holds by construction rather than by a list
             * of exceptions. Only the payroll pick puts a value back. */
            if (field === 'is_category') {
                next[idx].handled_by = handledByPick;
                next[idx].payrollHandled = payrollChipFor(
                    next[idx].is_category, handledByPick, next[idx].looksPayroll);
                // Commission ship 2b: a row that now shows "Handled by Payroll" gets a pay type (kept, else suggested); one
                // that no longer does ("Map instead", Skip) carries none, and the save sends NULL for it.
                next[idx] = withPayrollPaytype(next[idx]);
            }
            if (field === 'payroll_paytype') next[idx].paytypeSuggested = false;   // the operator's choice now, not a suggestion
            if (field === 'is_category' && next[idx].statement_type === 'IS') {
                /* ★★ NEVER OVERWRITE AN EXISTING KEY ON A CATEGORY CHANGE.
                 *
                 * This line used to be an unconditional
                 *     is_line_key = suggestISLineKey(...)
                 * whose fallback was `${prefix}_vendors` — so correcting a mis-categorized
                 * row silently re-keyed it, orphaning the vendors that join it (via
                 * vendors.is_line) and the is_actuals history stored under the old key.
                 *
                 * ★ THE KEY'S PREFIX IS NOT LOAD-BEARING FOR THE STATEMENT. buildISLines
                 * takes the leaf's `id` from is_line_key VERBATIM and its `subsection` from
                 * the `subsection` COLUMN (derived from is_category) — it never parses the
                 * prefix. So a leaf keyed `ga_office_rent` with subsection='rd' renders
                 * correctly under R&D and KEEPS its vendors and its history. Moving a line
                 * between sections and re-keying it are different operations, and only the
                 * first is a dropdown edit.
                 *
                 * Suggest ONLY for a row that has no key yet. */
                if (!next[idx].is_line_key) {
                    next[idx].is_line_key = suggestISLineKey(next[idx].gl_category, next[idx].gl_name, value);
                }
                // ★ role/subsection are DERIVED from is_category — re-infer, and clear the
                // stored override so the new category actually takes effect. Leaving a stale
                // role behind would route the line to the old section's engine source.
                next[idx].savedRole = null;
                next[idx].savedSubsection = null;
                /* ★ 'Skip' AND '' ARE THE UNDERSTOOD INERT STATE — NOT AN EXCEPTION.
                 *
                 * inferIsMetaLeaf classifies a line that IS on the statement; it accepts
                 * only Revenue/COGS/S&M/G&A/R&D/Other and THROWS on anything else — by
                 * design, so a NULL role can never be written silently. But Skip is a
                 * first-class choice this wizard goes out of its way to persist, and ''
                 * is what "Map instead" sets. Calling the classifier for either produced a
                 * red "Cannot classify this line" on the row AND blocked the save
                 * (handleSaveMappings throws on any inferError in `toSave`, which includes
                 * Skip rows) — so choosing Skip, or using the payroll escape hatch, wedged
                 * the step until the operator picked some other category and came back.
                 *
                 * A Skip row is inert by construction: role NULL ⇒ buildISLines.js:130
                 * emits no leaf. So null the derived fields and carry NO error. */
                if (!value || value === 'Skip') {
                    next[idx].role = null;
                    next[idx].subsection = null;
                    next[idx].inferError = null;
                } else {
                    try {
                        const meta = inferIsMetaLeaf(next[idx]);
                        next[idx].role = meta.role;
                        next[idx].subsection = meta.subsection;
                        next[idx].inferError = null;
                    } catch (e) {
                        next[idx].role = null; next[idx].subsection = null; next[idx].inferError = e.message;
                    }
                }
            }
            if (field === 'display_group') {
                next[idx].display_group = value || null;
                next[idx].savedDisplayGroup = value || null;
            }
            return next;
        });
        setMappingSaved(false);
    };

    const handleSaveMappings = async () => {
        /* ★ 'Skip' IS NOW PERSISTED. It used to be filtered out here, so an operator's
         * explicit "this line does not belong on the statement" decision was NEVER STORED —
         * and on reload the row came back blank unless isAutoSkip() happened to re-derive it
         * from the name. That is why the unmapped-IS blocker below could not ship without
         * this change: it would have fired on every re-entry for every deliberately-skipped
         * row, making Step 3 unexitable.
         *
         * ★ A PERSISTED SKIP IS INERT — role is NULL for it (classifyIsMappings nulls
         * role/subsection/display_group for Skip), and buildISLines.js:130 filters
         *     .filter(r => r.statement_type === 'IS' && r.role)
         * so a role-NULL row emits NO leaf. The same `role` gate that made B3-1 so dangerous
         * works FOR us here: a Skip row is a stored decision that the engine cannot see.
         * (Schema allows it: gl_line_mappings.is_category is varchar NULL, no CHECK.) */
        const toSave = mappings.filter(m => m.is_category);
        // ★ Refuse to save an unclassifiable IS line rather than write a NULL role — a NULL
        // role drops the line off the statement entirely and silently zeroes every vendor
        // mapped to it (§12.5 B3-1). The grid shows which row and why.
        const broken = toSave.filter(m => m.statement_type === 'IS' && m.inferError);
        if (broken.length) {
            throw new Error(`${broken.length} IS line(s) could not be classified: ` +
                broken.map(m => `"${m.gl_name}"`).join(', '));
        }
        // 2026-09-28 (B1): refuse a mapped IS row with no line key -- the gate disables the button; this is the write-path arm.
        const noKey = toSave.filter(m => lineKeyRequired(m) && !m.is_line_key);
        if (noKey.length) {
            throw new Error(`${noKey.length} income statement line(s) have no line key: ` +
                noKey.map(m => `"${m.gl_name}"`).join(', ') + '. Choose a line for each one.');
        }
        // Commission ship 2b: refuse a payroll line whose pay type is not an answer yet (blank, or a suggestion the page
        // confirm has not accepted) -- the gate disables Save & Continue; this is the write-path arm for "Save Mappings".
        const noPaytype = toSave.filter(m => m.handled_by === 'payroll' && unansweredPayrollPaytype(m, step3SuggestionsAccepted));
        if (noPaytype.length) {
            throw new Error(`${noPaytype.length} payroll line(s) have no confirmed pay type: ` +
                noPaytype.map(m => `"${m.gl_name}"`).join(', ') + '. Choose a pay type for each one, or accept the suggested ones.');
        }
        // ★ Refuse a re-key that would orphan vendors or history. The button is already
        // disabled; this is the defence-in-depth arm, so a "Save Mappings" click (which does
        // not consult step3Blocked) cannot slip past it either.
        if (step3Rekeys.length) {
            throw new Error(`${step3Rekeys.length} line key change(s) would detach vendors or history: ` +
                step3Rekeys.map(m => `${savedKeyOf(m)}→${currentKeyOf(m) || '(none)'}`).join(', ') +
                '. Restore the original key — re-keying is a data migration, not an edit.');
        }
        if (toSave.length > 0) {
            const rows = toSave.map(m => {
                // ★ A Skip row is a STORED DECISION, not a line. It carries NO key and NO
                // role/subsection/display_group — role NULL is what makes buildISLines drop it
                // (:130), so the engine literally cannot see it. Belt-and-braces: null them
                // here too, rather than trusting classifyIsMappings to have done it.
                const skipped = m.is_category === 'Skip';
                const isBS = m.statement_type === 'BS';
                return {
                    gl_name: m.gl_name, statement_type: m.statement_type, gl_category: m.gl_category || null,
                    is_category: m.is_category,
                    is_line_key: (isBS || skipped) ? null : m.is_line_key,
                    display_name: m.display_name,
                    // Step-3 pass: the four columns onboarding used to omit. BS rows carry none
                    // of them — the BS chart of accounts is built from bs_actuals_meta, which
                    // Step 4 classifies via inferBsMeta.
                    role: (isBS || skipped) ? null : (m.role || null),
                    subsection: (isBS || skipped) ? null : (m.subsection || null),
                    display_group: (isBS || skipped) ? null : (m.display_group || null),
                    sort_order: (isBS || skipped) ? 0 : (m.sort_order ?? 0),
                    /* ★★ B s2 — CARRY THE STORED handled_by BACK, OR THE SAVE DESTROYS IT.
                     *
                     * The server does `handled_by = EXCLUDED.handled_by` with NO COALESCE, so a
                     * payload that OMITS the field writes NULL over it. This payload is an
                     * explicit allowlist (§5) and omitted the column entirely, which meant the
                     * very first "Save Mappings" after s1 would have nulled all 80 backfilled
                     * values — the §5 data-wipe shape, already paid for twice (AE and CQ
                     * `existing_total_acv`). Round-tripping it here closes that on the only
                     * writer; a server-side COALESCE is worth adding as defence-in-depth when a
                     * later stage next touches server.js.
                     *
                     * NULL on anything that is not an IS Skip row: `handled_by` answers "why is
                     * this row Skip'd", so it is meaningless elsewhere, and the s1 migration
                     * asserts no non-Skip row carries it. */
                    handled_by: (isBS || !skipped) ? null : (m.handled_by || null),
                    /* Commission ship 2b -- THE CALLER OBLIGATION (diffs/commission-2a-build.txt B2). The server ASSIGNS
                     * payroll_paytype (= EXCLUDED, never COALESCE), so it is sent on EVERY row: the pay type of a row stored
                     * as "Handled by Payroll", else NULL. Omitting it would erase a stored answer on every re-save. */
                    payroll_paytype: payrollPaytypeForSave(m),
                };
            });
            /* W-078 (2026-09-30) -- PIN THE KEY AT BIRTH. Each BS and revenue row carries leaf_key = the key the
             * Step-4 import will book it under, from THE SAME resolvers the import calls (buildBsKeyResolver,
             * buildRevKeyResolver), evaluated over the rows as this save will leave them (the workbook lines and
             * the stored rows are re-read for it). The server writes leaf_key only when the row is NEW (INSERT-only),
             * so an existing row keeps its key and a later relabel stays label-only. IS rows keep their is_line_key
             * and send no leaf_key. */
            const [glRes, storedRes] = await Promise.all([
                getHistoricalGLLines(selectedCompanyId), getGLLineMappings(selectedCompanyId)]);
            const leafKeyOf = buildStep3LeafKeyResolver(glRes.data || [], step3StoredImage(storedRes.data || [], rows));
            await saveGLLineMappings(selectedCompanyId, rows.map(r => ({ ...r, leaf_key: leafKeyOf(r) })));
            // Commission ship 2b: a pay type just saved is a STORED answer now, no longer a suggestion.
            setMappings(prev => prev.map(m => (m.paytypeSuggested && payrollPaytypeForSave(m)) ? { ...m, paytypeSuggested: false } : m)
                .map(m => (m.guessAccepted ? { ...m, guessAccepted: false } : m)));   // Step-3 confirm: an accepted guess is stored now
        }
        setMappingSaved(true);
    };

    /* Up/down reorder on the Step-3 IS grid. Swaps a row's sort_order with its neighbour
     * WITHIN the same (subsection, display_group) block — moving a leaf out of its group
     * would change which subtotal it lands in, which is a re-CATEGORIZATION, not a
     * reorder, and belongs in the Category dropdown. Persists immediately (transactional
     * PUT); sort_order is display-only so this cannot move a number. */
    const moveMapping = async (idx, dir) => {
        const m = mappings[idx];
        if (!m || m.statement_type !== 'IS') return;
        const sibs = mappings
            .map((x, i) => ({ x, i }))
            .filter(({ x }) => x.statement_type === 'IS' && x.is_category === m.is_category
                && (groupIdOf(x.display_group) || null) === (groupIdOf(m.display_group) || null) && x.is_category !== 'Skip')   // 2026-10-28: a template group TEXT is its slug
            .sort((a, b) => (a.x.sort_order ?? 0) - (b.x.sort_order ?? 0));
        const pos = sibs.findIndex(s => s.i === idx);
        const swap = sibs[pos + dir];
        if (pos < 0 || !swap) return;                       // already at the block's edge

        const a = mappings[idx], b = mappings[swap.i];
        const next = [...mappings];
        next[idx]     = { ...a, sort_order: b.sort_order };
        next[swap.i]  = { ...b, sort_order: a.sort_order };
        setMappings(next);

        if (a.id && b.id) {
            try {
                await reorderGLLineMappings(selectedCompanyId, [
                    { id: a.id, sort_order: b.sort_order },
                    { id: b.id, sort_order: a.sort_order },
                ]);
            } catch (err) {
                setMappings(mappings);                       // roll the UI back to the server's truth
                setError('Failed to reorder: ' + (err?.response?.data?.error || err.message));
            }
        } else {
            // Not yet persisted (first pass, pre-Save). The new order rides along on Save.
            setMappingSaved(false);
        }
    };

    const handleSaveMappingsClick = async () => {
        try {
            setSaving(true); setError('');
            await handleSaveMappings();
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Unknown error';
            setError(`Failed to save mappings: ${msg}`);
            console.error(err);
        } finally { setSaving(false); }
    };

    const handleStep3Complete = async () => {
        setError('');
        const isMapped = mappings.filter(m => m.statement_type === 'IS' && m.is_category && m.is_category !== 'Skip');
        if (isMapped.length === 0) { setError('Please map at least one IS expense line before continuing.'); return; }
        const blankRows = [...step3UnmappedIs, ...step3UnmappedBs];
        if (blankRows.length) {
            setError('Choose a category for every line before continuing -- still blank: ' + blankRows.map(m => m.gl_name).join(', ') + '.');
            return;
        }
        if (step3BlankKeys.length) {
            setError('Choose a line for every mapped income statement line before continuing -- no line key: ' + step3BlankKeys.map(m => m.gl_name).join(', ') + '.');
            return;
        }

        try {
            setSaving(true);
            await handleSaveMappings();

            // Mark step 3 complete, advance to step 4
            const newSteps = { ...(obState?.steps || {}), gl_mapping: { completed: true } };
            await updateOnboardingState(selectedCompanyId, {
                // cursor is a high-water mark — never moves backward (see the Step-2 note)
                current_step: Math.max(obState?.current_step || 4, 4), steps: newSteps,
                close_month: obState.close_month, forecast_start: obState.forecast_start, historical_start: obState.historical_start,
            });
            await loadWizardState(selectedCompanyId);
            setViewingStep(4);
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Unknown error';
            setError(`Failed to complete step: ${msg}`);
            console.error(err);
        } finally { setSaving(false); }
    };

    // ── Step status ──────────────────────────────────────────
    const getStepStatus = (stepNum) => {
        if (!obState) return 'locked';
        const stepKeys = ['company_setup', 'is_bs_upload', 'gl_mapping', 'review_financials', 'payroll_setup', 'vendor_setup', 'revenue_modules', 'assets', 'module_setup'];
        const key = stepKeys[stepNum - 1];
        if (obState.steps?.[key]?.completed) return 'completed';
        if (stepNum === obState.current_step) return 'current';
        if (stepNum < obState.current_step) return 'current';
        // Step 9 unlocks on its PREREQUISITE'S SLUG, not the ordinal cursor: the cursor is a
        // soft, rewindable value (every step save writes N+1 unconditionally — cid=3 reads 8
        // because a Step-7 re-entry rewound it), while steps-completed is durable. Without
        // this, an assets-complete tenant could reach Module Setup only via Step 8 →
        // "View Completion" → Continue. (The general cursor-rewind is filed in §12 — its fix
        // is Math.max at the six advance-sites, the :2433 precedent; not this build.)
        if (stepNum === 9 && obState.steps?.assets?.completed) return 'current';
        return 'locked';
    };

    const selectedCompany = companies.find(c => c.id === selectedCompanyId);
    /* Batch 1b (2026-11-03, L3619): report the company whose wizard is open (the Onboarding tab, a company opened) to the shell's top
       bar, else null -- the Add User and Health Check tabs act on the switcher's company, so they report null. Display only. */
    const wizCoId = activeTab === 'onboarding' && selectedCompany ? selectedCompany.id : null;
    const wizCoName = wizCoId != null ? selectedCompany.name : null;
    const wizCoFy = wizCoId != null ? (selectedCompany.fiscal_year_end || null) : null;
    useEffect(() => {
        if (onWizardCompany) onWizardCompany(wizCoId != null ? { id: wizCoId, name: wizCoName, fiscal_year_end: wizCoFy } : null);
    }, [onWizardCompany, wizCoId, wizCoName, wizCoFy]);
    useEffect(() => () => { if (onWizardCompany) onWizardCompany(null); }, [onWizardCompany]);

    // ══════════════════════════════════════════════════════════
    // Renderers
    // ══════════════════════════════════════════════════════════

    // Jump directly to a completed/current step. Resets per-step sub-state so
    // each step's loader useEffect re-fires and lands the user on the right
    // view (re-entry summary when data exists, fresh upload when it doesn't).
    const handleStepClick = (stepNum) => {
        const status = getStepStatus(stepNum);
        if (status === 'locked') return;
        setError('');
        if (stepNum === 1) {
            setEditing(false);
        } else if (stepNum === 2) {
            setSubStep('2a'); setParsedData(null); setMappingSaved(false);
            setSingleFile(null); setIsFile(null); setBsFile(null); setParseError('');
            setSelectedRows(new Set());
        } else if (stepNum === 3) {
            setMappings([]);
        } else if (stepNum === 4) {
            setStep4SubStep(null); setStep4Data(null);
            setStep4ImportError(''); setStep4Collapsed(new Set());
        } else if (stepNum === 5) {
            setStep5SubStep(null); setStep5File(null); setStep5Parsed(null);
            setStep5PayStored(undefined); setStep5CommState(undefined);
            setStep5ParseError(''); setStep5ImportError('');
            setStep5EditMode(false); setStep5Employees([]); setStep5Page(0);
        } else if (stepNum === 6) {
            setStep6SubStep(null); setStep6File(null); setStep6Parsed(null);
            setStep6ParseError(''); setStep6ImportError('');
            setStep6EditMode(false);
        } else if (stepNum === 7) {
            setStep7Lines([]); setStep7Error('');
        } else if (stepNum === 8) {
            setStep8SubStep(null); setStep8File(null); setStep8ParseError('');
            setStep8Flags([]); setStep8Fixed([]); setStep8Intang([]);
            setStep8ImportError('');
            setStep8FaOptions([]); setStep8IaOptions([]);
            setStep8EditMode(null);
        }
        setViewingStep(stepNum);
    };

    const renderStepIndicator = () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 32 }}>
            {WIZARD_STEPS.map((step, i) => {
                const status = getStepStatus(step.num);
                const isLast = i === WIZARD_STEPS.length - 1;
                const clickable = status !== 'locked';
                return (
                    <React.Fragment key={step.num}>
                        <div
                            onClick={clickable ? () => handleStepClick(step.num) : undefined}
                            title={clickable ? `Go to ${step.label}` : 'Not available yet'}
                            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 100,
                                cursor: clickable ? 'pointer' : 'default',
                                opacity: clickable ? 1 : 0.9 }}>
                            <div style={{
                                width: 32, height: 32, borderRadius: '50%',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 14, fontWeight: 600, fontFamily: MONO,
                                background: status === 'completed' ? '#27AE60' : status === 'current' ? '#1A73E8' : '#E0E0E0',
                                color: status === 'locked' ? '#999' : '#fff',
                                boxShadow: (clickable && step.num === viewingStep) ? '0 0 0 3px rgba(26,115,232,0.25)' : 'none',
                                transition: 'box-shadow 0.15s',
                            }}>
                                {status === 'completed' ? '\u2713' : step.num}
                            </div>
                            <div style={{ marginTop: 6, fontSize: 11, fontFamily: FONT, fontWeight: 500,
                                color: status === 'locked' ? '#999' : status === 'current' ? '#1A73E8' : '#1A1C2E',
                                textAlign: 'center', lineHeight: '14px',
                                textDecoration: (clickable && step.num !== viewingStep) ? 'none' : 'none' }}>
                                {/* ★ Item-5 Part C: number prefixed so the step number reads whether
                                    the circle shows the number (incomplete) or a checkmark (complete). */}
                                {step.num}. {step.label}
                            </div>
                        </div>
                        {!isLast && (
                            <div style={{ flex: 1, height: 2, marginTop: -16,
                                background: getStepStatus(step.num + 1) !== 'locked' ? '#1A73E8' : '#E0E0E0' }} />
                        )}
                    </React.Fragment>
                );
            })}
        </div>
    );

    const labelStyle = { fontSize: 13, fontWeight: 600, fontFamily: FONT, color: '#1A1C2E', marginBottom: 4 };
    const helperStyle = { fontSize: 11, color: '#888', fontFamily: FONT, marginTop: 4 };

    // ── Step 1 renderer ──────────────────────────────────────
    const renderStep1 = () => (
        <div>
            <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 18, marginBottom: 24, marginTop: 0 }}>Company Setup</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 400 }}>
                <div>
                    <div style={labelStyle}>Historical Start</div>
                    <YearMonthPicker year={hsYear} month={hsMonth} onChangeYear={setHsYear} onChangeMonth={setHsMonth} disabled={isReadOnly} />
                    <div style={helperStyle}>{'Must be January of the start year (e.g. 2021-01) \u2014 or, for a Pre-Launch company (no earlier history), the same month as the Last Closed Month (the month of its opening balance sheet).'}</div>
                </div>
                <div>
                    <div style={labelStyle}>Last Closed Month</div>
                    <YearMonthPicker year={cmYear} month={cmMonth} onChangeYear={setCmYear} onChangeMonth={setCmMonth} disabled={isReadOnly} />
                    <div style={helperStyle}>The last month with actual financial data</div>
                    {isOneMonthStart(historicalStart, closeMonth) && <div data-one-month-start="" style={{ ...helperStyle, color: '#27AE60' }}>{'Pre-Launch company: starts in ' + formatMonthLabel(addYMMonths(closeMonth, 1), 'shortFull') + ' \u2014 no earlier history.'}</div>}
                </div>
                <div>
                    <div style={labelStyle}>Forecast Through</div>
                    <YearMonthPicker year={ftYear} month={ftMonth} onChangeYear={setFtYear} onChangeMonth={setFtMonth} disabled={isReadOnly} />
                    <div style={helperStyle}>Last month of the forecast period</div>
                </div>
                {error && <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT }}>{error}</div>}
                <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                    {isReadOnly ? (
                        <>
                            <button onClick={handleEditStep1} style={btnSecondary}>Edit</button>
                            <button onClick={() => setViewingStep(Math.max(obState?.current_step || 2, 2))} style={btnPrimary}>Continue &rarr;</button>
                        </>
                    ) : (
                        <button onClick={handleSaveStep1} disabled={saving} style={btnPrimary}>{saving ? 'Saving...' : 'Save & Continue \u2192'}</button>
                    )}
                </div>
            </div>
        </div>
    );

    // ── Step 2 sub-step indicator ────────────────────────────
    const SUB_STEPS = [
        { key: '2a', label: 'Upload' },
        { key: '2b', label: 'Review' },
        { key: '2c', label: 'Confirm' },
    ];
    const subStepIdx = SUB_STEPS.findIndex(s => s.key === subStep);

    const renderSubStepIndicator = () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 24, fontSize: 12, fontFamily: FONT }}>
            <span style={{ fontWeight: 600, color: '#1A1C2E' }}>Step 2: IS &amp; BS Upload</span>
            {SUB_STEPS.map((s, i) => (
                <React.Fragment key={s.key}>
                    <span style={{ color: '#ccc', margin: '0 2px' }}>&rsaquo;</span>
                    <span style={{
                        fontWeight: subStep === s.key ? 600 : 400,
                        color: i < subStepIdx ? '#27AE60' : subStep === s.key ? '#1A73E8' : '#999',
                    }}>{s.label}</span>
                </React.Fragment>
            ))}
        </div>
    );

    // ── Drop zone ────────────────────────────────────────────
    const DropZone = ({ file, onFile, label, inputRef }) => {
        const [dragOver, setDragOver] = useState(false);
        return (
            <div onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0]); }}
                onClick={() => inputRef.current?.click()}
                style={{ border: `2px dashed ${dragOver ? '#1A73E8' : '#D0D5DD'}`, borderRadius: 8,
                    padding: '32px 24px', textAlign: 'center', cursor: 'pointer',
                    background: dragOver ? '#F0F6FF' : '#FAFAFA', transition: 'all 0.15s', flex: 1, minWidth: 200 }}>
                <input ref={inputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                    onChange={e => { if (e.target.files[0]) onFile(e.target.files[0]); }} />
                {file ? (
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1A1C2E', fontFamily: FONT }}>{file.name}</div>
                        <div style={{ fontSize: 12, color: '#888', fontFamily: FONT, marginTop: 4 }}>Click to change</div>
                    </div>
                ) : (
                    <div>
                        <div style={{ fontSize: 14, color: '#666', fontFamily: FONT }}>{label}</div>
                        <div style={{ fontSize: 12, color: '#999', fontFamily: FONT, marginTop: 4 }}>.xlsx or .xls</div>
                    </div>
                )}
            </div>
        );
    };

    // ── Sub-step 2a: Upload ──────────────────────────────────
    const renderStep2a = () => (
        <div>
            <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, fontFamily: FONT }}>
                    <input type="radio" checked={uploadMode === 'single'} onChange={() => setUploadMode('single')} /> Single file (IS + BS tabs)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, fontFamily: FONT }}>
                    <input type="radio" checked={uploadMode === 'separate'} onChange={() => setUploadMode('separate')} /> Separate files
                </label>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontFamily: FONT, fontSize: 13, color: '#555' }}>
                <span>Need a blank?</span>
                <button onClick={handleDownloadISBlank} style={btnText}>Download IS template</button>
                <button onClick={handleDownloadBSBlank} style={btnText}>Download BS template</button>
            </div>
            {/* S1 (2026-11-02): ONLY for a Pre-Launch window (historical start == close month, utils/companyWindow.js) -- a company
                with history uploads its own statements; every other company's Step 2 is unchanged. */}
            {isOneMonthStart(obState?.historical_start, obState?.close_month) && (
            <div data-pre-launch-starter="" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontFamily: FONT, fontSize: 13, color: '#555' }}>
                <span>Download Pre-Launch starter templates:</span>
                <button onClick={handleDownloadISStarter} style={btnText}>Income statement</button>
                <button onClick={handleDownloadBSStarter} style={btnText}>Balance sheet</button>
            </div>
            )}
            {uploadMode === 'single' ? (
                <DropZone file={singleFile} onFile={setSingleFile} label="Drop your IS & BS Excel file here, or click to browse" inputRef={singleFileRef} />
            ) : (
                <div style={{ display: 'flex', gap: 16 }}>
                    <DropZone file={isFile} onFile={setIsFile} label="Income Statement (.xlsx)" inputRef={isFileRef} />
                    <DropZone file={bsFile} onFile={setBsFile} label="Balance Sheet (.xlsx)" inputRef={bsFileRef} />
                </div>
            )}
            {parseError && <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginTop: 16 }}>{parseError}</div>}
            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                <button onClick={() => handleBack(2)} style={btnText}>&larr; Back</button>
                <button onClick={handleParseFiles} disabled={parsing || (uploadMode === 'single' ? !singleFile : (!isFile || !bsFile))} style={btnPrimary}>
                    {parsing ? 'Parsing...' : 'Parse File(s) \u2192'}
                </button>
            </div>
        </div>
    );

    // ── Sub-step 2b: Data Quality Review ─────────────────────
    const renderStep2b = () => {
        // Batch 1 (2026-11-03, L3595): a BS row is tagged so its all-zero note says where a balance-sheet row is set up
        const allFlags = [...(parsedData?.is?.flags || []), ...(parsedData?.bs?.flags || []).map(f => ({ ...f, _bs: true }))];
        // Batch 1 (L3596): a Pre-Launch company's all-zero rows (expected: it has no history) collapse into ONE expandable line
        const plZero = isOneMonthStart(obState?.historical_start, obState?.close_month) ? allFlags.filter(f => f.type === 'zero_row') : [];
        const listFlags = plZero.length ? allFlags.filter(f => f.type !== 'zero_row') : allFlags;
        // W-068: blocking problems from the upload check -- counted in ONE heading, and they disable "continue".
        const nBlock2 = countBlocking(allFlags);
        return (
            <div>
                <h4 style={{ fontFamily: FONT, color: '#1A1C2E', margin: '0 0 16px' }}>
                    We found {allFlags.length} item{allFlags.length !== 1 ? 's' : ''} to review before importing
                </h4>
                {nBlock2 > 0 && (
                    <div data-spec-heading="blocking" style={{ padding: '10px 12px', marginBottom: 12, background: '#FEF3F2', border: '1px solid #FDA29B',
                        borderRadius: 6, fontSize: 13, fontFamily: FONT, color: '#B42318', fontWeight: 600 }}>
                        {'\u26D4'} {blockingHeading(nBlock2)}
                    </div>
                )}
                <div style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #E0E0E0', borderRadius: 6, marginBottom: 20 }}>
                    {listFlags.map((f, i) => (
                        <div key={i} style={{ padding: '8px 12px', borderBottom: (i < listFlags.length - 1 || plZero.length > 0) ? '1px solid #F0F0F0' : 'none',
                            fontSize: 13, fontFamily: FONT, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                            <span>{f.severity === BLOCKING ? '\u26D4' : f.severity === 'warning' || f.type === 'formula' || f.type === 'merged' ? '\u26A0\uFE0F' : '\u2139\uFE0F'}</span>
                            <span>
                                {f.type === 'formula' && `Row ${f.row}: Formula reference (${f.value?.slice(0, 40)}) \u2014 value used as-is`}
                                {f.type === 'subtotal' && `Row ${f.row}: Subtotal row skipped \u2014 "${f.label}"`}
                                {f.type === 'blank_row' && `Row ${f.row}: Blank GL name row skipped`}
                                {f.type === 'merged' && `Row ${f.row}, Col ${f.col}: Merged cell detected`}
                                {/* An all-zero row is KEPT, never skipped (017a62e, 'a zero is data'): it imports with 0 in every month and
                                    reaches Step 3 like any other line. This line said "Skipped" until 2026-10-28, which was false. */}
                                {f.type === 'zero_row' && (f._bs
                                    ? `Keeping all-zero row: "${f.label}" \u2014 it imports with 0 in every month. Balance sheet rows are set up at Step 4a.`
                                    : `Keeping all-zero row: "${f.label}" \u2014 it imports with 0 in every month; map it at Step 3, or choose Skip there.`)}
                                {f.type === 'no_dates' && f.label}
                                {f.type === 'info' && f.label}
                                {/* ★ §E1 — a DELETED optional column reads identically to an
                                    empty one at import, so this is the only chance to ask.
                                    Informational by design: a tenant with no sub-groups is
                                    legitimate input, so it must not block — continuing IS the
                                    acknowledgement. The `priorTagged` arm is the re-import
                                    tier: the tenant's own earlier import proves the column
                                    used to be there, so it stops hedging. */}
                                {f.type === 'no_display_group_column' && (f.priorTagged ? (
                                    <>
                                        {f.priorTagged} GL line{f.priorTagged !== 1 ? 's' : ''} previously carried a
                                        {' '}sub-group, but this workbook has no{' '}<code>display_group</code>{' '}column.
                                        {' '}If you removed it, re-download the template and re-upload.
                                        {' '}If you meant to drop your sub-groups, continue.
                                    </>
                                ) : (
                                    <>
                                        This workbook has no{' '}<code>display_group</code>{' '}column. If your GL lines
                                        {' '}have no sub-groups that is fine — continue. If you removed the column,
                                        {' '}re-download the template and re-upload, or set the groups yourself at
                                        {' '}Step 3 after Step 5.
                                    </>
                                ))}
                                {/* ★ §E1 — the only flag here that names an OPERATOR
                                    MISTAKE rather than a parser observation, so it says
                                    what was typed, what is allowed, and both ways out.
                                    Informational by design: Step 3 already blocks this
                                    case, so this exists to be seen EARLY. */}
                                {f.type === 'unknown_category' && (
                                    <>
                                        Row {f.row}: &ldquo;{f.value}&rdquo; is not a recognized category
                                        {' '}&mdash; use one of COGS, S&amp;M, G&amp;A, R&amp;D, Other, or Revenue.
                                        {' '}You can fix the workbook and re-upload, or pick a category
                                        {' '}for &ldquo;{f.label}&rdquo; at Step 3.
                                    </>
                                )}
                                {/* ★ NO CLAUSE MATCHED — show the flag's own sentence rather
                                    than an icon with blank text. Only a flag that CARRIES a
                                    `message` can render here; the nine claused types are
                                    unaffected because their arms match first. */}
                                {!STEP2B_CLAUSED.has(f.type) && f.message}
                            </span>
                        </div>
                    ))}
                    {plZero.length > 0 && (
                        <div data-pre-launch-zero-rows="" style={{ padding: '8px 12px', fontSize: 13, fontFamily: FONT, display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                            <span>{'\u2139\uFE0F'}</span>
                            <span>{plZero.length} all-zero row{plZero.length !== 1 ? 's' : ''} kept (expected for a Pre-Launch company)</span>
                            <button onClick={() => setStep2bZeroOpen(o => !o)} style={btnText}>{step2bZeroOpen ? 'Hide' : 'Show'}</button>
                            {step2bZeroOpen && <div data-pre-launch-zero-list="" style={{ flexBasis: '100%', fontSize: 12, color: '#555' }}>{plZero.map(f => f.label).join(', ')}</div>}
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                    <button onClick={() => { setSubStep('2a'); setParsedData(null); setSingleFile(null); setIsFile(null); setBsFile(null); setParseError(''); }} style={btnSecondary}>Re-upload files</button>
                    <button onClick={() => setSubStep('2c')} disabled={nBlock2 > 0} data-spec-continue="2b"
                        style={{ ...btnPrimary, opacity: nBlock2 > 0 ? 0.5 : 1 }}>These look correct, continue &rarr;</button>
                </div>
            </div>
        );
    };

    // ── Sub-step 2c: Date Range Confirmation ─────────────────
    const renderStep2c = () => {
        const isMo = parsedData?.is?.months || [];
        const bsMo = parsedData?.bs?.months || [];
        const openingMo = openingMonthOf(obState?.historical_start);   // 2026-11-02: the opening month (utils/companyWindow.js), not always a December
        const bsHasPriorDec = bsMo.length > 0 && (openingMo ? bsMo[0] === openingMo : bsMo[0].endsWith('-12'));
        return (
            <div>
                <h4 style={{ fontFamily: FONT, color: '#1A1C2E', margin: '0 0 16px' }}>We detected the following date range in your files:</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 14, fontFamily: FONT, marginBottom: 24 }}>
                    {parsedData?.is ? (
                        <div><strong>Income Statement:</strong> {isMo.length} months from {isMo[0] || '?'} to {isMo[isMo.length - 1] || '?'}
                            {' '}({parsedData.is.lines.length} GL lines)</div>
                    ) : <div style={{ color: '#E65100' }}>Income Statement: not detected</div>}
                    {parsedData?.bs ? (
                        <div><strong>Balance Sheet:</strong> {bsMo.length} months from {bsMo[0] || '?'} to {bsMo[bsMo.length - 1] || '?'}
                            {' '}({parsedData.bs.lines.length} GL lines)</div>
                    ) : <div style={{ color: '#E65100' }}>Balance Sheet: not detected</div>}
                    <div>
                        {openingMo ? 'Opening BS (' + formatMonthLabel(openingMo, 'shortFull') + ')' : 'Prior BS (Dec)'}: {bsHasPriorDec
                            ? <span style={{ color: '#27AE60' }}>{bsMo[0]} detected &#10003;</span>
                            : <span style={{ color: '#E65100' }}>not found (CFS opening balance may need manual entry)</span>}
                    </div>
                </div>
                {error && <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginBottom: 12 }}>{error}</div>}
                <div style={{ display: 'flex', gap: 12 }}>
                    <button onClick={() => { setSubStep('2a'); setParsedData(null); setSingleFile(null); setIsFile(null); setBsFile(null); setParseError(''); }} style={btnSecondary}>Re-upload files</button>
                    <button onClick={handleStep2Complete} disabled={saving} style={btnPrimary}>
                        {saving ? 'Importing...' : 'Confirm & Continue \u2192'}
                    </button>
                </div>
            </div>
        );
    };

    // ── Step 2 master renderer ───────────────────────────────
    const renderStep2 = () => (
        <div>
            <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 18, marginBottom: 4, marginTop: 0 }}>IS &amp; BS Upload</h3>
            {renderSubStepIndicator()}
            {subStep === '2a' && renderStep2a()}
            {subStep === '2b' && renderStep2b()}
            {subStep === '2c' && renderStep2c()}
        </div>
    );

    // ── Step 3: GL Mapping renderers ─────────────────────────
    const toggleRow = (idx) => {
        setSelectedRows(prev => { const next = new Set(prev); if (next.has(idx)) next.delete(idx); else next.add(idx); return next; });
    };
    const toggleAllInSection = (indices) => {
        setSelectedRows(prev => {
            const next = new Set(prev);
            const allSelected = indices.every(i => next.has(i));
            for (const i of indices) { if (allSelected) next.delete(i); else next.add(i); }
            return next;
        });
    };
    /* ★★ BULK-ASSIGNS THE CATEGORY ONLY — NEVER THE KEY.
     *
     * This used to stamp ONE is_line_key across every selected row:
     *     next[idx] = { ...next[idx], is_category: bulkCat, is_line_key: bulkKey };
     * Selecting 20 per-line vendor rows and bulk-assigning a category COLLAPSED all 20
     * distinct leaves onto a single key — silently detaching 20 vendors and 20 histories
     * in one click. It was the fastest way to destroy the model in the entire app.
     *
     * A key is an identity, not an attribute; there is no such thing as "the same key" for
     * many rows. Bulk-assign moves rows between SECTIONS (which is safe — see updateMapping)
     * and leaves each row's key alone. Keyless rows get an individually-suggested per-line
     * key so bulk-categorizing a fresh import still works. */
    const applyBulk = (indices) => {
        if (!bulkCat) return;
        setMappings(prev => {
            const next = [...prev];
            for (const idx of indices) {
                if (!selectedRows.has(idx)) continue;
                const r = next[idx];
                /* ★ B s3 — bulk-assign routes through the SAME sentinel mapping as the per-row
                 * select, so "Handled by Payroll" works here for free and can never leak the
                 * sentinel into is_category. This is also why s3 ships no bespoke bulk widget:
                 * selecting rows and assigning to them is the bulk affordance this grid already
                 * has, and it undoes just as easily (assign the same rows to Skip). */
                const bulkFields = categoryChoiceToFields(bulkCat);
                const bulkIsCat = bulkFields.is_category;
                const keep = bulkIsCat === 'Skip' ? '' : (r.is_line_key
                    || (r.statement_type === 'IS' ? suggestISLineKey(r.gl_category, r.gl_name, bulkIsCat) : ''));
                next[idx] = { ...r, is_category: bulkIsCat, is_line_key: keep,
                    handled_by: bulkFields.handled_by, categorySuggested: false, guessAccepted: false };
                next[idx].payrollHandled = payrollChipFor(
                    bulkIsCat, bulkFields.handled_by, r.looksPayroll);
                next[idx] = withPayrollPaytype(next[idx]);   // commission ship 2b: the same rule as the per-row pick
                if (r.statement_type === 'IS' && bulkIsCat !== 'Skip') {
                    next[idx].savedRole = null;
                    next[idx].savedSubsection = null;
                    try {
                        const meta = inferIsMetaLeaf(next[idx]);
                        next[idx].role = meta.role;
                        next[idx].subsection = meta.subsection;
                        next[idx].inferError = null;
                    } catch (e) {
                        next[idx].role = null; next[idx].subsection = null; next[idx].inferError = e.message;
                    }
                }
            }
            return next;
        });
        setMappingSaved(false);
        setBulkCat('');
    };

    // Distinct display_groups the tenant actually has (from department_mappings). Empty
    // until Step 5 runs — the dropdown then explains itself via its title attribute.
    const step3DgOptions = [...new Set((step3Depts || [])
        .map(d => d.display_group)
        .filter(Boolean))];
    /* 2026-10-28: a line whose stored group is template TEXT ("Cost of Revenue") shows the department group it means
       (deptSlug(text) -- utils/seatDepartments groupIdOf). The stored value is not rewritten unless the operator picks. */
    const step3DgValue = dg => (dg && !step3DgOptions.includes(dg) && step3DgOptions.includes(groupIdOf(dg))) ? groupIdOf(dg) : (dg || '');

    const reorderBtn = {
        border: '1px solid #D0D5DD', background: '#fff', borderRadius: 3, cursor: 'pointer',
        fontSize: 9, lineHeight: '14px', padding: '0 4px', margin: '0 1px', color: '#6B7280',
    };

    const renderMappingRow = (m, idx) => {
        const isIS = m.statement_type === 'IS';
        const categories = categoryOptionsFor(m.statement_type);
        // ★ Options from the TENANT'S OWN rows — so every existing key is representable and
        // the "—" (a value with no matching <option>) that invited the silent overwrite is gone.
        // 2026-09-28 (B1): the row's OWN-NAME key is always among its options (suggestISLineKey is own-name only).
        const lineKeyOptions = isIS ? lineKeyOptionsFor(mappings, m.is_category, lineKeyRequired(m) ? suggestISLineKey(m.gl_category, m.gl_name, m.is_category) : '', m.display_name || m.gl_name) : [];
        // The key's prefix is cosmetic for the statement (subsection comes from is_category),
        // but computeVendor's contractor-headcount bucketing DOES read it — so surface a
        // mismatch rather than silently "fixing" it by re-keying.
        const keyPrefix = (m.is_line_key || '').split('_')[0];
        const catPrefix = PAYROLL_KEYS_BY_CAT[m.is_category] || (m.is_category === 'Other' ? 'oi' : '');
        const prefixMismatch = isIS && m.is_line_key && catPrefix && keyPrefix !== catPrefix;
        const isSelected = selectedRows.has(idx);
        const isSkip = m.is_category === 'Skip';
        const rowBg = isSelected ? '#EBF3FF' : isSkip ? '#F9FAFB' : '#fff';
        return (
            <tr key={idx} style={{ borderBottom: '1px solid #F0F0F0', background: rowBg }}>
                <td style={{ ...tdStyle, width: 32, textAlign: 'center' }}>
                    <input type="checkbox" checked={isSelected} onChange={() => toggleRow(idx)} />
                </td>
                <td style={{ ...tdStyle, maxWidth: 200 }}>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>
                        {m.gl_name}
                        {m.autoSkip && <span style={{ marginLeft: 6, fontSize: 10, color: '#888', background: '#ECECEC',
                            padding: '1px 5px', borderRadius: 3, fontWeight: 400 }}>auto</span>}
                    </div>
                    {m.gl_category && <div style={{ fontSize: 11, color: '#888' }}>{m.gl_category}</div>}
                    {m.inferError && <div style={{ fontSize: 11, color: '#B42318', marginTop: 2 }}>
                        Cannot classify this line — pick a different Category.
                    </div>}
                </td>
                <td style={{ ...tdStyle, fontFamily: MONO, fontSize: 13, textAlign: 'right' }}>{fmt(m.annual_total)}</td>
                <td style={tdStyle}>
                    {/* ★★ B s3 — THE READ-ONLY "Handled by Payroll" CHIP IS GONE, and with it the
                        "Map instead" escape hatch. Both existed because the state was a DEFAULT
                        the system chose and the operator could only escape from; now it is a
                        value the operator PICKS, so it belongs in the same control as every
                        other category choice. A payroll row renders as an ordinary dropdown with
                        "Handled by Payroll" selected — which is also what makes it changeable in
                        one action instead of two. */}
                    {m.autoEquity ? (
                        /* ★ The BS "Net Income" row. It IS an equity leaf (role
                           ytd_net_income) and must be mapped for the leaf to exist — but
                           its value is COMPUTED from the income statement each forecast
                           month (computeBS: IS_REF.net_income_ytd), never entered. Showing
                           it as an editable dropdown invites both mistakes: skipping it
                           (which drops it from bs_actuals entirely and breaks the balance
                           check by exactly YTD net income) and hand-mapping it somewhere
                           else. Read-only, and say WHY. */
                        <div>
                            <div style={{ display: 'inline-block', fontSize: 12, color: '#0F766E', background: '#ECFDF5',
                                border: '1px solid #A7F3D0', borderRadius: 4, padding: '3px 8px', fontWeight: 500 }}>
                                Equity — computed from the income statement
                            </div>
                            <div style={{ fontSize: 10, color: '#9AA0AE', marginTop: 3, lineHeight: 1.3 }}>
                                Carried to equity automatically each month — no mapping needed.
                            </div>
                        </div>
                    ) : (
                        <>
                            <select value={categoryOptionValueFor(m)} onChange={e => updateMapping(idx, 'is_category', e.target.value)}
                                data-step3-cat={idx}
                                style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid ' + (m.is_category ? '#D0D5DD' : '#F59E0B'),
                                    fontSize: 13, fontFamily: FONT, width: '100%', minWidth: 165 }}>{/* Batch 1 (2026-11-03, L3620): "Handled by Payroll" fits */}
                                <option value="">&mdash;</option>
                                {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                            </select>
                            {/* 2026-09-24 -- Build 1's mark and idiom: a keyword guess is marked; blank is Required.
                                A category from the template's exact section name, or the stored answer, has no mark. */}
                            {m.is_category && m.categorySuggested && (
                                <div data-step3-mark="suggested" style={{ fontSize: 11, color: '#1A73E8', marginTop: 3 }}>
                                    {/* W-068 F1: a pre-set Skip on a total-looking / computed row says WHY it was suggested. */}
                                    {m.autoSkip && m.is_category === 'Skip'
                                        ? <>suggested &mdash; looks like a total row &mdash; confirm or change</>
                                        : <>suggested &mdash; confirm or change</>}
                                </div>
                            )}
                            {!m.is_category && (
                                <div data-step3-mark="required" style={{ fontSize: 11, color: '#B45309', fontWeight: 600, marginTop: 3 }}>
                                    Required &mdash; choose a category
                                </div>
                            )}
                            {/* 2026-09-28 (C): the "Handled by Payroll" PRE-SELECTION is a keyword guess (matchPayrollRole) -- marked with
                                Build 2's words. A stored answer (handled_by) or any pick clears it; the payroll gate reads the same predicate. */}
                            {isUnansweredPayrollGuess(m) && (
                                <div data-step3-mark="suggested" data-step3-field="payroll" style={{ fontSize: 11, color: '#1A73E8', marginTop: 3 }}>
                                    suggested &mdash; confirm or change
                                </div>
                            )}
                            {/* Commission ship 2b: the PAY TYPE of a "Handled by Payroll" line -- a stored answer; a suggestion from
                                the name is marked with Step 4a's words and accepted by the page confirm below; blank is Required. */}
                            {m.payrollHandled && (
                                <div style={{ marginTop: 6 }}>
                                    {/* Batch 1b (2026-11-03, L3597): one Step-3 row stands for a payroll line that several groups share (by design --
                                        one row per name per section; Step 5c splits it by group) -- say which. */}
                                    {(m.groupsCovered || []).length >= 2 && (
                                        <div data-step3-covers={idx} style={{ fontSize: 11, color: '#6B7280', marginBottom: 4 }}>
                                            {'covers ' + m.groupsCovered.map(t => (/[A-Z\s&]/.test(t) ? t : displayGroupLabel(t))).join(', ')}
                                        </div>
                                    )}
                                    <select value={m.payroll_paytype || ''} onChange={e => updateMapping(idx, 'payroll_paytype', e.target.value || null)}
                                        aria-label="Pay type" data-step3-paytype={idx}
                                        style={{ padding: '3px 6px', borderRadius: 4, border: '1px solid ' + (m.payroll_paytype ? '#D0D5DD' : '#F59E0B'),
                                            fontSize: 12, fontFamily: FONT, width: '100%' }}>
                                        <option value="">&mdash; pay type &mdash;</option>
                                        {PAYTYPE_CHOICES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                                    </select>
                                    {m.payroll_paytype && m.paytypeSuggested && (
                                        <div data-step3-mark="suggested" data-step3-field="paytype" style={{ fontSize: 11, color: '#1A73E8', marginTop: 3 }}>
                                            suggested &mdash; confirm or change
                                        </div>
                                    )}
                                    {!m.payroll_paytype && (
                                        <div data-step3-mark="required" data-step3-field="paytype" style={{ fontSize: 11, color: '#B45309', fontWeight: 600, marginTop: 3 }}>
                                            Required &mdash; choose a pay type
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </td>
                {isIS && (
                    <td style={tdStyle}>
                        <select value={m.is_line_key || ''} onChange={e => updateMapping(idx, 'is_line_key', e.target.value)}
                            disabled={!m.is_category || isSkip}
                            style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #D0D5DD', fontSize: 12, fontFamily: FONT, width: '100%',
                                background: (!m.is_category || isSkip) ? '#F5F6FA' : '#fff' }}>
                            <option value="">—</option>
                            {lineKeyOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                        </select>
                        {m.is_line_key && (
                            <div style={{ fontSize: 10, fontFamily: MONO, color: '#9AA0AE', marginTop: 2 }}>{m.is_line_key}</div>
                        )}
                        {/* 2026-09-28 (B1): Build 2's idiom -- a blank REQUIRED key reads Required. The blank option stays in the
                            select because a native select must be able to SHOW an empty value; the gate and the save refuse it. */}
                        {lineKeyRequired(m) && !m.is_line_key && (
                            <div data-step3-mark="required" data-step3-field="line-key" style={{ fontSize: 11, color: '#B45309', fontWeight: 600, marginTop: 3 }}>
                                Required &mdash; choose a line
                            </div>
                        )}
                        {prefixMismatch && (
                            <div style={{ fontSize: 10, color: '#B45309', marginTop: 2 }}>
                                key kept &mdash; its <code style={{ fontFamily: MONO }}>{keyPrefix}_</code> prefix no longer
                                matches {m.is_category}. Harmless for the statement; re-keying would detach its vendors and history.
                            </div>
                        )}
                    </td>
                )}
                {/* Role — DERIVED from Category + GL name, shown read-only so the operator can
                    see how the line will be routed (payroll vs vendor vs below-the-line) and
                    catch a mis-categorization before it silently zeroes a vendor. */}
                {isIS && (
                    <td style={{ ...tdStyle, fontFamily: MONO, fontSize: 11,
                        color: m.inferError ? '#B42318' : isSkip ? '#B0B4C0' : '#6B7280' }}>
                        {isSkip ? '—' : (m.role || '⚠')}
                    </td>
                )}
                {/* Sub-section (display_group) — the one field needing operator intent: merging
                    Sales + Marketing into one group is a judgement no rule can make. Empty on a
                    first pass (no departments yet); the dept-mappings save back-fills it from the workbook's display_group column. */}
                {isIS && (
                    <td style={tdStyle}>
                        {isSkip ? <span style={{ color: '#B0B4C0', fontSize: 12 }}>—</span> : (
                            <select value={step3DgValue(m.display_group)} onChange={e => updateMapping(idx, 'display_group', e.target.value)}
                                disabled={!step3DgOptions.length}
                                title={step3DgOptions.length ? '' : "Sub-groups come from the departments set up in Step 5. A line takes its sub-group from the workbook's display_group column; a blank stays blank."}
                                style={{ padding: '4px 6px', borderRadius: 4, border: '1px solid #D0D5DD', fontSize: 12, fontFamily: FONT, width: '100%',
                                    background: step3DgOptions.length ? '#fff' : '#F5F6FA' }}>
                                <option value="">— none —</option>
                                {step3DgOptions.map(o => <option key={o} value={o}>{o}</option>)}
                            </select>
                        )}
                    </td>
                )}
                {/* Up/down — reorders WITHIN the row's (category, sub-section) block. Moving a
                    leaf out of its block would change which subtotal it lands in; that is a
                    re-categorization and belongs in the Category dropdown. */}
                {isIS && (
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap', textAlign: 'center' }}>
                        {isSkip ? <span style={{ color: '#D0D5DD' }}>—</span> : (
                            <>
                                <button onClick={() => moveMapping(idx, -1)} title="Move up"
                                    style={reorderBtn}>▲</button>
                                <button onClick={() => moveMapping(idx, 1)} title="Move down"
                                    style={reorderBtn}>▼</button>
                            </>
                        )}
                    </td>
                )}
                <td style={tdStyle}>
                    <input type="text" value={m.display_name || ''} onChange={e => updateMapping(idx, 'display_name', e.target.value)}
                        style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #D0D5DD', fontSize: 13, fontFamily: FONT,
                            width: '100%', boxSizing: 'border-box' }} />
                </td>
            </tr>
        );
    };

    const renderMappingSection = (title, stType) => {
        const isISsec = stType === 'IS';
        let sectionLines = mappings.map((m, i) => ({ m, i })).filter(({ m }) => m.statement_type === stType);
        // IS rows render in the order they will appear ON THE STATEMENT — grouped by
        // category, then sub-section, then sort_order — so what the operator reorders is
        // what they see. (BS rows keep parse order; the BS chart of accounts is built from
        // bs_actuals_meta in Step 4, not from this grid's order.)
        if (isISsec) {
            const catRank = c => { const n = IS_CATEGORIES.indexOf(c); return n < 0 ? 99 : n; };
            /* ★ NO display_group TIER. It compared display_group ALPHABETICALLY, which is
             * not the template's order and not the operator's department order either.
             * It is INVISIBLE on a first pass — Step 3 runs before Step 5, so every row's
             * display_group is null and the sort falls through to sort_order — and then
             * REORDERS the grid on re-entry once Step 5c fills the groups in
             * (client_success < data_science < rev_ops < sales_mktg). sort_order already
             * IS the template row index, preserved end-to-end: the Step-2 import writes
             * `sort_order: lineIdx`, the GET orders by it, and buildMappingsFromLines
             * keeps first-appearance order. So within a section, template order is what
             * the operator sees — first pass and re-entry alike. */
            sectionLines = [...sectionLines].sort((a, b) =>
                catRank(a.m.is_category) - catRank(b.m.is_category)
                || (a.m.sort_order ?? 0) - (b.m.sort_order ?? 0)
                || a.i - b.i);
        }
        const indices = sectionLines.map(({ i }) => i);
        const selectedInSection = indices.filter(i => selectedRows.has(i));
        const allSelected = indices.length > 0 && selectedInSection.length === indices.length;
        const categories = categoryOptionsFor(stType);
        const isIS = stType === 'IS';

        return (
            <div style={{ marginBottom: 32 }}>
                {sectionLines.length === 0 ? (
                    <>
                        <h4 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 15, margin: '0 0 8px' }}>{title} (0 lines)</h4>
                        <div style={{ color: '#999', fontSize: 13, fontFamily: FONT, padding: 8 }}>No lines found</div>
                    </>
                ) : (
                    <div style={{ maxHeight: 500, overflowY: 'auto', border: '1px solid #E0E0E0', borderRadius: 6 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT, fontSize: 13 }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                                <tr><th colSpan={isIS ? 9 : 5} style={{ padding: '8px 12px', background: '#fff', borderBottom: '1px solid #E0E0E0', textAlign: 'left' }}>
                                    <h4 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 15, margin: 0 }}>{title} ({sectionLines.length} lines)</h4>
                                </th></tr>
                                <tr><th colSpan={isIS ? 9 : 5} style={{ padding: '6px 12px', textAlign: 'left',
                                    background: selectedInSection.length > 0 ? '#F0F6FF' : '#FAFAFA', borderBottom: '1px solid #E0E0E0', fontWeight: 400 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontFamily: FONT }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontWeight: 500 }}>
                                            <input type="checkbox" checked={allSelected} onChange={() => toggleAllInSection(indices)} /> Select All
                                        </label>
                                        {selectedInSection.length > 0 && (
                                            <>
                                                <span style={{ color: '#666', margin: '0 4px' }}>|</span>
                                                <span>Assign selected ({selectedInSection.length}) to:</span>
                                                <select value={bulkCat} onChange={e => setBulkCat(e.target.value)}
                                                    style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #D0D5DD', fontSize: 13, fontFamily: FONT }}>
                                                    <option value="">Category...</option>
                                                    {categories.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                                                </select>
                                                {/* ★ NO bulk Line Key picker. A key is an identity, not an
                                                    attribute — stamping one across N rows collapsed N leaves
                                                    into one and detached their vendors and history. Bulk moves
                                                    rows between SECTIONS; each row keeps its own key. */}
                                                <button onClick={() => applyBulk(indices)} disabled={!bulkCat}
                                                    style={{ ...btnSmall, opacity: bulkCat ? 1 : 0.5 }}>Apply</button>
                                                {isIS && bulkCat && bulkCat !== 'Skip' && (
                                                    <span style={{ fontSize: 11, color: '#6B7280' }}>
                                                        &mdash; category only; each row keeps its own line key
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </th></tr>
                                <tr style={{ background: '#fff' }}>
                                    <th style={{ ...stickyTh, width: 32 }}></th>
                                    <th style={{ ...stickyTh, textAlign: 'left' }}>GL Name</th>
                                    <th style={{ ...stickyTh, textAlign: 'right' }}>Annual Total</th>
                                    <th style={{ ...stickyTh, width: 130 }}>Category</th>
                                    {isIS && <th style={{ ...stickyTh, width: 150 }}>Line Key</th>}
                                    {isIS && <th style={{ ...stickyTh, width: 110 }}>Role</th>}
                                    {isIS && <th style={{ ...stickyTh, width: 120 }}>Sub-section</th>}
                                    {isIS && <th style={{ ...stickyTh, width: 60 }}>Order</th>}
                                    <th style={{ ...stickyTh, width: 180 }}>Display Name</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sectionLines.map(({ m, i }) => renderMappingRow(m, i))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    /* \u2605 Fail-loud gates (Step-3 pass). Both block Continue rather than guessing:
     *
     *  (1) UNMAPPED BS LINE (\u00a712.5 B3-2). An unmapped balance-sheet line never reaches
     *      bs_actuals_meta \u2014 it silently disappears from the balance sheet AND from the
     *      Total Cash rollup, which is the sweep's basis. The old auto-suggester hid this
     *      by pattern-matching our own tenants' bank names (chase, first republic, \u2026), so
     *      a tenant banking at Mercury or Ramp got no suggestion and no warning. The names
     *      are gone; the operator now has to decide. "Skip" is a valid decision \u2014 silence
     *      is not.
     *
     *  (2) UNCLASSIFIABLE IS LINE. inferIsMeta refuses to guess a role, and a NULL role
     *      drops the line off the income statement and zeroes every vendor mapped to it. */
    const step3UnmappedBs = mappings.filter(m => m.statement_type === 'BS' && !m.is_category);

    /* ★ UNMAPPED IS LINE — the blocker that was never mirrored from the BS side.
     *
     * An uncategorized IS row produces NO inferError (classifyIsMappings returns early for
     * it), so step3BadIs cannot catch it — and step3Blocked only ever checked the BS side.
     * The row is then silently dropped from the save (`filter(m => m.is_category)`), so a
     * real expense line with real dollars never appears on the income statement at all.
     * Same silent-drop family as B3-1.
     *
     * ★ 'Skip' SATISFIES THIS. The operator must make a DECISION — categorize it, or say it
     * does not belong. Both are fine; saying nothing is not. This is only shippable because
     * Skip now PERSISTS (see handleSaveMappings) — otherwise it would re-fire on every
     * re-entry for every previously-skipped row and Step 3 could never be exited. */
    const step3UnmappedIs = mappings.filter(m => m.statement_type === 'IS' && !m.is_category);
    const step3BadIs = mappings.filter(m => m.statement_type === 'IS' && m.inferError);

    /* ★ RE-KEY WITH DEPENDENTS — the third fail-loud gate.
     * A row whose is_line_key has CHANGED from what is persisted, where the OLD key is
     * joined by a vendor or carries historical actuals. Saving would silently zero them. */
    /* ★ C5 (Build 2) — THE BLOCK NOW SEES ALL THREE POPULATIONS.
     * It used to compare `is_line_key` only, which is the one population whose key was
     * already stored — so the BS and revenue leaves, whose keys were DERIVED, were
     * structurally invisible to it (a revenue row's savedLineKey is NULL, so the filter
     * could never fire). With leaf_key pinned they have a saved value to diff against.
     * ⓘ THIS STILL BLOCKS, deliberately. An ONBOARDING re-key is a different gesture from
     * the live rename: the live rename is label-only and harmless by construction, while
     * re-keying a leaf that carries history or vendor joins is a migration, not an edit. */
    const savedKeyOf = m => m.savedLineKey || m.savedLeafKey || null;
    const currentKeyOf = m => leafKeyFor(m);
    const step3Rekeys = mappings.filter(m => {
        const saved = savedKeyOf(m);
        if (!saved) return false;
        if (currentKeyOf(m) === saved) return false;
        return step3Deps.vendorKeys.has(saved) || step3Deps.histKeys.has(saved);
    }).map(m => ({
        ...m,
        _why: [
            step3Deps.vendorKeys.has(savedKeyOf(m)) ? 'vendors' : null,
            step3Deps.histKeys.has(savedKeyOf(m)) ? 'history' : null,
        ].filter(Boolean).join(' + '),
    }));

    /* ★★ B s4 — THE FIFTH GATE: A PAYROLL LINE NOBODY HAS ANSWERED FOR.
     *
     * The row is showing "Handled by Payroll" as a SUGGESTION (the name matched and the
     * category is a recognised expense section) but `handled_by` is still NULL — so the
     * wizard is displaying a guess and nobody has stated it. §E1: a suggestion is not an
     * answer, and the operator must choose before Step 3 completes.
     *
     * ⛔ IT KEYS ON `!handled_by`, NOT ON `!payrollHandled`. `payrollHandled` is TRUE for
     * an unanswered suggestion — it falls back to the guess when the stored value is NULL
     * (payrollChipFor) — so `!payrollHandled` would select the exact complement of the
     * rows we want. The gate asks about the STORED ANSWER; `looksPayroll` only narrows it
     * to the rows actually showing the suggestion.
     *
     * CORRECTED 2026-10-10 (measured on the read-only export, diffs/step3-quirks-build.txt P0.4): onExpenseSection now
     * tests matchISCategory, so cid=3's 15 unanswered legacy rows DO show the suggestion, marked, and this gate names them
     * ("15 payroll lines not confirmed"); Step 3's page confirm accepts them in one tick. This paragraph previously read:
     * "KNOWN LIMIT, SHIPPED DELIBERATELY: looksPayroll requires onExpenseSection, which exact-matches the five canonical
     * is_category labels. A LEGACY long-form workbook (gl_category = a department name like "Sales and Marketing") fails that
     * test, so its payroll rows show plain "Skip" and this gate stays SILENT on them -- measured: all 15 of cid=3's
     * unanswered rows." (diffs/phase1-B-s4-phase0.txt section 4b) */
    const step3UnansweredPayroll = mappings.filter(isUnansweredPayrollGuess);   // 2026-09-28 (C): the ONE predicate, shared with the mark
    /* 2026-10-10 (Brian, Q-B): the page confirm's state. ACCEPTED = ticked AND no payroll guess left unanswered -- a guess that
       appears after the tick (e.g. Skip picked on a payroll-named line, Q-A unchanged) shows the box UNticked again, so the box
       never claims to accept a line it has not. One tick then accepts everything suggested on screen. It is shown while a
       payroll guess or a suggested pay type is on screen, or while it holds accepted guesses (so it can be un-ticked). */
    const step3SuggestionsAccepted = step3PaytypesConfirmed && step3UnansweredPayroll.length === 0;
    const step3ConfirmHoldsGuesses = mappings.some(m => m.guessAccepted);
    const acceptStep3Suggestions = checked => { setMappings(prev => acceptPayrollGuesses(prev, checked)); setStep3PaytypesConfirmed(checked); };
    // 2026-09-28 (B1) -- the sixth gate: a mapped IS row with no line key. Named, every one.
    const step3BlankKeys = mappings.filter(m => lineKeyRequired(m) && !m.is_line_key);
    // Commission ship 2b -- the seventh gate: a payroll line whose pay type is not an answer (blank, or a suggestion the page
    // confirm has not accepted). Named, every one. step3PaytypeSuggested drives the page confirm's visibility.
    const step3UnansweredPaytype = mappings.filter(m => unansweredPayrollPaytype(m, step3SuggestionsAccepted));
    const step3PaytypeSuggested = mappings.some(m => m.payrollHandled && m.payroll_paytype && m.paytypeSuggested);

    const step3Blocked = step3UnmappedBs.length > 0 || step3UnmappedIs.length > 0
        || step3BadIs.length > 0 || step3Rekeys.length > 0
        || step3UnansweredPayroll.length > 0 || step3BlankKeys.length > 0
        || step3UnansweredPaytype.length > 0;

    const renderStep3 = () => {
        if (step3Loading) return <div style={{ padding: 32, color: '#999', fontFamily: FONT }}>Loading GL lines...</div>;
        const blocker = (title, rows, body, all) => (
            <div style={{ marginTop: 16, padding: '12px 16px', background: '#FEF3F2', border: '1px solid #FDA29B', borderRadius: 8 }}>
                <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: '#B42318', marginBottom: 4 }}>{title}</div>
                <div style={{ fontFamily: FONT, fontSize: 12, color: '#912018', marginBottom: 6 }}>{body}</div>
                <div style={{ fontFamily: MONO, fontSize: 12, color: '#912018' }}>
                    {(all ? rows : rows.slice(0, 12)).map(m => m.gl_name).join(' \u00b7 ')}
                    {!all && rows.length > 12 && ` \u2026 and ${rows.length - 12} more`}
                </div>
            </div>
        );
        return (
            <div>
                <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 18, marginBottom: 8, marginTop: 0 }}>GL Mapping</h3>
                <p style={{ fontSize: 13, color: '#888', fontFamily: FONT, margin: '0 0 20px' }}>
                    Map your GL lines to Finledom categories. Revenue lines can be skipped (handled later).
                    Role is derived from the category and the line&rsquo;s name; use &#9650;&#9660; to set the order lines
                    appear in on the statement.
                </p>
                {renderMappingSection('Income Statement Lines', 'IS')}
                {renderMappingSection('Balance Sheet Lines', 'BS')}
                {step3UnmappedIs.length > 0 && blocker(
                    `${step3UnmappedIs.length} income statement line${step3UnmappedIs.length === 1 ? '' : 's'} not mapped`,
                    step3UnmappedIs,
                    'An unmapped line does not appear on the income statement at all \u2014 if it has real dollars, they are silently missing from the model. Give each one a category, or mark it Skip. (Skip is a valid decision and is remembered; leaving it blank is not.)',
                    true
                )}
                {step3BlankKeys.length > 0 && blocker(
                    `${step3BlankKeys.length} income statement line${step3BlankKeys.length === 1 ? '' : 's'} with no line key`,
                    step3BlankKeys,
                    'A mapped line with no line key has nowhere to store its history \u2014 the import would drop every closed month of it from the income statement, silently. Choose a line for each one (its own line is always offered).',
                    true
                )}
                {step3UnmappedBs.length > 0 && blocker(
                    `${step3UnmappedBs.length} balance sheet line${step3UnmappedBs.length === 1 ? '' : 's'} not mapped`,
                    step3UnmappedBs,
                    'An unmapped line is dropped from the balance sheet and from Total Cash \u2014 which the cash sweep is based on. Give each one a category, or mark it Skip.',
                    true
                )}
                {step3UnansweredPayroll.length > 0 && blocker(
                    `${step3UnansweredPayroll.length} payroll line${step3UnansweredPayroll.length === 1 ? '' : 's'} not confirmed`,
                    step3UnansweredPayroll,
                    'These lines look like payroll from their names. Salaries, bonuses, payroll taxes, ' +
                    'benefits and commissions come from the Payroll module (Step 5), never from a GL mapping ' +
                    '\u2014 but nobody has said so for these yet. Tick the box below to accept the suggested lines, or pick another ' +
                    'category for any line that is really something else.'
                )}
                {step3UnansweredPaytype.length > 0 && blocker(
                    `${step3UnansweredPaytype.length} payroll line${step3UnansweredPaytype.length === 1 ? '' : 's'} with no confirmed pay type`,
                    step3UnansweredPaytype,
                    'Each line handled by Payroll needs its pay type (Wages, Bonuses, Commissions, Payroll taxes or Benefits). ' +
                    'Choose one for each line, or accept the suggested ones with the box below.',
                    true
                )}
                {step3BadIs.length > 0 && blocker(
                    `${step3BadIs.length} income statement line${step3BadIs.length === 1 ? '' : 's'} could not be classified`,
                    step3BadIs,
                    'These lines have no role, so they would not appear on the income statement at all \u2014 and any vendor mapped to them would silently book $0. Pick a different category.'
                )}
                {step3Rekeys.length > 0 && (
                    <div style={{ marginTop: 16, padding: '12px 16px', background: '#FEF3F2', border: '1px solid #FDA29B', borderRadius: 8 }}>
                        <div style={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, color: '#B42318', marginBottom: 4 }}>
                            {step3Rekeys.length} line{step3Rekeys.length === 1 ? '' : 's'} would change a line key that other data points at
                        </div>
                        <div style={{ fontFamily: FONT, fontSize: 12, color: '#912018', marginBottom: 6 }}>
                            A line key is the line&rsquo;s identity, not a label. Vendors are attached to it, and every closed
                            month&rsquo;s history is stored under it. Changing it would leave both pointing at a line that no longer
                            exists &mdash; the vendor&rsquo;s spend and the line&rsquo;s history would both silently become $0.
                            Re-keying is a data migration, not an edit here. Restore the original key to continue.
                        </div>
                        {step3Rekeys.slice(0, 10).map(m => (
                            <div key={savedKeyOf(m)} style={{ fontFamily: MONO, fontSize: 12, color: '#912018' }}>
                                {m.gl_name}: <strong>{savedKeyOf(m)}</strong> &rarr; {currentKeyOf(m) || '(none)'}
                                <span style={{ color: '#B45309' }}> &mdash; has {m._why}</span>
                            </div>
                        ))}
                        {step3Rekeys.length > 10 && (
                            <div style={{ fontFamily: MONO, fontSize: 12, color: '#912018' }}>\u2026 and {step3Rekeys.length - 10} more</div>
                        )}
                    </div>
                )}
                {/* Commission ship 2b -- Step 4a's PAGE CONFIRM, reused: a suggested pay type is accepted here and nowhere else.
                    2026-10-10 (Brian, Q-B): WIDENED -- the same one box also accepts the "Handled by Payroll" guesses. */}
                {(step3PaytypeSuggested || step3UnansweredPayroll.length > 0 || step3ConfirmHoldsGuesses) && (
                    <label data-step3-confirm="paytype" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '16px 0 0',
                        fontFamily: FONT, fontSize: 13, color: '#1A1C2E', cursor: 'pointer', maxWidth: 780 }}>
                        <input type="checkbox" checked={step3SuggestionsAccepted} onChange={e => acceptStep3Suggestions(e.target.checked)}
                            style={{ marginTop: 2, cursor: 'pointer' }} />
                        <span>I have reviewed every line marked <em>suggested</em> on this page. Lines and pay types marked
                            <em> suggested</em> are accepted as shown.</span>
                    </label>
                )}
                {error && <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginTop: 12 }}>{error}</div>}
                <div style={{ display: 'flex', gap: 12, marginTop: 24, alignItems: 'center' }}>
                    <button onClick={() => handleBack(3)} style={btnText}>&larr; Back</button>
                    <button onClick={handleSaveMappingsClick} disabled={saving} style={btnSecondary}>
                        {saving ? 'Saving...' : 'Save Mappings'}
                    </button>
                    {mappingSaved && <span style={{ color: '#27AE60', fontSize: 13, fontFamily: FONT }}>Saved &#10003;</span>}
                    <button onClick={handleStep3Complete} disabled={saving || step3Blocked}
                        style={{ ...btnPrimary, opacity: (saving || step3Blocked) ? 0.5 : 1,
                            cursor: step3Blocked ? 'not-allowed' : 'pointer' }}>
                        {saving ? 'Saving...' : 'Save & Continue \u2192'}
                    </button>
                </div>
            </div>
        );
    };

    // ── Step 4a: cash classification panel ──────────────────
    // Renders EVERY current-asset leaf. A filtered "cash candidates" view would HIDE an
    // undetected cash account — the exact failure this prevents (a name heuristic misses
    // real bank accounts like "Wells Fargo #4165" / "Emburse Prepaid Debit Card", and
    // false-positives on the de-facto-AR account "Undeposited Funds").
    // 2026-09-24 -- ONE type picker, used by the Separate rows and by the liability / equity panel.
    const renderStep4TypePicker = (o, t, onPick) => (
        <div style={{ marginTop: 6, textAlign: 'left' }}>
            <select value={t.answer || ''} onChange={e => onPick(e.target.value)} data-step4-type={o.key}
                style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid ' + (t.answer ? '#D0D5DD' : '#F59E0B'),
                    fontSize: 12, fontFamily: FONT, background: '#fff', color: '#1A1C2E', maxWidth: 300 }}>
                <option value="">&mdash; choose a type &mdash;</option>
                {BS_TYPE_OPTIONS[t.section].map(x => <option key={x.answer} value={x.answer}>{x.label}</option>)}
            </select>
            {t.suggested && (
                <span data-step4-mark="suggested" style={{ marginLeft: 8, fontSize: 11, color: '#1A73E8' }}>
                    suggested &mdash; confirm or change
                </span>
            )}
            {!t.answer && (
                <span data-step4-mark="required" style={{ marginLeft: 8, fontSize: 11, color: '#B45309', fontWeight: 600 }}>
                    Required &mdash; choose a type
                </span>
            )}
            {t.answer && (
                <div style={{ fontSize: 11, color: '#6B7280', marginTop: 3 }}>{step4DriverNote(t.section, t.answer)}</div>
            )}
        </div>
    );

    const renderStep4CashPanel = () => {
        const answerOf = step4AnswerOf;
        const setAnswer = (key, val) => {
            if (val === 'sweep') {
                setStep4SweepKey(key);                                  // sweep is single-select by construction
                setStep4Cls(p => { const n = { ...p }; delete n[key]; return n; });
            } else {
                if (step4SweepKey === key) setStep4SweepKey('');        // demoted out of the sweep slot
                setStep4Cls(p => ({ ...p, [key]: val }));
            }
        };
        const rollupOpts = step4BsOptions.filter(o => answerOf(o) === 'rollup');
        const totalCash = r2(rollupOpts.reduce((s, o) => s + (o.opening || 0), 0));
        const money = n => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const OPTS = [['sweep', 'Sweep'], ['rollup', 'Total Cash'], ['separate', 'Separate']];
        /* ★ BUILD B — the parent radio is still THREE options. A widened answer
         * ('separate_receivable' / 'separate_constant') must still light up "Separate",
         * so the checked test for that option asks isSeparateAnswer rather than equality. */
        const parentChecked = (o, v, a) => (v === 'separate' ? isSeparateAnswer(a) : a === v);

        return (
            <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 16, margin: '0 0 6px' }}>Classify Cash Accounts</h3>
                <p style={{ fontSize: 13, color: '#888', fontFamily: FONT, margin: '0 0 14px', maxWidth: 780 }}>
                    Every Current Asset is listed. Operating cash accounts roll into <strong>Total Cash</strong> (the
                    balance the forecast sweeps against); anything that isn&rsquo;t cash &mdash; receivables, prepaids,
                    deposits &mdash; stands <strong>Separate</strong>. Exactly one account is the <strong>Sweep</strong> plug.
                    Defaults are a starting point &mdash; <strong>check every row</strong>, especially bank accounts whose
                    name doesn&rsquo;t say &ldquo;bank&rdquo;. A <strong>Separate</strong> line also needs its
                    <strong> type</strong>: a type suggested from the account name is marked &mdash; confirm it or change it.
                </p>

                <div style={{ border: '1px solid #E2E5F0', borderRadius: 8, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT, fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: '#FAFBFE' }}>
                                <th style={{ textAlign: 'left', padding: '9px 12px', fontSize: 11, fontWeight: 700, color: '#6B7280' }}>Account</th>
                                <th style={{ textAlign: 'right', padding: '9px 12px', fontSize: 11, fontWeight: 700, color: '#6B7280', width: 130 }}>{openingBalanceHeader(obState?.historical_start)}</th>
                                <th style={{ textAlign: 'center', padding: '9px 12px', fontSize: 11, fontWeight: 700, color: '#6B7280', width: 280 }}>Treatment</th>
                            </tr>
                            {openingPreLaunchNoteRow(obState, 3)}
                        </thead>
                        <tbody>
                            {step4BsOptions.map(o => {
                                const a = answerOf(o);
                                const flagged = looksNonCash(o);
                                // The advisory ALIGNS with the default rather than contradicting it:
                                // a flagged row explains why it starts as Separate; if the operator
                                // moves it INTO Total Cash, it warns instead. Never auto-changes.
                                const warn = flagged && a === 'rollup';
                                return (
                                    <tr key={o.key} style={{ borderTop: '1px solid #F0F1F5', background: warn ? '#FFFBEB' : '#fff' }}>
                                        <td style={{ padding: '9px 12px', color: '#1A1C2E' }}>
                                            {o.label}
                                            {warn && (
                                                <span style={{ marginLeft: 8, fontSize: 11, color: '#B45309' }}>
                                                    &mdash; looks like a receivable/prepaid, but it&rsquo;s in Total Cash. Intended?
                                                </span>
                                            )}
                                            {flagged && a === 'separate' && (
                                                <span style={{ marginLeft: 8, fontSize: 11, color: '#9AA0AE' }}>
                                                    &mdash; detected as a receivable/prepaid
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: "'Space Mono',monospace", color: '#4B5563' }}>
                                            {money(o.opening || 0)}
                                        </td>
                                        <td style={{ padding: '9px 12px', textAlign: 'center' }}>
                                            {OPTS.map(([v, l]) => (
                                                <label key={v} style={{ marginRight: 12, fontSize: 12, cursor: 'pointer', color: parentChecked(o, v, a) ? '#1A1C2E' : '#6B7280', fontWeight: parentChecked(o, v, a) ? 600 : 400 }}>
                                                    <input type="radio" name={`cls-${o.key}`} checked={parentChecked(o, v, a)}
                                                        onChange={() => setAnswer(o.key, v)} style={{ marginRight: 4, cursor: 'pointer' }} />
                                                    {l}
                                                </label>
                                            ))}
                                            {/* 2026-09-24 -- EVERY Separate row carries its TYPE: the operator's,
                                                or the name test's suggestion (marked), or blank and required. */}
                                            {(() => {
                                                const t = step4TypeOf(o, a, step4Cls);
                                                return t && renderStep4TypePicker(o, t, v => setAnswer(o.key, v || SEPARATE_ANSWERS.base));
                                            })()}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Live preview — the operator's real check. This is the SAME sum written to
                    cfs_dec23.total_clearing (the opening the sweep seeds from). */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap',
                    marginTop: 14, padding: '12px 16px', background: '#F0F6FF', border: '1px solid #C7DBFF', borderRadius: 8 }}>
                    <div style={{ fontFamily: FONT, fontSize: 13, color: '#1A1C2E' }}>
                        <strong>Total Cash</strong> at opening{openingMonthText(obState?.historical_start)} ({rollupOpts.length} account{rollupOpts.length === 1 ? '' : 's'}):{' '}
                        <span style={{ fontFamily: "'Space Mono',monospace", fontWeight: 700 }}>{money(totalCash)}</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#5B6B8C' }}>
                        Does this match your actual bank balance? If not, an account is in the wrong bucket.
                    </div>
                </div>

                <div style={{ marginTop: 14, maxWidth: 320 }}>
                    <div style={labelStyle}>Total Cash line label (on the Balance Sheet)</div>
                    <input type="text" value={step4TotalCashLabel} onChange={e => setStep4TotalCashLabel(e.target.value)}
                        style={{ padding: '9px 12px', borderRadius: 6, border: '1px solid #D0D5DD', fontSize: 14,
                            fontFamily: FONT, width: '100%', background: '#fff', color: '#1A1C2E' }} />
                    <div style={helperStyle}>Leave blank to use &ldquo;Total Clearing&rdquo;.</div>
                </div>
            </div>
        );
    };

    // ── Step 4a: fixed / intangible asset lines — the asset itself vs its contra ───────
    //
    // ★ B5 s5. This panel exists because the answer used to be a NAME TEST: `/accum/i` on
    // the account label decided whether a leaf was the asset or its accumulated contra.
    // The two select OPPOSITE formulas — contra SUBTRACTS depreciation every month, gross
    // ADDS additions — so a tenant naming the line "Reserve for Depreciation" silently
    // broke the balance sheet by the cumulative depreciation. The role feeds four further
    // consumers besides the balance itself, so the blast radius is not one cell.
    const renderStep4AssetPanel = () => {
        if (!step4AssetOptions.length) return null;
        const money = n => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        // The contra option is worded from the leaf's OWN sub-type (the s1 vocabulary),
        // READ off the option rather than re-derived here.
        const contraLabel = o => o.subType === 'intangible_assets'
            ? 'Accumulated amortization'
            : 'Accumulated depreciation';
        return (
            <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 16, margin: '0 0 6px' }}>Classify Asset Lines</h3>
                <p style={{ fontSize: 13, color: '#888', fontFamily: FONT, margin: '0 0 14px', maxWidth: 780 }}>
                    Every Fixed and Intangible Asset line is listed. Say whether each one is
                    <strong> the asset itself</strong> (its cost, which grows with purchases) or the
                    <strong> accumulated depreciation / amortization</strong> that offsets it (a negative
                    line that grows as the asset is written down). These drive opposite formulas, so a
                    wrong answer breaks the balance sheet. <strong>Nothing is pre-selected</strong> &mdash;
                    the answer comes from you, not from the account&rsquo;s name.
                </p>

                <div style={{ border: '1px solid #E2E5F0', borderRadius: 8, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT, fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: '#FAFBFE' }}>
                                <th style={{ textAlign: 'left', padding: '9px 12px', fontSize: 11, fontWeight: 700, color: '#6B7280' }}>Account</th>
                                <th style={{ textAlign: 'left', padding: '9px 12px', fontSize: 11, fontWeight: 700, color: '#6B7280', width: 130 }}>Type</th>
                                <th style={{ textAlign: 'right', padding: '9px 12px', fontSize: 11, fontWeight: 700, color: '#6B7280', width: 130 }}>{openingBalanceHeader(obState?.historical_start)}</th>
                                <th style={{ textAlign: 'center', padding: '9px 12px', fontSize: 11, fontWeight: 700, color: '#6B7280', width: 330 }}>Treatment</th>
                            </tr>
                            {openingPreLaunchNoteRow(obState, 4)}
                        </thead>
                        <tbody>
                            {step4AssetOptions.map(o => {
                                const a = step4AssetCls[o.key];
                                const open = !a;
                                return (
                                    <tr key={o.key} style={{ borderTop: '1px solid #F0F1F5', background: open ? '#FFFBEB' : '#fff' }}>
                                        <td style={{ padding: '9px 12px', color: '#1A1C2E' }}>
                                            {o.label}
                                            {open && (
                                                <span style={{ marginLeft: 8, fontSize: 11, color: '#B45309' }}>
                                                    &mdash; needs an answer
                                                </span>
                                            )}
                                        </td>
                                        <td style={{ padding: '9px 12px', fontSize: 12, color: '#6B7280' }}>{o.is_category}</td>
                                        <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: "'Space Mono',monospace", color: '#4B5563' }}>
                                            {money(o.opening || 0)}
                                        </td>
                                        <td style={{ padding: '9px 12px', textAlign: 'center' }}>
                                            {[['gross', 'The asset itself'], ['contra', contraLabel(o)]].map(([v, l]) => (
                                                <label key={v} style={{ marginRight: 12, fontSize: 12, cursor: 'pointer', color: a === v ? '#1A1C2E' : '#6B7280', fontWeight: a === v ? 600 : 400 }}>
                                                    <input type="radio" name={`acls-${o.key}`} checked={a === v}
                                                        onChange={() => setStep4AssetCls(p => ({ ...p, [o.key]: v }))}
                                                        style={{ marginRight: 4, cursor: 'pointer' }} />
                                                    {l}
                                                </label>
                                            ))}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    // ── Step 4a: Sweep account + import ─────────────────────
    // 2026-09-24 -- the liability, long-term liability and equity lines, each with its TYPE.
    const renderStep4TypePanel = () => {
        if (!step4TypeOptions.length) return null;
        const money = n => '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const setType = (key, v) => setStep4Cls(p => { const n = { ...p }; if (v) n[key] = v; else delete n[key]; return n; });
        return (
            <div style={{ marginBottom: 24 }}>
                <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 16, margin: '0 0 6px' }}>Classify Liability and Equity Lines</h3>
                <p style={{ fontSize: 13, color: '#888', fontFamily: FONT, margin: '0 0 14px', maxWidth: 780 }}>
                    Every liability and equity line is listed with its <strong>type</strong>, which decides how it is
                    forecast. A type <strong>suggested from the account name</strong> is marked &mdash; confirm it or
                    change it. A line whose name suggests nothing is <strong>blank and required</strong>. Long-term
                    liabilities are debt, held constant.
                </p>
                <div style={{ border: '1px solid #E2E5F0', borderRadius: 8, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT, fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: '#FAFBFE' }}>
                                <th style={{ textAlign: 'left', padding: '9px 12px', fontSize: 11, fontWeight: 700, color: '#6B7280' }}>Account</th>
                                <th style={{ textAlign: 'left', padding: '9px 12px', fontSize: 11, fontWeight: 700, color: '#6B7280', width: 140 }}>Category</th>
                                <th style={{ textAlign: 'right', padding: '9px 12px', fontSize: 11, fontWeight: 700, color: '#6B7280', width: 130 }}>{openingBalanceHeader(obState?.historical_start)}</th>
                                <th style={{ textAlign: 'left', padding: '9px 12px', fontSize: 11, fontWeight: 700, color: '#6B7280', width: 360 }}>Type</th>
                            </tr>
                            {openingPreLaunchNoteRow(obState, 4)}
                        </thead>
                        <tbody>
                            {step4TypeOptions.map(o => {
                                const t = step4TypeOf(o, undefined, step4Cls);
                                const blank = !!t && !t.answer;
                                return (
                                    <tr key={o.key} data-step4-row={o.key} style={{ borderTop: '1px solid #F0F1F5', background: blank ? '#FFFBEB' : '#fff' }}>
                                        <td style={{ padding: '9px 12px', color: '#1A1C2E' }}>{o.label}</td>
                                        <td style={{ padding: '9px 12px', fontSize: 12, color: '#6B7280' }}>{o.is_category}</td>
                                        <td style={{ padding: '9px 12px', textAlign: 'right', fontFamily: "'Space Mono',monospace", color: '#4B5563' }}>
                                            {money(o.opening || 0)}
                                        </td>
                                        <td style={{ padding: '9px 12px' }}>
                                            {t ? renderStep4TypePicker(o, t, v => setType(o.key, v)) : (
                                                <div data-step4-fixed="debt" style={{ fontSize: 12, color: '#1A1C2E' }}>
                                                    Long-term debt
                                                    <div style={{ fontSize: 11, color: '#6B7280', marginTop: 3 }}>
                                                        Held constant; its changes are financing on the cash flow.
                                                    </div>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderStep4a = () => {
        // ★ B5 s5 — computed inside the render fn, not at component-body level: the
        // declaration phase runs before any loading guard (§9.5 checklist item 5).
        const step4AssetUnanswered = step4AssetOptions.filter(o => !step4AssetCls[o.key]);
        // ★ BUILD B — same shape, same place, same reason (§9.5 item 5: computed inside the
        // render fn, not at component-body level, so it runs after the loading guard).
        const step4SepUnanswered = step4TypeUnansweredOf(step4BsOptions, step4TypeOptions, step4AnswerOf, step4Cls);
        const step4FormulaClash = step4FormulaClashOf(step4TypeOptions, step4Cls);
        return (
            <div>
                <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 18, marginBottom: 8, marginTop: 0 }}>Configure Sweep Account</h3>
                <p style={{ fontSize: 13, color: '#888', fontFamily: FONT, margin: '0 0 24px' }}>
                    Select your Money Market / Sweep Account &mdash; this is the cash account whose balance drives cash flow sweeping in the forecast.
                </p>

                {step4BsOptions.length === 0 ? (
                    <div style={{ background: '#FFF3E0', border: '1px solid #E65100', borderRadius: 6,
                        padding: 16, fontFamily: FONT, fontSize: 13, color: '#E65100', marginBottom: 20 }}>
                        No Balance Sheet lines are mapped to "Current Assets" yet. Go back to Step 3 (GL Mapping) and map at least one cash/current-asset line.
                    </div>
                ) : (
                    <div style={{ marginBottom: 24, maxWidth: 500 }}>
                        <div style={labelStyle}>Select your Money Market / Sweep Account</div>
                        <select value={step4SweepKey} onChange={e => setStep4SweepKey(e.target.value)}
                            style={{ padding: '10px 12px', borderRadius: 6, border: '1px solid #D0D5DD',
                                fontSize: 14, fontFamily: FONT, width: '100%', background: '#fff', color: '#1A1C2E' }}>
                            <option value="">— Select account —</option>
                            {step4BsOptions.map(o => (
                                <option key={o.key} value={o.key}>{o.label}</option>
                            ))}
                        </select>
                        <div style={helperStyle}>
                            Options shown are your Current Assets GL lines. Cash/bank/MM accounts typically qualify.
                        </div>
                    </div>
                )}

                {step4BsOptions.length > 0 && renderStep4CashPanel()}

                {renderStep4AssetPanel()}

                {renderStep4TypePanel()}

                {step4Importing && (
                    <div style={{ background: '#F0F6FF', border: '1px solid #1A73E8', borderRadius: 6,
                        padding: '14px 18px', marginBottom: 20, fontFamily: FONT, color: '#1A73E8', fontSize: 14, fontWeight: 500 }}>
                        Importing financial data&hellip;
                    </div>
                )}

                {/* Exactly-one-sweep is enforced by construction: step4SweepKey holds a single
                    key, and the radio column writes it. The only reachable violation is ZERO
                    (the operator demoted the sweep row to Total Cash / Separate). */}
                {step4BsOptions.length > 0 && !step4SweepKey && (
                    <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6,
                        padding: '10px 14px', marginBottom: 12, fontFamily: FONT, fontSize: 13, color: '#991B1B' }}>
                        Select exactly one <strong>Sweep</strong> account &mdash; it is the plug the cash-flow forecast
                        draws against. Import is blocked until one is chosen.
                    </div>
                )}

                {/* ★ B5 s5 — an unanswered asset line blocks, and the offenders are NAMED.
                    Silence would leave the operator hunting a disabled button. */}
                {step4AssetUnanswered.length > 0 && (
                    <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6,
                        padding: '10px 14px', marginBottom: 12, fontFamily: FONT, fontSize: 13, color: '#991B1B' }}>
                        Classify every <strong>fixed / intangible asset</strong> line &mdash;{' '}
                        {step4AssetUnanswered.length} still unanswered:{' '}
                        {step4AssetUnanswered.map(o => o.label).join(', ')}. Import is blocked until each is answered.
                    </div>
                )}

                {/* ★ BUILD B — an unrecognised Separate line blocks, and the offenders are NAMED.
                    Same grammar as the asset banner above; silence would leave the operator
                    hunting a disabled button. */}
                {step4SepUnanswered.length > 0 && (
                    <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6,
                        padding: '10px 14px', marginBottom: 12, fontFamily: FONT, fontSize: 13, color: '#991B1B' }}>
                        Choose the <strong>type</strong> of every balance-sheet line &mdash;{' '}
                        {step4SepUnanswered.length} still blank:{' '}
                        {step4SepUnanswered.map(o => o.label).join(', ')}. Import is blocked until each has a type.
                    </div>
                )}

                {step4FormulaClash.length > 0 && (
                    <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6,
                        padding: '10px 14px', marginBottom: 12, fontFamily: FONT, fontSize: 13, color: '#991B1B' }}>
                        {step4FormulaClashText(step4FormulaClash)} Import is blocked until each is on one line.
                    </div>
                )}

                {/* 2026-09-24 -- THE PAGE CONFIRM. A suggested type is accepted here and nowhere else. */}
                <label data-step4-confirm="1" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '4px 0 14px',
                    fontFamily: FONT, fontSize: 13, color: '#1A1C2E', cursor: 'pointer', maxWidth: 780 }}>
                    <input type="checkbox" checked={step4TypesConfirmed} onChange={e => setStep4TypesConfirmed(e.target.checked)}
                        style={{ marginTop: 2, cursor: 'pointer' }} />
                    <span>I have reviewed the type of every balance-sheet line on this page. Types marked
                        <em> suggested</em> are accepted as shown.</span>
                </label>

                {step4ImportError && (
                    <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginBottom: 12 }}>{step4ImportError}</div>
                )}

                <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                    <button onClick={() => handleStep4BackToStep(3)} style={btnText}>&larr; Back</button>
                    <button onClick={handleStep4Import}
                        disabled={step4Importing || !step4SweepKey || step4BsOptions.length === 0 || step4AssetUnanswered.length > 0 || step4SepUnanswered.length > 0 || step4FormulaClash.length > 0 || !step4TypesConfirmed}
                        style={{ ...btnPrimary, opacity: (step4Importing || !step4SweepKey || step4BsOptions.length === 0 || step4AssetUnanswered.length > 0 || step4SepUnanswered.length > 0 || step4FormulaClash.length > 0 || !step4TypesConfirmed) ? 0.5 : 1 }}>
                        {step4Importing ? 'Importing...' : 'Import Financial Data \u2192'}
                    </button>
                </div>
            </div>
        );
    };

    // ── Step 4 dispatcher: routes to 4a (import) or 4b (review) ───────
    const renderStep4 = () => {
        if (step4Loading || step4SubStep === null) {
            return <div style={{ padding: 32, color: '#999', fontFamily: FONT }}>Loading financial data...</div>;
        }
        if (step4SubStep === '4a') return renderStep4a();
        return renderStep4b();
    };

    // ── Step 4b: Review Financials renderer ───────────────────
    const renderStep4b = () => {
        if (step4Loading || !step4Data) {
            return <div style={{ padding: 32, color: '#999', fontFamily: FONT }}>Loading financial data...</div>;
        }
        const { is, bs, cfs, months, defrevData, priorDec } = step4Data;
        const bsMonths = bs.months;

        // BS balance check
        const outOfBalanceMonths = bsMonths.filter(m => Math.abs(bs.checkRow[m] || 0) >= 0.01);
        const balanced = outOfBalanceMonths.length === 0;

        // Group months by year for year separators
        const yearsOf = (list) => {
            const years = [];
            const byYear = {};
            for (const m of list) {
                const y = m.split('-')[0];
                if (!byYear[y]) { byYear[y] = []; years.push(y); }
                byYear[y].push(m);
            }
            return { years, byYear };
        };
        const isYears = yearsOf(months);
        const bsYears = yearsOf(bsMonths);
        const cfsYears = yearsOf(cfs.months);

        const colMinWidth = 84;
        const SL_LABEL = { position: 'sticky', left: 0, zIndex: 2, background: '#fff', borderRight: '2px solid #E2E5F0' };

        const thYear = (y, count) => (
            <th key={y} colSpan={count} style={{ padding: '6px', textAlign: 'center', fontSize: 10, fontWeight: 700,
                color: BLUE, background: '#FAFBFE', borderRight: '2px solid rgba(61,133,198,0.15)' }}>
                {y} — ACTUALS
            </th>
        );

        const thMonth = (m) => (
            <th key={m} style={{ padding: '8px 4px', textAlign: 'center', fontSize: 9, fontWeight: 600, color: BLUE,
                whiteSpace: 'nowrap', minWidth: colMinWidth,
                borderRight: m.endsWith('-12') ? '2px solid rgba(61,133,198,0.15)' : 'none' }}>
                {formatMonthLabel(m, "short")} {m.split('-')[0].slice(2)}
            </th>
        );

        const valTd = (m, v, opts = {}) => (
            <td key={m} style={{
                padding: `${opts.bold ? 10 : 6}px 4px`, textAlign: 'right',
                fontFamily: MONO, fontSize: opts.bold ? 11 : 10, fontWeight: opts.bold ? 700 : 400,
                color: opts.color || BLUE, background: opts.bg || '#fff',
                borderTop: opts.borderTop || 'none', borderBottom: opts.borderBottom || 'none',
                borderRight: m.endsWith('-12') ? '2px solid rgba(61,133,198,0.15)' : 'none',
                whiteSpace: 'nowrap',
            }}>{opts.zeroDash && (!v || Math.abs(v) < 0.005) ? '—' : fmt2(v || 0)}</td>
        );
        const pctTd = (m, v) => (
            <td key={m} style={{
                padding: '4px', textAlign: 'right', fontFamily: MONO, fontSize: 9, fontWeight: 400,
                color: '#8B8FA3', background: '#fff',
                borderRight: m.endsWith('-12') ? '2px solid rgba(61,133,198,0.15)' : 'none',
                whiteSpace: 'nowrap',
            }}>{v == null ? '—' : v.toFixed(1) + '%'}</td>
        );

        // ── Heterogeneous table renderer ────────────────────
        // items: array of { type: 'section' | 'keyTotal' | 'margin', ... }
        const renderStatementTable = (cols, yearGroups, items) => {
            const colCount = cols.length;
            return (
                <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #E2E5F0',
                    overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 360px)', maxWidth: '100%' }}>
                    <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: 'max-content', minWidth: '100%' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                            <tr style={{ background: '#FAFBFE' }}>
                                <th style={{ ...SL_LABEL, padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700,
                                    color: '#A0A3B8', background: '#FAFBFE', zIndex: 12 }}></th>
                                {yearGroups.years.map(y => thYear(y, yearGroups.byYear[y].length))}
                            </tr>
                            <tr style={{ background: '#fff', borderBottom: '2px solid #E2E5F0' }}>
                                <th style={{ ...SL_LABEL, padding: '8px 12px', textAlign: 'left', fontSize: 10, fontWeight: 600,
                                    color: '#8B8FA3', minWidth: 260, background: '#fff', zIndex: 12 }}>Line Item</th>
                                {cols.map(m => thMonth(m))}
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, itemIdx) => {
                                if (item.type === 'section') {
                                    const sec = item;
                                    const isCollapsed = step4Collapsed.has(sec.key);
                                    return (
                                        <React.Fragment key={sec.key}>
                                            <tr style={{ background: '#F7F8FC', cursor: 'pointer' }} onClick={() => toggleStep4Section(sec.key)}>
                                                <td style={{ ...SL_LABEL, padding: '8px 12px', fontSize: 11, fontWeight: 700,
                                                    color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px',
                                                    background: '#F7F8FC', userSelect: 'none' }}>
                                                    <span style={{ display: 'inline-block', width: 14, fontSize: 10, color: '#B0B3C8',
                                                        transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform 0.15s' }}>▾</span>
                                                    {sec.label} <span style={{ fontWeight: 400, fontSize: 10, color: '#A0A3B8' }}>({sec.lines.length})</span>
                                                </td>
                                                <td colSpan={colCount} style={{ background: '#F7F8FC' }}></td>
                                            </tr>
                                            {!isCollapsed && sec.lines.map((ln, i) => (
                                                <tr key={`${sec.key}_${i}`}>
                                                    <td style={{ ...SL_LABEL, padding: '6px 12px 6px 32px', fontSize: 10, color: '#1A1C2E',
                                                        fontFamily: FONT, whiteSpace: 'nowrap' }}>
                                                        {ln.display_name || ln.gl_name}
                                                        {/* ★ Build 1 (PART 1b): department suffix, ONLY where a name repeats */}
                                                        {ln.display_suffix && (
                                                            <span style={{ color: '#8B8FA3', fontWeight: 400 }}> — {ln.display_suffix}</span>
                                                        )}
                                                    </td>
                                                    {cols.map(m => valTd(m, ln.amounts[m]))}
                                                </tr>
                                            ))}
                                            {/* ★ Build 1 (PART 2): operator-defined sub-rollups (Total Cash) — rendered
                                                after the members, before the section total, and only when present. */}
                                            {!isCollapsed && (sec.subTotals || []).map((st, i) => (
                                                <tr key={`${sec.key}_sub_${i}`}>
                                                    <td style={{ ...SL_LABEL, padding: '6px 12px 6px 32px', fontSize: 10, fontWeight: 600,
                                                        color: '#1A1C2E', fontFamily: FONT, whiteSpace: 'nowrap',
                                                        borderTop: '1px solid #EEF0F6' }}>{st.label}</td>
                                                    {cols.map(m => valTd(m, st.amounts[m], { bold: true, borderTop: '1px solid #EEF0F6' }))}
                                                </tr>
                                            ))}
                                            {sec.totalLabel && (
                                                <tr>
                                                    <td style={{ ...SL_LABEL, padding: '8px 12px', fontSize: 10, fontWeight: 700,
                                                        color: '#1A1C2E', fontFamily: FONT, whiteSpace: 'nowrap',
                                                        borderTop: '1px solid #E2E5F0', borderBottom: '1px solid #E2E5F0' }}>{sec.totalLabel}</td>
                                                    {cols.map(m => valTd(m, sec.totalMap[m], {
                                                        bold: true, borderTop: '1px solid #E2E5F0', borderBottom: '1px solid #E2E5F0' }))}
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                }
                                /* ★ Build A: the BS and CFS now render the MODEL's skeleton, whose
                                   rows are headers, leaves, totals and spacers rather than the
                                   bridge's old collapsible sections. Indentation comes from the
                                   row's own `level`, so nesting is the model's, not a local rule. */
                                if (item.type === 'plainHeader') {
                                    return (
                                        <tr key={`ph_${itemIdx}`} style={{ background: '#F7F8FC' }}>
                                            <td style={{ ...SL_LABEL, padding: '8px 12px', fontSize: 11, fontWeight: 700,
                                                color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.5px',
                                                background: '#F7F8FC', paddingLeft: 12 + (item.level || 0) * 14 }}>{item.label}</td>
                                            <td colSpan={colCount} style={{ background: '#F7F8FC' }}></td>
                                        </tr>
                                    );
                                }
                                if (item.type === 'spacer') {
                                    return (
                                        <tr key={`sp_${itemIdx}`}><td style={{ ...SL_LABEL, padding: '4px 12px' }}></td>
                                            <td colSpan={colCount}></td></tr>
                                    );
                                }
                                if (item.type === 'plainLine') {
                                    return (
                                        <tr key={`pl_${itemIdx}`}>
                                            <td style={{ ...SL_LABEL, padding: '6px 12px', fontSize: 10, color: '#1A1C2E',
                                                fontFamily: FONT, whiteSpace: 'nowrap',
                                                paddingLeft: 12 + (item.level || 0) * 14 }}>{item.label}</td>
                                            {cols.map(m => valTd(m, item.amounts[m]))}
                                        </tr>
                                    );
                                }
                                if (item.type === 'keyTotal') {
                                    return (
                                        <tr key={`kt_${itemIdx}`}>
                                            <td style={{ ...SL_LABEL, padding: '10px 12px', fontSize: 11, fontWeight: 700,
                                                color: item.color || '#1A1C2E', fontFamily: FONT, whiteSpace: 'nowrap',
                                                background: '#F7F8FC', borderTop: '2px solid #E2E5F0', borderBottom: '1px solid #E2E5F0' }}>{item.label}</td>
                                            {cols.map(m => valTd(m, item.amounts[m], {
                                                bold: true, color: item.color, bg: '#F7F8FC',
                                                borderTop: '2px solid #E2E5F0', borderBottom: '1px solid #E2E5F0' }))}
                                        </tr>
                                    );
                                }
                                if (item.type === 'margin') {
                                    return (
                                        <tr key={`mg_${itemIdx}`}>
                                            <td style={{ ...SL_LABEL, padding: '4px 12px 4px 32px', fontSize: 9, color: '#8B8FA3',
                                                fontFamily: FONT, whiteSpace: 'nowrap', fontStyle: 'italic' }}>{item.label}</td>
                                            {cols.map(m => pctTd(m, item.amounts[m]))}
                                        </tr>
                                    );
                                }
                                return null;
                            })}
                        </tbody>
                    </table>
                </div>
            );
        };

        const nonEmpty = (sec, totalMap, key, cols) => {
            if (!sec.lines.length) return null;
            const tm = {};
            for (const m of cols) {
                let t = 0;
                for (const ln of sec.lines) t = r2(t + (ln.amounts[m] || 0));
                tm[m] = r2(t);
            }
            return {
                type: 'section', key, label: sec.label, lines: sec.lines,
                totalLabel: `Total ${sec.label}`, totalMap: totalMap || tm,
            };
        };

        // ── IS items: interleaved sections + calcs + margins ──
        // ══ ★ Build C (D3b) — THE INCOME STATEMENT IS THE MODEL'S SKELETON TOO ═════════════════
        //
        // The screen no longer holds an IS section order. It builds LEAVES from the recovered GL
        // rows — under the model's own leaf ids — and hands them to the model's assembleISSkeleton,
        // which supplies the section order, the labels, the SG&A wrapper, and the per-department
        // sub-headers and "Total <dept>" sub-totals inside a multi-department section.
        //
        // Values come from the bridge: leaves by leaf id, plain section totals summed from each
        // total's own sumOf, and the derived rows (Gross Profit, the margins, Total SG&A, Operating
        // Income, Total OIE, Net Income, EBITDA) taken from the bridge's arithmetic, which owns this
        // screen's sign conventions.
        const isSkeleton = assembleISSkeleton({
            leavesBySub: is.isLeavesBySub || {},
            dgLabels: is.isDgLabels || {},
        });
        const isNamed = {
            gross_profit: is.grossProfit, gp_margin: is.gpMargin,
            total_sga: is.totalSGA, operating_income: is.operatingIncome, ebit_margin: is.ebitMargin,
            total_oie: is.totalOIE, net_income: is.netIncome, ebitda: is.ebitda,
        };
        const isEval = evaluateISSkeleton(isSkeleton, is.isValuesById || {}, isNamed, months);

        const isItems = isSkeleton.map(row => {
            if (row.type === 'header') return { type: 'plainHeader', label: row.label, level: row.level };
            const amounts = isEval.values[row.id] || {};
            if (row.type === 'margin') return { type: 'margin', label: row.label, amounts };
            if (row.type === 'total' || row.type === 'calc')
                return { type: 'keyTotal', label: row.label, amounts, color: row.id === 'net_income' ? '#27AE60' : undefined };
            return { type: 'plainLine', label: row.label, level: row.level, amounts };
        });

        // ══ ★ Build A (D1/D2) — THE BS AND CFS ARE THE MODEL'S SKELETONS ══════════════════════
        //
        // The review screen no longer keeps a section list of its own. It asks the model's builders
        // for the ordered rows and supplies the values it computed, keyed by leaf id. Everything
        // structural — section order, nesting, which totals exist, what each total sums, the Total
        // Cash sub-rollup's POSITION, the working-capital sub-header and sub-total, the fixed-asset
        // write-off row, the "Total Cash From …" wording — comes from the model. Deleting the old
        // parallel lists is the point: two implementations of one layout is what let this screen
        // drift from the statement in the first place.
        //
        // The builders are called AS-IS with the Step-4 inputs (rmm=[], empty moduleContext);
        // bs_actuals_meta is written by the Step-4 import itself, so it is present here.
        const modelMeta = defrevData?.bs_actuals_meta || {};
        const modelBSLines = Object.keys(modelMeta).length
            ? buildBSLinesForCompany(selectedCompanyId, modelMeta, [], {}) : [];
        const modelCFSLines = Object.keys(modelMeta).length
            ? buildCFSLinesForCompany(modelMeta, [], {}, defrevData?.cfs_oneoffs || {}) : [];

        // The bridge hands over ONE map: values keyed by the model's leaf id, plus the two rows the
        // model expects that are not GL leaves — its own balance CHECK row, and the fixed-asset
        // write-off (see the note below).
        const bsValues = { ...(bs.bsValuesByKey || {}), [CHECK_ROW_ID]: bs.checkRow };
        const bsEval = evaluateBSSkeleton(modelBSLines, bsValues, bsMonths);
        // ★ Build A v2 — THE BRIDGE NO LONGER OVERRIDES THE MODEL'S CASH DERIVATION.
        // v1 supplied fa_writeoff as zero and handed over the bridge's own Change-in-Cash and
        // Ending-Cash. Measured (diffs/fa-writeoff.txt): the bridge's investing carries a disposal's
        // gross-FA drop as a +4,074.18 inflow — identical to the model's — and the bridge had no
        // offsetting row at all, so Ending Cash was overstated by 4,074.18 in 2021-12, the one month
        // it also failed to equal the actual balance-sheet cash. (My v1 note claimed the bridge
        // "already absorbs" that movement. It does not; that was reasoning, not measurement.)
        //
        // Now the bridge supplies only PRIMITIVES — balances by leaf id, the two income-statement
        // references, the sweep, and the opening cash seed — and the model's own descriptors do the
        // rest: fa_writeoff derives from the gross-FA keys, and Total Cash From Operating →
        // Change in Cash → Ending Cash follow. The row and the totals move together.
        const cfsEval = evaluateCFSSkeleton(modelCFSLines, {
            bsValuesByKey: bs.bsValuesByKey || {},
            isRef: { net_income_monthly: is.netIncome, da_monthly: is.totalDA },
            sweep: cfs.mmSweepAmounts,
            // The opening seed only — Total Cash at the prior December, the same figure the model
            // seeds beg_cash from (cfs_dec23.total_clearing). Everything downstream is a true roll.
            openingCash: bsEval.values.total_clearing?.[priorDec] || 0,
        }, cfs.months, priorDec);

        // Fail-loud, never silent: a model row we could not value, or a bridge value with no row.
        const skeletonNotes = [];
        if (bsEval.missing.length) skeletonNotes.push(`Balance Sheet rows with no value: ${bsEval.missing.join(', ')}`);
        if (bsEval.orphans.length) skeletonNotes.push(`Balance Sheet values with no row: ${bsEval.orphans.join(', ')}`);
        if (bs.bsUnkeyed?.length) skeletonNotes.push(`Balance Sheet lines with no leaf key: ${bs.bsUnkeyed.map(u => u.gl_name).join(', ')}`);
        if (cfsEval.missing.length) skeletonNotes.push(`Cash Flow rows with no value: ${cfsEval.missing.join(', ')}`);
        // ★ Build C (D5) — a row we could place nowhere is NEITHER dropped NOR given an invented
        // "Personnel"/"Unmapped" section. It is named, outside the statement, so the operator can go
        // fix the GL mapping. Empty on cid=3 and cid=8 (every row resolves) — this renders only when
        // it is not.
        const isUnplacedRows = is.isUnplaced || [];

        // Render one model row. Headers/spacers/totals/leaves all come from the row's own descriptor.
        const rowToItem = (row, values) => {
            if (row.type === 'header') return { type: 'plainHeader', label: row.label, level: row.level };
            if (row.type === 'spacer') return { type: 'spacer' };
            const amounts = values[row.id] || {};
            if (row.type === 'check')
                return { type: 'keyTotal', label: 'Balance Check (Assets − L&E)', amounts, color: balanced ? '#27AE60' : '#E53935' };
            if (row.type === 'total' || row.type === 'formula')
                return { type: 'keyTotal', label: row.label, amounts, color: row.bold ? '#27AE60' : undefined };
            return { type: 'plainLine', label: row.label, level: row.level, amounts };
        };

        const bsItems = modelBSLines.map(r => rowToItem(r, bsEval.values));
        const cfsItems = modelCFSLines.map(r => rowToItem(r, cfsEval.values));

        return (
            <div>
                <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 18, marginBottom: 8, marginTop: 0 }}>Review Financials</h3>
                <p style={{ fontSize: 13, color: '#888', fontFamily: FONT, margin: '0 0 20px' }}>
                    Review the historical Income Statement, Balance Sheet, and derived Cash Flow. The Balance Sheet must balance before continuing.
                </p>

                {/* Balance check banner */}
                {balanced ? (
                    <div style={{ background: '#E8F5E9', border: '1px solid #27AE60', borderRadius: 8,
                        padding: '14px 18px', marginBottom: 20, fontFamily: FONT, color: '#2E7D32', fontSize: 14, fontWeight: 600 }}>
                        ✓ Balance Sheet balances for all months
                    </div>
                ) : (
                    <div style={{ background: '#FFEBEE', border: '1px solid #E53935', borderRadius: 8,
                        padding: '16px 18px', marginBottom: 20, fontFamily: FONT, color: '#C62828' }}>
                        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
                            ⚠ Balance Sheet does not balance in {outOfBalanceMonths.length} month{outOfBalanceMonths.length !== 1 ? 's' : ''}. You must resolve this before continuing.
                        </div>
                        <div style={{ maxHeight: 100, overflowY: 'auto', fontSize: 12, fontFamily: MONO, marginBottom: 12 }}>
                            {outOfBalanceMonths.map(m => (
                                <div key={m}>{m}: {fmt2(bs.checkRow[m])}</div>
                            ))}
                        </div>
                        <div style={{ display: 'flex', gap: 10 }}>
                            <button onClick={() => handleStep4BackToStep(3)} style={btnSecondary}>Fix GL Mapping</button>
                            <button onClick={() => handleStep4BackToStep(2)} style={btnSecondary}>Re-upload IS & BS</button>
                        </div>
                    </div>
                )}

                {/* ★ N4 (Build A): fail-loud revenue-sign guard — surfaced, never silently wrong */}
                {is.revenueSignWarning && (
                    <div style={{ background: '#FFF8E1', border: '1px solid #F5A623', borderRadius: 8,
                        padding: '14px 18px', marginBottom: 20, fontFamily: FONT, color: '#8A6D00', fontSize: 13 }}>
                        ⚠ {is.revenueSignWarning}
                    </div>
                )}

                {/* ★ Build 1 (PART 3b): fail-loud unresolved-sweep guard. Without it the cash rows
                    silently redefine "cash" to include the money market and the sweep row is all
                    zeros, under headings that say the opposite. */}
                {cfs.sweepWarning && (
                    <div style={{ background: '#FFF8E1', border: '1px solid #F5A623', borderRadius: 8,
                        padding: '14px 18px', marginBottom: 20, fontFamily: FONT, color: '#8A6D00', fontSize: 13 }}>
                        ⚠ {cfs.sweepWarning}
                    </div>
                )}

                {/* ★ Build C (D5): rows that could not be placed on any income-statement line. Shown
                    only when there are some — never silently dropped, never given an invented home. */}
                {isUnplacedRows.length > 0 && (
                    <div style={{ background: '#FFF8E1', border: '1px solid #F5A623', borderRadius: 8,
                        padding: '14px 18px', marginBottom: 20, fontFamily: FONT, color: '#8A6D00', fontSize: 13 }}>
                        ⚠ {isUnplacedRows.length} row{isUnplacedRows.length === 1 ? '' : 's'} couldn&rsquo;t be
                        categorized and {isUnplacedRows.length === 1 ? 'is' : 'are'} not shown on the Income Statement
                        &mdash; check the GL mapping in Step 3:{' '}
                        <span style={{ fontFamily: MONO }}>
                            {isUnplacedRows.slice(0, 8).map(u => u.gl_name).join(', ')}
                            {isUnplacedRows.length > 8 ? ` … +${isUnplacedRows.length - 8} more` : ''}
                        </span>
                    </div>
                )}

                {/* Tabs */}
                <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '2px solid #E2E5F0' }}>
                    {[['is', 'Income Statement'], ['bs', 'Balance Sheet'], ['cfs', 'Cash Flow Statement']].map(([k, lbl]) => (
                        <div key={k} onClick={() => setStep4Tab(k)} style={{
                            padding: '10px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: FONT,
                            color: step4Tab === k ? '#1A73E8' : '#6B7280',
                            borderBottom: step4Tab === k ? '2px solid #1A73E8' : '2px solid transparent',
                            marginBottom: -2,
                        }}>{lbl}</div>
                    ))}
                </div>

                {step4Tab === 'is' && renderStatementTable(months, isYears, isItems)}
                {step4Tab === 'bs' && renderStatementTable(bsMonths, bsYears, bsItems)}
                {step4Tab === 'cfs' && renderStatementTable(cfs.months, cfsYears, cfsItems)}

                {error && <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginTop: 12 }}>{error}</div>}
                <div style={{ display: 'flex', gap: 12, marginTop: 24, alignItems: 'center' }}>
                    <button onClick={() => handleBack(4)} style={btnText}>&larr; Back</button>
                    <button onClick={handleStep4ReImport} style={btnSecondary}>Re-import Data</button>
                    <button onClick={handleStep4Complete} disabled={saving || !balanced} style={{ ...btnPrimary, opacity: (!balanced || saving) ? 0.5 : 1 }}>
                        {saving ? 'Saving...' : 'Continue to Payroll Setup \u2192'}
                    </button>
                </div>
            </div>
        );
    };

    // ── Step 5: Payroll Setup renderers ──────────────────────
    const step5SubStepIdx = STEP5_SUB_STEPS.findIndex(s => s.key === step5SubStep);
    const renderStep5SubIndicator = () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 24, fontSize: 12, fontFamily: FONT }}>
            <span style={{ fontWeight: 600, color: '#1A1C2E' }}>Step 5: Payroll Setup</span>
            {STEP5_SUB_STEPS.map((s, i) => (
                <React.Fragment key={s.key}>
                    <span style={{ color: '#ccc', margin: '0 2px' }}>&rsaquo;</span>
                    <span style={{
                        fontWeight: step5SubStep === s.key ? 600 : 400,
                        color: step5SubStepIdx >= 0 && i < step5SubStepIdx ? '#27AE60' : step5SubStep === s.key ? '#1A73E8' : '#999',
                    }}>{s.label}</span>
                </React.Fragment>
            ))}
        </div>
    );

    const Step5DropZone = () => {
        const [dragOver, setDragOver] = useState(false);
        /* Bi-weekly step 4: NO UPLOAD UNTIL A PAY FREQUENCY IS STORED. Every way in is closed -- the click, the drop and the
           file input -- and the zone says why. (handleStep5Parse asks the owner again before it parses anything.) */
        const uploadOff = !step5PayFrequencyStored;
        return (
            <div data-testid="step5-dropzone" aria-disabled={uploadOff} onDragOver={e => { e.preventDefault(); if (!uploadOff) setDragOver(true); }} onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); if (uploadOff) return; if (e.dataTransfer.files[0]) setStep5File(e.dataTransfer.files[0]); }}
                onClick={() => { if (uploadOff) return; step5FileRef.current?.click(); }}
                style={{ border: `2px dashed ${dragOver ? '#1A73E8' : '#D0D5DD'}`, borderRadius: 8,
                    padding: '32px 24px', textAlign: 'center', cursor: uploadOff ? 'not-allowed' : 'pointer', opacity: uploadOff ? 0.55 : 1,
                    background: dragOver ? '#F0F6FF' : '#FAFAFA', transition: 'all 0.15s' }}>
                <input ref={step5FileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }} disabled={uploadOff}
                    onChange={e => { if (uploadOff) return; if (e.target.files[0]) setStep5File(e.target.files[0]); }} />
                {uploadOff ? (
                    <div data-testid="step5-upload-required" style={{ fontSize: 14, color: '#B5543A', fontFamily: FONT, fontWeight: 600 }}>{UPLOAD_REQUIRED_MESSAGE}</div>
                ) : step5File ? (
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1A1C2E', fontFamily: FONT }}>{step5File.name}</div>
                        <div style={{ fontSize: 12, color: '#888', fontFamily: FONT, marginTop: 4 }}>Click to change</div>
                    </div>
                ) : (
                    <div>
                        <div style={{ fontSize: 14, color: '#666', fontFamily: FONT }}>Upload your employee roster (.xlsx)</div>
                        <div style={{ fontSize: 12, color: '#999', fontFamily: FONT, marginTop: 4 }}>Drop here or click to browse</div>
                    </div>
                )}
            </div>
        );
    };

    const renderStep5ReEntry = () => {
        const statLabel = { fontSize: 11, color: '#6B7094', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, marginBottom: 4 };
        const statValue = { fontSize: 22, fontFamily: MONO, fontWeight: 600, color: '#1A1C2E' };
        return (
            <div>
                {renderStep5SubIndicator()}
                <div style={{ padding: 24, background: '#F0F6FF', borderRadius: 8, marginBottom: 20 }}>
                    <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 18, margin: '0 0 16px' }}>Employees already imported</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 16 }}>
                        <div>
                            <div style={statLabel}>Total employees</div>
                            <div style={statValue}>{step5ExistingCount}</div>
                        </div>
                        <div>
                            <div style={statLabel}>Sales Reps</div>
                            <div style={statValue}>{step5ExistingSalesReps}</div>
                        </div>
                        <div>
                            <div style={statLabel}>Departments</div>
                            <div style={statValue}>{step5ExistingDepts.length}</div>
                        </div>
                    </div>
                    <div style={{ borderTop: '1px solid rgba(26,115,232,0.15)', paddingTop: 14, display: 'flex', gap: 16, fontSize: 13, fontFamily: FONT, flexWrap: 'wrap' }}>
                        <button onClick={() => handleStep5Edit('5c')} disabled={step5Loading}
                            style={{ ...btnText, padding: 0, opacity: step5Loading ? 0.5 : 1 }}>
                            Edit Department Mappings
                        </button>
                        <span style={{ color: '#C8D0E0' }}>|</span>
                        <button onClick={() => handleStep5Edit('5b')} disabled={step5Loading}
                            style={{ ...btnText, padding: 0, opacity: step5Loading ? 0.5 : 1 }}>
                            Edit Benefit Components
                        </button>
                        <span style={{ color: '#C8D0E0' }}>|</span>
                        <button onClick={() => handleStep5Edit('5d')} disabled={step5Loading}
                            style={{ ...btnText, padding: 0, opacity: step5Loading ? 0.5 : 1 }}>
                            Edit Employee Roster
                        </button>
                    </div>
                </div>
                {/* Commission ship 5: how the books treat commissions -- asked here, where the roster is stored (C1 / Q3 a). */}
                <CommissionAccrualPanel key={selectedCompanyId} companyId={selectedCompanyId} onStored={setStep5CommState} />
                {step5ExistingDepts.length > 0 && (
                    <div style={{ marginBottom: 20, fontSize: 13, fontFamily: FONT, color: '#555' }}>
                        <div style={{ marginBottom: 6, fontWeight: 500 }}>Department breakdown:</div>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {step5ExistingDepts.map(d => (
                                <li key={d.name} style={{ marginBottom: 2 }}>
                                    {d.name} <span style={{ color: '#888' }}>({d.count} employee{d.count !== 1 ? 's' : ''})</span>
                                    {d.is_category && <span style={{ marginLeft: 6, fontSize: 11, color: '#1A73E8', background: '#E8F1FE', padding: '1px 6px', borderRadius: 3 }}>{d.is_category}</span>}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                {step5ImportError && <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginBottom: 12 }}>{step5ImportError}</div>}
                <div style={{ display: 'flex', gap: 12 }}>
                    <button onClick={() => handleBack(5)} style={btnText}>&larr; Back</button>
                    <button onClick={handleStep5ReImport} style={btnSecondary}>Re-import Employees</button>
                    <button onClick={() => { if (step5CommBlock) return; setViewingStep(6); }} disabled={!!step5CommBlock} title={step5CommBlock || undefined}
                        style={{ ...btnPrimary, opacity: step5CommBlock ? 0.5 : 1 }}>Continue to Vendor Setup &rarr;</button>
                    {step5CommState !== undefined && step5CommBlock && <span data-testid="step5-commission-required" style={{ alignSelf: 'center', fontSize: 13, color: '#B5543A', fontFamily: FONT, fontWeight: 600 }}>{step5CommBlock}</span>}
                </div>
            </div>
        );
    };

    const renderStep5a = () => (
        <div>
            {renderStep5SubIndicator()}
            {/* Bi-weekly step 4 (Brian Q14): asked HERE, above the roster upload -- the upload below stays disabled until it is saved. */}
            <PaySchedulePanel key={selectedCompanyId} companyId={selectedCompanyId} closeMonth={obState?.close_month || null} onStored={setStep5PayStored} />
            <h4 style={{ fontFamily: FONT, color: '#1A1C2E', margin: '0 0 16px' }}>Upload Employee Roster</h4>
            <Step5DropZone />
            <div style={{ marginTop: 20 }}>
                <label style={{ fontSize: 13, fontFamily: FONT, color: '#1A1C2E', fontWeight: 500, display: 'block', marginBottom: 6 }}>
                    Default work state for employees without a state specified
                </label>
                <select value={step5DefaultState} onChange={e => setStep5DefaultState(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #D0D5DD', fontSize: 14, fontFamily: MONO, minWidth: 120 }}>
                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, fontFamily: FONT, fontSize: 13, color: '#555' }}>
                <span>Need a blank?</span>
                <button onClick={handleDownloadEmployeeBlank} style={btnText}>Download employee template</button>
            </div>
            {step5ParseError && <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginTop: 16 }}>{step5ParseError}</div>}
            <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                <button onClick={() => handleBack(5)} style={btnText}>&larr; Back</button>
                <button onClick={handleStep5Parse} disabled={step5Parsing || !step5File || !step5PayFrequencyStored} style={{ ...btnPrimary, opacity: (!step5File || step5Parsing || !step5PayFrequencyStored) ? 0.5 : 1 }}>
                    {step5Parsing ? 'Parsing...' : 'Parse File \u2192'}
                </button>
            </div>
        </div>
    );

    const addManualBenefitRow = () => {
        setStep5BenefitCols(prev => [...prev, {
            headerName: '', normalizedKey: '', sampleValues: [],
            include: true, benefitName: '',
            isManual: true, defaultAmount: 0,
        }]);
    };
    const removeBenefitRow = (idx) => {
        setStep5BenefitCols(prev => prev.filter((_, j) => j !== idx));
    };
    const updateBenefitName = (idx, newName) => {
        setStep5BenefitCols(prev => prev.map((x, j) => {
            if (j !== idx) return x;
            // For manual rows, keep normalizedKey synced to the name.
            const patch = { ...x, benefitName: newName };
            if (x.isManual) patch.normalizedKey = normalizeBenefitKey(newName);
            return patch;
        }));
    };

    const renderStep5EditBenefits = () => {
        const hasRows = step5BenefitRenames.length > 0;
        const changedCount = step5BenefitRenames.filter(r => r.currentName && r.currentName !== r.originalKey).length;
        const anyEmptyName = step5BenefitRenames.some(r => !r.currentName || !r.currentName.trim());
        return (
            <div>
                {renderStep5SubIndicator()}
                <h4 style={{ fontFamily: FONT, color: '#1A1C2E', margin: '0 0 12px' }}>
                    Edit Benefit Components
                </h4>
                <div style={{ fontSize: 13, color: '#555', fontFamily: FONT, marginBottom: 16 }}>
                    Rename benefit components. Changes apply to every employee that has the component.
                </div>
                {!hasRows && (
                    <div style={{ padding: 16, background: '#FAFAFA', borderRadius: 6, fontSize: 13, fontFamily: FONT, color: '#888', marginBottom: 20 }}>
                        No benefit components found on the imported employees.
                    </div>
                )}
                {hasRows && (
                    <div style={{ border: '1px solid #E0E0E0', borderRadius: 6, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT, fontSize: 13 }}>
                            <thead>
                                <tr style={{ background: '#FAFAFA', borderBottom: '1px solid #E0E0E0' }}>
                                    <th style={thStyle}>Current Name</th>
                                    <th style={thStyle}>Rename To</th>
                                </tr>
                            </thead>
                            <tbody>
                                {step5BenefitRenames.map((r, i) => {
                                    const isChanged = r.currentName !== r.originalKey;
                                    return (
                                        <tr key={r.originalKey} style={{ borderBottom: '1px solid #F0F0F0' }}>
                                            <td style={{ ...tdStyle, fontFamily: MONO, color: '#555' }}>{r.originalKey}</td>
                                            <td style={tdStyle}>
                                                <input type="text" value={r.currentName}
                                                    onChange={e => setStep5BenefitRenames(prev => prev.map((x, j) => j === i ? { ...x, currentName: e.target.value } : x))}
                                                    style={{ padding: '4px 8px', borderRadius: 4,
                                                        border: `1px solid ${isChanged ? '#1A73E8' : '#D0D5DD'}`,
                                                        fontSize: 13, fontFamily: FONT, width: '100%', boxSizing: 'border-box',
                                                        background: isChanged ? '#F0F7FF' : '#fff' }} />
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                {step5ImportError && <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginTop: 12 }}>{step5ImportError}</div>}
                <div style={{ display: 'flex', gap: 12, marginTop: 24, alignItems: 'center' }}>
                    <button onClick={handleStep5BackToSummary} style={btnText}>&larr; Back to Summary</button>
                    <button onClick={handleStep5SaveEditBenefits}
                        disabled={step5Importing || anyEmptyName || changedCount === 0}
                        style={{ ...btnPrimary, opacity: (step5Importing || anyEmptyName || changedCount === 0) ? 0.5 : 1 }}>
                        {step5Importing ? 'Saving...' : 'Save Changes'}
                    </button>
                    {changedCount > 0 && (
                        <span style={{ fontSize: 12, color: '#1A73E8', fontFamily: FONT }}>
                            {changedCount} rename{changedCount !== 1 ? 's' : ''} pending
                        </span>
                    )}
                </div>
            </div>
        );
    };

    const renderStep5b = () => {
        const detectedCount = step5BenefitCols.filter(c => !c.isManual).length;
        const hasAnyRows = step5BenefitCols.length > 0;
        return (
            <div>
                {renderStep5SubIndicator()}
                <h4 style={{ fontFamily: FONT, color: '#1A1C2E', margin: '0 0 12px' }}>
                    {detectedCount > 0
                        ? `We detected ${detectedCount} benefit column${detectedCount !== 1 ? 's' : ''} in your file`
                        : 'No benefit columns detected in your file'}
                </h4>
                <div style={{ fontSize: 13, color: '#555', fontFamily: FONT, marginBottom: 16 }}>
                    Confirm which columns are benefit components and edit the benefit name as needed. You can also add components that aren{"\u2019"}t in the file.
                </div>
                {/* ★ THE PARSER'S NOTICES — mounted on 5b because 5b is the FIRST screen
                    after the parse (`handleStep5Parse` advances to 5b unconditionally), and
                    because the no-Salary-column notice's own instruction is "re-download the
                    template and re-upload". Surfaced at 5d instead, the operator would confirm
                    benefits, map departments and review 62 rows BEFORE learning the upload was
                    broken — which defeats the notice. Placement matches Steps 2b and 6b:
                    directly under the counting heading, above the data.
                    ⛔ `?.` — renderStep5b has no `step5Parsed` null-guard of its own (unlike
                    5d), and the Step-5 re-entry path stubs the object with `flags: []`, which
                    correctly renders nothing. */}
                <EmployeeFlagNotices flags={step5Parsed?.flags} />
                {hasAnyRows && (
                    <div style={{ border: '1px solid #E0E0E0', borderRadius: 6, overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT, fontSize: 13 }}>
                            <thead>
                                <tr style={{ background: '#FAFAFA', borderBottom: '1px solid #E0E0E0' }}>
                                    <th style={{ ...thStyle, width: 60, textAlign: 'center' }}>Include</th>
                                    <th style={thStyle}>Column in File</th>
                                    <th style={thStyle}>Sample / Default Amount</th>
                                    <th style={thStyle}>Benefit Name</th>
                                    <th style={{ ...thStyle, width: 50 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {step5BenefitCols.map((c, i) => {
                                    const disabledStyle = { background: c.include ? '#fff' : '#F5F6FA' };
                                    return (
                                        <tr key={i} style={{ borderBottom: '1px solid #F0F0F0', background: c.include ? '#fff' : '#F9FAFB' }}>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                <input type="checkbox" checked={c.include}
                                                    onChange={e => setStep5BenefitCols(prev => prev.map((x, j) => j === i ? { ...x, include: e.target.checked } : x))} />
                                            </td>
                                            <td style={{ ...tdStyle, fontWeight: c.isManual ? 400 : 500, color: c.isManual ? '#888' : '#1A1C2E', fontStyle: c.isManual ? 'italic' : 'normal' }}>
                                                {c.isManual ? 'Manually added' : c.headerName}
                                            </td>
                                            <td style={tdStyle}>
                                                {c.isManual ? (
                                                    <NumInput value={c.defaultAmount} decimals={2} disabled={!c.include} placeholder="0.00"
                                                        onChange={v => setStep5BenefitCols(prev => prev.map((x, j) => j === i ? { ...x, defaultAmount: v } : x))}
                                                        style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #D0D5DD',
                                                            fontSize: 13, fontFamily: MONO, width: 120, boxSizing: 'border-box', textAlign: 'right', ...disabledStyle }} />
                                                ) : (
                                                    <span style={{ fontFamily: MONO, fontSize: 12, color: '#666' }}>
                                                        {c.sampleValues.map(v => fmt(v)).join(', ') || '\u2014'}
                                                    </span>
                                                )}
                                            </td>
                                            <td style={tdStyle}>
                                                <input type="text" value={c.benefitName} disabled={!c.include}
                                                    placeholder={c.isManual ? 'e.g. Gym Reimbursement' : ''}
                                                    onChange={e => updateBenefitName(i, e.target.value)}
                                                    style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #D0D5DD',
                                                        fontSize: 13, fontFamily: FONT, width: '100%', boxSizing: 'border-box', ...disabledStyle }} />
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                {c.isManual && (
                                                    <button onClick={() => removeBenefitRow(i)}
                                                        title="Remove"
                                                        style={{ border: 'none', background: 'transparent', color: '#E53935',
                                                            fontSize: 18, cursor: 'pointer', padding: '0 4px', lineHeight: 1 }}>
                                                        {'\u00D7'}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                <div style={{ marginTop: 12 }}>
                    <button onClick={addManualBenefitRow} style={{ ...btnSmall, color: '#1A73E8', borderColor: '#1A73E8' }}>
                        + Add Benefit Component
                    </button>
                </div>
                <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                    <button onClick={() => setStep5SubStep('5a')} style={btnText}>&larr; Back</button>
                    {hasAnyRows && (
                        <button onClick={() => setStep5BenefitCols(prev => prev.map(c => ({ ...c, include: false })))} style={btnSecondary}>{'Skip \u2014 No Benefits'}</button>
                    )}
                    {/* W-068: a roster with blocking upload problems goes no further until it is fixed and re-uploaded. */}
                    <button onClick={() => setStep5SubStep('5c')} disabled={countBlocking(step5Parsed?.flags) > 0} data-spec-continue="5b"
                        style={{ ...btnPrimary, opacity: countBlocking(step5Parsed?.flags) > 0 ? 0.5 : 1 }}>{'Confirm Benefits \u2192'}</button>
                </div>
            </div>
        );
    };

    /* ── Department grouping model (Step 5c) ──────────────────────────────────────
     * A GROUP is a set of departments sharing one display_group. Every department starts
     * as its OWN group; merging is an explicit operator action expressed in DEPARTMENT
     * names ("group Marketing with Sales"). The operator NEVER types a slug — the old
     * free-text box made merging depend on typing the same string twice, where a typo
     * ('sales_mtkg') silently un-merged the group AND, post-history, orphaned its actuals.
     *
     * ANCHOR = the group's lowest-sort_order member (id/name tiebreak). It owns the group's
     * slug and is where the group-name field renders. A row whose anchor is itself shows
     * "(its own group)".
     */
    const dgMembers = (dg) => step5DeptMap.filter(d => (d.display_group || deptSlug(d.department_name)) === dg);
    const dgOf = (d) => d.display_group || deptSlug(d.department_name);
    const dgAnchor = (dg) => {
        const ms = dgMembers(dg);
        if (!ms.length) return null;
        return [...ms].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
            || a.department_name.localeCompare(b.department_name))[0];
    };
    // ★ D2: a group whose slug is already baked into is_actuals keys cannot change members.
    const dgFrozen = (dg) => step5DgsWithHistory.has(dg);

    /* "Group with…" — X joins Y's group (or leaves, becoming its own).
     * Writes only display_group. sort_order and the label are untouched. */
    const setDeptGroup = (idx, targetDeptName) => {
        setStep5DeptMap(prev => {
            const next = [...prev];
            const me = next[idx];
            if (!targetDeptName) {
                // Leave the group → become my own. Only reachable when NOT frozen.
                next[idx] = { ...me, display_group: deptSlug(me.department_name), display_group_label: '' };
                return next;
            }
            const target = next.find(d => d.department_name === targetDeptName);
            if (!target) return prev;
            const groupDg = dgOf(target);
            // Inherit the target group's label so the merged block renders one header.
            const label = next.find(d => dgOf(d) === groupDg && d.display_group_label)?.display_group_label || '';
            next[idx] = { ...me, display_group: groupDg, display_group_label: label };
            return next;
        });
        setStep5ImportError('');
    };

    /* Group name → written to EVERY member, so the rows agree and any of them can answer
     * "what is this group called" — and so every member derives the SAME slug without an
     * anchor lookup (slugForDeptRow). ★ NOT cosmetic any more: for a group that has never
     * been saved this name DERIVES the slug, and therefore the payroll IS-line keys. For a
     * saved group the slug is frozen and this only changes the caption. */
    const setDeptGroupLabel = (dg, label) => {
        setStep5DeptMap(prev => prev.map(d => (dgOf(d) === dg ? { ...d, display_group_label: label } : d)));
    };

    /* Reorder WITHIN an is_category — group order is min(sort_order) among a group's
     * members, so moving a department can move its group. Display-only: the slug and every
     * leaf's group membership are untouched, so no subtotal's addend set changes. */
    const moveDept = (idx, dir) => {
        setStep5DeptMap(prev => {
            const me = prev[idx];
            const sibs = prev.map((d, i) => ({ d, i }))
                .filter(({ d }) => d.is_category === me.is_category)
                .sort((a, b) => (a.d.sort_order ?? 0) - (b.d.sort_order ?? 0)
                    || a.d.department_name.localeCompare(b.d.department_name));
            const pos = sibs.findIndex(s => s.i === idx);
            const swap = sibs[pos + dir];
            if (pos < 0 || !swap) return prev;            // already at the block's edge
            const next = [...prev];
            const a = prev[idx], b = prev[swap.i];
            next[idx]    = { ...a, sort_order: b.sort_order ?? 0 };
            next[swap.i] = { ...b, sort_order: a.sort_order ?? 0 };
            // Equal sort_orders (e.g. everything still at the 0 default) would make the swap
            // a no-op. Re-band the whole category so the move is real and deterministic.
            const cat = next.map((d, i) => ({ d, i })).filter(({ d }) => d.is_category === me.is_category);
            const ordered = [...cat].sort((x, y) => (x.d.sort_order ?? 0) - (y.d.sort_order ?? 0)
                || (x.i === idx ? -1 : y.i === idx ? 1 : 0)
                || x.d.department_name.localeCompare(y.d.department_name));
            ordered.forEach(({ i }, n) => { next[i] = { ...next[i], sort_order: (n + 1) * 10 }; });
            return next;
        });
        setStep5ImportError('');
    };

    const renderStep5c = () => {
        /* ★ `.every()` ON AN EMPTY ARRAY IS TRUE — which is precisely how a zero-department
           roster enabled this button and walked past 5c writing nothing. The length test
           makes the empty case explicit rather than vacuously satisfied. The handler's own
           deptMappingsBlocker still refuses independently: a write path never relies on a
           render guard having run. */
        const allMapped = step5DeptMap.length > 0 && step5DeptMap.every(d => d.is_category);
        // ★ THE SAME map the save writes from (resolveDeptGroupIds), computed ONCE per render
        // so the id caption under each Group Name is exactly what saving would persist.
        // Never a second derivation — a preview that computed its own could show one id and
        // save another, which is the whole reason the caption exists.
        const groupIds = resolveDeptGroupIds(step5DeptMap);
        // W-022 (B): payroll rows the back-fill will refuse for these departments (see step5PayrollRefusedOf).
        const payrollRefused = step5PayrollRefusedOf(step5PayrollSrc, step5DeptMap, obState);
        const groupMismatches = step5PayrollGroupMismatchesOf(step5PayrollSrc, step5DeptMap, obState);   // Batch 1b (L3624)
        const rows = [...step5DeptMap]
            .map((d, i) => ({ d, i }))
            .sort((a, b) => {
                const ca = DEPT_IS_CATEGORIES.indexOf(a.d.is_category);
                const cb = DEPT_IS_CATEGORIES.indexOf(b.d.is_category);
                return (ca < 0 ? 99 : ca) - (cb < 0 ? 99 : cb)
                    || (a.d.sort_order ?? 0) - (b.d.sort_order ?? 0)
                    || a.d.department_name.localeCompare(b.d.department_name);
            });
        return (
            <div>
                {renderStep5SubIndicator()}
                <h4 style={{ fontFamily: FONT, color: '#1A1C2E', margin: '0 0 12px' }}>
                    Map your departments to Finledom IS categories
                </h4>
                <div style={{ fontSize: 13, color: '#555', fontFamily: FONT, marginBottom: 16 }}>
                    Each department routes employee wages, taxes, and benefits to a section of the Income Statement.
                    <br />
                    By default every department is its own block. To show two departments as
                    <strong> one block</strong> (e.g. Sales and Marketing), set <strong>Group with</strong> on one of
                    them to the other. Use <strong>&#9650;&#9660;</strong> to set the order the blocks appear in.
                    <br />
                    <strong>Group Name</strong> is what this payroll group is called on the Income Statement — and it
                    becomes the group's internal id, so it is set once and cannot be renamed after the first
                    save. Leave it blank to use the department's own name.
                </div>
                <div style={{ border: '1px solid #E0E0E0', borderRadius: 6, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT, fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: '#FAFAFA', borderBottom: '1px solid #E0E0E0' }}>
                                <th style={thStyle}>Department</th>
                                <th style={{ ...thStyle, textAlign: 'right', width: 90 }}>Employees</th>
                                <th style={{ ...thStyle, width: 140 }}>IS Category</th>
                                <th style={{ ...thStyle, width: 170 }}>Group with</th>
                                <th style={{ ...thStyle, width: 170 }}>Group Name</th>
                                <th style={{ ...thStyle, width: 60 }}>Order</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(({ d, i }) => {
                                const dg = dgOf(d);
                                const anchor = dgAnchor(dg);
                                const members = dgMembers(dg);
                                const isAnchor = anchor && anchor.department_name === d.department_name;
                                const merged = members.length > 1;
                                const frozen = dgFrozen(dg);
                                // Only departments in the SAME is_category may be grouped — a group
                                // lives inside one subsection; cross-category grouping would silently
                                // mis-route payroll.
                                const candidates = step5DeptMap.filter(x =>
                                    x.department_name !== d.department_name
                                    && x.is_category === d.is_category
                                    && d.is_category);
                                const groupLabel = members.find(m => m.display_group_label)?.display_group_label || '';
                                const frozenTip = `${groupLabel || displayGroupLabel(dg)} has historical actuals; `
                                    + `changing membership would re-key them and zero every closed month. `
                                    + `Renaming and reordering are still fine.`;
                                return (
                                    <tr key={d.department_name} style={{ borderBottom: '1px solid #F0F0F0',
                                        background: merged ? '#FAFBFE' : '#fff' }}>
                                        <td style={{ ...tdStyle, fontWeight: 500 }}>
                                            {d.department_name}
                                            {merged && !isAnchor && (
                                                <span style={{ marginLeft: 6, fontSize: 10, color: '#5B6B8C',
                                                    background: '#EBF1FF', padding: '1px 5px', borderRadius: 3 }}>grouped</span>
                                            )}
                                        </td>
                                        <td style={{ ...tdStyle, fontFamily: MONO, textAlign: 'right' }}>{d.employee_count}</td>
                                        <td style={tdStyle}>
                                            <select value={d.is_category || ''}
                                                onChange={e => setStep5DeptMap(prev => prev.map((x, j) => j === i ? { ...x, is_category: e.target.value } : x))}
                                                style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #D0D5DD', fontSize: 13, fontFamily: FONT, width: '100%' }}>
                                                <option value="">—</option>
                                                {DEPT_IS_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                                            </select>
                                        </td>
                                        {/* ★ D2: membership is FROZEN once the group's slug is baked into
                                            is_actuals keys. Reorder + rename stay enabled — always. */}
                                        <td style={tdStyle} title={frozen ? frozenTip : ''}>
                                            <select
                                                value={isAnchor ? '' : (anchor?.department_name || '')}
                                                disabled={frozen || !d.is_category}
                                                onChange={e => setDeptGroup(i, e.target.value)}
                                                style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #D0D5DD',
                                                    fontSize: 12, fontFamily: FONT, width: '100%',
                                                    background: (frozen || !d.is_category) ? '#F5F6FA' : '#fff',
                                                    cursor: frozen ? 'not-allowed' : 'pointer' }}>
                                                <option value="">(its own group)</option>
                                                {candidates.map(c => (
                                                    <option key={c.department_name} value={c.department_name}>{c.department_name}</option>
                                                ))}
                                            </select>
                                            {frozen && (
                                                <div style={{ fontSize: 10, color: '#9AA0AE', marginTop: 2 }}>locked &mdash; has actuals</div>
                                            )}
                                        </td>
                                        {/* Group Name renders ONCE per group, on its anchor. ★ NO LONGER
                                            COSMETIC: on a group's FIRST save it also derives the group's
                                            slug (slugForDeptRow), which composes the persisted payroll
                                            IS-line keys. Once saved the slug is frozen, so a later edit
                                            here changes the caption only. */}
                                        <td style={tdStyle}>
                                            {isAnchor ? (
                                                <>
                                                    <input type="text" value={groupLabel}
                                                        onChange={e => setDeptGroupLabel(dg, e.target.value)}
                                                        placeholder={displayGroupLabel(dg)} data-suggested-placeholder=""
                                                        style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #D0D5DD',
                                                            fontSize: 12, fontFamily: FONT, width: '100%', boxSizing: 'border-box' }} />
                                                    {/* ★ THE RESOLVED ID, LIVE — read from the same groupIds map the
                                                        save writes from, so what is shown is what will be stored. A
                                                        frozen group shows its frozen id and a relabel does not move
                                                        it, matching the save. */}
                                                    <div style={{ fontSize: 10, color: '#9AA0AE', fontFamily: MONO, marginTop: 3 }}
                                                        title={frozen
                                                            ? 'This id is already stored and cannot change \u2014 renaming only changes the caption.'
                                                            : 'The id this group will be saved under, derived from the name above.'}>
                                                        id: {groupIds.get(dg) || dgOf(d)}
                                                    </div>
                                                </>
                                            ) : (
                                                <span style={{ color: '#B0B4C0', fontSize: 12 }}>&mdash;</span>
                                            )}
                                        </td>
                                        <td style={{ ...tdStyle, whiteSpace: 'nowrap', textAlign: 'center' }}>
                                            <button onClick={() => moveDept(i, -1)} title="Move up" style={reorderBtn}>&#9650;</button>
                                            <button onClick={() => moveDept(i, 1)} title="Move down" style={reorderBtn}>&#9660;</button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                {/* W-022 (B): the payroll-group refusal, ON SCREEN -- a WARNING (the save still runs;
                    go-live blocks on the missing history via pre-flight P4). Step 4b's amber notice idiom. */}
                {payrollRefused.length > 0 && (
                    <div data-step5-payroll-refused="1" style={{ background: '#FFF8E1', border: '1px solid #F5A623', borderRadius: 8,
                        padding: '14px 18px', marginTop: 16, fontFamily: FONT, color: '#8A6D00', fontSize: 13 }}>
                        {'\u26A0'} {payrollRefused.length === 1 ? 'A payroll line' : payrollRefused.length + ' payroll lines'} in the workbook
                        {' '}cannot be assigned to a department group, so {payrollRefused.length === 1 ? 'its' : 'their'} history
                        {' '}({fmtMoney(payrollRefused.reduce((sum, f) => sum + (f.amount || 0), 0))}) will not be written:
                        <ul style={{ margin: '6px 0 6px 18px', padding: 0 }}>
                            {payrollRefused.map((f, k) => (
                                <li key={k}>{f.gl_name} ({f.gl_category || 'no category'}) {'\u2014'} {fmtMoney(f.amount)}</li>
                            ))}
                        </ul>
                        Fill the display_group column on these payroll rows in the workbook with the department group of
                        {' '}each row, then re-upload it at Step 2. You can still save these departments; the go-live check
                        {' '}names any payroll history that is missing.
                    </div>
                )}
                {/* Batch 1b (L3624): the safety net, shown before the save is pressed -- the save refuses with the same words */}
                {groupMismatches.length > 0 && (
                    <div data-step5-group-mismatch="1" style={{ background: '#FDECEA', border: '1px solid #F5C6C2', borderRadius: 8, padding: '12px 16px', marginTop: 16, fontFamily: FONT, color: '#B42318', fontSize: 13 }}>
                        {'\u26D4 ' + step5GroupMismatchMessage(groupMismatches).replace(/^Not saved: the /, 'The ')}
                    </div>
                )}
                {step5ImportError && <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginTop: 12 }}>{step5ImportError}</div>}
                <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                    {step5EditMode ? (
                        <button onClick={handleStep5BackToSummary} style={btnText}>&larr; Back to Summary</button>
                    ) : (
                        <button onClick={() => setStep5SubStep('5b')} style={btnText}>&larr; Back</button>
                    )}
                    <button onClick={step5EditMode ? handleStep5SaveEditDepts : handleStep5SaveDepts}
                        disabled={!allMapped || step5Importing}
                        style={{ ...btnPrimary, opacity: (!allMapped || step5Importing) ? 0.5 : 1 }}>
                        {step5Importing ? 'Saving...' : (step5EditMode ? 'Save Changes' : 'Save & Continue \u2192')}
                    </button>
                </div>
            </div>
        );
    };

    const updateEmployeeField = (absoluteIdx, field, value) => {
        setStep5Employees(prev => prev.map((e, j) => j === absoluteIdx ? { ...e, [field]: value } : e));
    };
    const updateEmployeeBenefit = (absoluteIdx, key, value) => {
        setStep5Employees(prev => prev.map((e, j) => {
            if (j !== absoluteIdx) return e;
            return { ...e, benefitAmounts: { ...(e.benefitAmounts || {}), [key]: value } };
        }));
    };
    const toggleStep5Expanded = (absoluteIdx) => {
        setStep5Expanded(prev => { const n = new Set(prev); if (n.has(absoluteIdx)) n.delete(absoluteIdx); else n.add(absoluteIdx); return n; });
    };

    const renderStep5d = () => {
        if (!step5Parsed) return null;
        const emps = step5Employees.length > 0 ? step5Employees : step5Parsed.employees;
        const pageSize = 20;
        const totalPages = Math.max(1, Math.ceil(emps.length / pageSize));
        const page = Math.min(step5Page, totalPages - 1);
        const pageStart = page * pageSize;
        const pageEmps = emps.slice(pageStart, pageStart + pageSize);
        const deptToCat = {};
        for (const d of step5DeptMap) deptToCat[d.department_name] = d.is_category;
        const validBenefits = step5BenefitCols.filter(c => c.include && c.normalizedKey);
        const includedBenefits = new Set(validBenefits.map(c => c.normalizedKey));
        const benefitNameByKey = {};
        for (const c of validBenefits) benefitNameByKey[c.normalizedKey] = c.benefitName || c.normalizedKey;
        const benefitsTotal = (e) => {
            let t = 0;
            for (const [k, v] of Object.entries(e.benefitAmounts || {})) {
                if (!includedBenefits.has(k)) continue;
                t += Number(v) || 0;
            }
            return t;
        };
        const COLSPAN = 10;
        const cellInput = {
            padding: '4px 6px', borderRadius: 4, border: '1px solid #D0D5DD',
            fontSize: 13, fontFamily: MONO, boxSizing: 'border-box',
        };
        const cellSelect = {
            padding: '4px 6px', borderRadius: 4, border: '1px solid #D0D5DD',
            fontSize: 13, fontFamily: MONO, width: 64, boxSizing: 'border-box', background: '#fff',
        };
        const fmtD2 = (n) => `$${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        return (
            <div>
                <style>{`
                    .s5-benefit-btn { background: #F5F7FA; border: 1px solid #D0D5DD; transition: background 0.12s, border-color 0.12s; }
                    .s5-benefit-btn:hover { background: #E8F1FE; border-color: #1A73E8; }
                    .s5-benefit-btn.active { background: #E8F1FE; border-color: #1A73E8; }
                `}</style>
                {renderStep5SubIndicator()}
                <h4 style={{ fontFamily: FONT, color: '#1A1C2E', margin: '0 0 12px' }}>
                    Review {emps.length} employee{emps.length !== 1 ? 's' : ''} before importing
                </h4>
                <div style={{ fontSize: 12, color: '#666', fontFamily: FONT, marginBottom: 12 }}>
                    Click the Benefits cell to expand per-component amounts. State, Salary, Bonus, and Sales Quota are editable.
                </div>
                <div style={{ border: '1px solid #E0E0E0', borderRadius: 6, overflow: 'hidden', marginBottom: 12 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT, fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: '#FAFAFA', borderBottom: '1px solid #E0E0E0' }}>
                                <th style={thStyle}>Name</th>
                                <th style={thStyle}>Title</th>
                                <th style={thStyle}>Department</th>
                                <th style={{ ...thStyle, textAlign: 'right', width: 120 }}>Salary</th>
                                <th style={thStyle}>Start Date</th>
                                <th style={{ ...thStyle, width: 80 }}>State</th>
                                <th style={{ ...thStyle, textAlign: 'right', width: 140 }}>Benefits/mo</th>
                                <th style={{ ...thStyle, textAlign: 'right', width: 200 }}>Bonus</th>
                                <th style={{ ...thStyle, textAlign: 'right', width: 120 }}>Sales Quota</th>
                                <th style={{ ...thStyle, width: 50 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {pageEmps.map((e, i) => {
                                const absIdx = pageStart + i;
                                const isCat = deptToCat[e.department] || e.is_category || 'G&A';
                                const stateVal = e.state || step5DefaultState;
                                const bTotal = benefitsTotal(e);
                                const quotaVal = Number(e.annual_quota) || 0;
                                const isRep = quotaVal > 0;
                                const expanded = step5Expanded.has(absIdx);
                                return (
                                    <React.Fragment key={absIdx}>
                                        <tr style={{ borderBottom: expanded ? 'none' : '1px solid #F0F0F0' }}>
                                            <td style={{ ...tdStyle, fontWeight: 500 }}>{e.name}</td>
                                            <td style={{ ...tdStyle, color: '#555' }}>{e.title || '\u2014'}</td>
                                            <td style={tdStyle}>
                                                {e.department || '\u2014'}
                                                {isCat && <span style={{ marginLeft: 6, fontSize: 10, color: '#1A73E8', background: '#E8F1FE', padding: '1px 5px', borderRadius: 3 }}>{isCat}</span>}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                                                <NumInput value={e.annual_salary} decimals={0}
                                                    onChange={v => updateEmployeeField(absIdx, 'annual_salary', v)}
                                                    style={{ ...cellInput, width: 110, textAlign: 'right' }} />
                                            </td>
                                            <td style={{ ...tdStyle, fontFamily: MONO, fontSize: 12 }}>{e.start_date || '\u2014'}</td>
                                            <td style={tdStyle}>
                                                <select value={stateVal}
                                                    onChange={ev => updateEmployeeField(absIdx, 'state', ev.target.value)}
                                                    style={cellSelect}>
                                                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                                                <button onClick={() => toggleStep5Expanded(absIdx)}
                                                    className={`s5-benefit-btn${expanded ? ' active' : ''}`}
                                                    style={{ color: '#1A1C2E', fontFamily: MONO, fontSize: 13,
                                                        cursor: 'pointer', padding: '4px 10px', borderRadius: 4,
                                                        display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 110, justifyContent: 'flex-end' }}>
                                                    {bTotal > 0 ? fmtD2(bTotal) : '\u2014'}
                                                    <span style={{ color: '#1A73E8', fontSize: 14, fontWeight: 700, display: 'inline-block',
                                                        transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.12s' }}>{'\u203A'}</span>
                                                </button>
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                                                    <NumInput value={e.bonus_amount} decimals={0}
                                                        onChange={v => updateEmployeeField(absIdx, 'bonus_amount', Number(v) || 0)}
                                                        style={{ ...cellInput, width: 100, textAlign: 'right' }} />
                                                    <select value={e.bonus_frequency || ''}
                                                        onChange={ev => updateEmployeeField(absIdx, 'bonus_frequency', ev.target.value || null)}
                                                        style={{ ...cellSelect, width: 120 }}>
                                                        <option value="">&mdash;</option>
                                                        {BONUS_FREQ_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                    </select>
                                                </div>
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                                                <NumInput value={quotaVal} decimals={0}
                                                    onChange={v => updateEmployeeField(absIdx, 'annual_quota', v)}
                                                    style={{ ...cellInput, width: 110, textAlign: 'right' }} />
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                {isRep && <span style={{ fontSize: 10, color: '#fff', background: '#27AE60', padding: '2px 6px', borderRadius: 3, fontWeight: 600 }}>REP</span>}
                                            </td>
                                        </tr>
                                        {expanded && (
                                            <tr style={{ borderBottom: '1px solid #F0F0F0', background: '#F7FAFF' }}>
                                                <td colSpan={COLSPAN} style={{ padding: '10px 16px' }}>
                                                    {validBenefits.length === 0 ? (
                                                        <div style={{ fontSize: 12, color: '#888', fontFamily: FONT }}>No benefit components confirmed.</div>
                                                    ) : (
                                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                                                            {validBenefits.map(c => {
                                                                const val = Number((e.benefitAmounts || {})[c.normalizedKey]) || 0;
                                                                return (
                                                                    <div key={c.normalizedKey} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                        <span style={{ fontSize: 12, fontFamily: FONT, color: '#444', flex: 1 }}>
                                                                            {benefitNameByKey[c.normalizedKey] || c.normalizedKey}:
                                                                        </span>
                                                                        <NumInput value={val} decimals={2}
                                                                            onChange={v => updateEmployeeBenefit(absIdx, c.normalizedKey, v)}
                                                                            style={{ ...cellInput, width: 110, textAlign: 'right' }} />
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, fontSize: 13, fontFamily: FONT, color: '#666' }}>
                    <span>Showing {pageStart + 1}{'\u2013'}{Math.min(pageStart + pageSize, emps.length)} of {emps.length}</span>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button onClick={() => setStep5Page(p => Math.max(0, p - 1))} disabled={page === 0}
                            style={{ ...btnSmall, opacity: page === 0 ? 0.4 : 1 }}>&larr; Prev</button>
                        <span style={{ padding: '6px 8px' }}>Page {page + 1} of {totalPages}</span>
                        <button onClick={() => setStep5Page(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                            style={{ ...btnSmall, opacity: page >= totalPages - 1 ? 0.4 : 1 }}>Next &rarr;</button>
                    </div>
                </div>
                {step5ImportError && <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginBottom: 12 }}>{step5ImportError}</div>}
                <div style={{ display: 'flex', gap: 12 }}>
                    <button onClick={() => setStep5SubStep('5c')} style={btnText}>&larr; Back</button>
                    <button onClick={() => setStep5SubStep('5e')} style={btnPrimary}>
                        Continue to SUTA Rates &rarr;
                    </button>
                </div>
            </div>
        );
    };

    const renderStep5EditRoster = () => {
        const emps = step5Employees;
        const pageSize = 20;
        const totalPages = Math.max(1, Math.ceil(emps.length / pageSize));
        const page = Math.min(step5Page, totalPages - 1);
        const pageStart = page * pageSize;
        const pageEmps = emps.slice(pageStart, pageStart + pageSize);
        const deptToCat = {};
        for (const d of step5DeptMap) deptToCat[d.department_name] = d.is_category;
        const cellInput = { padding: '4px 8px', borderRadius: 4, border: '1px solid #D0D5DD',
            fontSize: 13, fontFamily: MONO, boxSizing: 'border-box' };
        const cellText = { ...cellInput, fontFamily: FONT };
        const cellSelect = { padding: '4px 6px', borderRadius: 4, border: '1px solid #D0D5DD',
            fontSize: 13, fontFamily: MONO, width: 64, boxSizing: 'border-box', background: '#fff' };

        // Same split-table sticky header pattern Step 6 uses.
        const COLS = (
            <colgroup>
                <col style={{ width: 'auto' }} />
                <col style={{ width: 80 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 140 }} />
                <col style={{ width: 200 }} />
                <col style={{ width: 120 }} />
                <col style={{ width: 50 }} />
            </colgroup>
        );
        return (
            <div>
                {renderStep5SubIndicator()}
                <h4 style={{ fontFamily: FONT, color: '#1A1C2E', margin: '0 0 12px' }}>
                    Edit Employee Roster &mdash; {emps.length} employee{emps.length !== 1 ? 's' : ''}
                </h4>
                <div style={{ fontSize: 13, color: '#555', fontFamily: FONT, marginBottom: 16 }}>
                    Inline edit common corrections. Name, State, Salary, Sales Quota, and Bonus update on save.
                </div>
                <div style={{ border: '1px solid #E0E0E0', borderRadius: 6, overflow: 'hidden', marginBottom: 12 }}>
                    <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontFamily: FONT, fontSize: 13 }}>
                        {COLS}
                        <thead>
                            <tr style={{ background: '#FAFAFA', borderBottom: '1px solid #E0E0E0' }}>
                                <th style={thStyle}>Name</th>
                                <th style={thStyle}>State</th>
                                <th style={{ ...thStyle, textAlign: 'right' }}>Annual Salary</th>
                                <th style={{ ...thStyle, textAlign: 'right' }}>Sales Quota</th>
                                <th style={{ ...thStyle, textAlign: 'right' }}>Bonus</th>
                                <th style={thStyle}>IS Category</th>
                                <th style={thStyle}></th>
                            </tr>
                        </thead>
                    </table>
                    <div style={{ maxHeight: 560, overflowY: 'auto' }}>
                        <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', fontFamily: FONT, fontSize: 13 }}>
                            {COLS}
                            <tbody>
                                {pageEmps.map((e, i) => {
                                    const absIdx = pageStart + i;
                                    const isCat = deptToCat[e.department] || e.is_category || '';
                                    const quotaVal = Number(e.annual_quota) || 0;
                                    const isRep = quotaVal > 0;
                                    return (
                                        <tr key={e._dbId || absIdx} style={{ borderBottom: '1px solid #F0F0F0' }}>
                                            <td style={tdStyle}>
                                                <input type="text" value={e.name || ''}
                                                    onChange={ev => updateEmployeeField(absIdx, 'name', ev.target.value)}
                                                    style={{ ...cellText, width: '100%' }} />
                                            </td>
                                            <td style={tdStyle}>
                                                <select value={e.state || step5DefaultState}
                                                    onChange={ev => updateEmployeeField(absIdx, 'state', ev.target.value)}
                                                    style={cellSelect}>
                                                    {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                                                </select>
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                                                <NumInput value={e.annual_salary} decimals={0}
                                                    onChange={v => updateEmployeeField(absIdx, 'annual_salary', v)}
                                                    style={{ ...cellInput, width: 120, textAlign: 'right' }} />
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                                                <NumInput value={quotaVal} decimals={0}
                                                    onChange={v => updateEmployeeField(absIdx, 'annual_quota', v)}
                                                    style={{ ...cellInput, width: 120, textAlign: 'right' }} />
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, justifyContent: 'flex-end' }}>
                                                    <NumInput value={e.bonus_amount} decimals={0}
                                                        onChange={v => updateEmployeeField(absIdx, 'bonus_amount', v)}
                                                        style={{ ...cellInput, width: 100, textAlign: 'right' }} />
                                                    <select value={e.bonus_frequency || ''}
                                                        onChange={ev => updateEmployeeField(absIdx, 'bonus_frequency', ev.target.value || null)}
                                                        style={{ ...cellSelect, width: 120 }}>
                                                        <option value="">&mdash;</option>
                                                        {BONUS_FREQ_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                                    </select>
                                                </div>
                                            </td>
                                            <td style={tdStyle}>
                                                {isCat
                                                    ? <span style={{ fontSize: 11, color: '#1A73E8', background: '#E8F1FE', padding: '2px 8px', borderRadius: 3 }}>{isCat}</span>
                                                    : <span style={{ color: '#AAA' }}>{'\u2014'}</span>}
                                            </td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                                                {isRep && <span style={{ fontSize: 10, color: '#fff', background: '#27AE60', padding: '2px 6px', borderRadius: 3, fontWeight: 600 }}>REP</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, fontSize: 13, fontFamily: FONT, color: '#666' }}>
                    <span>Showing {emps.length === 0 ? 0 : pageStart + 1}{'\u2013'}{Math.min(pageStart + pageSize, emps.length)} of {emps.length}</span>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <button onClick={() => setStep5Page(p => Math.max(0, p - 1))} disabled={page === 0}
                            style={{ ...btnSmall, opacity: page === 0 ? 0.4 : 1 }}>&larr; Prev</button>
                        <span style={{ padding: '6px 8px' }}>Page {page + 1} of {totalPages}</span>
                        <button onClick={() => setStep5Page(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
                            style={{ ...btnSmall, opacity: page >= totalPages - 1 ? 0.4 : 1 }}>Next &rarr;</button>
                    </div>
                </div>
                {step5ImportError && <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginBottom: 12 }}>{step5ImportError}</div>}
                <div style={{ display: 'flex', gap: 12 }}>
                    <button onClick={handleStep5BackToSummary} style={btnText}>&larr; Back to Summary</button>
                    <button onClick={handleStep5SaveEditRoster} disabled={step5Importing}
                        style={{ ...btnPrimary, opacity: step5Importing ? 0.5 : 1 }}>
                        {step5Importing ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        );
    };

    // ── Step 5e: SUTA Rates walk (build 2b-2b) ───────────────
    const renderStep5e = () => {
        const states = distinctRosterStates();
        const sourceEmps = step5Employees.length > 0 ? step5Employees : (step5Parsed?.employees || []);
        const btnOutline = { fontSize: 12, fontWeight: 600, color: '#4A4E69', cursor: 'pointer', background: '#fff', border: '1px solid #D0D5DD', borderRadius: 6, padding: '5px 10px', fontFamily: FONT };
        const rateInput = { padding: '6px 8px', borderRadius: 6, border: '1px solid #D0D5DD', fontSize: 13, fontFamily: MONO, width: 90, boxSizing: 'border-box' };
        return (
            <div>
                {renderStep5SubIndicator()}
                <h4 style={{ fontFamily: FONT, color: '#1A1C2E', margin: '0 0 8px' }}>State Unemployment (SUTA) Rates</h4>
                <div style={{ fontSize: 12, color: '#666', fontFamily: FONT, marginBottom: 16, lineHeight: 1.5, maxWidth: 640 }}>
                    SUTA is experience-rated — these states will use their published new-employer default unless you enter your company's assigned rate. You can change any of these later in Tax Config.
                </div>
                {step5SutaLoading && <div style={{ padding: 20, color: '#999', fontFamily: FONT }}>Loading state rate tables…</div>}
                {step5SutaFetchError && <div style={{ background: 'rgba(212,133,31,0.08)', border: '1px solid rgba(212,133,31,0.3)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#8A5A12', fontFamily: FONT, marginBottom: 16, lineHeight: 1.5 }}>{step5SutaFetchError}</div>}
                {!step5SutaLoading && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 640 }}>
                        {states.length === 0 && <div style={{ fontSize: 13, color: '#999', fontFamily: FONT }}>No employee states to configure.</div>}
                        {states.map(st => {
                            const tax = step5StateTax[st];
                            const isNullRate = !tax || tax.globalRate == null;
                            const mode = step5SutaMode[st] || (isNullRate ? 'override' : 'default');
                            const rowErr = step5SutaRowErr[st];
                            const empN = sourceEmps.filter(e => (e.state || step5DefaultState) === st).length;
                            return (
                                <div key={st} style={{ border: `1px solid ${isNullRate ? 'rgba(212,133,31,0.35)' : '#E5E7EB'}`, borderRadius: 8, padding: '12px 14px', background: isNullRate ? 'rgba(212,133,31,0.05)' : '#fff' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                                        <div>
                                            <span style={{ fontSize: 14, fontWeight: 700, color: '#1A1C2E', fontFamily: MONO }}>{st}</span>
                                            <span style={{ fontSize: 11, color: '#999', fontFamily: FONT, marginLeft: 8 }}>{empN} employee{empN !== 1 ? 's' : ''}</span>
                                        </div>
                                        {!isNullRate
                                            ? <span style={{ fontSize: 12, color: '#666', fontFamily: FONT }}>new-employer default <strong style={{ fontFamily: MONO, color: '#1A1C2E' }}>{r2(tax.globalRate * 100)}%</strong></span>
                                            : <span style={{ fontSize: 11, fontWeight: 700, color: '#B5731A', fontFamily: FONT, textTransform: 'uppercase', letterSpacing: 0.3 }}>no standard rate</span>}
                                    </div>
                                    {!isNullRate && mode === 'default' && (
                                        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                            <span style={{ fontSize: 12, color: '#5A9E7C', fontFamily: FONT }}>✓ Will use the {r2(tax.globalRate * 100)}% default</span>
                                            <button style={btnOutline} onClick={() => { setStep5SutaMode(p => ({ ...p, [st]: 'override' })); setStep5SutaInput(p => ({ ...p, [st]: p[st] ?? String(r2(tax.globalRate * 100)) })); }}>Enter own rate</button>
                                        </div>
                                    )}
                                    {!isNullRate && mode === 'override' && (
                                        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                            <input value={step5SutaInput[st] ?? ''} onChange={e => setStep5SutaRate(st, e.target.value)} placeholder="e.g. 3.1" style={rateInput} />
                                            <span style={{ fontSize: 12, color: '#666' }}>%</span>
                                            <button style={btnOutline} onClick={() => { setStep5SutaMode(p => ({ ...p, [st]: 'default' })); setStep5SutaInput(p => ({ ...p, [st]: '' })); setStep5SutaRowErr(p => ({ ...p, [st]: '' })); }}>Use default</button>
                                        </div>
                                    )}
                                    {isNullRate && (
                                        <div style={{ marginTop: 8 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                <input value={step5SutaInput[st] ?? ''} onChange={e => setStep5SutaRate(st, e.target.value)} placeholder="your assigned rate" style={rateInput} />
                                                <span style={{ fontSize: 12, color: '#666' }}>%</span>
                                                {tax && tax.rateRangeLow != null && tax.rateRangeHigh != null && <span style={{ fontSize: 11, color: '#999', fontFamily: MONO }}>typical {r2(tax.rateRangeLow * 100)}%–{r2(tax.rateRangeHigh * 100)}%</span>}
                                            </div>
                                            <div style={{ fontSize: 11, color: '#B5731A', fontFamily: FONT, marginTop: 5, lineHeight: 1.4 }}>
                                                {st} has no standard new-employer rate — enter your assigned rate, or set it later in Tax Config. Left blank, {st} employees book $0 SUTA until you set it.
                                            </div>
                                            {/* Batch 1 (2026-11-03, L3625): the typed rate, read back -- the stored rate is this number / 100 */}
                                            {(() => { const n = parseFloat(step5SutaInput[st]); return (step5SutaInput[st] || '').trim() !== '' && !rowErr && n > 0 && n <= 15 ? (
                                                <div data-suta-echo={st} style={{ fontSize: 12, color: '#5A9E7C', fontFamily: FONT, marginTop: 5 }}>
                                                    {'\u2713 Using ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 }) + '%' + (tax && tax.globalCap != null ? ' on the first $' + Number(tax.globalCap).toLocaleString('en-US') + ' of each employee\u2019s wages' : '') + ' for ' + st + '.'}
                                                </div>) : null; })()}
                                        </div>
                                    )}
                                    {rowErr && <div style={{ fontSize: 11, color: '#E53935', fontFamily: FONT, marginTop: 4 }}>{rowErr}</div>}
                                </div>
                            );
                        })}
                    </div>
                )}
                {step5ImportError && <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginTop: 16 }}>{step5ImportError}</div>}
                <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                    <button onClick={() => setStep5SubStep('5d')} style={btnText}>&larr; Back</button>
                    <button onClick={handleStep5Import} disabled={step5Importing || !step5SutaLoaded} style={{ ...btnPrimary, opacity: (step5Importing || !step5SutaLoaded) ? 0.5 : 1 }}>
                        {!step5SutaLoaded ? 'Loading rates…' : (step5Importing ? 'Importing…' : `Import ${sourceEmps.length} Employee${sourceEmps.length !== 1 ? 's' : ''} →`)}
                    </button>
                </div>
            </div>
        );
    };

    const renderStep5 = () => {
        if (step5Loading || step5SubStep === null) return <div style={{ padding: 32, color: '#999', fontFamily: FONT }}>Loading...</div>;
        if (step5SubStep === 're-entry') return renderStep5ReEntry();
        if (step5SubStep === '5a') return renderStep5a();
        if (step5SubStep === '5b') return step5EditMode ? renderStep5EditBenefits() : renderStep5b();
        if (step5SubStep === '5c') return renderStep5c();
        if (step5SubStep === '5d') return step5EditMode ? renderStep5EditRoster() : renderStep5d();
        if (step5SubStep === '5e') return renderStep5e();
        return renderStep5a();
    };

    // ── Step 6: Vendor Setup ─────────────────────────────────
    /* ★ 4b (stale-screen fix, part 2 of 3): `obStateOverride`. This callback is memoized
     * on `obState` and reads it for the completion gate below, so a caller that has JUST
     * refreshed the wizard state cannot get the fresh value out of the closure — it is the
     * instance built by the CURRENT render. Passing the value explicitly is the only shape
     * that works in one tick. Omitted → identical to before. */
    const loadStep6Data = useCallback(async (companyId, obStateOverride) => {
        try {
            setStep6Loading(true);
            setStep6ImportError('');
            const [vRes, mRes, glmRes, dRes] = await Promise.all([
                getVendors(companyId).catch(() => ({ data: [] })),
                getVendorCategoryMappings(companyId).catch(() => ({ data: [] })),
                // ★ The tenant's REAL IS leaves. Step 6 used to default a vendor's is_line to
                // `${prefix}_vendors` from a hardcoded map — a leaf that exists on no tenant,
                // so the vendor silently booked $0. Now it picks from these, or blocks.
                getGLLineMappings(companyId).catch(() => ({ data: [] })),
                // ★ 4b: the per-seat department options the embedded wizard needs.
                getDepartmentMappings(companyId).catch(() => ({ data: [] })),
            ]);
            setStep6DeptMappings(dRes.data || []);
            const vs = vRes.data || [];
            const savedRows = mRes.data || [];
            setStep6Glm(glmRes.data || []);
            setStep6ExistingCount(vs.length);
            setStep6ExistingActiveCount(vs.filter(v => v.is_active === true || v.is_active === 'true').length);
            setStep6ExistingContractorCount(vs.filter(v => v.is_contractor === true || v.is_contractor === 'true').length);
            setStep6ExistingPrepaidCount(0);

            // Cache saved mappings by gl_category for later lookup during parse.
            const savedMap = {};
            for (const row of savedRows) {
                savedMap[row.gl_category] = {
                    is_category: row.is_category,
                    is_line_key: row.is_line_key,
                };
            }
            setStep6SavedCatMap(savedMap);

            /* ★ THREE states, not two. The old gate was
             *     hasSavedProgress = vs.length > 0 || savedRows.length > 0
             *     → 're-entry' : '6a'
             * and its own comment already named the state it could not express —
             * "partial progress from a prior session". With only two screens, PARTIAL was
             * routed to the COMPLETED one, so a tenant who saved 6c and logged out came
             * back to "Vendors already imported" over three zeroes — and, worse, to a
             * "Continue to Step 7 →" button that would mark vendor_setup complete with no
             * vendors at all. "Save Progress" makes that partial state COMMON, so the gate
             * has to learn it.
             *
             * COMPLETION is the only honest signal for "imported", and it is authoritative:
             * `steps.vendor_setup.completed` is written by exactly one place — the
             * import's own step-5 block — which a draft save now skips. */
            const ob = obStateOverride || obState;
            const completed = !!(ob?.steps?.vendor_setup?.completed);
            const hasAnyProgress = vs.length > 0 || savedRows.length > 0;
            setStep6SubStep(completed ? 're-entry' : (hasAnyProgress ? 'resume' : '6a'));
        } catch (err) {
            console.error('Failed to load Step 6 data:', err);
            setError('Failed to load vendor data: ' + (err?.message || 'Unknown error'));
        } finally { setStep6Loading(false); }
        /* ★ `obState` IS LOAD-BEARING IN THE DEP LIST, not lint appeasement. The gate
         * above reads `obState.steps.vendor_setup.completed`, and this callback used to
         * be memoized with `[]` — so it captured the FIRST-RENDER obState, which is null
         * (the wizard state loads async; the caller effect only waits for it to become
         * truthy, which does not re-memoize the function). With `[]`, `completed` would
         * evaluate to false FOREVER and a fully-onboarded tenant like cid=3 would land on
         * the in-progress 'resume' screen instead of its summary.
         * ★ Caught by the eslint exhaustive-deps warning, NOT by the verify — V3 tested
         * the predicate's LOGIC with a freshly-supplied obState and never exercised the
         * closure that binds it. Same shape as "the link renders vs the link resolves". */
    }, [obState]);

    useEffect(() => {
        if (viewingStep === 6 && selectedCompanyId && obState && step6SubStep === null && !step6Loading) {
            loadStep6Data(selectedCompanyId);
        }
    }, [viewingStep, selectedCompanyId, obState, step6SubStep, step6Loading, loadStep6Data]);

    // Self-heal: if the user somehow lands in an edit sub-step with empty
    // step6Vendors, re-fetch via loadStep6ForEdit so 6e's name-intersection
    // (Not in Expenses badge) has real data to compare against.
    useEffect(() => {
        const inEditSub = step6SubStep === '6c' || step6SubStep === S6_PREPAID || step6SubStep === S6_FORECAST;
        if (!inEditSub) return;
        if (step6Loading) return;
        if (!selectedCompanyId) return;
        // ★ Exit on ATTEMPTED, not on populated. `step6Vendors.length > 0` can never
        // become true for a 0-vendor tenant, so it looped forever (see the ref's note).
        const attemptKey = `${selectedCompanyId}:${step6SubStep}`;
        if (step6EditLoadAttempted.current[attemptKey]) return;
        if (step6Vendors.length > 0) {           // genuinely already loaded — nothing to do
            step6EditLoadAttempted.current[attemptKey] = true;
            return;
        }
        (async () => {
            try {
                setStep6Loading(true);
                // Mark BEFORE awaiting: one attempt per (company, sub-step), whatever the
                // outcome. A throw must not re-arm the loop either.
                step6EditLoadAttempted.current[attemptKey] = true;
                await loadStep6ForEdit();
            } catch (err) {
                console.error('[Step6 self-heal] loadStep6ForEdit failed:', err);
            } finally {
                setStep6Loading(false);
            }
        })();
        // Intentionally omit loadStep6ForEdit from deps — it's a plain function
        // that closes over selectedCompanyId/obState which are in the dep list.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step6SubStep, selectedCompanyId, step6Vendors.length, step6Loading, obState]);

    /* ★ 6d UNSAVED-CHANGES GUARD. Dirty = the grid differs from the last loaded/saved
     * snapshot, scoped to the sub-steps where the forecast grid is editable. Uses the
     * ONE shared browser guard (utils/useUnsavedChangesWarning) — the same effect the
     * Vendor Modal, Prepaid Modal and Forecast Wizard use; only the dirty question is
     * local, because only this surface knows what its grid means.
     * ⚠ Chromium suppresses beforeunload on a tab-X close without user activation
     * (CLAUDE.md §12) — hence the visible "Unsaved changes" indicator in the 6d footer
     * as defence-in-depth, not this alone. */
    const step6Dirty = useMemo(() => {
        if (step6SubStep !== S6_FORECAST) return false;
        if (step6SavedSnapshot.current === null) return false;   // nothing loaded yet
        return step6ForecastFingerprint(step6Vendors) !== step6SavedSnapshot.current;
    }, [step6SubStep, step6Vendors]);
    /* ★★ C1 s2a — THE PREPAID ARM. Deliberately NOT sub-step-gated, and that asymmetry
     * with the forecast half above is the whole design decision:
     *   the operator Applies a contract edit on S6_PREPAID, walks forward to S6_FORECAST
     *   (which is the intended flow — Prepaid precedes Forecast since Build 3), and closes
     *   the tab. The prepaid edits are STILL unsaved. A sub-step gate would disarm the
     *   warning at exactly the moment the operator moved on, which is the case most
     *   likely to happen and the one that loses the most work.
     * The baseline ref is the real guard instead: it is null until data is loaded and is
     * re-stamped on every save, so this can only speak while a live Step-6 session is
     * holding genuinely-unsaved prepaid work. It is silent everywhere else by construction.
     * ⓘ The FORECAST half above is untouched — same gate, same fingerprint, same ref. */
    const step6PrepaidDirty = useMemo(() => {
        if (step6PrepaidSnapshot.current === null) return false;  // nothing loaded yet
        return step6PrepaidFingerprint(step6Prepaid) !== step6PrepaidSnapshot.current;
    }, [step6Prepaid]);
    /* The shared hook is NOT modified (4 consumers); only the value handed in changed. */
    useUnsavedChangesWarning(step6Dirty || step6PrepaidDirty);

    const handleStep6ReImport = () => {
        setStep6File(null); setStep6Parsed(null); setStep6ParseError('');
        setStep6CatMap([]); setStep6Vendors([]); setStep6Prepaid([]);
        setStep6ImportError(''); setStep6DraftSaved('');
        step6SavedSnapshot.current = null;   // no baseline until the re-parse lands
        step6PrepaidSnapshot.current = null; // C1 s2a — same lifecycle, same reset
        /* ITEM 2 — same lifecycle again. A re-upload produces a DIFFERENT set of at-risk
         * rows, so an acknowledgement given for the previous workbook must not silently
         * cover it. The fingerprint check would catch a changed set anyway; clearing here
         * makes the intent explicit rather than relying on that. */
        setStep6Clobber(null); setStep6ClobberChoice('keep'); setStep6ClobberAck(null);
        // Re-arm the self-heal: this is a deliberate restart, so a later edit-sub-step
        // entry should be allowed its one load attempt again.
        step6EditLoadAttempted.current = {};
        setStep6SubStep('6a');
    };

    // Re-entry "Continue" when vendors were already imported in a prior session:
    // flip onboarding_state to step 7 and mark vendor_setup complete, then advance UI.
    const handleStep6ContinueFromReEntry = async () => {
        try {
            setStep6Importing(true);
            setStep6ImportError('');
            const existingSteps = (obState?.steps) || {};
            const newSteps = { ...existingSteps, vendor_setup: { completed: true } };
            await updateOnboardingState(selectedCompanyId, {
                // cursor is a high-water mark — never moves backward (see the Step-2 note)
                current_step: Math.max(obState?.current_step || 7, 7),
                steps: newSteps,
                close_month: obState?.close_month,
                forecast_start: obState?.forecast_start,
                historical_start: obState?.historical_start,
            });
            await loadWizardState(selectedCompanyId);
            setViewingStep(7);
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Unknown error';
            setStep6ImportError('Failed to advance step: ' + msg);
            console.error(err);
        } finally { setStep6Importing(false); }
    };

    // ── Re-entry edit mode: load vendor + actuals + mappings + prepaid from DB,
    // rebuild step6 state so 6c/6d/6e render from DB, flip edit-mode flag on.
    const loadStep6ForEdit = async () => {
        const [vRes, aRes, mRes, pRes, glmForEdit] = await Promise.all([
            getVendors(selectedCompanyId),
            getVendorMonthlyActuals(selectedCompanyId).catch(() => ({ data: [] })),
            getVendorCategoryMappings(selectedCompanyId).catch(() => ({ data: [] })),
            getPrepaidContracts(selectedCompanyId).catch(() => ({ data: [] })),
            getGLLineMappings(selectedCompanyId).catch(() => ({ data: [] })),
        ]);
        const vendors = vRes.data || [];
        const actuals = aRes.data || [];
        const mappings = mRes.data || [];
        // Live app's getPrepaidContracts returns frontend shape {vendor, start, length, prepaid, ...}
        // — unpack back into the onboarding-shape used by step6Prepaid.
        // ★ THE MAPPING ITSELF MOVED TO MODULE SCOPE (`step6PrepaidFromGetRow`, above the
        //   component) so the ITEM-2 import reconcile reads the stored contracts through
        //   the EXACT same shape this path does. Two normalizations of one GET would be
        //   the §6 duplication that made the clobber possible in the first place.
        const prepaidRaw = pRes.data || [];
        /* 2026-10-29: a contract stored with NO line (a re-save before this fix) whose label names several lines takes the
         * line of the vendor it is LINKED to, when that line is one of the label's (prepaidLineFromVendorLink -- a stored
         * link, never a name guess). Measured on cid=57: it restores the 10-28 line on every such contract with a link. A
         * contract with no link stays blank -- the editor opens it with Account blank and required. Legacy tenants: 0 rows. */
        const _lineMap = prepaidLabelToLineFor(glmForEdit.data || []);
        const _vendorLine = new Map(vendors.map(v => [v.id, v.is_line || null]));
        const prepaid = prepaidRaw.map(pr => {
            const row = step6PrepaidFromGetRow(pr);
            const fromLink = prepaidLineFromVendorLink(row, _vendorLine.get(pr.vendorId ?? pr.vendor_id), _lineMap);
            return fromLink ? { ...row, is_line: fromLink } : row;
        });

        /* ★★ F2 — ACTUALS BY vendor_id, NEVER BY NAME.
         *
         * This block used to key on `LOWER(TRIM(a.vendor_name))` and then hand the
         * SAME merged dict to every same-name vendor below. AE Model has four
         * duplicate-name groups — Misc ×9, Regus ×2, Robert Half ×2, Videoask ×2 —
         * which are DIFFERENT VENDORS SHARING A LABEL, separated by is_line (the nine
         * Misc sit on nine distinct IS lines). Merging them replaced nine real expense
         * histories with one union-of-months, last-wins series, and the next save wrote
         * that back: measured +$219,748.18 of phantom historical spend on cid=8
         * (9,322,353.12 vs the oracle's 9,102,604.94), with Robert Half showing
         * 221,879 + 221,879 where the truth is 11,879 + 210,000.
         *
         * ★ THIS IS V1's DECISION (`f4fada7`, "key actuals by vendor id, not by name"),
         * APPLIED TO ITS OWN SIBLING READ PATH. V1 fixed the OUTBOUND flatten-and-rejoin
         * — the parser already nested actuals per vendor, the client flattened them to a
         * name-keyed array and the server re-derived identity from a name. This is the
         * INBOUND half of the same mistake: the GET (server.js:3081) already SELECTs
         * `vma.vendor_id`, and this function was throwing it away.
         *
         * ★ SHAPE (a) — ELIMINATE THE JOIN, DON'T FIX THE KEY. Not (name, is_line):
         * V1 measured and rejected that ("it works for cid=3 and breaks on the first
         * workbook with a true (name, is_line) duplicate"), and deliberately did NOT add
         * a UNIQUE that would outlaw them — two real 'AWS' accounts under ga_software are
         * legal. There is no lookup to check here anyway: a vendor's history is its own,
         * and `vendor_monthly_actuals.vendor_id` IS that relationship, already on the wire.
         *
         * The name key is REMOVED rather than kept alongside — two identity schemes in one
         * loop is the bug's shape left in place for someone to reach for again.
         */
        const actualsById = {};
        for (const a of actuals) {
            if (a.vendor_id == null) continue;
            if (!actualsById[a.vendor_id]) actualsById[a.vendor_id] = {};
            actualsById[a.vendor_id][a.month] = Number(a.amount) || 0;
        }

        // Reverse lookup so step6Vendors can display gl_category label.
        const glByIsLine = {};
        for (const m of mappings) {
            // First write wins — later entries sharing the same is_line_key don't overwrite.
            if (!glByIsLine[m.is_line_key]) glByIsLine[m.is_line_key] = m.gl_category;
        }
        /* ★ L3M WINDOW — CLAMPED TO CLOSED MONTHS.
         * This used to be every month present in vendor_monthly_actuals, and the GET
         * applies no month filter — so once a tenant has FORECAST rows, `slice(-3)`
         * below silently became "the last three FORECAST months". Measured on cid=3
         * (1,638 forecast rows): last3 = 2023-10/11/12 instead of 2023-01/02/03.
         * Latent until now because a freshly-imported tenant had no forecast rows;
         * FIX 0 (`6bd2b8e`) gives every tenant them on the next import, which makes it
         * reachable. "L3M Avg" means the trailing three CLOSED months, always.
         * Display-only — this feeds the 6d "L3M Avg" column and nothing else. */
        const closeMonthForL3M = obState?.close_month || null;
        const allMonths = [...new Set(actuals.map(a => a.month))]
            .filter(m => !closeMonthForL3M || m <= closeMonthForL3M)
            .sort();

        // Build step6Vendors from DB rows. The GET endpoint already returns
        // vendors in sort_order ASC, name ASC — trust that order end-to-end.
        const rebuiltVendors = vendors.map(v => {
            const monthly_amounts = actualsById[v.id] || {};
            const last3 = allMonths.slice(-3);
            const sum = last3.reduce((s, m) => s + (monthly_amounts[m] || 0), 0);
            const l3Avg = last3.length > 0 ? r2(sum / last3.length) : 0;
            const glCat = glByIsLine[v.is_line] || v.is_line || '(Uncategorized)';
            return {
                vendor_name: v.name,
                gl_category: glCat,
                last3Avg: l3Avg,
                detectedFreq: null,
                lastPayment: 0,
                lastPaymentMonth: null,
                forecast_type: v.forecast_type || 'constant',
                /* ★ 7a — THE TYPE THIS ROW WAS LOADED UNDER, so the payload builder can tell
                 * an operator-authored forecast from one inherited by a type switch. A
                 * one_off vendor's forecast cells ARE its manual amounts (cid=3's Grant
                 * Thornton carries 9 of them, $96,000.00, and computeVendor books them at
                 * :208) — they must survive a re-save. Cells left behind by a PREVIOUS type
                 * are stale. Only the loaded value distinguishes the two, and only this read
                 * path has it. Entry-mode rows (fresh parse) carry no _loadedType and no
                 * forecast cells, so the rule that reads it is a no-op there by construction. */
                _loadedType: v.forecast_type || null,
                billing_frequency: v.billing_frequency || 'monthly',
                monthly_amount: Number(v.monthly_amount) || 0,
                pct_rate: Number(v.pct_rate) || 0,
                per_seat_cost: Number(v.per_seat_cost) || 0,
                seat_basis: v.seat_basis || 'fte',
                is_contractor: !!v.is_contractor,
                // null only when the company has no forecast start at all — the Step-6
                // render gate blocks before this list is ever shown or acted on, and
                // the import re-checks independently. Never an invented month.
                start_date: v.start_date || firstForecastMonth(obState),
                monthly_amounts,
                sort_order: Number.isFinite(Number(v.sort_order)) ? Number(v.sort_order) : 0,
                /* ★ THE STORED ROUTE, CARRIED. This row used to drop `is_line` entirely: the
                 * value was read once (to reverse-derive gl_category above) and then thrown
                 * away, so on a re-entry the wizard held a LABEL where the authoritative leaf
                 * id had been, and the editor's `|| v.is_line` fallback was dead by
                 * construction. Nothing downstream can prefer the stored route without this.
                 * `is_line_edited_at` rides beside it: non-null means a human set this line on
                 * the Vendor Dashboard, and a re-import must not overwrite it. Both come
                 * straight off the GET (SELECT * — no server change was needed to expose them). */
                is_line: v.is_line || null,
                is_line_edited_at: v.is_line_edited_at || null,
                _dbId: v.id,
                /* Batch 1b (2026-11-03, L3613 -- THE 7b RE-ENTRY GAP): every stored column the Step-6 save writes is read back,
                 * so re-opening Step 6 and saving with no edit stores what was there. Before this a re-save reset the end month,
                 * the seat departments, the % of Revenue base, a non-monthly vendor's billing month, its price changes, notes,
                 * archived state and contractor schedule (measured per company: diffs/post-walk-batch1b-build.txt p1). The
                 * payload builder reads the _stored* values only for a LOADED row (_dbId); a freshly parsed row has none. */
                end_date: v.end_date || 'until_canceled',
                seat_departments: Array.isArray(v.seat_departments) ? v.seat_departments
                    : (typeof v.seat_departments === 'string' && v.seat_departments ? JSON.parse(v.seat_departments) : []),
                pct_revenue_source: v.pct_revenue_source || null,
                renewals: Array.isArray(v.renewals) ? v.renewals : (typeof v.renewals === 'string' && v.renewals ? JSON.parse(v.renewals) : []),
                /* the billing month of a non-monthly constant is not a column -- it is WHICH forecast months carry the amount.
                 * Read it back as the first booked forecast month (firstNonzeroForecast, the Forecast Wizard's own rule); without
                 * it the save re-derived the month from history and moved the booking (cid=3: two annual vendors, Oct / Nov -> Apr). */
                billing_anchor: (closeMonthForL3M && (v.forecast_type || 'constant') === 'constant' && (v.billing_frequency || 'monthly') !== 'monthly')
                    ? ((firstNonzeroForecast({ monthlyData: monthly_amounts }, closeMonthForL3M) || {}).month || null) : null,
                _storedNotes: v.notes,
                _storedIsActive: v.is_active !== false,
                _storedAmortStart: v.amort_start || '',
                _storedContractorCount: v.contractor_count,
                _storedContractorMonthly: (v.contractor_monthly && typeof v.contractor_monthly === 'object') ? v.contractor_monthly
                    : (typeof v.contractor_monthly === 'string' && v.contractor_monthly ? JSON.parse(v.contractor_monthly) : {}),
            };
        });

        // Build step6CatMap from saved mappings, with vendor counts.
        const catCounts = {};
        for (const v of rebuiltVendors) {
            catCounts[v.gl_category] = (catCounts[v.gl_category] || 0) + 1;
        }
        const rebuiltCatMap = mappings.map(m => ({
            gl_category: m.gl_category,
            count: catCounts[m.gl_category] || 0,
            is_category: m.is_category,
            is_line_key: m.is_line_key,
        }));

        // 6e renders use step6Parsed.vendors to detect prepaid-only contracts —
        // stub it with the DB vendors so the lookup still works in edit mode.
        setStep6Parsed({
            vendors: rebuiltVendors.map(v => ({ vendor_name: v.vendor_name })),
            prepaidContracts: prepaid,
            months: allMonths,
            flags: [],
        });
        setStep6Vendors(rebuiltVendors);
        // Freshly loaded from the DB ⇒ this IS the saved state.
        step6SavedSnapshot.current = step6ForecastFingerprint(rebuiltVendors);
        step6PrepaidSnapshot.current = step6PrepaidFingerprint(prepaid);  // C1 s2a
        setStep6CatMap(rebuiltCatMap);
        setStep6Prepaid(prepaid);
        setStep6EditMode(true);
    };

    const handleStep6Edit = async (targetSubStep) => {
        // loadStep6ForEdit runs for EVERY edit target (6c, 6d, 6e) so
        // step6Vendors / step6CatMap / step6Prepaid are all populated before
        // any edit sub-step renders. 6e's "Not in Expenses" badge needs
        // step6Vendors loaded even when the user jumps straight to prepaid.
        try {
            setStep6Loading(true);
            setStep6ImportError('');
            // A "Saved —" acknowledgement belongs to the screen that produced it. Both
            // Step-6 editors now render it, so it must be dropped on any move between them.
            setStep6DraftSaved('');
            await loadStep6ForEdit();
            setStep6SubStep(targetSubStep);
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Unknown error';
            setStep6ImportError('Failed to load data for edit: ' + msg);
            console.error(err);
        } finally { setStep6Loading(false); }
    };
    const handleStep6BackToSummary = () => {
        // Exit edit mode and drop transient state; the re-entry card re-reads DB
        // counts via a fresh loadStep6Data call.
        setStep6EditMode(false);
        setStep6DraftSaved('');
        // Re-arm the self-heal for the next edit entry.
        step6EditLoadAttempted.current = {};
        // Let loadStep6Data pick the landing screen — it owns the three-way gate, so a
        // not-yet-completed tenant lands on 'resume', not the completed-only summary.
        setStep6SubStep(null);
        loadStep6Data(selectedCompanyId);
    };

    // Build default per-vendor forecast config from parsed data + close_month context.
    // Map a prepaid contract length (months) to our billing_frequency enum.
    const lengthToFreq = (lm) => {
        // 5d-2: an indefinite prepaid (length_months NULL → Number(null)=0, or any
        // non-positive/NaN) has no billing cycle; default to 'monthly' so we don't
        // imply a real multi-month cycle or crash. Real lengths map as before.
        if (lm == null || !Number.isFinite(lm) || lm <= 0) return 'monthly';
        if (lm === 1) return 'monthly';
        if (lm === 3) return 'quarterly';
        if (lm === 6) return 'semi_annual';
        if (lm === 12) return 'annual';
        return 'annual';
    };

    // 3rd param is the RESOLVED first forecast month (firstForecastMonth(obState)),
    // not close_month — one derivation, done by the caller. Never called with null:
    // the Step-6 render gate blocks before any sub-step renders (see renderStep6).
    const buildStep6Vendors = (parsedVendors, prepaidContracts, firstForecast) => {
        if (!firstForecast) throw new Error('buildStep6Vendors: no first forecast month');

        /* ★★ THE PREPAID MATCH IS ON IDENTITY, NOT ON A NAME.
         *
         * This was `prepaidByName`, a Map<trim+lowercase name, ONE contract>, read once
         * per vendor row. Both halves of that were wrong on duplicate names: the build
         * side OVERWROTE (many contracts share a vendor_name, only the last survived) and
         * the read side handed the SAME contract to EVERY vendor row sharing the name — so
         * a vendor that owns no contract at all was still forced forecast_type='prepaid'
         * with monthly_amount 0, which books $0 FOREVER and hides it from the forecast
         * editor. That is exactly the contractless-prepaid state the PREPAID ABSOLUTE RULE
         * (§E1) forbids, and it is the last producer of it that C2 did not close.
         *
         * MEASURED on the corrected operator templates (diffs/regus-remeasure.txt): the
         * name-only key leaves 12 of 170 contracts ambiguous and wrongly force-prepaids
         * ONE vendor row — "Regus" on Software <$1000, whose 12 contracts belong to the
         * OTHER "Regus", on Office Rent. Adding the GL line leaves 0 ambiguous. The two
         * Regus rows are genuinely distinct vendors with distinct real spend ($14,818.45
         * vs $575.00 on all four tenants), so no template correction can remove this —
         * only the key can.
         *
         * ★ THE ONLY BEHAVIOURAL CHANGE IS *WHICH* VENDOR ROWS GET FORCE-PREPAID.
         * A vendor that legitimately owns a contract still derives billing_frequency,
         * start_date and monthly_amount=0 from the SAME source contract by the SAME
         * arithmetic — the contract-side last-wins is preserved verbatim below (it is
         * filed separately and is money-inert: the match carries no amort).
         *
         * ⛔ LEGACY UPLOADS TAKE THE OLD PATH, VERBATIM. A legacy vendor sheet has no
         * gl_line_name column at all (parseVendorExcel emits null for every row), so there
         * is no line vocabulary to compare and a pair key would match NOTHING — every
         * legacy prepaid vendor would silently lose its flag. The mode switch is the same
         * `some(gl_line_name != null)` predicate renderStep6c already uses for `perLine`,
         * and it is a property of the TEMPLATE, not a per-contract permissive fallback. */
        const perLine = WIZ_PER_LINE(parsedVendors);
        const matchByVendorIdx = new Map();   // vendor row index -> the contract that types it prepaid
        const unresolvedPrepaid = [];         // contracts resolving to no single vendor -> the operator FLAG

        if (perLine) {
            /* ONE vocabulary on both sides: the workbook gl_line_name. The contract side
             * arrives as `account` — parseVendorExcel:558 maps the prepaid sheet's
             * gl_line_name column into it. Declared explicitly; the resolver refuses to
             * guess (a label compared against a slug would match nothing, silently). */
            /* 2026-10-13: through the ONE wizard resolver (wizResolvePrepaidVendor). A contract with no is_category is
             * matched on the label, as the comment above says and exactly as before; one that names its section is
             * matched on section + line (the vendor rule's key) -- see wizVendorSection. */
            const vIndex = wizPrepaidVendorIndexes(parsedVendors);
            for (const p of (prepaidContracts || [])) {
                if (!p.vendor_name) continue;
                const r = wizResolvePrepaidVendor(p, vIndex);
                if (r.entry) { matchByVendorIdx.set(r.entry.idx, p); continue; }   // last-wins, as before
                /* 'orphan' = a prepaid-only vendor (AE Model has 4, all _archive_*). ALREADY
                 * a named handled state — the 6e "Not in Expenses" badge and the import
                 * writing vendor_id NULL. Do NOT newly flag it. */
                if (r.reason === 'orphan') continue;
                unresolvedPrepaid.push({ contract: p, reason: r.reason, candidates: r.candidates || [] });
            }
        } else {
            // LEGACY: no gl_line_name anywhere -> reproduce the old name-only match exactly.
            const legacyByName = new Map();
            (parsedVendors || []).forEach((v, i) => {
                const k = String(v.vendor_name == null ? '' : v.vendor_name).trim().toLowerCase();
                if (!legacyByName.has(k)) legacyByName.set(k, []);
                legacyByName.get(k).push(i);
            });
            for (const p of (prepaidContracts || [])) {
                if (!p.vendor_name) continue;
                const k = String(p.vendor_name).trim().toLowerCase();
                for (const i of (legacyByName.get(k) || [])) matchByVendorIdx.set(i, p);
            }
        }

        // Only expense-sheet vendors appear in step6Vendors. Prepaid-only
        // vendors (in Prepaid Sch but not in Expenses) surface in 6e with the
        // "Not in Expenses" badge and are handled there by rename or accept-as-new.
        const builtVendors = parsedVendors.map((v, _vIdx) => {
            const prepaidMatch = matchByVendorIdx.get(_vIdx);

            let forecast_type = v.forecastTypeDefault || 'constant';
            let billing_frequency = v.detectedFreq === 'quarterly' ? 'quarterly'
                : v.detectedFreq === 'semi_annual' ? 'semi_annual'
                : v.detectedFreq === 'annual' ? 'annual'
                : 'monthly';
            let monthly_amount = 0;
            let start_date = firstForecast;

            if (prepaidMatch) {
                // Prepaid Sch tab match → force the prepaid FLAG.
                forecast_type = 'prepaid';
                billing_frequency = lengthToFreq(Number(prepaidMatch.length_months));
                // sc-rework-2 (single-source): a prepaid vendor is a FLAG — its
                // cycle data lives in prepaid_contracts (the Prepaid Sch), not on
                // the vendor row. Zero monthly_amount (the removed synth was its
                // only reader); keep forecast_type='prepaid' + the match detection.
                // TODO (single-source onboarding, Session 6): create the
                // prepaid_contracts row from prepaidMatch here (vendor_id link +
                // amort schedule) instead of relying on a vendor amount — its own step.
                monthly_amount = 0;
                // Next payment = last contract start + one cycle, bumped forward to firstForecast.
                /* ★★ D6 FOLLOW-UP — THE CYCLE *IS* THE TERM FOR A PREPAID CONTRACT.
                 * This read `VENDOR_FREQ_MONTHS[billing_frequency]`, which round-trips
                 * `length_months` through a FOUR-VALUE enum and back: `lengthToFreq` collapses
                 * every non-standard term to 'annual', so a 7-month contract's next renewal was
                 * computed as start + 12. The integer was correct at the start of that chain and
                 * was discarded on the way through. Read it directly — it is on `prepaidMatch`,
                 * one line above; no lookup, no new parameter.
                 * ★ STANDARD TERMS ARE BYTE-IDENTICAL BY CONSTRUCTION: the two maps are mutual
                 * inverses on exactly 1/3/6/12 (1→'monthly'→1, 3→'quarterly'→3, 6→'semi_annual'→6,
                 * 12→'annual'→12), so freqM === termM there and only the wrong cells move.
                 * ★ `: 1` MIRRORS lengthToFreq's OWN INDEFINITE GUARD, and the `|| 12` it replaces
                 * was UNREACHABLE. A NULL `length_months` makes lengthToFreq return 'monthly'
                 * (its 5d-2 arm), so `VENDOR_FREQ_MONTHS['monthly']` was 1 — never 12. Defaulting
                 * to 12 here would have been the one way this fix could silently move a row that
                 * is not odd-length.
                 * ⛔ `lengthToFreq` IS DELIBERATELY LEFT ALONE. It still derives
                 * `billing_frequency`, a genuine four-value enum with a DB CHECK constraint
                 * (`vendors_billing_frequency_check`) that cannot hold 7. This removes it from the
                 * DATE arithmetic only; what a non-standard cadence should be CALLED is a separate,
                 * display-only question. Phase 0: diffs/lengthtofreq-phase0.txt */
                const termM = Number(prepaidMatch.length_months);
                const freqM = (Number.isFinite(termM) && termM > 0) ? termM : 1;
                let next = prepaidMatch.start_month ? addYMMonths(prepaidMatch.start_month, freqM) : firstForecast;
                while (next < firstForecast) next = addYMMonths(next, freqM);
                start_date = next;
            } else if (forecast_type === 'constant') {
                monthly_amount = Number(v.last3Avg) || 0;
                start_date = firstForecast;
            } else if (forecast_type === 'prepaid') {
                /* ★★ C2 — UNREACHABLE BY CONSTRUCTION: AN INVARIANT VIOLATION, NOT A CASE.
                 * Reaching here means forecast_type === 'prepaid' while prepaidMatch is
                 * falsy — i.e. a vendor is prepaid WITHOUT being on the prepaid template,
                 * which is exactly what the ABSOLUTE RULE forbids. C2 removed the only
                 * producer of that state (the cadence inference in parseVendorExcel), so
                 * the sole remaining way to get here is a future regression.
                 *
                 * ★ WHY A THROW RATHER THAN A DELETE. Deleting this arm would let such a
                 * vendor fall through to the `one_off` else-branch — monthly_amount 0, no
                 * synthesized forecast, $0 booked forever, silently. That is precisely the
                 * failure class this item exists to eliminate, so a silent fall-through is
                 * the one outcome we must not choose.
                 * ★ WHY A THROW RATHER THAN console.warn + fallback. A warning with no
                 * reader is not a check (§12.5) — nobody watches this console during an
                 * upload, and the import would proceed on data we have just declared
                 * invalid.
                 * ★ AND IT IS A NAMED HANDLED STATE, NOT A CRASH (§12.5 rule 7): the only
                 * caller (the Step-6 parse handler) wraps this in try/catch and renders
                 * `err.message` as the operator-visible "Failed to parse file: …" banner.
                 * buildStep6Vendors already throws in exactly this style on its own
                 * precondition (`no first forecast month`), so this matches the function's
                 * established contract rather than inventing one.
                 * It cannot fire on valid data, so it costs nothing in normal operation. */
                throw new Error(
                    `buildStep6Vendors: vendor "${v.vendor_name}" is typed 'prepaid' but has no ` +
                    `Prepaid Schedule row. A vendor is prepaid ONLY if it appears on the prepaid ` +
                    `template — a contractless prepaid vendor books $0 forever and is hidden from ` +
                    `the forecast editor. This is a code-level invariant violation (the parser can ` +
                    `no longer infer prepaid from payment cadence), not something the workbook can cause.`
                );
            } else {
                // one_off — user will set exact per-month amounts in VendorPage post-go-live
                monthly_amount = 0;
                start_date = firstForecast;
            }

            /* ★★ 4a — THE DETECTOR PROPOSES A TYPE, NOT A CADENCE.
             * `detectedFreq` seeded billing_frequency for EVERY row above. That was
             * harmless only because the import then FORCED 'monthly' on every
             * non-prepaid vendor — the coercion 4a removes. Take the seed away with the
             * coercion, or removing it turns a HEURISTIC into money:
             * MEASURED on cid=3 — 7 non-prepaid vendors detect a non-monthly cadence
             * (Travel-COGS + Goodhire quarterly; TIPS, Beth Newton, Elizabeth Gilligan
             * semi-annual; Weglot, Squarespace annual) and are all stored 'monthly'
             * today. Honouring the detector would make those seven book lumpy and move
             * them AWAY from the oracle.
             * ★ And it would not buy the case this exists for: cid=3's three real
             * non-monthly constants — Michael Stilton Board Fee (quarterly), Prescient
             * ISO 27001 and Prescient SOC (annual) — all detect as 'manual'. Their
             * cadence is an OPERATOR choice, entered through the Dashboard's bulk
             * editor; no detector can produce it.
             * ⇒ a non-prepaid row carries 'monthly' until somebody CHOOSES otherwise
             * (4b's embedded ForecastWizard). `detectedFreq` is still returned below and
             * still shown in the grid — it just stops silently becoming a billing term.
             * PREPAID is untouched: its frequency is set from the contract length above. */
            if (forecast_type !== 'prepaid') billing_frequency = 'monthly';

            return {
                vendor_name: v.vendor_name,
                /* ★ THE 6c JOIN KEY. On a canonical upload the 6c map is keyed PER LINE, so
                 * a vendor must carry the same key or the import's catByGl lookup misses.
                 * Both sides go through catMapKey — ONE derivation, so they cannot drift.
                 * (Legacy uploads have no gl_line_name, so this stays the category and the
                 * old per-category behaviour is preserved verbatim.) */
                gl_category: catMapKey(v),
                gl_line_name: v.gl_line_name || null,
                last3Avg: v.last3Avg,
                detectedFreq: v.detectedFreq,
                lastPayment: v.lastPayment,
                lastPaymentMonth: v.lastPaymentMonth,
                forecast_type,                      // 'constant' | 'prepaid' | 'pct_revenue' | 'per_seat' | 'one_off' | 'skip'
                billing_frequency,
                monthly_amount,
                pct_rate: 0,                        // fraction (0.09 for 9%)
                per_seat_cost: 0,
                seat_basis: 'fte',
                is_contractor: suggestContractor(v.gl_category, v.vendor_name),
                start_date,
                monthly_amounts: v.monthly_amounts, // kept for actuals import
            };
        });
        /* ★ RETURNS BOTH HALVES OF ONE PASS. `unresolved` is the flag input; deriving it
         * a second time in the caller would be two derivations of one thing (§6). The
         * single caller destructures — an object reaching `.map()` breaks loudly. */
        return { vendors: builtVendors, unresolved: unresolvedPrepaid };
    };

    const handleStep6Parse = async () => {
        setStep6ParseError('');
        const singleMode = step6UploadMode === 'single';
        if (singleMode && !step6File) { setStep6ParseError('Please select a file.'); return; }
        if (!singleMode && !step6VendorFile) { setStep6ParseError('A vendor expenses file is required.'); return; }
        try {
            setStep6Parsing(true);
            /* Upload rework step 2 — the ONLY branch is how `result` is built; both modes
             * converge on the same shape immediately below (Step 2's converge-after-parse
             * pattern), so every guard, the category map, buildStep6Vendors and the 6b/6c
             * routing are shared and unchanged.
             *
             * SEPARATE mode hands both halves to the SAME assembleVendorResult the
             * single-file path uses — the vendor enrichment (detectedFreq /
             * forecastTypeDefault / last3Avg / lastPayment / lastPaymentMonth) and the
             * cross-sheet orphan notice live there, and buildStep6Vendors reads all of
             * them. A caller that stopped at the raw sheet parsers would silently produce
             * every vendor as constant/$0. */
            let result;
            if (singleMode) {
                result = parseVendorExcel(await step6File.arrayBuffer());
            } else {
                const exp = parseVendorSheet(await step6VendorFile.arrayBuffer(), 'expenses');
                // The prepaid file is OPTIONAL. Absent → the same empty shape the
                // single-file path substitutes, and sheets.prepaid stays null so Step 1's
                // no-prepaid warning fires identically here.
                const prep = step6PrepaidFile
                    ? parseVendorSheet(await step6PrepaidFile.arrayBuffer(), 'prepaid')
                    : { contracts: [], flags: [], sheetName: null };
                result = assembleVendorResult(exp, prep, exp.sheetName, prep.sheetName);
            }
            /* ★ Upload rework step 1 — FAIL LOUD ON THE TWO SILENT SHEET-PICKER FAILURES.
             * pickExpensesSheet can never return null (it falls back to the first
             * non-prepaid sheet, then to names[0]), and pickPrepaidSheet returns null and
             * the parser substitutes { contracts: [], flags: [] }. So today a workbook with
             * no prepaid tab loses every contract silently, and a prepaid-only workbook is
             * parsed AS the expenses sheet. Both look like a clean parse. The parser
             * already reports which sheets it chose (result.sheets); nothing read it.
             *
             * This guard runs BEFORE the no-vendors guard on purpose: a prepaid-only
             * workbook yields a non-empty but meaningless vendor list (172 rows off the
             * Prepaid Schedule), so the vendor guard does not catch it — and when such a
             * sheet DOES parse to zero vendors, "this is a prepaid sheet" is the more
             * useful message than "no vendors found". */
            if (/prepaid/i.test(result.sheets.expenses || '')) {
                setStep6ParseError('This looks like a prepaid sheet, not a vendor/expenses sheet. Upload the vendor expenses file, or a workbook whose expenses tab is not named "prepaid".');
                setStep6Parsing(false);
                return;
            }
            if (!result.vendors || result.vendors.length === 0) {
                setStep6ParseError('No vendors found in the file. Expected an Expenses sheet with vendor rows + monthly columns.');
                setStep6Parsing(false);
                return;
            }
            /* No prepaid tab: legitimate for a tenant with no prepaid contracts, so this is
             * a NON-BLOCKING notice rather than an error. Pushing it into result.flags
             * reuses the ed4a71d 6b renderer (severity-driven icon, message || label, the
             * [sheet] tag) AND routes through 6b — setStep6SubStep(hasFlags ? '6b' : '6c')
             * below — so the operator must read it instead of skipping straight to 6c.
             * (Upload-rework step 3 adds the explicit "this company has none" confirm;
             * this step only makes the state visible.) */
            if (result.sheets.prepaid == null) {
                (result.flags = result.flags || []).push({
                    type: 'no_prepaid_sheet',
                    severity: 'warning',
                    row: 0,
                    sheet: '',
                    label: 'No prepaid sheet found in this workbook',
                    message: 'No prepaid sheet found in this workbook — no prepaid contracts will be imported. If this company has prepaid contracts, re-upload a workbook that includes a prepaid tab.',
                });
            } else if ((result.prepaidContracts || []).length === 0) {
                /* Step 3 — the OTHER zero-prepaid shape, and a DIFFERENT diagnosis: a
                 * prepaid sheet/file WAS supplied and parsed to nothing. Step 2's V4 found
                 * this is what a wrong file in the prepaid slot looks like, and it is the
                 * one case the no_prepaid_sheet warning cannot describe (a file WAS given).
                 * Distinct type so the two are independently reachable and diagnosable. */
                (result.flags = result.flags || []).push({
                    type: 'prepaid_empty',
                    severity: 'warning',
                    row: 0,
                    sheet: result.sheets.prepaid || '',
                    label: 'Prepaid sheet found but no contracts in it',
                    message: 'A prepaid file was provided but no prepaid contracts were found in it — check you uploaded the right file.',
                });
            }
            /* W-068 pass 3 -- THE UPLOAD CHECK, both tabs against their template specs, merged into this upload's
             * flag list (the 6b review renders it; a BLOCKING problem disables "These look correct" and the import).
             * The prepaid tab's B8 resolves each contract's line through the SAME label map the import uses. */
            result.flags = mergeSpecProblems(result.flags,
                checkAgainstSpec('prepaid', { specScan: result.specScans?.prepaid }, { prepaidLabelToLine: prepaidLabelToLineFor(step6Glm) }),
                { sheet: result.sheets?.prepaid || '' });
            result.flags = mergeSpecProblems(result.flags,
                checkAgainstSpec('vendor', { specScan: result.specScans?.vendor }),
                { sheet: result.sheets?.expenses || '' });
            // Step 3 — a fresh parse always invalidates a previous assertion, so an upload
            // that DOES contain prepaid can never inherit a stale confirmation.
            setStep6NoPrepaidConfirmed(false);
            setStep6Parsed(result);

            /* ★★ THE 6c MAP IS NOW PER-LINE, NOT PER-CATEGORY.
             *
             * It used to group on `v.gl_category` — which the canonical parser fills with
             * the FIVE is_category literals — so 229 vendors could receive at most FIVE
             * is_lines, against cid=3's 34. The template's per-row `gl_line_name` (33
             * distinct values) was located by the parser and discarded; step C carries it,
             * and each row's key is derived by the ONE shared buildVendorIsLineKey — the
             * same rule the 4c migration used to build cid=3, proven equivalent 34/34.
             *
             * The CATEGORY now comes FROM THE TEMPLATE, not from suggestVendorCategory:
             * that keyword matcher was being fed the five canonical labels, matched none
             * of COGS/S&M/Other, and fell through to a hardcoded 'G&A' — which is why the
             * screen pre-filled 208 of 229 vendors onto ga_accounting_finance.
             *
             * LEGACY FALLBACK: a non-canonical sheet has no gl_line_name, so those rows
             * group on gl_category exactly as before. One branch, not a second screen. */
            const rowMap = new Map();
            for (const v of result.vendors) {
                const line = v.gl_line_name == null ? null : v.gl_line_name;   // '' = canonical-but-blank
                const cat = line == null ? null : (v.gl_category || null);   // canonical: category is the template's
                const key = catMapKey(v);
                const cur = rowMap.get(key);
                if (cur) { cur.count += 1; continue; }
                rowMap.set(key, {
                    gl_category: key,
                    gl_line_name: line,
                    is_category: cat,
                    count: 1,
                });
            }
            const cats = [...rowMap.values()].map(row => {
                // A saved choice always wins, so re-entry preserves operator overrides.
                const saved = step6SavedCatMap[row.gl_category];
                /* ★ §E1 (1d) — NO THIRD ARM. The legacy fallback here was
                 * suggestVendorCategory(row.gl_category), a keyword guess off the
                 * account name. §E1: a classification comes from the operator's
                 * explicit template column, and where that is absent the operator
                 * chooses. A legacy sheet has no such column, so is_category is
                 * UNDEFINED here by design — the row arrives blank, is flagged by
                 * rowUnsure, and blocks 6c until the operator picks. */
                const is_category = saved?.is_category
                    || row.is_category;                                  // canonical: the template's
                const derived_is_line = row.gl_line_name == null
                    ? ''
                    : buildVendorIsLineKey(is_category, row.gl_line_name);
                const is_line_key = saved?.is_line_key
                    || derived_is_line
                    || defaultVendorLineKey(step6Glm, is_category);      // legacy only
                return {
                    ...row,
                    is_category,
                    derived_is_line,
                    is_line_key,
                    /* ★ §E1 (1a/1b) — `unsure` is NO LONGER STORED ON THE ROW. It is
                     * derived at every point of use by rowUnsure(), so it tracks the
                     * operator's edits. The old `row.gl_line_name == null ? null :`
                     * guard also silently EXEMPTED every legacy row from flagging,
                     * which is how a guessed category used to sail through 6c. */
                };
            });
            setStep6CatMap(cats);
            setStep6CatConfirmed(false);   // a fresh parse always re-asks for the confirm

            /* Undederivable rows are INFORMATIONAL here (they route through 6b via the
             * step-A predicate) and BLOCKING at 6c, where they are actually fixed. Not
             * gated twice — renderStep6b needs no gating logic. */
            for (const c of cats) {
                const cUnsure = rowUnsure(c, step6Glm);
                if (!cUnsure) continue;
                (result.flags = result.flags || []).push({
                    type: 'vendor_line_unsure',
                    severity: 'warning',
                    row: 0,
                    sheet: result.sheets?.expenses || '',
                    /* ★ STAGE 3 — `cause` is the RAW reason, carried so renderStep6b can
                     * group identical ones into a single line without re-deriving or
                     * string-matching a rendered sentence. It is display-only: nothing
                     * stores it and the routing predicate reads flags.LENGTH, which is
                     * unchanged. */
                    cause: cUnsure,
                    label: `Could not map "${c.gl_line_name || c.gl_category}" to an income-statement line`,
                    /* ★ STAGE 3 BUGFIX — this interpolated `c.unsure`, a field Stage 1
                     * DELETED from the row when it made the flag derived. Every one of
                     * these notices has been rendering the literal text "undefined" since
                     * Stage 1 shipped. It reads `cUnsure`, the value computed two lines
                     * up, which is what it always meant. */
                    message: `"${c.gl_line_name || c.gl_category}" (${c.is_category || 'no category'}, ${c.count} vendor${c.count === 1 ? '' : 's'}) could not be mapped automatically — ${cUnsure}. Pick a line for it on the next screen.`,
                });
            }

            // Build default vendor forecast config — cross-references Prepaid Sch.
            const _b6 = buildStep6Vendors(result.vendors, result.prepaidContracts || [], firstForecastMonth(obState));
            const _built6d = _b6.vendors;
            setStep6Vendors(_built6d);

            /* ★ THE PREPAID-MATCH FLAG — IT MUST REACH A READER (§12.5 shape 7).
             * A contract whose vendor cannot be resolved to exactly one expense-sheet row
             * is an OPERATOR question, not a console line: either the two sheets disagree
             * about which GL line the vendor sits on, or two vendors are indistinguishable.
             * Pushing onto result.flags is the existing §E1 idiom — it renders in the 6b
             * Data Quality Review (the generic flag list, message || label) AND it makes
             * `hasFlags` route the operator there instead of skipping to 6c.
             * ⛔ 'orphan' never reaches here (buildStep6Vendors drops it): a prepaid-only
             * vendor is already the 6e "Not in Expenses" badge, and a second notice for one
             * fault is how a review screen becomes a wall nobody reads.
             * ⓘ MEASURED EMPTY on the corrected templates — this is a forward safety net,
             * and the A/B proves it stays silent on real data and fires on a synthetic. */
            for (const u of (_b6.unresolved || [])) {
                const vn = u.contract.vendor_name;
                const cl = (u.contract.is_category ? `${u.contract.is_category} / ` : '') + (u.contract.account || '(no GL line)');   // 2026-10-13: + the section, when the row names one
                const lines = [...new Set((u.candidates || []).map(e => (u.contract.is_category ? `${wizVendorSection(e.vendor)} / ` : '') + (e.vendor.gl_line_name || '(no GL line)')))];
                (result.flags = result.flags || []).push({
                    type: 'prepaid_vendor_unresolved',
                    severity: 'warning',
                    row: 0,
                    sheet: result.sheets?.prepaid || '',
                    label: `Prepaid contract for "${vn}" does not match one expense vendor`,
                    message: u.reason === 'ambiguous'
                        ? `Prepaid contract for "${vn}" (GL line "${cl}", start ${u.contract.start_month || '—'}) matches ` +
                          `${u.candidates.length} expense vendors that share BOTH that name and that line — nothing can tell them ` +
                          `apart, so the contract will not be linked to a vendor and its amortization will not reach the income ` +
                          `statement. Rename one of them on the expense sheet so they are distinguishable.`
                        : `Prepaid contract for "${vn}" (GL line "${cl}", start ${u.contract.start_month || '—'}) has no expense ` +
                          `vendor of that name on that line — "${vn}" appears on ${lines.map(l => `"${l}"`).join(', ')}. The two ` +
                          `sheets disagree about which line this vendor sits on, so the contract will not be linked to a vendor ` +
                          `and its amortization will not reach the income statement. Correct the GL line on whichever sheet is ` +
                          `wrong and re-upload.`,
                });
            }
            // A fresh parse is the new baseline — untouched defaults are not 'unsaved edits'.
            step6SavedSnapshot.current = step6ForecastFingerprint(_built6d);

            // Build prepaid list (editable).
            /* 2026-10-13 (Brian, SubBase): a row that NAMES ITS SECTION (the template's is_category) gets its line here --
             * section + name by the vendor rule, through the one owner (prepaidRouting.stampPrepaidRowLine) -- stored on
             * the row as is_line, so the import, the prepaid editor and the engine book it through the existing
             * slug-first path. A row with no section, or whose section + name is no line of this company (the upload
             * check's B8 blocks that file), is copied unchanged, exactly as `({ ...p })` did. */
            const _stampMap = prepaidLabelToLineFor(step6Glm);
            const _builtPrepaid = (result.prepaidContracts || []).map(p => stampPrepaidRowLine(p, _stampMap));
            setStep6Prepaid(_builtPrepaid);
            // C1 s2a — a fresh parse is the prepaid baseline too.
            step6PrepaidSnapshot.current = step6PrepaidFingerprint(_builtPrepaid);

            /* Go to 6b (Review) if ANY flag exists, else skip to 6c.
             *
             * ★ This used to read `.some(f => f.type === 'formula' || f.type === 'no_dates')`
             * — a TWO-TYPE ALLOWLIST, despite the comment above it (which said "if flags
             * exist") and despite everything written since assuming it meant any flag. Six
             * of the eight flag types the 6b renderer can display were silently dropped:
             * orphan_vendor and duplicate_key (both added by ed4a71d), no_prepaid_sheet
             * (upload-rework step 1), prepaid_empty (step 3), and both `info` types. The
             * consequences were not cosmetic — the step-3 no-prepaid confirm lives INSIDE
             * renderStep6b, so it never rendered and never gated; and the duplicate-key
             * warning guarding the two byte-identical $13,000 RTM prepaid rows was
             * unreachable unless the operator navigated backwards on a hunch.
             *
             * ★ DELIBERATELY `length > 0`, NOT A WIDER ALLOWLIST. Every flag this array can
             * hold is one the 6b renderer displays and the operator should read; an
             * allowlist has to be updated each time a notice is added, which is precisely
             * how this survived — ed4a71d added two types in July and this line was never
             * revisited. Presence, not type.
             *
             * FLOW NOTE: an upload whose only notices are informational now STOPS on 6b
             * rather than flowing to 6c. That is the intent of a review screen. */
            const hasFlags = (result.flags || []).length > 0;
            setStep6SubStep(hasFlags ? '6b' : '6c');
        } catch (err) {
            console.error(err);
            setStep6ParseError('Failed to parse file: ' + (err?.message || 'Unknown error'));
        } finally { setStep6Parsing(false); }
    };

    const handleStep6SaveCats = async () => {
        try {
            setStep6Importing(true);
            setStep6ImportError('');
            // sort_order = position in step6CatMap preserves the parse-time order
            // (Excel row order) across reloads/edits. Filter happens after the
            // index is captured so skipped rows don't compact the numbering.
            const mappings = step6CatMap
                .map((c, idx) => ({
                    gl_category: c.gl_category,
                    is_category: c.is_category,
                    is_line_key: c.is_line_key,
                    sort_order: idx,
                }))
                .filter(c => c.gl_category && c.is_category && c.is_line_key);
            if (mappings.length > 0) await saveVendorCategoryMappings(selectedCompanyId, mappings);
            // Cache locally so re-parse in the same session picks up the same choices.
            const nextSaved = { ...step6SavedCatMap };
            for (const m of mappings) nextSaved[m.gl_category] = { is_category: m.is_category, is_line_key: m.is_line_key };
            setStep6SavedCatMap(nextSaved);
            if (step6EditMode) {
                // In edit mode: re-import so vendors' is_line reflects new mappings.
                setStep6Importing(false); // handleStep6Import manages its own flag
                await handleStep6Import();
            } else {
                setStep6SubStep(S6_PREPAID);
                setStep6Importing(false);
            }
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Unknown error';
            setStep6ImportError('Failed to save category mappings: ' + msg);
            setStep6Importing(false);
        }
    };

    /* c1: was `handleStep6SaveForecast`, wired to the FORECAST screen's
     * "Save & Continue" — a navigation-only handler behind a save label (Phase 0 F5).
     * After the reorder it belongs to the PREPAID screen and is named for what it
     * does: advance to the forecast grid. It still saves nothing, and its button now
     * says "Continue". Entry-mode only; edit mode uses handleStep6Import. */
    const handleStep6PrepaidContinue = () => {
        setStep6DraftSaved('');   // see handleStep6Edit — the note is per-screen
        setStep6SubStep(S6_FORECAST);
    };

    /* ★ `draft` (Step-6 "Save Progress"): write the vendor rows WITHOUT completing Step 6.
     * Forecast fields live only as `vendors` columns (no staging table), so an incremental
     * 6d save must write real rows; the server skips ONLY its onboarding_state completion
     * block when draft=true. The PAYLOAD BUILDER BELOW IS UNCHANGED — a draft save and the
     * real import write identical vendor rows, which is the property the verify asserts. */
    const handleStep6Import = async ({ draft = false, then = null } = {}) => {
        // W-068: a vendor file with blocking upload problems is never imported (6b already stops it; this is the backstop).
        { const nBlock = countBlocking(step6Parsed?.flags); if (nBlock) { setStep6ImportError(blockingHeading(nBlock)); return; } }
        // Batch 1 (2026-11-03, L3591): an uploaded vendor-forecast file whose change list is showing but NOT applied is not saved by this
        // button -- say so and stop (PantryProof's first save stored the vendors without the file's forecasts). Nothing is written.
        { const nPend = step6FcFile && step6FcFile.plan && step6FcFile.plan.changed ? step6FcFile.plan.changed.length : 0;
          if (nPend) { const s = nPend === 1 ? '' : 's'; setStep6ImportError(`Not saved: the vendor forecast file ${step6FcFile.fileName} has ${nPend} change${s} that are not applied yet. Press "Apply ${nPend} change${s}" on the Forecast screen (or Cancel there to discard them), then save.`); return; } }
        try {
            setStep6Importing(true);
            setStep6ImportError('');
            setStep6DraftSaved('');
            setStep6PrepaidNotice(null); // Build 4c: recompute on each import
            setStep6RouteNotice(null);   // recomputed below, per import
            const preservedRoutes = [];  // vendor names whose hand-set IS line survived

            const catByGl = {};
            for (const c of step6CatMap) catByGl[c.gl_category] = c;

            /* ★★ FAIL LOUD BEFORE ANY WRITE — never fall back to a leaf that cannot exist.
             *
             * This loop used to read `const is_line = cat.is_line_key || 'ga_vendors';`. No
             * tenant has a `ga_vendors` leaf (verified: cid=1/2/3 all have zero `*_vendors`
             * leaves), so every vendor that hit that fallback was imported pointing at a
             * non-existent line — and computeVendor.js:108 then silently dropped it: $0 spend,
             * no error, forever. The comment above (buildStep6Vendors) even ADMITS this is how
             * prepaid-only vendors are handled.
             *
             * A fallback to a ghost is strictly WORSE than an error: it converts a loud,
             * fixable mapping gap into a silent zero. Validate against the tenant's ACTUAL IS
             * leaves and refuse the whole import if any vendor would be orphaned. */
            const validLeaves = new Set((step6Glm || [])
                .filter(g => g.statement_type === 'IS' && g.is_line_key && g.role)
                .map(g => g.is_line_key));
            const orphans = new Map();   // gl_category -> vendor names that would be orphaned
            for (const v of step6Vendors) {
                if (v.forecast_type === 'skip') continue;
                /* ★ THE GUARD ASKS THE SAME QUESTION AS THE WRITE. It used to test the CATEGORY's
                 * key only, so a hand-edited vendor whose category happens to be unmapped would
                 * block the entire import even though its own route is perfectly valid — the
                 * predicates disagreeing one layer down. Both spots read vendorRoute or neither. */
                const key = vendorRoute(v, catByGl);
                if (!key || !validLeaves.has(key)) {
                    const glc = v.gl_category || '(Uncategorized)';
                    if (!orphans.has(glc)) orphans.set(glc, []);
                    orphans.get(glc).push(v.vendor_name);
                }
            }
            if (orphans.size > 0) {
                const detail = [...orphans.entries()]
                    .map(([glc, names]) => `"${glc}" (${names.length} vendor${names.length === 1 ? '' : 's'}: ` +
                        `${names.slice(0, 4).join(', ')}${names.length > 4 ? `, +${names.length - 4} more` : ''})`)
                    .join('; ');
                setStep6ImportError(
                    `Import blocked — ${orphans.size} GL categor${orphans.size === 1 ? 'y is' : 'ies are'} not mapped to a real income-statement line: ` +
                    `${detail}. These vendors would be attached to a line that does not exist, and their spend would ` +
                    `silently book $0. Go back to the category mapping step and pick a line for each — or mark those ` +
                    `vendors Skip.`
                );
                setStep6Importing(false);
                return;
            }

            /* W-002 -- THE VENDOR-BOOKABLE GUARD (2026-09-24). The orphan test above passes any line that has a
             * gl_line_mappings row with a role -- and payroll and D&A rows have one. A vendor routed there imported
             * and then booked $0: computeVendor skips it, and must (the payroll/vendor merge ASSIGNS, so a
             * vendor-owned payroll line would overwrite the payroll engine). This tests each vendor's FINAL routed
             * line -- vendorRoute: the 6c key, a hand-set line, or the STORED fallback -- against the ONE rule
             * (engines/lineRoles.js vendorLineBlockReason) and blocks the whole import, naming every vendor.
             * Brian 2026-09-24: block, never a partial import. The server refuses the same payload as the backstop. */
            const roleByLine = new Map();
            (step6Glm || []).forEach(g => {
                if (g.statement_type !== 'IS') return;
                const k = leafKeyFor(g);
                if (k && (!roleByLine.has(k) || g.role)) roleByLine.set(k, g.role);
            });
            const unbookable = [];
            for (const v of step6Vendors) {
                if (v.forecast_type === 'skip') continue;
                const key = vendorRoute(v, catByGl);
                const why = vendorLineBlockReason(key, roleByLine.has(key) ? roleByLine.get(key) : undefined);
                if (why) unbookable.push(`"${v.vendor_name || '(unnamed)'}" -> ${key} (${VENDOR_LINE_BLOCK_MESSAGE[why]})`);
            }
            if (unbookable.length > 0) {
                setStep6ImportError(
                    `Import blocked \u2014 ${unbookable.length} vendor${unbookable.length === 1 ? ' points' : 's point'} at a line the vendor forecast does not book: ` +
                    `${unbookable.join('; ')}. Their spend would silently book $0. Go back to the category mapping step and ` +
                    `pick a line for each \u2014 or mark those vendors Skip.`
                );
                setStep6Importing(false);
                return;
            }

            /* ★ C1b — REFUSE THE WRITE rather than guess a start date.
             * `vendors.start_date` is engine-consumed (the 5a start gate), so an invented
             * anchor silently forecasts $0 for every vendor written under it. This is the
             * write-path analog of assertVendorRouted: a guess is worse than a refusal.
             * Independent of the Step-6 render gate on purpose — a write path never relies
             * on a render guard to have run. */
            const firstForecast = firstForecastMonth(obState);
            if (!firstForecast) {
                setStep6ImportError(`Import blocked — ${NO_FORECAST_START_MSG}`);
                setStep6Importing(false);
                return;
            }

            /* ★ FIX 0 — the forecast WINDOW. Same derivation the Step-5 employee import
             * uses (`companies.forecast_through` + the ONE month builder, :2559-2560);
             * `forecast_through` lives on `companies`, not onboarding_state (Build 1b).
             *
             * BLOCKED, not degraded, when the window is empty. buildAllMonths returns []
             * on a null horizon (honest-empty), and an empty window would make the
             * synthesis below a silent no-op — i.e. exactly the $0-forecast bug this
             * build exists to remove, re-created one level up. Step 1 writes close_month
             * and forecast_through in ONE transaction (c9d8352), so a company that can
             * reach Step 6 has both; this fails SAFE if that invariant ever breaks.
             *
             * ★★ H1 — AND IT IS NOW READ FROM ITS OWNER. This line was
             * `companies.find(c => c.id === selectedCompanyId)?.forecast_through` — the
             * client's in-memory array, which NOTHING refreshes after Step 1 writes the
             * column (`loadCompanies` runs on mount and on the two "← Back to Companies"
             * buttons, and on no other path). The block below is what made the Step-6 half
             * of the defect LOUD; Step 5 had no such block and wrote an empty grid in
             * silence. Both now read the server through the ONE owner, `readForecastThrough`.
             * ⛔ THE AWAITED RETURN VALUE, not `horizonFt` — a write path may not depend on a
             *   render having happened. See the fuller note at the state declaration. */
            const forecastThrough = await readForecastThrough(selectedCompanyId);
            const forecastMonths = buildAllMonths(firstForecast, forecastThrough);
            if (!forecastMonths.length) {
                setStep6ImportError(
                    'Import blocked — this company has no forecast horizon, so vendor forecasts ' +
                    'cannot be generated. Set Forecast Through in Step 1 (Company Setup), then ' +
                    'return to this step.'
                );
                setStep6Importing(false);
                return;
            }

            /* ★ 7a — the seam, from the ONE shared classifier (module scope, shared with the
             * wizard-save fold). Also handed to the prepaid loop below, which used to read
             * obState again. */
            const closeMonth = obState?.close_month || null;
            const isForecastMonth = (m) => isForecastMonthFor(m, closeMonth, forecastMonths);

            // Build vendors payload — skip any marked 'skip'.
            const validForecastTypes = new Set(['constant', 'prepaid', 'pct_revenue', 'per_seat', 'one_off']);
            const vendorsPayload = [];
            for (let vi = 0; vi < step6Vendors.length; vi++) {
                const v = step6Vendors[vi];
                if (v.forecast_type === 'skip') continue;
                /* ★ THE MOVER. The sheet's category wins for every vendor EXCEPT one a human
                 * hand-edited on the Dashboard, which keeps the line they set. Same helper the
                 * guard above and the editor use, so a row that passed the guard is the row
                 * that gets written — still guaranteed valid, now for the right reason. */
                const is_line = vendorRoute(v, catByGl);   // guaranteed valid by the guard above
                if (vendorRoutePreserved(v, catByGl)) preservedRoutes.push(v.vendor_name || '(unnamed)');
                // Fallback: unknown forecast_type → treat as constant.
                const ft = validForecastTypes.has(v.forecast_type) ? v.forecast_type : 'constant';
                // Batch 1b (L3613): a row LOADED from the database (re-entry) whose type is unchanged keeps its stored values
                // as stored; a freshly parsed row (no _dbId) or a type change takes the rules below exactly as before.
                const isLoaded = v._dbId != null;
                const keepStored = isLoaded && v._loadedType === ft;
                /* ★ 4a — THE COERCION IS GONE. This read
                 *     ft === 'prepaid' ? (v.billing_frequency || 'quarterly') : 'monthly'
                 * so EVERY non-prepaid vendor was forced monthly no matter what the row
                 * said — which is why a quarterly board fee could be set and then stored
                 * as monthly, and why the forecast synthesis below booked it level.
                 * Monthly is now the DEFAULT when the row carries nothing, not an
                 * override. PREPAID IS BYTE-UNCHANGED (same expression, same fallback). */
                const bf = ft === 'prepaid' ? (v.billing_frequency || 'quarterly') : (v.billing_frequency || 'monthly');
                // V1: NEST the historical actuals under this vendor (non-zero months only)
                // so the server keys them by the vendor's RETURNING id — no name re-join, so
                // duplicate vendor names (Misc ×9, Regus ×2, …) cannot collide. ★ DISTINCT
                // from `monthly_amount` below (the forecast run-rate scalar) — this is the
                // historical monthly series.
                const monthly_actuals = {};
                for (const [m, amt] of Object.entries(v.monthly_amounts || {})) {
                    if ((amt || 0) === 0 && !isLoaded) continue;   // Batch 1b: a loaded row's stored 0 rows are written back too
                    monthly_actuals[m] = Number(amt) || 0;
                }

                /* ★★ FIX 0 — THE IMPORT NOW WRITES THE FORECAST TOO.
                 *
                 * It never did. The 6d grid writes `vendors.monthly_amount`, and NO ENGINE
                 * READS THAT COLUMN: computeVendor.js:208 sums `monthlyData[m]` — i.e. the
                 * stored vendor_monthly_actuals rows — for every type except pct_revenue and
                 * per_seat. So every vendor this import created forecast $0, silently, on
                 * every tenant, for the life of the step. MEASURED 2026-08-03: cid=8 (Step-6
                 * imported) had ZERO forecast vmas rows; cid=3's 175 constant vendors carry
                 * $1,489,005.71 of them — entered through the Vendor Dashboard's bulk
                 * Forecast Wizard, never through onboarding.
                 *
                 * ★ SHAPE = Build 4c (`fillForecastPrepaidAmort`): MAKE THE IMPORT CALL THE
                 * ENGINE IT SKIPPED. `buildVendorForecastMd` is the ONE synthesizer, already
                 * used by the Vendor Modal (VendorDashboard.js:586) and the bulk Forecast
                 * Wizard (:2010). It is called here UNCHANGED — no copy, no second generator.
                 *
                 * ★ WHY `actualsMonths = []`: the historical half is built directly above,
                 * from the PARSED series, and must stay byte-identical — the workbook carries
                 * months outside the model window (cid=3 prepaid starts 2020-07 against a
                 * 2021-01 historical start), and letting the helper's historical-preserve
                 * loop own them would silently DROP those cells. An empty actualsMonths makes
                 * that loop a no-op, so the returned object holds ONLY the synthesized
                 * forecast months. `monthlyData` is still passed because
                 * `deriveAnchorCalMonth` reads it to place a non-monthly billing anchor.
                 *
                 * ★ PREPAID (footgun C) IS PROTECTED BY THE HELPER'S OWN TYPE GATE
                 * (vendorForecast.js:136 — synthesis runs only for `constant`). A prepaid
                 * vendor therefore receives NO forecast rows at all, which is REQUIRED: its
                 * expense comes entirely from prepaid_contracts via runFullModel §12b, and
                 * that booking is gated on the vendor having EMPTY forecast monthlyData
                 * (the Axis-2 anti-double-book gate, runFullModel.js:602). Writing a
                 * zero-fill here — the way persistVendor:1941-1945 does — would be harmless
                 * numerically but would put the suppression trigger one edit away. We do NOT
                 * zero-fill. Reading the gate from the ONE gated helper rather than
                 * re-testing the type here is the whole point: there is no second copy of
                 * the rule that protects prepaid.
                 *
                 * ★ The synthesis reads the values THIS PAYLOAD PERSISTS (ft / bf /
                 * persistedAmount / persistedStart), not the raw grid row, so the vendor
                 * config row and its vmas cannot disagree — the payload coerces
                 * billing_frequency to 'monthly' for every non-prepaid type and zeroes the
                 * amount for non-constant/prepaid types, and the forecast must follow that.
                 */
                const persistedAmount = (ft === 'constant' || ft === 'prepaid') ? (Number(v.monthly_amount) || 0) : 0;
                const persistedStart = v.start_date || firstForecast;   // non-null: blocked above
                const forecastMd = buildVendorForecastMd(
                    {
                        type: ft,
                        monthly_amount: persistedAmount,
                        billing_frequency: bf,
                        /* ★ THE OPERATOR'S BOOKING MONTH. Absent here while the wizard's own
                         * preview call (:7047) passed it — two calls of the ONE synthesizer
                         * with different arguments, which is how a single source of truth
                         * still produced two answers: the grid showed October and the save
                         * wrote April. With no anchor, synthesizeForecastVmas re-derives one
                         * from history and falls back to the first forecast month, so a
                         * non-monthly constant's cadence was right and its PHASE was not.
                         * `readAnchor` (vendorForecast.js:38) accepts either spelling; the
                         * camel key makes this call character-identical to the fold's.
                         * ★ Reaches ONLY non-monthly constants — buildVendorObj sets an
                         * anchor solely for those (ForecastWizard:452) and the fold clears it
                         * on any re-save (`o.billingAnchor || null`), which is what keeps it
                         * off a MONTHLY row, where an anchor would suppress every month
                         * before it (measured: 6 of 9 booked months lost). */
                        billingAnchor: v.billing_anchor || undefined,
                        start_date: persistedStart,
                        /* ★ THE ROW'S REAL SERVICE WINDOW, not a hardcode. This read
                         * `'until_canceled'` while the payload below persisted the row's
                         * actual `end_date` (4a) — so a vendor with a service window was
                         * STORED as ending and SYNTHESIZED as perpetual, and its forecast
                         * cells ran past its end. Inert on the IS (computeVendor's
                         * `isVendorActiveInMonth` gates the booking at :208) but wrong in
                         * storage, and wrong in the wizard preview now that the fold shows
                         * the same series. `synthesizeForecastVmas` writes an explicit 0 for
                         * an out-of-range month (vendorForecast.js:98,120) — the months are
                         * still emitted, they just stop carrying the amount. Same default as
                         * the payload so a row with no end date is byte-unchanged. */
                        end_date: v.end_date || 'until_canceled',
                        renewals: v.renewals || [],   // Batch 1b: the stored price changes (none on a freshly parsed row)
                        monthlyData: monthly_actuals,
                    },
                    [],                 // historical half owned by the loop above — see comment
                    forecastMonths,
                    { startFallback: firstForecast }
                );
                for (const [m, amt] of Object.entries(forecastMd)) {
                    monthly_actuals[m] = r2(Number(amt) || 0);
                }

                /* ★★ 7a — DROP FORECAST CELLS THIS ROW'S TYPE CANNOT OWN.
                 *
                 * THE CARRY: the copy loop above takes every non-zero month of
                 * `v.monthly_amounts`, and in EDIT MODE that series comes from the DB
                 * (loadStep6ForEdit :5544; the GET applies no month filter,
                 * server.js:3102-3107) — so it INCLUDES forecast rows a previous import
                 * wrote. `buildVendorForecastMd` then overwrites the forecast window ONLY
                 * for `constant` (its type gate, vendorForecast.js:136), so a row switched
                 * constant → anything-else KEEPS the old constant cells and re-persists them.
                 *
                 * WHY IT MATTERS, worst first:
                 *   prepaid  — the stale cells book as expense (computeVendor.js:208) AND
                 *              trip the Axis-2 gate (runFullModel.js:602), which suppresses
                 *              EVERY contract linked to that vendor. `.some(… !== 0)`: one
                 *              stale cent is enough. Money booked wrong AND money missing.
                 *   one_off  — the stale cells book straight through (:208): the old run-rate
                 *              keeps charging after the operator chose "manual".
                 *   pct_revenue / per_seat — IS-inert (the engine recomputes), but they still
                 *              feed the contractor-headcount pass (:120-128, `spend > 0`).
                 *
                 * ★ RULE 1 (unconditional) — engine-derived types have no legitimate stored
                 *   forecast. Closes the prepaid case permanently, including a stale row
                 *   nobody switched. Measured target: cid=3's prepaid and pct_revenue rows
                 *   carry only ZEROS (18 + 36 rows, 0 non-zero), and absent ≡ 0 to every
                 *   reader (`monthlyData[m] || 0`, and the gate's `!== 0`).
                 * ★ RULE 2 (type change only) — one_off is NOT in the set, because a one_off
                 *   vendor's forecast cells are its per-month MANUAL AMOUNTS and are real:
                 *   cid=3's Grant Thornton carries $96,000.00 across 2023-04..12. The naive
                 *   "ft !== constant → drop" guard would DELETE that. Cells authored under
                 *   one_off are kept; cells inherited from a DIFFERENT prior type are stale.
                 * ★ BOTH RULES BOUND ON THE FORECAST SIDE ONLY — historical actuals are
                 *   authoritative for m <= cutoff and are never touched, for any type. */
                if (inheritedForecastIsStale(ft, v._loadedType)) {
                    for (const m of Object.keys(monthly_actuals)) {
                        // Batch 1b: a stored 0 cell on a loaded, type-unchanged row is kept (inert -- absent == 0); a non-zero one goes
                        if (isForecastMonth(m) && !(keepStored && monthly_actuals[m] === 0)) delete monthly_actuals[m];
                    }
                }

                vendorsPayload.push({
                    name: v.vendor_name,
                    is_line,
                    // preserves Excel parse order across re-imports; a loaded row keeps its stored value (Batch 1b)
                    sort_order: (isLoaded && Number.isFinite(Number(v.sort_order))) ? Number(v.sort_order) : vi,
                    forecast_type: ft,
                    billing_frequency: bf,
                    // For constant: monthly run-rate. For prepaid: per-cycle payment. For others: 0 (a loaded, type-unchanged row: as stored).
                    monthly_amount: keepStored ? (Number(v.monthly_amount) || 0) : persistedAmount,
                    // For pct_revenue: decimal rate (0.09 = 9%).
                    pct_rate: (ft === 'pct_revenue' || keepStored) ? (Number(v.pct_rate) || 0) : 0,
                    /* ★ 4a: the % SOURCE LINE. Absent from this payload AND from the import
                     * endpoint's INSERT column list, while the per-vendor CRUD endpoints
                     * (server.js:411-426) have always written it — so a source set on the
                     * Dashboard was silently reset to NULL by the next import. NULL means
                     * "total revenue" to computeVendor.js:148-151, which for a sourceless %
                     * vendor books rate x TOTAL REVENUE. Mirrors the CRUD writer's shape
                     * exactly (`|| null`) rather than inventing a second one.
                     * ★ THE UNSAFE STATE IS NOT CLOSED HERE — making the column persistable
                     * is 4a; making a sourceless % vendor unreachable in the editor is 4b.
                     * Today nothing in the wizard can set one. */
                    pct_revenue_source: (ft === 'pct_revenue' || keepStored) ? (v.pct_revenue_source || null) : null,
                    notes: isLoaded ? (v._storedNotes || '') : '',
                    is_active: isLoaded ? v._storedIsActive !== false : true,
                    start_date: persistedStart,
                    /* ★ 4a: carry the row's own end date. Was hardcoded 'until_canceled',
                     * so a service-window end set in an editor was discarded. Same default
                     * when unset. `end_date` was ALREADY a column on the import INSERT —
                     * only the client was dropping it. */
                    end_date: v.end_date || 'until_canceled',
                    amort_start: isLoaded ? (v._storedAmortStart || '') : '',
                    renewals: v.renewals || [],
                    is_contractor: !!v.is_contractor,
                    // default 1; user can adjust in Vendor Dashboard post-go-live (a loaded row keeps its stored value -- Batch 1b)
                    contractor_count: (isLoaded && Number(v._storedContractorCount) > 0) ? Number(v._storedContractorCount) : 1,
                    contractor_monthly: isLoaded ? (v._storedContractorMonthly || {}) : {},
                    // Per-seat fields only meaningful when forecast_type = 'per_seat'.
                    per_seat_cost: (ft === 'per_seat' || keepStored) ? (Number(v.per_seat_cost) || 0) : 0,
                    seat_basis: (ft === 'per_seat' || keepStored) ? (v.seat_basis || 'fte') : 'fte',
                    /* ★ 4a: carry the chosen seat departments. Was hardcoded [], which for
                     * a dept-scoped per_seat vendor means COMPANY-WIDE headcount — the
                     * item-7 class (a per-seat vendor billing the wrong heads, silently).
                     * Gated on the type exactly like per_seat_cost/seat_basis above, so a
                     * non-per_seat row still stores []. Already an INSERT column; the
                     * client was dropping it. */
                    seat_departments: (ft === 'per_seat' || keepStored) ? (v.seat_departments || []) : [],
                    /* Batch 2 (2026-11-03, item 11): a LOADED row names its stored vendor and whether it carries the hand-edit mark
                     * (is_line_edited_at); the import writes the STORED mark back when the line is unchanged (server.js editedAtFor). */
                    db_id: isLoaded ? v._dbId : null,
                    is_line_edited_at: isLoaded ? (v.is_line_edited_at || null) : null,
                    monthly_actuals,
                });
            }

            // 5d-2: length_months is now nullable in the DB (indefinite contracts),
            // but onboarding import has no indefinite-entry path — that lands in 5d-4
            // (VendorDashboard PrepaidTab). Here a 0/blank length is still a data
            // error, so we keep requiring a valid positive length and skip otherwise.
            // Guard vendor_name + start_month + length_months; prepaid_amount
            // defaults to 0 when missing.
            // sort_order = index in step6Prepaid preserves Prepaid Sch tab order
            // across round-trips (skipped rows leave gaps — harmless; ORDER BY
            // still sorts correctly).
            // V1.1: resolve each prepaid contract's is_line CLIENT-SIDE via the SAME
            // resolver the engine uses (the WALL: the resolver is client-side, a server
            // copy is forbidden), so the server can set prepaid_contracts.vendor_id by
            // (vendor_name, is_line). Labels come from the tenant's own glMappings
            // (step6Glm) \u2014 the same source ISLinePicker/buildISLineTaxonomy uses.
            /* ★ CORRECTED (prepaid slug fix, 2026-09-02). This used to read "the resolved
             * line is SENT and USED, never PERSISTED (pc.is_line stays as-is so AE Model 2's
             * prepaid shape matches cid=3's — no manufactured divergence)". IT IS NOW
             * PERSISTED, deliberately: the server names is_line in the Step-6 INSERT.
             * WHY THE OLD REASONING NO LONGER HOLDS: routing by the account LABEL was
             * measured to be a MONEY bug — renaming a GL line severed the join and the
             * forecast amortization stopped booking (+29,870.61 on cid=36). Every contract
             * that resolves is now stamped with its slug, so cid=8 WILL carry is_line where
             * cid=3 mostly does not. That is not a manufactured divergence to avoid; it is
             * the correct state, and the backfill gives cid=3 the same. */
            // W-068: the ONE label map (prepaidLabelToLineFor) -- the upload check's B8 reads the same one.
            const _prepaidLabelToLine = prepaidLabelToLineFor(step6Glm);
            // Build 4c: fill the FORECAST prepaid amort for straight-line contracts whose forward
            // cells the (truncated) workbook lacks \u2014 CLIENT-computes via the shared clean-schedule
            // generator; the server persists p.amort verbatim (the wall). Historical cells pass
            // through untouched. `cutoff` = close_month; `siblings` = all parsed contracts (for the
            // sibling lag carry). Collect NON-BLOCKING notices: irregular histories (generate
            // nothing \u2192 operator sets the schedule) + true conferences with no event booking in the
            // template (operator sets the Event Month in the Prepaid tab).
            const _cutoff = closeMonth;   // ★ 7a: ONE derivation of the seam, shared (was a second obState read)
            const _ppIrregular = [];
            const _ppConferences = [];
            const _ppLagAmbiguous = [];
            const prepaidPayload = [];
            const _payloadStagedIdx = [];   // 2026-10-29: _payloadStagedIdx[j] = the step6Prepaid index prepaidPayload[j] came from
            for (let pi = 0; pi < step6Prepaid.length; pi++) {
                const p = step6Prepaid[pi];
                const lenNum = Number(p.length_months);
                const hasValidLength = Number.isFinite(lenNum) && lenNum > 0;
                const hasStartMonth = p.start_month && String(p.start_month).trim() !== '';
                if (!p.vendor_name || !hasStartMonth || !hasValidLength) {
                    // eslint-disable-next-line no-console
                    console.log('[Step6Import] skipping prepaid contract \u2014 missing required fields:', p);
                    continue;
                }
                const amt = Number(p.prepaid_amount);
                const parsedAmort = p.amort || {};
                // 4c generator (clean-schedule). Skipped safely when close_month is unknown.
                let outAmort = parsedAmort;
                if (_cutoff) {
                    const fill = fillForecastPrepaidAmort(
                        { vendor_name: p.vendor_name, account: p.account, conference_name: p.conference_name, event_identifier: p.event_identifier,
                          start_month: p.start_month, length_months: lenNum, prepaid_amount: amt, amort: parsedAmort },
                        _cutoff, step6Prepaid);
                    outAmort = fill.amort;
                    if (fill.irregular) _ppIrregular.push({ vendor_name: p.vendor_name, account: p.account || null, start_month: p.start_month });
                    // conference with NO booking in the (truncated) template \u2192 its event month is
                    // unrepresented \u2192 the operator must set it (the 6-of-cid3 class).
                    else if (fill.event && Object.keys(parsedAmort).length === 0) {
                        _ppConferences.push({ name: p.conference_name || p.vendor_name, amount: Number.isFinite(amt) ? amt : 0, paid: p.start_month });
                    }
                    // fresh renewal whose predecessor lag is ambiguous (siblings disagree) \u2192 generated
                    // at lag 0 (sane default) but FLAGGED \u2014 the operator sets the amort start.
                    else if (fill.lagAmbiguous) {
                        _ppLagAmbiguous.push({ vendor_name: p.vendor_name, account: p.account || null, start_month: p.start_month, sibLags: fill.lagAmbiguous });
                    }
                }
                // account \u2192 is_line via the shared resolver (slug-first trusts p.is_line if present).
                // 2026-10-13: through the row owner -- a row that names its section never falls back to the name alone.
                const { lineKey: resolvedLine } = resolvePrepaidRowLine(
                    { account: p.account, is_line: p.is_line, is_category: p.is_category }, _prepaidLabelToLine);
                _payloadStagedIdx.push(pi);
                prepaidPayload.push({
                    vendor_name: p.vendor_name,
                    account: p.account || null,
                    resolved_is_line: resolvedLine || null, // the slug: the server matches on it AND now STORES it (see above)
                    start_month: p.start_month,
                    length_months: Math.round(lenNum),
                    prepaid_amount: Number.isFinite(amt) ? amt : 0,
                    amort: outAmort,
                    /* ★ 3a — PERSIST THE LAG THAT DESCRIBES THE SCHEDULE WE ARE WRITING.
                     * The import never wrote this column, so it took its DEFAULT 0 while the
                     * amort dict carried a real offset: measured, cid=8 has 0 on all 170
                     * contracts where cid=3 has 68 non-zero. Inert to the engine (nothing
                     * reads it), but PrepaidModal's regenerate is
                     * buildCycleAmort(addMonths(start, lag), …) — so an editor over lag=0 data
                     * would silently shift those 68 schedules by 1-13 months. Derived FROM
                     * `outAmort` (the exact dict on the line above), so the column and the
                     * schedule cannot disagree. */
                    amort_lag_months: deriveAmortLag({
                        vendor_name: p.vendor_name, conference_name: p.conference_name,
                        event_identifier: p.event_identifier,
                        start_month: p.start_month, length_months: lenNum,
                        prepaid_amount: amt, amort: outAmort,
                    }),
                    /* ★ ITEM 4 (§12.5 P-1) — SEND THE EVENT-BOOKING MARKER.
                     * `step6PrepaidFromModal` already stores it on the wizard row (:8489), and
                     * `step6PrepaidToModal` already reads it back (:8473) — so the editor round
                     * trip worked IN MEMORY and the value died at the payload boundary, which is
                     * the §12.5 PAYLOAD-BOUNDARY OMISSION CLASS exactly: the editor computes it,
                     * the row carries it, and the hand-written object literal never named it.
                     * ⓘ A PARSED row has no such field (parseVendorExcel emits no event_month),
                     *   so a fresh upload sends null — correct: the workbook cannot express an
                     *   event booking, only the operator can.
                     * ⛔ `event_identifier` was NOT sent until Batch 2 (2026-11-03, item 1) -- it is sent below now; the import
                     *   DELETEs the whole set first, so the key only decides which rows OF THIS PAYLOAD are one contract (see the
                     *   server INSERT's note). */
                    event_month: p.event_month || null,
                    // Batch 2 (2026-11-03, item 1): the event identifier the prepaid editor set (a parsed row has none); the import stores it
                    event_identifier: p.event_identifier || null,
                    is_historical: p.is_historical !== false,
                    sort_order: pi,
                    // prepaid renewals part 1 (2026-10-29): the Renews answer + the payload keys the import maps to renewal_of_id
                    renews: p.renews === true ? true : p.renews === false ? false : null,
                    client_key: p.client_key ?? null,
                    renewal_of_key: p.renewal_of_key ?? null,
                });
            }
            // Stash the non-blocking notices (rendered after the import succeeds).
            setStep6PrepaidNotice((_ppIrregular.length || _ppConferences.length || _ppLagAmbiguous.length)
                ? { irregular: _ppIrregular, conferences: _ppConferences, lagAmbiguous: _ppLagAmbiguous } : null);

            /* ═══ ITEM 2 — RECONCILE AGAINST THE STORED CONTRACTS BEFORE POSTING ═══
             *
             * THE DEFECT: the server DELETEs every prepaid_contracts row for this company
             * (server.js:3116) and re-INSERTs from this payload — and the draft gate is
             * BELOW that DELETE (server.js:3271), so all four "Save Progress" buttons do
             * it too. When `step6Prepaid` came from a FRESH 6a PARSE, every schedule the
             * operator entered in the app that the workbook does not carry is destroyed:
             * MEASURED 15 rows / $76,957.53 on cid=3 and 9 rows / $71,957.52 on
             * cid=8 / 36 / 40, of which the six Conferences event bookings are $61,645.00.
             *
             * ★ THIS RUNS UNCONDITIONALLY, ON BOTH PRODUCERS, AND THAT IS THE POINT.
             * There is no "was this a fresh upload?" flag to key on — and there must not
             * be one. On the EDIT producer (`loadStep6ForEdit`) the reconcile is a
             * MEASURED no-op: 0 affected rows on all four tenants, because that path
             * already round-trips the stored schedules. So the gate is silent on the
             * ordinary re-entry checkpoint and speaks only when a real decision exists.
             * A producer flag would be a second source of truth for "is this safe", and
             * the measurement says the condition itself already answers it.
             *
             * ★ THE READ-BACK IS THE ONE THE EDIT PATH ALREADY USES — `getPrepaidContracts`
             * through the shared `step6PrepaidFromGetRow`. Same API, same shape, one map.
             * ⓘ `.catch` → []: a failed read yields NO affected rows, i.e. today's
             *   behaviour. That is deliberate and it is the honest direction: a network
             *   failure must not manufacture a block on rows we cannot see. It is also
             *   the §12.5 "X || <empty> turns 'could not load X' into 'X is clean'" shape,
             *   so it is named here rather than left implicit — the alternative (block on
             *   a failed GET) would make an unrelated outage look like data loss.
             */
            let _prepaidPayloadOut = prepaidPayload;
            if (_cutoff) {
                const _storedRes = await getPrepaidContracts(selectedCompanyId).catch(() => ({ data: [] }));
                /* Prepaid renewals part 2: a stored GENERATED RENEWAL (it names its predecessor) is owned by the renewal planner -- an
                 * edit or a Renews answer on this screen regenerates or removes it on purpose -- so it is not a schedule this gate
                 * protects (its "Keep" re-adds a stored row, which would bring back a renewal the reconcile removed). No legacy
                 * tenant stores one, so the gate is unchanged for every existing contract. */
                const _stored = (_storedRes.data || []).map(step6PrepaidFromGetRow).filter(st => st.renewal_of_key == null);
                const _rec = reconcilePrepaidImport(prepaidPayload, _stored, _cutoff);
                if (_rec.affected.length) {
                    /* BLOCK until THIS SET is acknowledged. Acknowledging the fingerprint
                     * rather than a boolean means an unchanged set stays quiet across the
                     * remaining Save-Progress clicks, and a CHANGED set re-asks. */
                    if (_rec.fingerprint !== step6ClobberAck) {
                        setStep6Clobber(_rec);
                        setStep6Importing(false);
                        return;                       // nothing is posted; the DB is untouched
                    }
                    if (step6ClobberChoice === 'keep') {
                        /* KEEP — the stored FORECAST half wins on each affected row and the
                         * orphans are re-added, so the server's DELETE + re-INSERT rebuilds
                         * them. IMPORT-MUST-REPLACE is untouched: the table is still fully
                         * rebuilt from a payload; the operator decided what it says. */
                        _prepaidPayloadOut = applyPrepaidKeep(prepaidPayload, _rec.affected, _cutoff, {
                            // TRANSIENT server-side vendor-match hint only, resolved from the
                            // account LABEL exactly as the edit path does (the shared GET map
                            // carries `is_line` since 2026-10-29, so the stored line goes first; before that it did not — §12.5 P-2).
                            resolveIsLine: (st) => resolvePrepaidContractLine(
                                { account: st.account, is_line: st.is_line || undefined }, _prepaidLabelToLine).lineKey,   // 2026-10-29: the stored line, slug-first
                        });
                    }
                    // REPLACE — send `prepaidPayload` untouched: the workbook wins, which is
                    // exactly today's behaviour. That arm is the A/B's control.
                }
                setStep6Clobber(null);
            }

            const importRes = await importOnboardingVendors(selectedCompanyId, {
                vendors: vendorsPayload,   // actuals are NESTED per vendor (v.monthly_actuals)
                prepaid_contracts: _prepaidPayloadOut,
                draft,                     // true ⇒ server skips the Step-6 completion write
            });
            // V1.1: surface prepaid contracts on a DUPLICATE vendor name that could not be
            // pinned to one vendor \u2014 a READER (5d logged and nobody read it). Block advance
            // so the operator fixes the GL/category mapping and re-imports.
            /* Prepaid renewals part 2 (U4): a contract the import left with NO vendor (no vendor of that name) books its forecast
             * cost nowhere -- P27 blocks go-live -- so the response names it and Step 6 says so (non-blocking: it is linked on the
             * Vendor Dashboard after Step 8). A renewal link the import could not resolve is named too. */
            /* 2026-10-29 (the Step-6 line loss, L4): THE STAGED ROWS TAKE THE IDS THE SAVE GAVE THEM. The import DELETEs and
             * re-INSERTs every contract, so after a Save Progress the rows on this screen still carry the PREVIOUS ids as their
             * client_key ("id:<old>") while the database holds new ones. The clobber gate recognises the operator's own row by
             * that key (reconcilePrepaidImport), so without this an edit made after one Save Progress and saved by the next was
             * named "not in this workbook -- the whole contract would be dropped", and Keep posted the old row beside the edited
             * one. The server returns prepaidIds[j] for payload row j (the first prepaidPayload.length rows are this screen's,
             * in order; a Keep-rescued row follows them and is not on the screen). A row is re-keyed only when it is still the row
             * that was sent (same client_key); renewal_of_key and the flagged list follow the same map. An older server sends no
             * prepaidIds -> nothing is re-keyed (the behaviour before this fix). */
            let _prepaidStampRows = step6Prepaid;   // Batch 1 (2026-11-03, L3612): the rows the saved baseline is stamped from (re-keyed below)
            const _savedIds = importRes?.data?.prepaidIds;
            if (Array.isArray(_savedIds) && _savedIds.length === _prepaidPayloadOut.length) {
                const _newKeyAt = new Map(), _reKey = new Map();
                for (let j = 0; j < prepaidPayload.length; j++) {
                    if (_savedIds[j] == null) continue;
                    _newKeyAt.set(_payloadStagedIdx[j], { was: prepaidPayload[j].client_key ?? null, key: 'id:' + _savedIds[j] });
                    if (prepaidPayload[j].client_key != null) _reKey.set(prepaidPayload[j].client_key, 'id:' + _savedIds[j]);
                }
                const _rekeyRows = rows => rows.map((r, i) => {
                    const nk = _newKeyAt.get(i);
                    const out = nk && (r.client_key ?? null) === nk.was ? { ...r, client_key: nk.key } : r;
                    return out.renewal_of_key != null && _reKey.has(out.renewal_of_key) ? { ...out, renewal_of_key: _reKey.get(out.renewal_of_key) } : out;
                });
                _prepaidStampRows = _rekeyRows(step6Prepaid);   // renewal_of_key is in the fingerprint: stamping the pre-re-key rows made every save read "unsaved"
                setStep6Prepaid(_rekeyRows);
                setStep6RenewFlagged(prev => prev.map(f => (_reKey.has(f.ref) ? { ...f, ref: _reKey.get(f.ref) } : f)));
            }
            const _unlinked = importRes?.data?.unlinkedPrepaid || [];
            const _unresolvedLinks = importRes?.data?.unresolvedRenewalLinks || [];
            setStep6UnlinkedNotice((_unlinked.length || _unresolvedLinks.length) ? { unlinked: _unlinked, unresolvedLinks: _unresolvedLinks } : null);
            const _unresolved = importRes?.data?.unresolvedPrepaid || [];
            if (_unresolved.length) {
                setStep6ImportError(
                    `Saved, but ${_unresolved.length} prepaid contract(s) on a duplicate vendor name could not be linked to a unique vendor: ` +
                    _unresolved.map(u => `"${u.vendor_name}" (${u.account || '\u2014'}, ${u.reason})`).join('; ') +
                    `. They are saved with no vendor link, so their forecast cost may book to no line (the pre-flight names it before go-live). Give each of these vendors its own IS line in the Categories sub-step (or rename one of them) and save again \u2014 or link each contract to its vendor in the prepaid editor on the Vendor Dashboard after onboarding.`
                );
                setStep6Importing(false);
                return;
            }

            /* ★ DRAFT: no navigation at all. The operator is mid-entry on 6d; the whole
             * point is that they keep their place. Nothing is marked complete, the cursor
             * does not move, and we do NOT bounce to the summary — just acknowledge. */
            /* ★ THE PRESERVED-ROUTE NOTICE, set on the SUCCESS tail so it is common to the
             * draft and non-draft paths alike. Informational: the import already happened and
             * nothing is blocked. Empty list ⇒ stays null ⇒ renders nothing, so a normal
             * import is visually unchanged. */
            setStep6RouteNotice(preservedRoutes.length ? { names: preservedRoutes } : null);
            // ★ Persisted ⇒ the current grid IS the saved state, for BOTH paths.
            step6SavedSnapshot.current = step6ForecastFingerprint(step6Vendors);
            // C1 s2a — the prepaid rows were persisted by the SAME transaction, so they
            // are the saved state too. Both baselines re-stamp together or the guard lies.
            step6PrepaidSnapshot.current = step6PrepaidFingerprint(_prepaidStampRows);
            if (draft) {
                /* ★ THE STATUS LINE MUST BE TRUE IN EVERY MODE. A draft save changes no
                 * completion state, so the line differs only by what was ALREADY true.
                 * ★ KEYED ON THE STORED COMPLETION FLAG, NOT ON `step6EditMode`. This read
                 * `step6EditMode ? "…remains complete" : …` on the assumption that edit mode
                 * implies a completed step — false: the RESUME card enters edit mode too
                 * (renderStep6Resume's three links) on a tenant whose Step 6 is NOT complete, and that
                 * operator would have been told it was. `steps.vendor_setup.completed` is the
                 * authoritative signal (it is what loadStep6Data's own gate reads, :5383). */
                const _n = `${vendorsPayload.length} vendor${vendorsPayload.length === 1 ? '' : 's'}`;
                setStep6DraftSaved(obState?.steps?.vendor_setup?.completed
                    ? `Saved — ${_n}. Still editing; Step 6 remains complete.`
                    : `Saved — ${_n}. Step 6 is still in progress; nothing has been marked complete.`);
                /* ★★ SAVE-AND-CONTINUE — THE ONLY PLACE THIS CAN LIVE.
                 *
                 * Prepaid is a MIDDLE sub-step after Build 3's reorder and Forecast is the
                 * TERMINAL one, but the prepaid edit footer had no way forward: its primary
                 * ran the COMPLETING branch, which exits to the summary. From the RESUME card
                 * — which offers no Forecast link at all — that made the forecast editor
                 * UNREACHABLE, and completing from Prepaid marked `vendor_setup.completed`
                 * with the terminal screen never shown.
                 *
                 * ★ WHY HERE AND NOT IN THE COMPLETING BRANCH (measured, Phase 0 §2): that
                 * branch calls `loadWizardState` (:6472), which is not a fetch but a FULL
                 * WIZARD RESET — `setStep6Vendors([])`, `setStep6EditMode(false)`,
                 * `setStep6SubStep(null)` (:1785-1790) — and then `loadStep6Data`, whose gate
                 * re-routes unconditionally (:5385). An explicit destination set there is
                 * overwritten twice, once destructively: the operator would land on an EMPTY
                 * grid in ENTRY mode. The draft branch calls NEITHER, so a single
                 * setStep6SubStep survives, `step6EditMode` stays true, and `step6Vendors`
                 * keeps the config just entered AND the folded forecast series — which is
                 * richer than a reload, since loadStep6ForEdit does not read back end_date /
                 * pct_revenue_source / seat_departments (the 7b gap).
                 * ★ DRAFT IS ALSO THE RIGHT VERB: continuing mid-flow must not complete the
                 * step. The Forecast screen keeps sole ownership of that. */
                if (then) setStep6SubStep(then);
            } else if (step6EditMode) {
                // Edit mode: stay on Step 6, bounce back to summary card, refresh counts.
                setStep6EditMode(false);
                setStep6SubStep('re-entry');
                /* ★ 4b (stale-screen fix, part 3 of 3). This branch called loadStep6Data
                 * WITHOUT refreshing obState, so the gate inside it read a pre-import value:
                 * `steps.vendor_setup.completed` was still false and the screen routed to
                 * "Vendor setup in progress — not yet imported" until a hard refresh, even
                 * though the import had just succeeded.
                 * ★ THE FILED ONE-LINER ("call loadWizardState first") CANNOT WORK: this
                 * `loadStep6Data` reference belongs to the current render and closes over the
                 * OLD obState, so sequencing the two calls changes nothing. Phase 0 named
                 * three shapes that do; this is the explicit one — fetch, then HAND THE VALUE
                 * DOWN. The alternative (clear the sub-step and let the landing effect notice
                 * once obState updates) works too but depends on effect ordering, which is
                 * exactly the kind of thing that breaks silently later. */
                const freshOb = await loadWizardState(selectedCompanyId);
                await loadStep6Data(selectedCompanyId, freshOb);
            } else {
                await loadWizardState(selectedCompanyId);
                setViewingStep(7);
            }
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Unknown error';
            setStep6ImportError('Failed to import vendors: ' + msg);
            console.error(err);
        } finally { setStep6Importing(false); }
    };

    // ── Step 6 renderers ─────────────────────────────────────
    const step6SubStepIdx = STEP6_SUB_STEPS.findIndex(s => s.key === step6SubStep);
    const renderStep6SubIndicator = () => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 24, fontSize: 12, fontFamily: FONT }}>
            <span style={{ fontWeight: 600, color: '#1A1C2E' }}>Step 6: Vendor Setup</span>
            {STEP6_SUB_STEPS.map((s, i) => (
                <React.Fragment key={s.key}>
                    <span style={{ color: '#ccc', margin: '0 2px' }}>&rsaquo;</span>
                    <span style={{
                        fontWeight: step6SubStep === s.key ? 600 : 400,
                        color: step6SubStepIdx >= 0 && i < step6SubStepIdx ? '#27AE60' : step6SubStep === s.key ? '#1A73E8' : '#999',
                    }}>{s.label}</span>
                </React.Fragment>
            ))}
        </div>
    );

    // (The private Step6DropZone duplicate lived here. Deleted in upload-rework
    //  step 2 — Step 6 now consumes the generic, prop-driven DropZone that Step 2
    //  uses, which is what lets it render one zone or two.)

    const renderStep6ReEntry = () => {
        const statLabel = { fontSize: 11, color: '#6B7094', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, marginBottom: 4 };
        const statValue = { fontSize: 22, fontFamily: MONO, fontWeight: 600, color: '#1A1C2E' };
        return (
            <div>
                {renderStep6SubIndicator()}
                <div style={{ padding: 24, background: '#F0F6FF', borderRadius: 8, marginBottom: 20 }}>
                    <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 18, margin: '0 0 16px' }}>Vendors already imported</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 16 }}>
                        <div>
                            <div style={statLabel}>Total vendors</div>
                            <div style={statValue}>{step6ExistingCount}</div>
                        </div>
                        <div>
                            <div style={statLabel}>Active</div>
                            <div style={statValue}>{step6ExistingActiveCount}</div>
                        </div>
                        <div>
                            <div style={statLabel}>Contractors</div>
                            <div style={statValue}>{step6ExistingContractorCount}</div>
                        </div>
                    </div>
                    <div style={{ borderTop: '1px solid rgba(26,115,232,0.15)', paddingTop: 14, display: 'flex', gap: 16, fontSize: 13, fontFamily: FONT, flexWrap: 'wrap' }}>
                        <button onClick={() => handleStep6Edit('6c')} disabled={step6Loading}
                            style={{ ...btnText, padding: 0, opacity: step6Loading ? 0.5 : 1 }}>
                            Edit Category Mappings
                        </button>
                        <span style={{ color: '#C8D0E0' }}>|</span>
                        {/* c1: listed in WALK ORDER — prepaid before forecasts, matching the
                            sub-step indicator and the flow the operator just took. */}
                        <button onClick={() => handleStep6Edit(S6_PREPAID)} disabled={step6Loading}
                            style={{ ...btnText, padding: 0, opacity: step6Loading ? 0.5 : 1 }}>
                            Edit Prepaid Contracts
                        </button>
                        <span style={{ color: '#C8D0E0' }}>|</span>
                        <button onClick={() => handleStep6Edit(S6_FORECAST)} disabled={step6Loading}
                            style={{ ...btnText, padding: 0, opacity: step6Loading ? 0.5 : 1 }}>
                            Edit Vendor Forecasts
                        </button>
                    </div>
                </div>
                {step6ImportError && <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginBottom: 12 }}>{step6ImportError}</div>}
                <div style={{ display: 'flex', gap: 12 }}>
                    <button onClick={() => handleBack(6)} style={btnText}>&larr; Back</button>
                    <button onClick={handleStep6ReImport} disabled={step6Importing} style={btnSecondary}>Re-import Vendors</button>
                    {/* \u2605 THE MONEY GUARD. This button writes vendor_setup:{completed:true}
                      * and moves the cursor past 6. With zero vendors that completes Step 6
                      * on a tenant whose entire vendor opex forecast is $0 \u2014 silently, with
                      * a balanced model (cid=3's comparable figure is $9,102,604.94 of
                      * spend). Reachable whenever saved category mappings exist but no
                      * import ever ran. */}
                    <button onClick={handleStep6ContinueFromReEntry}
                        disabled={step6Importing || step6ExistingCount === 0}
                        title={step6ExistingCount === 0 ? 'No vendors imported \u2014 re-import before continuing' : undefined}
                        style={{ ...btnPrimary, opacity: (step6Importing || step6ExistingCount === 0) ? 0.5 : 1 }}>
                        {step6Importing ? 'Saving...' : 'Continue to Step 7 \u2192'}
                    </button>
                    {step6ExistingCount === 0 && (
                        <span style={{ color: '#8B2C24', fontSize: 13, fontFamily: FONT, alignSelf: 'center' }}>
                            No vendors imported \u2014 re-import before continuing.
                        </span>
                    )}
                </div>
            </div>
        );
    };

    /* \u2605 THE THIRD STATE \u2014 vendor rows and/or category mappings exist, but Step 6 was
     * never completed. Distinct from re-entry in the two ways that matter: the copy is
     * honest about being unfinished, and there is NO "Continue to Step 7 \u2192" here at all.
     * The primary action resumes the forecast entry where the operator left it. */
    const renderStep6Resume = () => {
        const statLabel = { fontSize: 11, color: '#6B7094', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, marginBottom: 4 };
        const statValue = { fontSize: 22, fontFamily: MONO, fontWeight: 600, color: '#1A1C2E' };
        const savedCats = Object.keys(step6SavedCatMap || {}).length;
        return (
            <div>
                {renderStep6SubIndicator()}
                <div style={{ padding: 24, background: '#FFF8E7', border: '1px solid #F0D9A0', borderRadius: 8, marginBottom: 20 }}>
                    <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 18, margin: '0 0 6px' }}>Vendor setup in progress</h3>
                    <div style={{ fontFamily: FONT, fontSize: 13, color: '#6B5A2E', marginBottom: 16 }}>
                        Saved, but <strong>not yet imported</strong> \u2014 Step 6 is not complete. Pick up where you left off.
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16, marginBottom: 16 }}>
                        <div>
                            <div style={statLabel}>Vendors saved</div>
                            <div style={statValue}>{step6ExistingCount}</div>
                        </div>
                        <div>
                            <div style={statLabel}>Category mappings</div>
                            <div style={statValue}>{savedCats}</div>
                        </div>
                        <div>
                            <div style={statLabel}>Contractors</div>
                            <div style={statValue}>{step6ExistingContractorCount}</div>
                        </div>
                    </div>
                    <div style={{ borderTop: '1px solid rgba(240,217,160,0.9)', paddingTop: 14, display: 'flex', gap: 16, fontSize: 13, fontFamily: FONT, flexWrap: 'wrap' }}>
                        <button onClick={() => handleStep6Edit('6c')} disabled={step6Loading}
                            style={{ ...btnText, padding: 0, opacity: step6Loading ? 0.5 : 1 }}>
                            Edit Category Mappings
                        </button>
                        <span style={{ color: '#C8D0E0' }}>|</span>
                        <button onClick={() => handleStep6Edit(S6_PREPAID)} disabled={step6Loading}
                            style={{ ...btnText, padding: 0, opacity: step6Loading ? 0.5 : 1 }}>
                            Edit Prepaid Contracts
                        </button>
                        {/* \u2605 THE THIRD DOOR, so this card matches the completed-summary card
                          * (:6541) and the forecast editor is reachable from BOTH states rather
                          * than only through the prepaid screen's primary.
                          * \u2605 GATED ON HAVING VENDORS: `hasAnyProgress` admits a tenant whose only
                          * progress is saved category mappings (:5384 \u2014 `vs.length > 0 ||
                          * savedRows.length > 0`), and for that tenant the forecast screen renders
                          * "No non-prepaid vendors to forecast". A link that can only land on an
                          * empty screen is a dead click, so it appears once there is something to
                          * forecast. */}
                        {step6ExistingCount > 0 && (
                            <>
                                <span style={{ color: '#C8D0E0' }}>|</span>
                                <button onClick={() => handleStep6Edit(S6_FORECAST)} disabled={step6Loading}
                                    style={{ ...btnText, padding: 0, opacity: step6Loading ? 0.5 : 1 }}>
                                    Edit Vendor Forecasts
                                </button>
                            </>
                        )}
                    </div>
                </div>
                {step6ImportError && <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginBottom: 12 }}>{step6ImportError}</div>}
                <div style={{ display: 'flex', gap: 12 }}>
                    <button onClick={() => handleBack(6)} style={btnText}>&larr; Back</button>
                    <button onClick={handleStep6ReImport} disabled={step6Importing} style={btnSecondary}>Re-import Vendors</button>
                    {/* c1: resume lands on the FIRST unfinished screen, which after the
                        reorder is PREPAID \u2014 not the forecast grid. */}
                    <button onClick={() => handleStep6Edit(S6_PREPAID)} disabled={step6Loading}
                        style={{ ...btnPrimary, opacity: step6Loading ? 0.5 : 1 }}>
                        {step6Loading ? 'Loading...' : 'Resume Vendor Setup \u2192'}
                    </button>
                </div>
            </div>
        );
    };

    const renderStep6a = () => {
        const hasExisting = step6ExistingCount > 0;
        return (
            <div>
                {renderStep6SubIndicator()}
                <h4 style={{ fontFamily: FONT, color: '#1A1C2E', margin: '0 0 16px' }}>Upload Vendor Expenses</h4>
                {/* Upload rework step 2 — the same radio pattern as Step 2 (renderStep2a).
                    In SEPARATE mode the operator's slot choice DECLARES each file's role,
                    so neither sheet picker runs and there is nothing to mis-detect. */}
                <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, fontFamily: FONT }}>
                        <input type="radio" checked={step6UploadMode === 'single'} onChange={() => setStep6UploadMode('single')} /> Single file (Expenses + Prepaid tabs)
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, fontFamily: FONT }}>
                        <input type="radio" checked={step6UploadMode === 'separate'} onChange={() => setStep6UploadMode('separate')} /> Separate files
                    </label>
                </div>
                {step6UploadMode === 'single' ? (
                    <DropZone file={step6File} onFile={setStep6File}
                        label="Drop your vendor expenses file here, or click to browse" inputRef={step6FileRef} />
                ) : (
                    <div style={{ display: 'flex', gap: 16 }}>
                        <DropZone file={step6VendorFile} onFile={setStep6VendorFile}
                            label="Vendor Expenses (.xlsx)" inputRef={step6VendorFileRef} />
                        <DropZone file={step6PrepaidFile} onFile={setStep6PrepaidFile}
                            label={'Prepaid Schedule (.xlsx) — optional'} inputRef={step6PrepaidFileRef} />
                    </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16, fontFamily: FONT, fontSize: 13, color: '#555' }}>
                    <span>Need a blank?</span>
                    <button onClick={handleDownloadVendorBlank} style={btnText}>Download vendor template</button>
                </div>
                {step6ParseError && <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginTop: 16 }}>{step6ParseError}</div>}
                {hasExisting && (
                    <div style={{ marginTop: 12 }}>
                        <button onClick={() => setStep6SubStep('re-entry')} style={btnText}>
                            {'\u2190 Cancel, keep existing vendors'}
                        </button>
                    </div>
                )}
                <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                    <button onClick={() => handleBack(6)} style={btnText}>&larr; Back</button>
                    <button onClick={handleStep6Parse}
                        disabled={step6Parsing || (step6UploadMode === 'single' ? !step6File : !step6VendorFile)}
                        style={{ ...btnPrimary, opacity: ((step6UploadMode === 'single' ? !step6File : !step6VendorFile) || step6Parsing) ? 0.5 : 1 }}>
                        {step6Parsing ? 'Parsing...' : 'Parse File(s) \u2192'}
                    </button>
                </div>
            </div>
        );
    };

    const renderStep6b = () => {
        const flags = step6Parsed?.flags || [];
        /* ★ §E1 (STAGE 3) — COLLAPSE THE BLANK-CATEGORY RUN. Stage 1 brought legacy rows
         * under the flag gate, which is right, but a legacy sheet has NO is_category
         * column at all, so EVERY row raises the SAME notice — cid=3's shape produces 26
         * identical sentences and the screen stops being a review and becomes a wall. One
         * summary says the same thing and can actually be read.
         * ⓘ COLLAPSED AT THE RENDER SITE ON PURPOSE: `step6Parsed.flags` is left exactly as
         * built, so the routing predicate (`hasFlags = flags.length > 0`) and every other
         * consumer see the identical array. This changes what is DRAWN, not what exists.
         * ⓘ ONLY the blank-category cause collapses, and only when there is more than one.
         * Any other reason — an unrecognised value, a missing IS line, a payroll-line
         * collision — still lists per row, because each of those is individually
         * actionable. A mixed upload therefore keeps every non-blank notice verbatim. */
        const blankCatFlags = flags.filter(f => f.cause === MSG_NO_CATEGORY);
        const collapseBlankCat = blankCatFlags.length > 1;
        const shownFlags = collapseBlankCat
            ? flags.filter(f => f.cause !== MSG_NO_CATEGORY)
            : flags;
        /* Step 3 — ONE predicate for both zero-prepaid shapes: no sheet/file supplied
         * (no_prepaid_sheet) and supplied-but-empty (prepaid_empty). What matters to the
         * import is identical in both — it would write zero contracts — so the gate keys
         * on the OUTCOME, not on which warning fired. */
        const noPrepaid = (step6Parsed?.prepaidContracts || []).length === 0;
        // W-068: blocking upload problems keep the operator here until the file is fixed and re-uploaded.
        const nBlock6 = countBlocking(flags);
        const canLeave6b = (!noPrepaid || step6NoPrepaidConfirmed) && nBlock6 === 0;
        return (
            <div>
                {renderStep6SubIndicator()}
                <h4 style={{ fontFamily: FONT, color: '#1A1C2E', margin: '0 0 16px' }}>
                    Data Quality Review &mdash; {flags.length} item{flags.length !== 1 ? 's' : ''} to review
                </h4>
                {nBlock6 > 0 && (
                    <div data-spec-heading="blocking" style={{ padding: '10px 12px', marginBottom: 12, background: '#FEF3F2', border: '1px solid #FDA29B',
                        borderRadius: 6, fontSize: 13, fontFamily: FONT, color: '#B42318', fontWeight: 600 }}>
                        {'\u26D4'} {blockingHeading(nBlock6)}
                    </div>
                )}
                {collapseBlankCat && (
                    <div style={{ padding: '10px 12px', marginBottom: 8, background: '#FFF8E1',
                        border: '1px solid #FFE082', borderRadius: 6, fontSize: 13, fontFamily: FONT,
                        color: '#7A5B00', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        <span>{'⚠️'}</span>
                        <span>{blankCatFlags.length} rows have no category on the template &mdash; choose one for each on the next screen.</span>
                    </div>
                )}
                {shownFlags.length === 0 ? (
                    <div style={{ padding: 16, background: '#E8F5E9', borderRadius: 8, fontSize: 13, fontFamily: FONT, color: '#27AE60', marginBottom: 20 }}>
                        {collapseBlankCat ? 'No other data quality issues detected.' : 'No data quality issues detected.'}
                    </div>
                ) : (
                    <div style={{ maxHeight: 400, overflowY: 'auto', border: '1px solid #E0E0E0', borderRadius: 6, marginBottom: 20 }}>
                        {shownFlags.map((f, i) => (
                            <div key={i} style={{ padding: '8px 12px', borderBottom: i < shownFlags.length - 1 ? '1px solid #F0F0F0' : 'none',
                                fontSize: 13, fontFamily: FONT, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                {/* warning icon for severity:'warning' or the legacy 'formula' type; info otherwise.
                                    Text = message || label (backward-compatible: formula used message, the rest label). */}
                                <span>{f.severity === BLOCKING ? '\u26D4' : (f.severity === 'warning' || f.type === 'formula') ? '\u26A0\uFE0F' : '\u2139\uFE0F'}</span>
                                <span>
                                    {f.message || f.label}
                                    {f.sheet && <span style={{ color: '#888', marginLeft: 6 }}>[{f.sheet}]</span>}
                                </span>
                            </div>
                        ))}
                    </div>
                )}
                {/* Step 3 \u2014 PREPAID OPTIONAL-BUT-CONFIRMED. The import would proceed with
                    zero prepaid contracts, which is legitimate for a tenant that has none
                    and a silent data loss for one that simply forgot the file. Neither the
                    parser nor the engine can tell those apart, so the operator asserts it.
                    When contracts ARE present this whole block is absent and the advance is
                    exactly today's unconditional button. */}
                {noPrepaid && (
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer',
                        padding: 12, marginBottom: 12, background: '#FFF8E1', border: '1px solid #FFE082',
                        borderRadius: 6, fontSize: 13, fontFamily: FONT, color: '#7A5B00' }}>
                        <input type="checkbox" checked={step6NoPrepaidConfirmed}
                            onChange={e => setStep6NoPrepaidConfirmed(e.target.checked)} style={{ marginTop: 2 }} />
                        <span>I confirm this company has no prepaid contracts.</span>
                    </label>
                )}
                <div style={{ display: 'flex', gap: 12 }}>
                    <button onClick={() => setStep6SubStep('6a')} style={btnSecondary}>Re-upload File</button>
                    <button onClick={() => setStep6SubStep('6c')} disabled={!canLeave6b} data-spec-continue="6b"
                        style={{ ...btnPrimary, opacity: canLeave6b ? 1 : 0.5 }}>{'These look correct \u2192'}</button>
                </div>
            </div>
        );
    };

    const renderStep6c = () => {
        const allMapped = step6CatMap.every(c => c.is_category && c.is_line_key);
        /* ★ §E1 (1a) — ONE derivation, read by the gate below AND by the row render,
         * keyed on gl_category (already this table's React key, so unique). Two
         * surfaces reading one Map cannot disagree; two surfaces each calling the
         * predicate would be the §12.5 "two surfaces, one derivation" trap. */
        const unsureBy = new Map(step6CatMap.map(c => [c.gl_category, rowUnsure(c, step6Glm)]));
        const unsureRows = step6CatMap.filter(c => unsureBy.get(c.gl_category));
        const perLine = step6CatMap.some(c => c.gl_line_name != null);   // canonical upload
        /* Batch 1b (2026-11-03, L3626): the rows are SHOWN in income-statement order -- the chosen line's place in the IS (the taxonomy's
           indexMap, the Vendor Dashboard's own order), then rows with no line by section, then file order. Display only: step6CatMap (and
           so what the save writes) keeps its order, and each row is still edited by its own index. */
        const _isIdx = (step6PrepaidTaxonomy && step6PrepaidTaxonomy.indexMap) || new Map();
        const _rank = c => _isIdx.has(c.is_line_key) ? _isIdx.get(c.is_line_key) : 100000 + (VENDOR_IS_CATEGORIES.indexOf(c.is_category) < 0 ? 99 : VENDOR_IS_CATEGORIES.indexOf(c.is_category));
        const catOrder = step6CatMap.map((c, i) => i).sort((a, b) => _rank(step6CatMap[a]) - _rank(step6CatMap[b]) || a - b);
        // B+C gate: block on anything undederivable, AND require one whole-screen tick.
        const canLeave6c = allMapped && unsureRows.length === 0 && step6CatConfirmed;
        return (
            <div>
                {renderStep6SubIndicator()}
                <h4 style={{ fontFamily: FONT, color: '#1A1C2E', margin: '0 0 12px' }}>
                    {perLine ? 'Confirm where each expense line flows on the income statement'
                             : 'Map your vendor categories to Finledom IS categories'}
                </h4>
                <div style={{ fontSize: 13, color: '#555', fontFamily: FONT, marginBottom: 16 }}>
                    {perLine
                        ? 'One row per expense line from your file. Each is pre-filled from the line name — change any that look wrong.'
                        : 'Each vendor will flow to the chosen IS line. Auto-suggestions are based on the GL category name.'}
                </div>
                {unsureRows.length > 0 && (
                    <div style={{ padding: 12, marginBottom: 16, background: '#FEF3F2', border: '1px solid #FDA29B',
                        borderRadius: 6, fontSize: 13, fontFamily: FONT, color: '#B42318' }}>
                        <strong>{unsureRows.length} line{unsureRows.length === 1 ? '' : 's'} need{unsureRows.length === 1 ? 's' : ''} a choice.</strong>{' '}
                        These could not be matched to an income-statement line automatically — pick one for each highlighted row below.
                    </div>
                )}
                <div style={{ border: '1px solid #E0E0E0', borderRadius: 6, maxHeight: 500, overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT, fontSize: 13 }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                            <tr>
                                <th style={{ ...stickyTh, textAlign: 'left' }}>{perLine ? 'Expense Line' : 'GL Category'}</th>
                                <th style={{ ...stickyTh, textAlign: 'right', width: 100 }}>Vendors</th>
                                <th style={{ ...stickyTh, width: 140 }}>IS Category</th>
                                <th style={{ ...stickyTh, width: 200 }}>IS Line Key</th>
                            </tr>
                        </thead>
                        <tbody>
                            {catOrder.map(i => [step6CatMap[i], i]).map(([c, i]) => (
                                <tr key={c.gl_category} style={{ borderBottom: '1px solid #F0F0F0',
                                    background: unsureBy.get(c.gl_category) ? '#FEF3F2' : undefined }}>
                                    <td style={{ ...tdStyle, fontWeight: 500 }}>
                                        {c.gl_line_name || c.gl_category}
                                        {unsureBy.get(c.gl_category) && (
                                            <div style={{ fontSize: 11, color: '#B42318', fontWeight: 400, marginTop: 2 }}>
                                                {unsureBy.get(c.gl_category)}
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ ...tdStyle, fontFamily: MONO, textAlign: 'right' }}>{c.count}</td>
                                    <td style={tdStyle}>
                                        <select value={c.is_category || ''}
                                            onChange={e => {
                                                const newCat = e.target.value;
                                                const newKey = defaultVendorLineKey(step6Glm, newCat);
                                                setStep6CatMap(prev => prev.map((x, j) => j === i ? { ...x, is_category: newCat, is_line_key: newKey } : x));
                                            }}
                                            style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #D0D5DD', fontSize: 13, fontFamily: FONT, width: '100%' }}>
                                            <option value="">—</option>
                                            {VENDOR_IS_CATEGORIES.map(x => <option key={x} value={x}>{x}</option>)}
                                        </select>
                                    </td>
                                    {/* \u2605 A REAL PICKER, from the tenant's OWN IS leaves. This used to be a
                                        read-only <span> displaying `${prefix}_vendors` \u2014 a leaf that exists on
                                        NO tenant \u2014 with no way for the operator to choose a real line. */}
                                    <td style={tdStyle}>
                                        {(() => {
                                            if (!c.is_category) return <span style={{ color: '#B0B4C0', fontSize: 12 }}>{'\u2014'}</span>;
                                            const opts = vendorLineKeyOptions(step6Glm, c.is_category);
                                            if (!opts.length) return (
                                                <span style={{ fontSize: 11, color: '#B42318' }}>
                                                    no {c.is_category} lines on the income statement &mdash; map one in Step 3 first
                                                </span>
                                            );
                                            return (
                                                <select value={c.is_line_key || ''}
                                                    onChange={e => setStep6CatMap(prev => prev.map((x, j) => j === i ? { ...x, is_line_key: e.target.value } : x))}
                                                    style={{ padding: '4px 8px', borderRadius: 4, fontSize: 12, fontFamily: FONT, width: '100%',
                                                        border: c.is_line_key ? '1px solid #D0D5DD' : '1px solid #FDA29B',
                                                        background: c.is_line_key ? '#fff' : '#FEF3F2' }}>
                                                    <option value="">{'\u2014 pick a line \u2014'}</option>
                                                    {opts.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                                                </select>
                                            );
                                        })()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {/* ★ THE WHOLE-SCREEN CONFIRM (the "C" of the B+C gate). The rows above are
                    pre-filled from each line's own name, which is right for AE Model's
                    template 229/229 — but these choices decide where every vendor's spend
                    lands on the income statement, and a wrong one books real money to the
                    wrong line SILENTLY (computeVendor accepts any existing leaf). Good
                    enough to pre-fill, not good enough to accept unread. */}
                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer',
                    padding: 12, marginTop: 16, background: step6CatConfirmed ? '#F0FDF4' : '#FFF8E1',
                    border: `1px solid ${step6CatConfirmed ? '#A7F3D0' : '#FFE082'}`, borderRadius: 6,
                    fontSize: 13, fontFamily: FONT, color: step6CatConfirmed ? '#166534' : '#7A5B00' }}>
                    <input type="checkbox" checked={step6CatConfirmed}
                        onChange={e => setStep6CatConfirmed(e.target.checked)} style={{ marginTop: 2 }} />
                    <span>I have reviewed these lines and they are correct.</span>
                </label>
                <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                    {step6EditMode ? (
                        <button onClick={handleStep6BackToSummary} style={btnText}>&larr; Back to Summary</button>
                    ) : (
                        <button onClick={() => setStep6SubStep('6b')} style={btnText}>&larr; Back</button>
                    )}
                    <button onClick={handleStep6SaveCats} disabled={!canLeave6c || step6Importing}
                        style={{ ...btnPrimary, opacity: (!canLeave6c || step6Importing) ? 0.5 : 1 }}>
                        {step6Importing ? 'Saving...' : (step6EditMode ? 'Save Changes' : 'Save & Continue \u2192')}
                    </button>
                    {step6ImportError && step6SubStep === '6c' && (
                        <span style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, alignSelf: 'center' }}>{step6ImportError}</span>
                    )}
                </div>
            </div>
        );
    };

    /* ═══ Build 4b — THE FORECAST STEP'S SUPPORTING DERIVATIONS ══════════════════
     * All pure, all read-only over wizard state. Nothing here writes.
     */

    /* ForecastWizard reads `id / name / isCat / monthlyData` un-dual-read, and dual-reads
     * every CONFIG field (v.type||v.forecast_type, v.amount??v.monthly_amount, …), so the
     * adapter only has to supply those four. The ARRAY INDEX stands in for `id` — wizard
     * vendors have none until the import inserts them (the c2 precedent). `isCat` is the
     * resolved IS line so the wizard can label the row; `monthlyData` is the parsed
     * historical series, which drives its sparkline, 3-mo average and done-detection. */
    const step6ForecastVendorRows = useMemo(() => {
        const byGl = {};
        /* byGlRow keeps the whole mapping row because vendorRoute's contract is
         * { [gl_category]: { is_line_key } } -- the same shape the IMPORT's own catByGl has,
         * so the editor and the import hand the helper identical structures. byGl (key-only)
         * is retained for the display readers that still want the bare key.
         * !! A THIRD copy of this two-line build sits in step6PrepaidVendorOpts (~:8479).
         * It is display-only (prepaid vendor labels) and is NOT a route decision, so it is
         * deliberately left alone here rather than folded in on a mover's commit. */
        const byGlRow = {};
        for (const c of (step6CatMap || [])) { byGl[c.gl_category] = c.is_line_key; byGlRow[c.gl_category] = c; }
        return (step6Vendors || []).map((v, i) => ({
            ...v,
            id: i,
            name: v.vendor_name,
            /* ★ ONE DERIVATION, not this surface's own. `byGl[...] || v.is_line` was the
             * THIRD spelling of "where does this vendor book?" and it disagreed with both the
             * Dashboard's and the engine's — measured 0/229 here against 229/229 there on
             * cid=3. vendorRoute is the shared answer; assertVendorRouted below is unchanged
             * and now receives the same input every other surface uses. */
            isCat: vendorRoute(v, byGlRow),
            monthlyData: v.monthly_amounts || {},
        }));
    }, [step6Vendors, step6CatMap]);

    /* ★ ONLY NON-PREPAID ROWS. ForecastWizard does NOT filter prepaid itself — its one
     * mention of the word is a display note promising they are absent, a promise the
     * component cannot keep on its own (the Vendor Dashboard's caller filters, at
     * VendorDashboard.js:1807). Handing it a prepaid row would let the operator give it
     * an amount, and a prepaid vendor with nonzero forecast monthlyData SUPPRESSES its
     * contract booking via runFullModel §12b's Axis-2 gate — footgun C reached through a
     * UI. Prepaid is set on the (now earlier) prepaid step, from its contracts.
     * `skip` rows are excluded too: the import drops them, so editing them is theatre. */
    const step6ForecastTargets = useMemo(
        () => step6ForecastVendorRows.filter(v => v.forecast_type !== 'prepaid' && v.forecast_type !== 'skip'),
        [step6ForecastVendorRows]);

    /* The month windows the wizard needs. Same derivation the import uses — and, since H1,
       the same SOURCE: `horizonFt`, refreshed from the server on step entry.
       ★★ THIS MEMO HAD TO MOVE WITH THE IMPORTS, AND NOT MOVING IT WOULD HAVE BEEN WORSE THAN
         LEAVING BOTH STALE. It reads `forecast_through` off the same in-memory array the
         Step-6 import used to, and it decides how many columns the operator is offered in the
         forecast editor. With the import fixed and this left alone, a stale array gave an
         EMPTY editor beside an import that now succeeds — the operator enters nothing and the
         vendors are written with no forecast at all. That converts the loud block this build
         inherited into exactly the silent $0 forecast it exists to remove, one level up. */
    const step6ForecastMonths = useMemo(() => {
        const ff = firstForecastMonth(obState);
        return (ff && horizonFt) ? buildAllMonths(ff, horizonFt) : [];
    }, [obState, horizonFt]);
    const step6ActualsMonths = useMemo(() => {
        const hs = obState?.historical_start, cm = obState?.close_month;
        return (hs && cm) ? buildAllMonths(hs, cm) : [];
    }, [obState]);

    /* ★ PRE-STEP-7 THIS IS EMPTY, AND THAT IS THE POINT. revenue_module_mappings do not
     * exist until Step 7, so the wizard's source picker has nothing to offer — which is
     * exactly why it disables the '% of Revenue' type and refuses to mark such a row
     * done (ForecastWizard's `hasSourceOptions`). Passing [] is honest, not a stub: a
     * sourceless % vendor books rate × TOTAL REVENUE. */
    const step6PctSourceOptions = useMemo(() => [], []);

    /* ── the wizard's save — WIZARD-LOCAL. No await, no api call, no markStale, no HTTP.
     * ForecastWizard hands back the CHECKED rows as whole vendor objects (a spread of the
     * original plus its camel edits); this folds them back into step6Vendors in the SNAKE
     * shape the import reads. 4a taught the import to persist all of it — frequency,
     * end_date, seat_departments, pct_revenue_source — so nothing the wizard emits is
     * dropped any more. That handshake is the whole reason 4a shipped first. */
    const step6SaveForecastRows = (objs) => {
        /* ★★ item 8b — REFUSE AN UNROUTED VENDOR, ALL-OR-NOTHING, BEFORE THE FOLD.
         * The Vendor Dashboard has refused this since 8b's first half; onboarding —
         * where a vendor is MOST likely to be unrouted, because the Categories step
         * is where routing is assigned — did not. A vendor with no `is_line` books
         * $0 spend forever and nothing says so: computeVendor keys on it, the BS
         * still balances, and pre-flight P5 only speaks after the data is written.
         * ★ SAME shared predicate as the Dashboard, so the two can never disagree;
         * only the REMEDY differs, because the operator fixes this on a different
         * screen here (the Categories sub-step, not the Vendor Dashboard, which a
         * mid-onboarding tenant cannot even reach). The verb matches this host's
         * button: C3 made it say Apply, so the refusal says "applied".
         * ★ THROWN, NOT RETURNED: ForecastWizard's doSave already wraps this call in
         * try/catch and renders an ERR_NO_IS_LINE message verbatim in red, so the
         * named offenders reach the operator through machinery that already exists.
         * ★ ENTRY, ABOVE THE FOLD: the fold below is the payload-boundary code from
         * §12.5 and stays byte-identical — the guard must not reach into it. */
        assertVendorRouted(objs, {
            action: 'applied',
            remedy: 'go back to the Categories step and map its GL category to an IS line',
        });
        const byIdx = new Map((objs || []).map(o => [o.id, o]));
        const _ffm = firstForecastMonth(obState);
        const _closeMonth = obState?.close_month || null;
        setStep6Vendors(prev => prev.map((v, i) => {
            const o = byIdx.get(i);
            if (!o) return v;
            /* 2026-10-29: the fold body moved, unchanged, to utils/step6ForecastFold.js -- the vendor-forecast file's change list
             * calls the same function, so what it shows is what this folds (the comments that explained it moved with it). */
            return foldWizardObjIntoVendor(v, o, { firstForecast: _ffm, closeMonth: _closeMonth, forecastMonths: step6ForecastMonths });
        }));
    };

    /* ★ PRESERVED FROM THE DELETED GRID (c2 added it, 4b keeps it): removing a vendor
     * also drops its prepaid contracts, and the operator has already worked the prepaid
     * screen by now, so the confirm NAMES the count and lists up to five. ForecastWizard
     * has no delete affordance of its own, so this lives beside the embed and is reachable
     * from the row list under it. */
    const step6RemoveVendor = (idx) => {
        const v = step6Vendors[idx];
        if (!v) return;
        const removedNameLower = String(v.vendor_name || '').trim().toLowerCase();
        const doomed = step6Prepaid.filter(p =>
            String(p.vendor_name || '').trim().toLowerCase() === removedNameLower);
        const msg = doomed.length
            ? `Remove ${v.vendor_name} from import?\n\nThis ALSO removes ${doomed.length} prepaid contract${doomed.length === 1 ? '' : 's'} for this vendor:\n` +
              doomed.slice(0, 5).map(p => `  • ${p.start_month || '—'}, ${p.length_months == null ? 'indefinite' : p.length_months + ' mo'}, $${Number(p.prepaid_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`).join('\n') +
              (doomed.length > 5 ? `\n  • …and ${doomed.length - 5} more` : '')
            : `Remove ${v.vendor_name} from import?`;
        if (!window.confirm(msg)) return;
        setStep6Vendors(prev => prev.filter((_, j) => j !== idx));
        setStep6Prepaid(prev => prev.filter(p => String(p.vendor_name || '').trim().toLowerCase() !== removedNameLower));
    };

    /* === 2026-10-29 (Brian, SubBase) -- FILL VENDOR FORECASTS FROM A FILE ========================================
     * The vendor-forecast template (templateSpecs.js vendor_forecast) uploaded HERE: parsed, checked against its spec,
     * matched to the staged vendors, shown as a change list -- and only then, on Apply, folded through the grid's own save
     * (step6SaveForecastRows: assertVendorRouted, then the fold). Nothing is written to the database (Save Progress does
     * that), and step6Prepaid is never touched: the plan reads the staged contracts only to refuse a prepaid vendor whose
     * contracts book a forecast. The matching and the change list: utils/vendorForecastFile.js. */
    const step6ForecastFilePlan = (rows) => {
        const prepaidByIdx = new Map();
        for (const p of step6Prepaid || []) {
            const idx = wizPrepaidVendorIdxs(p, step6Vendors)[0];
            if (idx == null) continue;
            if (!prepaidByIdx.has(idx)) prepaidByIdx.set(idx, []);
            prepaidByIdx.get(idx).push(p);
        }
        return planVendorForecastFile(rows, {
            staged: step6Vendors, wizardRows: step6ForecastVendorRows,
            forecastMonths: step6ForecastMonths, actualsMonths: step6ActualsMonths,
            closeMonth: obState?.close_month || null, firstForecast: firstForecastMonth(obState),
            seatDeptOptions: step6SeatDeptOptions, pctSourceOptions: step6PctSourceOptions, prepaidByIdx,
            lineLabel: k => step6PrepaidTaxonomy?.byKey?.get(k)?.label || k,
        });
    };
    const handleStep6ForecastFile = async (file) => {
        setStep6FcNote('');
        try {
            const parsed = parseVendorForecastExcel(await file.arrayBuffer());
            const problems = checkAgainstSpec('vendor_forecast', parsed);
            const blocked = !parsed.headerFound || problems.some(p => p.severity === BLOCKING);
            setStep6FcFile({ fileName: file.name, rows: parsed.rows, problems, headerFound: parsed.headerFound,
                plan: blocked ? null : step6ForecastFilePlan(parsed.rows) });
        } catch (e) {
            setStep6FcFile({ fileName: file.name, rows: [], problems: [], headerFound: false, plan: null, error: (e && e.message) || String(e) });
        }
    };
    const applyStep6ForecastFile = () => {
        if (!step6FcFile || !step6FcFile.plan) return;
        // re-planned against the rows as they are NOW (the grid may have been used since the upload)
        const plan = step6ForecastFilePlan(step6FcFile.rows);
        if (!plan.objs.length) { setStep6FcNote('Nothing to apply -- every matched vendor already has the forecast the file gives.'); setStep6FcFile(null); return; }
        try {
            step6SaveForecastRows(plan.objs);
            setStep6FcNote(`${plan.objs.length} vendor${plan.objs.length === 1 ? '' : 's'} updated from ${step6FcFile.fileName} -- not yet saved. Click Save Progress to save.`);
            setStep6FcFile(null);
        } catch (e) {
            setStep6FcNote(`Not applied -- ${(e && e.message) || String(e)}`);
        }
    };
    const renderStep6ForecastFilePanel = () => {
        const f = step6FcFile;
        if (!f) return null;
        const blocking = (f.problems || []).filter(p => p.severity === BLOCKING);
        const warnings = (f.problems || []).filter(p => p.severity !== BLOCKING);
        const plan = f.plan;
        const box = { border: '1px solid #E0E0E0', borderRadius: 8, padding: 16, marginBottom: 16, background: '#FFFFFF', fontFamily: FONT, fontSize: 13 };
        return (
            <div style={box} data-vendor-forecast-file="1">
                <div style={{ fontWeight: 600, color: '#1A1C2E', marginBottom: 6 }}>
                    Vendor forecasts from {f.fileName}
                    {plan && <span style={{ fontWeight: 400, color: '#555' }}>{` -- ${f.rows.length} row${f.rows.length === 1 ? '' : 's'}: ${plan.changed.length} change${plan.changed.length === 1 ? '' : 's'} a vendor, ${plan.unchanged.length} no change, ${plan.refused.length} not applied`}</span>}
                </div>
                {f.error && <div style={{ color: '#E53935', marginBottom: 8 }}>The file could not be read: {f.error}</div>}
                {!f.error && !f.headerFound && <div style={{ color: '#E53935', marginBottom: 8 }}>No header row naming vendor_name and forecast_type was found -- use the vendor forecast template.</div>}
                {blocking.length > 0 && (
                    <div style={{ color: '#E53935', marginBottom: 8 }}>
                        <div style={{ fontWeight: 600 }}>{blockingHeading(blocking.length)}</div>
                        <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>{blocking.map((p, i) => <li key={i}>{p.message}</li>)}</ul>
                    </div>
                )}
                {warnings.length > 0 && (
                    <ul style={{ margin: '0 0 8px 18px', padding: 0, color: '#8B7400' }}>{warnings.map((p, i) => <li key={i}>{p.message}</li>)}</ul>
                )}
                {plan && plan.refused.length > 0 && (
                    <div style={{ marginBottom: 10 }} data-vendor-forecast-refused="1">
                        <div style={{ fontWeight: 600, color: '#B54708' }}>Not applied ({plan.refused.length}) -- correct the file and upload it again, or set these in the grid:</div>
                        <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                            {plan.refused.map((r, i) => <li key={i}>{`Row ${r.row}: ${r.vendor} (${r.lineText}) -- ${r.reason}`}</li>)}
                        </ul>
                    </div>
                )}
                {plan && plan.changed.length > 0 && (
                    <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid #F0F0F0', borderRadius: 6, marginBottom: 10 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }} data-vendor-forecast-changes="1">
                            <thead style={{ position: 'sticky', top: 0, background: '#FAFAFA' }}>
                                <tr><th style={thStyle}>Vendor</th><th style={thStyle}>Line</th><th style={thStyle}>Field</th><th style={thStyle}>Before</th><th style={thStyle}>After</th></tr>
                            </thead>
                            <tbody>
                                {plan.changed.flatMap((c, i) => c.changes.map((ch, j) => (
                                    <tr key={`${i}-${j}`} style={{ borderTop: j === 0 ? '1px solid #EEE' : 'none' }}>
                                        <td style={tdStyle}>{j === 0 ? c.vendor : ''}</td>
                                        <td style={{ ...tdStyle, color: '#555' }}>{j === 0 ? c.lineText : ''}</td>
                                        <td style={tdStyle}>{ch.field}</td>
                                        <td style={{ ...tdStyle, fontFamily: MONO }}>{ch.before}</td>
                                        <td style={{ ...tdStyle, fontFamily: MONO }}>{ch.after}</td>
                                    </tr>
                                )))}
                            </tbody>
                        </table>
                    </div>
                )}
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <button onClick={applyStep6ForecastFile} disabled={!plan || !plan.changed.length}
                        style={{ ...btnPrimary, opacity: (!plan || !plan.changed.length) ? 0.5 : 1 }}>
                        {plan ? `Apply ${plan.changed.length} change${plan.changed.length === 1 ? '' : 's'}` : 'Apply'}
                    </button>
                    <button onClick={() => setStep6FcFile(null)} style={btnText}>Cancel</button>
                    <span style={{ fontSize: 12, color: '#666' }}>Nothing is saved until Save Progress.</span>
                </div>
            </div>
        );
    };

    const renderStep6Forecast = () => {
        const fmtAmt = n => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const skipped = step6Vendors.filter(v => v.forecast_type === 'skip').length;
        const prepaidCount = step6Vendors.filter(v => v.forecast_type === 'prepaid').length;
        const TYPE_LABEL = { constant: 'Fixed Monthly', pct_revenue: '% of Revenue', per_seat: 'Per Seat', one_off: 'One-Off / Manual', prepaid: 'Prepaid', skip: 'Skip' };
        return (
            <div>
                {renderStep6SubIndicator()}
                <h4 style={{ fontFamily: FONT, color: '#1A1C2E', margin: '0 0 6px' }}>
                    Forecast: {step6ForecastTargets.length} vendor{step6ForecastTargets.length === 1 ? '' : 's'}
                </h4>
                <div style={{ fontSize: 12, color: '#666', fontFamily: FONT, marginBottom: 12 }}>
                    {prepaidCount} prepaid vendor{prepaidCount === 1 ? '' : 's'} {prepaidCount === 1 ? 'is' : 'are'} forecast from
                    {' '}their contracts on the previous step and {prepaidCount === 1 ? 'is' : 'are'} not listed here
                    {skipped ? `; ${skipped} marked Skip` : ''}.
                </div>

                <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button onClick={() => setStep6ForecastWizardOpen(true)} style={btnPrimary}>Set Vendor Forecasts</button>
                    {/* 2026-10-29: the same forecasts from a file -- parsed, checked and shown as a change list before Apply */}
                    <label style={{ ...btnSecondary, cursor: canEditModel(user) ? 'pointer' : 'not-allowed', opacity: canEditModel(user) ? 1 : 0.5 }}>
                        Fill forecasts from a file
                        <input type="file" accept=".xlsx,.xls" style={{ display: 'none' }} disabled={!canEditModel(user)} data-vendor-forecast-input="1"
                            onChange={e => { const file = e.target.files && e.target.files[0]; e.target.value = ''; if (file) handleStep6ForecastFile(file); }} />
                    </label>
                    <button onClick={() => downloadVendorForecastBlank(selectedCompany?.name || 'Your Company Name')} style={btnText}>Download the forecast template</button>
                </div>
                {step6FcNote && <div style={{ fontSize: 13, fontFamily: FONT, marginBottom: 12, color: /^Not applied/.test(step6FcNote) ? '#E53935' : '#1E7E34' }}>{step6FcNote}</div>}
                {renderStep6ForecastFilePanel()}

                {step6ForecastTargets.length === 0 ? (
                    <div style={{ padding: 16, background: '#FAFAFA', borderRadius: 8, fontSize: 13, fontFamily: FONT, color: '#666', marginBottom: 20 }}>
                        No non-prepaid vendors to forecast.
                    </div>
                ) : (
                    <div style={{ border: '1px solid #E0E0E0', borderRadius: 6, overflow: 'hidden', maxHeight: 460, overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT, fontSize: 13 }}>
                            <thead style={{ position: 'sticky', top: 0, background: '#FAFAFA' }}>
                                <tr style={{ borderBottom: '1px solid #E0E0E0' }}>
                                    <th style={thStyle}>Vendor</th>
                                    <th style={thStyle}>GL Line</th>
                                    <th style={{ ...thStyle, width: 140 }}>Type</th>
                                    <th style={{ ...thStyle, width: 110 }}>Frequency</th>
                                    <th style={{ ...thStyle, textAlign: 'right', width: 130 }}>Amount / Rate</th>
                                    <th style={{ ...thStyle, textAlign: 'center', width: 90 }}>Contractor</th>
                                    <th style={{ ...thStyle, textAlign: 'right', width: 60 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {step6Vendors.map((v, i) => {
                                    if (v.forecast_type === 'prepaid' || v.forecast_type === 'skip') return null;
                                    const t = v.forecast_type || 'constant';
                                    const rate = t === 'pct_revenue' ? `${(Number(v.pct_rate) || 0) * 100}%`
                                        : t === 'per_seat' ? `$${fmtAmt(v.per_seat_cost)}/seat`
                                            : t === 'one_off' ? '—' : `$${fmtAmt(v.monthly_amount)}`;
                                    return (
                                        <tr key={i} style={{ borderBottom: '1px solid #F0F0F0' }}>
                                            <td style={{ ...tdStyle, fontWeight: 500 }}>{v.vendor_name}</td>
                                            <td style={{ ...tdStyle, color: '#555' }}>{v.gl_category || '—'}</td>
                                            <td style={tdStyle}>{TYPE_LABEL[t] || t}</td>
                                            <td style={tdStyle}>{t === 'constant' ? (v.billing_frequency || 'monthly') : '—'}</td>
                                            <td style={{ ...tdStyle, fontFamily: MONO, textAlign: 'right' }}>{rate}</td>
                                            <td style={{ ...tdStyle, textAlign: 'center' }}>{v.is_contractor ? 'yes' : ''}</td>
                                            <td style={{ ...tdStyle, textAlign: 'right' }}>
                                                <button onClick={() => step6RemoveVendor(i)} title="Remove vendor from import"
                                                    style={{ border: 'none', background: 'transparent', color: '#E53935', fontSize: 16, lineHeight: 1, cursor: 'pointer', padding: '2px 6px' }}>
                                                    {'×'}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                    {step6EditMode ? (
                        <>
                            <button onClick={handleStep6BackToSummary} style={btnText}>&larr; Back to Summary</button>
                            {/* ★ SAVE-AND-STAY. The edit footer used to offer ONE save, and it was
                              * the COMPLETING import — so every checkpoint during a long re-entry
                              * session ended the session (setStep6SubStep('re-entry') below in
                              * handleStep6Import). The draft path already persisted without
                              * navigating and was wired only to the ENTRY footer; this exposes it
                              * here. Same handler, same payload — `draft` is read in exactly one
                              * place, server.js:3071, and skips ONLY the completion write. */}
                            <button onClick={() => handleStep6Import({ draft: true })} disabled={step6Importing}
                                style={{ ...btnSecondary, opacity: step6Importing ? 0.5 : 1 }}>
                                {step6Importing ? 'Saving...' : 'Save Progress'}
                            </button>
                            {/* ★ EXPLICIT `{draft:false}`. This was `onClick={handleStep6Import}`,
                              * which handed the React SyntheticEvent in as the options object and
                              * worked only because a DOM event happens to carry no `draft` key.
                              * With a draft button beside it, the flag that decides whether Step 6
                              * is marked complete must be stated, not inferred. */}
                            <button onClick={() => handleStep6Import({ draft: false })} disabled={step6Importing}
                                style={{ ...btnPrimary, opacity: step6Importing ? 0.5 : 1 }}>
                                {step6Importing ? 'Saving...' : 'Save & Complete Step 6 →'}
                            </button>
                        </>
                    ) : (
                        <>
                            <button onClick={() => { setStep6DraftSaved(''); setStep6SubStep(S6_PREPAID); }} style={btnText}>&larr; Back</button>
                            <button onClick={() => handleStep6Import({ draft: true })} disabled={step6Importing}
                                style={{ ...btnSecondary, opacity: step6Importing ? 0.5 : 1 }}>
                                {step6Importing ? 'Saving...' : 'Save Progress'}
                            </button>
                            <button onClick={() => handleStep6Import({ draft: false })} disabled={step6Importing}
                                style={{ ...btnPrimary, opacity: step6Importing ? 0.5 : 1 }}>
                                {step6Importing ? 'Importing...' : 'Import All & Continue →'}
                            </button>
                        </>
                    )}
                    {step6DraftSaved && (
                        <span style={{ color: '#1E7E34', fontSize: 13, fontFamily: FONT, alignSelf: 'center' }}>{step6DraftSaved}</span>
                    )}
                    {step6ImportError && (
                        <span style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, alignSelf: 'center' }}>{step6ImportError}</span>
                    )}
                </div>

                {/* ★ THE EMBEDDED EDITOR — the same component the Vendor Dashboard uses, and
                  * the one cid=3's 181 vendor forecasts were actually entered through. Its
                  * handlers here mutate step6Vendors only; the Step-6 import persists the set. */}
                {step6ForecastWizardOpen && (
                    <ForecastWizard
                        canEdit={canEditModel(user)}
                        vendors={step6ForecastTargets}
                        forecastMonths={step6ForecastMonths}
                        actualsMonths={step6ActualsMonths}
                        cutoff={obState?.close_month || ''}
                        firstForecast={firstForecastMonth(obState)}
                        isLineTaxonomy={step6PrepaidTaxonomy}
                        pctRevenueSourceOptions={step6PctSourceOptions}
                        seatDeptOptions={step6SeatDeptOptions}
                        onSaveVendors={step6SaveForecastRows}
                        onClose={() => setStep6ForecastWizardOpen(false)}
                        /* ★ C3 — DECLARE THE SEMANTICS. step6SaveForecastRows is a
                         * setStep6Vendors fold with no api call, so the editor must say
                         * "Apply", must not claim "Saved", and must keep the browser's
                         * unsaved-changes guard armed until "Save Progress" (the draft
                         * import below) actually writes. */
                        saveMode="local"
                    />
                )}
            </div>
        );
    };

    // 6e edit handlers (Fix 3 + 4): when vendor name changes, re-run cross-ref
    // and update step6Vendors accordingly — promoting an expense vendor to
    // prepaid or retiring an orphaned prepaid-only entry.
    /* ═══ Build 3 c2 — THE PREPAID EDITOR'S SUPPORTING DERIVATIONS ═══════════════
     * All pure, all read-only over wizard state. Nothing here writes.
     */

    /* Per-contract flags, computed by CALLING THE IMPORT'S OWN GENERATOR rather than
     * re-deciding anything. `fillForecastPrepaidAmort` already returns event /
     * irregular / lagAmbiguous, and the import reads exactly those to build its
     * post-import banners — so the badges on this screen and those banners cannot
     * disagree. Pure and cheap (a few hundred contracts, arithmetic only). */
    const step6PrepaidFlags = useMemo(() => {
        const cutoff = obState?.close_month || null;
        if (!cutoff) return [];
        return step6Prepaid.map(p => {
            const fill = fillForecastPrepaidAmort({
                vendor_name: p.vendor_name, account: p.account,
                conference_name: p.conference_name, event_identifier: p.event_identifier,
                start_month: p.start_month, length_months: Number(p.length_months),
                prepaid_amount: Number(p.prepaid_amount), amort: p.amort || {},
            }, cutoff, step6Prepaid);
            /* ★★ C1 s1 (defect 4) — CHIP-LOCAL STATE, READ OFF THE CONTRACT ITSELF.
             * `fill.event` is `isPrepaidEventStyle(c)`, which on live data reduces to
             * `vendor_name === 'Conferences'` (event_identifier is NULL on every row of
             * both tenants). It describes a VENDOR, not a contract's state, so the chip
             * fired on all 19 Conferences rows forever — done or not — and the operator
             * worked a checklist of 19 when 6 were actionable.
             * ⛔ THE FIX IS DELIBERATELY *NOT* IN `isPrepaidEventStyle`. That predicate is
             * SHARED ENGINE LOGIC: `deriveAmortLag` returns 0 for the event class (cid=3
             * stores 0 on all 19 while firstAmort−start is 1-13), and
             * `fillForecastPrepaidAmort` DECLINES to generate for it. Re-scoping it would
             * re-derive those lags and start generating schedules the import currently
             * refuses to guess — a silent number-mover on 19 rows per tenant. So the
             * engine keeps deciding "is this the event CLASS"; the two locals below decide
             * "has this contract been DEALT WITH", which only the screen needs to know. */
            const amortKeys = Object.keys(p.amort || {});
            const hasForecastCells = amortKeys.some(m => m > cutoff);
            /* ACTIONABLE = event-style AND no event month chosen yet. A row that already
             * carries `event_month` has been answered; showing it a prompt is the lie. */
            const eventNeedsMonth = !!fill.event && !p.event_month;
            /* TERM ENTIRELY CLOSED = it booked, everything it booked is historical, and its
             * nominal term (start + lag + length − 1) ended on or before the cutoff. Such a
             * row has no forecast window to fill, so asking for an entry is asking for
             * nothing. CONSERVATIVE BY CONSTRUCTION — it requires cells to EXIST, so a row
             * that booked nothing at all is never suppressed, and a row still running past
             * the cutoff is never suppressed. Lag is read straight off `p` (never via
             * step6RowLag, which is declared BELOW this memo — calling it here would be a
             * TDZ ReferenceError on first render). */
            const lagN = p.amort_lag_months == null ? 0 : (Number(p.amort_lag_months) || 0);
            const lenN = Number(p.length_months);
            const nominalEnd = (p.start_month && Number.isFinite(lenN) && lenN > 0)
                ? addYMMonths(p.start_month, lagN + lenN - 1) : null;
            const termClosed = !hasForecastCells && amortKeys.length > 0
                && nominalEnd != null && nominalEnd <= cutoff;
            return {
                event: !!fill.event, irregular: !!fill.irregular,
                lagAmbiguous: fill.lagAmbiguous || null,
                /* ★ what the CHIP renders (defect 4): event-style AND still unanswered. */
                eventNeedsMonth,
                termClosed,
                /* A flagged contract with NOTHING booked forward is the ACTIONABLE case:
                 * the generator declined and only the operator can supply the answer. One
                 * that already carries forecast cells has been answered (or never needed
                 * answering), so it is not put on the checklist.
                 * ★ C1 s1: `!termClosed` added — a correctly-completed historical-only row
                 * (96 of them on cid=3) has no forecast window and was being flagged for an
                 * entry it does not need. And the event arm now uses `eventNeedsMonth`, so a
                 * row with its event month already set drops off the checklist. */
                needsEntry: (eventNeedsMonth || !!fill.irregular || !!fill.lagAmbiguous)
                    && !hasForecastCells && !termClosed,
            };
        });
    }, [step6Prepaid, obState]);

    /* ★ 4b: per-seat department options from the tenant's OWN department_mappings via the
     * ONE shared resolver — the same call the Vendor Dashboard makes. */
    const step6SeatDeptOptions = useMemo(
        () => buildSeatDeptResolver(step6DeptMappings || []).options, [step6DeptMappings]);

    /* The tenant's own IS taxonomy, from the SAME builder the Dashboard picker uses. */
    const step6PrepaidTaxonomy = useMemo(() => buildISLineTaxonomy(step6Glm || []), [step6Glm]);

    /* PrepaidModal's vendor dropdown wants {id, name, isCat}. Wizard vendors have no DB
     * id yet (the import inserts them), so the ARRAY INDEX stands in. That id is used
     * only to fill vendor_name and the default account inside the modal, and is DROPPED
     * on the way back — the import still resolves the real vendor_id server-side from
     * (vendor_name, resolved_is_line), exactly as before. */
    const step6PrepaidVendorOpts = useMemo(() => {
        const byGl = {};
        for (const c of (step6CatMap || [])) byGl[c.gl_category] = c.is_line_key;
        return (step6Vendors || []).map((v, i) => ({
            id: i, name: v.vendor_name, isCat: byGl[v.gl_category] || null,
        }));
    }, [step6Vendors, step6CatMap]);

    /* The month window the modal offers (historical start … forecast horizon).
       H1: same owner as everything else — a stale horizon here offered the operator an empty
       month dropdown, so a prepaid contract could not be given a start month at all. */
    const step6PrepaidMonths = useMemo(() => {
        const hs = obState?.historical_start;
        return (hs && horizonFt) ? buildAllMonths(hs, horizonFt) : [];
    }, [obState, horizonFt]);

    /* ── shape adapters ──────────────────────────────────────────────────────────
     * step6Prepaid rows are onboarding-shape (snake, as the import sends them);
     * PrepaidModal reads the Dashboard's frontend shape and emits DB shape on save.
     * Two small total functions — renaming only, no logic, so nothing can drift. */
    /* ★ THE ONE LAG READER — used by the Lag column and the modal adapter, so a
     * contract cannot show one lag and regenerate from another.
     * ★ THE FALLBACK FIRES ON `null`/ABSENT ONLY, NEVER ON 0. A row loaded from the
     * DB always carries a NUMBER (the re-map above coerces), and 0 there is a real
     * stored decision: `deriveAmortLag` returns 0 for the event class, so cid=3's
     * and cid=8's 13 Conferences rows store 0 while `firstAmort − start_month` is
     * 1-13. Blanket-deriving would display — and hand the modal — a lag the write
     * path never persists, corrupting exactly those rows on the next save.
     * A PARSED row (6a entry) has no such key at all: the workbook has no lag
     * column, the value is derived at import time (:6408), so deriving here shows
     * precisely what the import will store. */
    const step6RowLag = (p) => (
        p?.amort_lag_months == null ? deriveAmortLag(p || {}) : Number(p.amort_lag_months) || 0
    );

    const step6PrepaidToModal = (p, idx) => ({
        id: idx,
        /* ★★ BUILD 2 — IDENTITY, NOT THE FIRST ROW WITH A MATCHING NAME.
         * This was `.find(name-key match)`, i.e. FIRST-WINS: on a duplicated name it
         * seeded the modal with whichever row happened to come first in the sheet,
         * regardless of which vendor the contract actually belongs to.
         * ★ THE OPTS id IS THE ARRAY INDEX (see step6PrepaidVendorOpts above), and the
         * resolver returns that same index, so this stays a direct substitution.
         * A no-match still yields null, exactly as `?? null` did. */
        vendorId: wizPrepaidVendorIdxs(p, step6Vendors)[0] ?? null,
        vendor: p.vendor_name || '',
        account: p.account || '',
        is_line: p.is_line || '',
        start: p.start_month || '',
        length: p.length_months == null ? null : Number(p.length_months),
        prepaid: Number(p.prepaid_amount) || 0,
        eventIdentifier: p.event_identifier || '',
        eventMonth: p.event_month || '',
        amortLagMonths: step6RowLag(p),
        amort: p.amort || {},
        renews: p.renews === true ? true : p.renews === false ? false : null,   // prepaid renewals part 2
    });
    /* payload (DB shape) → the onboarding row. `vendor_id` is deliberately NOT carried:
     * the wizard's ids are synthetic indices, and the import resolves the real one. */
    const step6PrepaidFromModal = (payload, prev) => ({
        ...(prev || {}),
        vendor_name: payload.vendor_name,
        account: payload.account || null,
        is_line: payload.is_line || null,
        start_month: payload.start_month,
        length_months: payload.length_months,
        prepaid_amount: Number(payload.prepaid_amount) || 0,
        amort: payload.amort || {},
        event_identifier: payload.event_identifier || null,
        event_month: payload.event_month || null,
        /* ★★ C1 s2b (defect 2) — THE FOLD STORES THE *DERIVED* LAG, NOT THE KEYSTROKE.
         * The modal emits `amort_lag_months: lag` — whatever the operator typed. The
         * Step-6 IMPORT, however, re-derives it from the schedule it is about to persist
         * (`deriveAmortLag({… amort: outAmort})`, the 3a rule that the stored lag must
         * DESCRIBE the stored schedule). So a wizard draft held the keystroke while the
         * DB would hold the derived value: `step6RowLag` showed 2 where the database
         * would say 0, and that displayed value then drove `lagChanged` (which branch
         * handleSave takes) and Regenerate's start month.
         * Deriving it HERE, from the payload's own dict, makes the wizard row agree with
         * the import BY CONSTRUCTION — one rule, applied at both write points, so they
         * cannot disagree.
         * ★ THE EVENT CLASS STAYS 0 BY CONSTRUCTION: deriveAmortLag returns 0 whenever
         * isPrepaidEventStyle(c) is true, and that predicate reads `event_identifier` /
         * `vendor_name` — both present on the payload — so a Conferences row keeps the 0
         * it stores today rather than gaining the 1-13 month offset its single cell
         * would otherwise imply. That is exactly the corruption `c2759be` closed; the
         * fields it needs are passed explicitly below so the call cannot silently see a
         * partial row. */
        amort_lag_months: deriveAmortLag({
            vendor_name: payload.vendor_name,
            event_identifier: payload.event_identifier || null,
            start_month: payload.start_month,
            length_months: payload.length_months,
            prepaid_amount: Number(payload.prepaid_amount) || 0,
            amort: payload.amort || {},
        }),
        is_historical: prev ? prev.is_historical !== false : true,
        /* Prepaid renewals part 1: a generated renewal row arrives with its answer and keys; a modal save carries none and keeps
         * the row's own (the spread of prev above). */
        ...(payload.renews !== undefined ? { renews: payload.renews } : {}),
        ...(payload.client_key !== undefined ? { client_key: payload.client_key } : {}),
        ...(payload.renewal_of_key !== undefined ? { renewal_of_key: payload.renewal_of_key } : {}),
    });

    /* ── handlers — WIZARD-LOCAL. No API call, no markStale, no await: nothing is
     * written until the operator runs Save Progress or the import, which persists the
     * whole prepaid set transactionally. This is the difference between the two hosts
     * of the SAME modal — the Dashboard PUTs, the wizard stages. ───────────────── */
    /* -- PREPAID RENEWALS PART 2 (Brian 10-29): renewals are GENERATED, never typed. Every action -- a Renews answer, Accept
     * all, an edit through the modal, a flagged row's choice -- builds a PLAN with the shared planner (utils/prepaidRenewals;
     * the Vendor Dashboard runs the same functions) and folds it into the staged rows. Nothing is written until Save Progress /
     * the import, whose payload carries client_key / renewal_of_key; the server maps them to renewal_of_id. */
    const step6RenewsView = useMemo(() => prepaidRenewsView(step6Prepaid, { cutoff: obState?.close_month || null }), [step6Prepaid, obState]);
    const newRenewAcc = () => ({ flagged: [], done: new Set(), notices: [] });
    const step6FoldRenewPlan = (rows, plan, acc) => {
        const byRef = new Map(rows.map(r => [prepaidRef(r), r]));
        (plan.flagged || []).forEach(f => acc.flagged.push({ ref: f.ref, label: prepaidRowLabel(byRef.get(f.ref)) }));
        (plan.remove || []).forEach(r => acc.done.add(r));
        if (plan.collision) acc.notices.push(prepaidCollisionText(plan.collision, rows));
        return applyPrepaidPlanLocal(rows, plan, step6PrepaidFromModal);
    };
    const step6CommitRenew = (rows, acc) => {
        const live = new Set(rows.map(prepaidRef));
        setStep6Prepaid(rows);
        setStep6RenewFlagged(prev => [...prev.filter(x => !acc.done.has(x.ref) && !acc.flagged.some(f => f.ref === x.ref)), ...acc.flagged]
            .filter(x => live.has(x.ref)));
        setStep6RenewNotice(acc.notices.length ? acc.notices.join(' ') : null);
    };
    const step6AnswerRenews = (index, answer) => {
        const rows = ensurePrepaidRefs(step6Prepaid);
        const ref = prepaidRef(rows[index]);
        if (!ref) return;
        const acc = newRenewAcc();
        step6CommitRenew(step6FoldRenewPlan(rows, planPrepaidRenewsAnswer(rows, ref, answer, { forecastThrough: horizonFt }), acc), acc);
    };
    /* "Accept all suggestions": every UNANSWERED row that can renew takes its suggestion, in list order, each through the
     * same plan as a click (a Yes generates its renewals before the next row is judged). It never overwrites an answer and
     * never runs by itself. */
    const step6AcceptAllRenews = () => {
        let rows = ensurePrepaidRefs(step6Prepaid);
        const acc = newRenewAcc();
        const todo = prepaidRenewsView(rows, { cutoff: obState?.close_month || null })
            .filter(v => v.answer == null && !v.dash && v.ref).map(v => ({ ref: v.ref, suggest: v.suggest }));
        for (const t of todo) {
            if (!rows.some(r => prepaidRef(r) === t.ref)) continue;
            rows = step6FoldRenewPlan(rows, planPrepaidRenewsAnswer(rows, t.ref, t.suggest, { forecastThrough: horizonFt }), acc);
        }
        step6CommitRenew(rows, acc);
    };
    const step6RenewFlagKeep = (ref) => {
        const rows = ensurePrepaidRefs(step6Prepaid);
        const acc = newRenewAcc(); acc.done.add(ref);
        step6CommitRenew(step6FoldRenewPlan(rows, planPrepaidFlaggedKeep(ref), acc), acc);
    };
    const step6RenewFlagDelete = (ref) => {
        const rows = ensurePrepaidRefs(step6Prepaid);
        const acc = newRenewAcc();
        step6CommitRenew(step6FoldRenewPlan(rows, planPrepaidFlaggedDelete(ref), acc), acc);
    };
    /* The modal's Apply. A changed Renews answer runs that answer's plan (judged against the row BEFORE this edit -- D4);
     * otherwise an edit reconciles the chain the row belongs to (the later renewals follow the edit; a chain whose root
     * does not renew -- every contract no one answered Yes on -- is untouched). */
    const step6SavePrepaid = (payload, id) => {
        const rows0 = ensurePrepaidRefs(step6Prepaid);
        let rows, ref, before = null;
        if (id == null) {
            rows = ensurePrepaidRefs([...rows0, step6PrepaidFromModal(payload, null)]);
            ref = prepaidRef(rows[rows.length - 1]);
        } else {
            before = rows0[id];
            rows = rows0.map((p, i) => i === id ? step6PrepaidFromModal(payload, p) : p);
            ref = prepaidRef(rows[id]);
        }
        const acc = newRenewAcc();
        const newAnswer = payload.renews;   // undefined when the contract cannot renew (the modal omits the key)
        const oldAnswer = before ? (before.renews === true ? true : before.renews === false ? false : null) : null;
        if (newAnswer === true || newAnswer === false) {
            if (newAnswer !== oldAnswer) rows = step6FoldRenewPlan(rows, planPrepaidRenewsAnswer(rows, ref, newAnswer, { forecastThrough: horizonFt, before }), acc);
            else if (before) rows = step6FoldRenewPlan(rows, planPrepaidEdit(rows, ref, before, { forecastThrough: horizonFt }), acc);
        } else if (before) {
            rows = step6FoldRenewPlan(rows, planPrepaidEdit(rows, ref, before, { forecastThrough: horizonFt }), acc);
        }
        step6CommitRenew(rows, acc);
        setStep6PrepaidModal(null);
    };
    /* Remove. A renewal that named the removed row loses its link (the database's own rule: renewal_of_id ON DELETE SET NULL),
     * so the import never carries a key that names no row. */
    const step6DeletePrepaid = (id) => {
        setStep6Prepaid(prev => {
            const gone = prepaidRef(prev[id]);
            return prev.filter((_, i) => i !== id).map(p => (gone && prepaidParentRef(p) === gone) ? { ...p, renewal_of_key: null } : p);
        });
        setStep6PrepaidModal(null);
    };
    /* Recurring series: the modal hands back N ready-built rows, each already carrying
     * its own buildCycleAmort schedule and the inherited lag, so appending them is the
     * whole job. The Dashboard POSTs each row; the wizard just grows the staged set. */
    const step6GeneratePrepaid = (rows) => {
        setStep6Prepaid(prev => [...prev, ...rows.map(r => step6PrepaidFromModal(r, null))]);
        setStep6PrepaidModal(null);
    };

    /* ★ Build 3 c2 — the promote that used to live in the rename box.
     * Renaming a prepaid-only contract onto a real expense vendor must retype that
     * vendor to 'prepaid' (its expense then comes from the contract via §12b). The
     * rename box is gone, but the modal's vendor picker does the same thing, so the
     * behaviour MOVED here rather than being lost. FIRST-MATCH-WINS on a duplicate
     * name is pre-existing (the sibling collision filed in the F2 Phase 0) — carried
     * over unchanged, not introduced. */
    /* ★★ BUILD 2 — TAKES THE CONTRACT, NOT A BARE NAME.
     * It used to take `vendorName` and findIndex the first row with that name-key —
     * FIRST-WINS, so saving a contract for the "Regus" on Software <$1000 promoted the
     * "Regus" on Office Rent instead: the wrong vendor typed prepaid, and the right one
     * left booking its own average. A name alone cannot answer this question, so the
     * signature now carries the whole contract and the shared resolver reads its GL line.
     * ⓘ Both callers already hold the full object (the modal payload / the first
     * generated row), so nothing upstream had to be threaded. */
    const step6PromoteVendorToPrepaid = (contract) => {
        if (!contract || !String(contract.vendor_name || '').trim()) return;
        setStep6Vendors(prev => {
            const idx = wizPrepaidVendorIdxs(contract, prev)[0];
            if (idx == null) return prev;                      // prepaid-only → nothing to promote
            if (prev[idx].forecast_type === 'prepaid') return prev;
            return prev.map((v, i) => i === idx ? { ...v, forecast_type: 'prepaid' } : v);
        });
    };

    const renderStep6Prepaid = () => {
        const closeMonth = obState?.close_month || '';
        // Loading / self-heal guard: the badges rely on step6Vendors being populated.
        if (step6EditMode && step6Vendors.length === 0 && step6Prepaid.length > 0) {
            return (
                <div>
                    {renderStep6SubIndicator()}
                    <div style={{ padding: 32, color: '#999', fontFamily: FONT }}>Loading vendors...</div>
                </div>
            );
        }
        const expenseNamesLower = new Set(
            step6Vendors.map(v => String(v.vendor_name || '').trim().toLowerCase()).filter(Boolean)
        );
        const notInExpenses = (p) => !!p.vendor_name
            && !expenseNamesLower.has(String(p.vendor_name).trim().toLowerCase());
        const activeCount = step6Prepaid.filter(p => {
            if (!p.start_month) return false;
            if (p.length_months == null) return true;          // indefinite = ongoing
            return addYMMonths(p.start_month, (Number(p.length_months) || 0) - 1) >= closeMonth;
        }).length;

        // The three generator outcomes, as a CHECKLIST that points at rows.
        const needIdx = step6PrepaidFlags
            .map((f, i) => (f && f.needsEntry) ? i : -1).filter(i => i >= 0);
        /* ★ C1 s1 (defect 4): the checklist counts rows that still NEED an event month,
         * not every row that happens to belong to the event class. */
        const evIdx = needIdx.filter(i => step6PrepaidFlags[i].eventNeedsMonth);
        const lagIdx = needIdx.filter(i => step6PrepaidFlags[i].lagAmbiguous);
        const irrIdx = needIdx.filter(i => step6PrepaidFlags[i].irregular);
        const fmtAmt = n => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const amortSummary = (p) => {
            const ks = Object.keys(p.amort || {}).sort();
            if (!ks.length) return { text: 'none', warn: true };
            const fc = ks.filter(m => m > closeMonth).length;
            return { text: ks.length + ' mo (' + ks[0] + '…' + ks[ks.length - 1] + ')' + (fc ? ', ' + fc + ' fc' : ''), warn: false };
        };
        const chip = (bg, color, label, title) => (
            <span title={title} style={{ marginLeft: 6, fontSize: 10, padding: '1px 5px', borderRadius: 3, background: bg, color, fontWeight: 600, whiteSpace: 'nowrap' }}>{label}</span>
        );

        return (
            <div>
                {renderStep6SubIndicator()}
                <h4 style={{ fontFamily: FONT, color: '#1A1C2E', margin: '0 0 6px' }}>
                    Prepaid contracts: {step6Prepaid.length}
                </h4>
                <div style={{ fontSize: 12, color: '#666', fontFamily: FONT, marginBottom: 12 }}>
                    {activeCount} still amortizing at close month ({closeMonth || '—'}).
                    {' '}These drive the prepaid balance AND the forecast prepaid expense — a vendor
                    flagged prepaid takes its whole forecast from its contracts, so set them here first.
                </div>

                {/* ★ THE CHECKLIST — the three cases the generator refuses to guess, each
                    pointing at the rows that need an answer. These conditions were previously
                    advisory text on a LATER screen, with nothing to act on. */}
                {needIdx.length > 0 && (
                    <div style={{ padding: 12, marginBottom: 16, background: '#FFF8E7', border: '1px solid #F0D9A0', borderRadius: 8, fontSize: 13, fontFamily: FONT, color: '#6B5A2E' }}>
                        <strong>{needIdx.length} contract{needIdx.length === 1 ? '' : 's'} need{needIdx.length === 1 ? 's' : ''} an entry before the forecast is right.</strong>
                        <div style={{ marginTop: 6, fontSize: 12 }}>
                            {evIdx.length > 0 && <div>{'•'} <strong>{evIdx.length} event booking{evIdx.length === 1 ? '' : 's'}</strong> {'—'} set an <em>Event Month</em>; the amount books entirely in that month. No rule can derive it.</div>}
                            {lagIdx.length > 0 && <div>{'•'} <strong>{lagIdx.length} ambiguous amortization start{lagIdx.length === 1 ? '' : 's'}</strong> {'—'} earlier cycles of the same vendor/account used different lags, so nothing was generated. Set the <em>Amortization Lag</em>.</div>}
                            {/* ★ C1 s1 (defect 3) — THIS LINE USED TO NAME THE BUTTON THAT DESTROYS
                                THE ROWS IT FLAGS. It read "Open the row and <em>Regenerate</em>",
                                but Regenerate is buildCycleAmort(start+lag, length, amount) — a pure
                                function of the TERMS that never reads the existing cells, so on an
                                irregular history it discards exactly the imported information the
                                flag exists to protect. The sibling notice at the post-import banner
                                already words this correctly ("Set the schedule in the Prepaid tab");
                                the two disagreed and this was the wrong one.
                                ★ UPDATED BY s2b: Regenerate is now a SAFE forecast-only reset
                                (it preserves every closed cell), so the warning below is about
                                losing manual FORECAST entry, not about destroying history. */}
                            {irrIdx.length > 0 && <div>{'•'} <strong>{irrIdx.length} irregular histor{irrIdx.length === 1 ? 'y' : 'ies'}</strong> {'—'} the imported schedule is not a clean straight line, so nothing was generated forward. Open the row and <em>set the schedule</em> for the forecast months. <em>Regenerate</em> rebuilds the forecast straight-line from the terms — closed months are preserved, but any forecast entry you made is replaced.</div>}
                        </div>
                    </div>
                )}

                <div style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'center' }}>
                    <button onClick={() => setStep6PrepaidModal('new')} style={btnSecondary}>+ Add contract</button>
                    {/* Prepaid renewals part 2: answers every UNANSWERED contract with its suggestion; never overwrites an answer. */}
                    {step6RenewsView.some(v => v.answer == null && !v.dash) && (
                        <ViewOnly canEdit={canEditModel(user)}><button onClick={step6AcceptAllRenews} style={btnSecondary}
                            title="Answer Renews on every contract you have not answered, using its suggestion. Answers you gave are not changed.">
                            Accept all suggestions ({step6RenewsView.filter(v => v.answer == null && !v.dash).length})
                        </button></ViewOnly>
                    )}
                    {/* ★ C1 s2b (S2a follow-up) — THE VISIBLE DIRTY INDICATOR. S2a closed the
                        BROWSER-level hole (beforeunload now covers step6Prepaid), but the
                        shared hook's own header says a surface should show a visible marker
                        TOO — because Chromium suppresses beforeunload on a tab-X close without
                        prior user activation, so the prompt is defence-in-depth and not a
                        guarantee. The 6d forecast footer already has one; this screen had none.
                        ★ REUSES `step6PrepaidDirty` — the SAME signal S2a computes for the
                        guard, never a second dirty question, so the marker and the prompt can
                        never disagree about whether there is unsaved work. */}
                    {step6PrepaidDirty && (
                        <span style={{ fontSize: 12, fontFamily: FONT, color: '#E67E22', fontWeight: 600 }}>
                            {step6EditMode ? '\u25CF Unsaved changes \u2014 click Save Progress to save.' : '\u25CF Unsaved changes \u2014 they are saved with the vendors: click Continue, then Save Progress on the Forecast screen.'}
                        </span>
                    )}
                </div>

                {step6RenewNotice && (
                    <div data-renews-notice="1" style={{ padding: 10, marginBottom: 12, background: '#FDECEA', border: '1px solid #F5C6C2', borderRadius: 8, fontSize: 12, fontFamily: FONT, color: '#8B2C24' }}>{step6RenewNotice}</div>
                )}
                <ViewOnly canEdit={canEditModel(user)}><PrepaidRenewsFlagged items={step6RenewFlagged} onKeep={step6RenewFlagKeep} onDelete={step6RenewFlagDelete} /></ViewOnly>
                {step6Prepaid.length === 0 ? (
                    <div style={{ padding: 16, background: '#FAFAFA', borderRadius: 8, fontSize: 13, fontFamily: FONT, color: '#666', marginBottom: 20 }}>
                        No prepaid contracts found in the file. Add any that exist, or continue.
                    </div>
                ) : (
                    <div style={{ border: '1px solid #E0E0E0', borderRadius: 6, overflow: 'hidden', maxHeight: 500, overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT, fontSize: 13 }}>
                            <thead style={{ position: 'sticky', top: 0, background: '#FAFAFA' }}>
                                <tr style={{ borderBottom: '1px solid #E0E0E0' }}>
                                    <th style={thStyle}>Vendor</th>
                                    <th style={thStyle}>IS Account</th>
                                    <th style={{ ...thStyle, width: 100 }}>Start</th>
                                    <th style={{ ...thStyle, textAlign: 'right', width: 70 }}>Length</th>
                                    <th style={{ ...thStyle, textAlign: 'right', width: 60 }}>Lag</th>
                                    <th style={{ ...thStyle, textAlign: 'right', width: 130 }}>Amount</th>
                                    <th style={{ ...thStyle, width: 180 }}>Amortization</th>
                                    <th style={{ ...thStyle, width: 230 }} title="Does the contract renew? Yes creates its renewals as contracts through the forecast. A suggestion is marked until you answer.">Renews</th>
                                    <th style={{ ...thStyle, textAlign: 'right', width: 70 }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {prepaidDisplayOrder(step6Prepaid).map(i => { const p = step6Prepaid[i];   // display order: vendor A-Z, start, renewals under their seed (Brian 10-29)
                                    const flags = step6PrepaidFlags[i] || {};
                                    const isIndef = !!p.start_month && p.length_months == null;
                                    const am = amortSummary(p);
                                    const rowBg = flags.needsEntry ? '#FFF8E7' : (notInExpenses(p) ? '#F0F7FF' : '#fff');
                                    return (
                                        <tr key={i} style={{ borderBottom: '1px solid #F0F0F0', background: rowBg }}>
                                            <td style={{ ...tdStyle, fontWeight: 500 }}>
                                                {p.vendor_name}
                                                {notInExpenses(p) && chip('#E8F1FE', '#1A73E8', 'Not in Expenses', 'No expense-sheet vendor of this name — relink it in the editor or accept it as prepaid-only.')}
                                                <PrepaidRenewalChip label={step6RenewsView[i] ? step6RenewsView[i].parentLabel : null} />
                                                {/* ★ C1 s1 (defect 4): keys on eventNeedsMonth (event-style AND no
                                                    event_month stored), not on the vendor name. A row whose event
                                                    month is set is DONE and shows no chip. */}
                                                {flags.eventNeedsMonth && chip('#FDEBD0', '#8A5B00', 'Event month?', 'Books entirely in the event month; set it in the editor.')}
                                                {flags.lagAmbiguous && chip('#FDEBD0', '#8A5B00', 'Lag? (' + flags.lagAmbiguous.join('/') + ')', 'Earlier cycles of this vendor/account used different lags — set the amortization lag.')}
                                                {flags.irregular && chip('#FDEBD0', '#8A5B00', 'Irregular', 'Imported schedule is not a clean straight line — regenerate it.')}
                                            </td>
                                            <td style={{ ...tdStyle, color: '#555' }}>{p.account || '—'}</td>
                                            <td style={{ ...tdStyle, fontFamily: MONO, fontSize: 12 }}>{p.start_month || '—'}</td>
                                            <td style={{ ...tdStyle, fontFamily: MONO, textAlign: 'right' }}>{isIndef ? 'Indef.' : (p.length_months || '—')}</td>
                                            <td style={{ ...tdStyle, fontFamily: MONO, textAlign: 'right' }}>{step6RowLag(p)}</td>
                                            <td style={{ ...tdStyle, fontFamily: MONO, textAlign: 'right' }}>${fmtAmt(p.prepaid_amount)}</td>
                                            <td style={{ ...tdStyle, fontFamily: MONO, fontSize: 11, color: am.warn ? '#B42318' : '#555' }}>{am.text}</td>
                                            <td style={tdStyle}><ViewOnly canEdit={canEditModel(user)}><PrepaidRenewsCell view={step6RenewsView[i]} onAnswer={a => step6AnswerRenews(i, a)} /></ViewOnly></td>
                                            <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                                                <button onClick={() => setStep6PrepaidModal(i)} style={{ ...btnText, padding: '2px 8px' }}>Edit</button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
                {step6ImportError && <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginTop: 12 }}>{step6ImportError}</div>}
                <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                    {/* ★ c1: the two modes no longer share a primary button. EDIT mode saves —
                      * now as a PAIR: a draft checkpoint that stays, and a completing save that
                      * is named for what it does. ENTRY mode CONTINUES to the forecast grid,
                      * which is the last screen and owns the import. */}
                    {step6EditMode ? (
                        <>
                            <button onClick={handleStep6BackToSummary} style={btnText}>&larr; Back to Summary</button>
                            {/* ★ SAVE-AND-STAY, same shape as the forecast footer. Build 3 made this
                              * screen a real editor, so it earns the same checkpoint the forecast
                              * grid has: the draft path persists the whole set and does not
                              * navigate. */}
                            <button onClick={() => handleStep6Import({ draft: true })} disabled={step6Importing}
                                style={{ ...btnSecondary, opacity: step6Importing ? 0.5 : 1 }}>
                                {step6Importing ? 'Saving...' : 'Save Progress'}
                            </button>
                            {/* ★ THE DEAD-END IS CLOSED HERE. Prepaid is a MIDDLE sub-step; the
                              * terminal one is Forecast, and from the RESUME card there was no
                              * other door to it. This primary now SAVES A DRAFT AND CONTINUES —
                              * so the label is true, the step is not falsely completed on the way
                              * through, and the operator lands on the forecast editor with the
                              * grid they just worked still in memory. ONE button for both modes:
                              * a draft save cannot un-complete an already-complete Step 6, so the
                              * re-entry path is safe on the same code. See the draft branch of
                              * handleStep6Import for why this cannot live in the completing one. */}
                            <button onClick={() => handleStep6Import({ draft: true, then: S6_FORECAST })} disabled={step6Importing}
                                style={{ ...btnPrimary, opacity: step6Importing ? 0.5 : 1 }}>
                                {step6Importing ? 'Saving...' : 'Save & Continue to Forecast →'}
                            </button>
                        </>
                    ) : (
                        <>
                            <button onClick={() => setStep6SubStep('6c')} style={btnText}>&larr; Back</button>
                            <button onClick={handleStep6PrepaidContinue} style={btnPrimary}>{'Continue →'}</button>
                        </>
                    )}
                    {/* ★ THE DRAFT SAVE NEEDS A READER HERE. The forecast footer renders this span
                      * in both modes (its own footer, below the mode ternary); this screen rendered
                      * only step6ImportError, so a successful Save Progress would have been
                      * SILENT — a save with no acknowledgement reads as a no-op. */}
                    {step6DraftSaved && (
                        <span style={{ color: '#1E7E34', fontSize: 13, fontFamily: FONT, alignSelf: 'center' }}>{step6DraftSaved}</span>
                    )}
                </div>

                {/* ★ THE EMBEDDED EDITOR — the same component the Vendor Dashboard uses.
                  * Its handlers here mutate step6Prepaid ONLY; the Step-6 import persists the
                  * whole set transactionally when the operator saves. */}
                {step6PrepaidModal !== null && (
                    <PrepaidModal
                        canEdit={canEditModel(user)}
                        contract={step6PrepaidModal === 'new' ? null : step6PrepaidToModal(step6Prepaid[step6PrepaidModal], step6PrepaidModal)}
                        renewsSuggestion={step6PrepaidModal === 'new' ? null : (step6RenewsView[step6PrepaidModal] || null)}
                        prelinkVendor={null}
                        vendors={step6PrepaidVendorOpts}
                        isLineTaxonomy={step6PrepaidTaxonomy}
                        allMonths={step6PrepaidMonths}
                        firstForecast={firstForecastMonth(obState)}
                        lastForecast={step6PrepaidMonths.length ? step6PrepaidMonths[step6PrepaidMonths.length - 1] : null}
                        onSave={(payload, id) => { step6SavePrepaid(payload, id); step6PromoteVendorToPrepaid(payload); }}
                        onGenerate={(rows) => { step6GeneratePrepaid(rows); if (rows[0]) step6PromoteVendorToPrepaid(rows[0]); }}
                        onDelete={step6DeletePrepaid}
                        onClose={() => setStep6PrepaidModal(null)}
                        /* ★ C1 s1 (H) — DECLARE THE SEMANTICS. Every handler above mutates
                         * step6Prepaid only; nothing here reaches the database until the
                         * Step-6 import. So the editor must say "Apply", must not assert a
                         * deletion that has not happened, and must name where the real
                         * write is. */
                        saveMode="local"
                        error={null}
                    />
                )}
            </div>
        );
    };

    const renderStep6 = () => {
        if (step6Loading || step6SubStep === null) return <div style={{ padding: 32, color: '#999', fontFamily: FONT }}>Loading...</div>;
        /* ★ C1b — the NAMED HANDLED state for "no forecast start month".
         * Every Step-6 sub-step dates vendors off this month, so none of them can render
         * honestly without it. Blocking here with a named notice is the alternative to
         * the deleted '2026-01' guess — and to a bare addMonths(null,1), which would
         * TypeError (§12.5 rule 7: fail-loud means NAMED and HANDLED, not a crash and
         * not a blank page). Unreachable on a real tenant today — renderCurrentStep
         * short-circuits to Step 1 until company_setup is complete, and Step 1 cannot
         * complete without a close month — so this costs nothing and fails safe if that
         * invariant (client guard + server COALESCE, no DB constraint) ever breaks. */
        if (!firstForecastMonth(obState)) {
            return (
                <div>
                    <div style={{ padding: 16, background: '#FDECEA', border: '1px solid #F5C6C2', borderRadius: 8,
                        fontFamily: FONT, fontSize: 13, color: '#8B2C24', marginBottom: 16 }}>
                        <strong>Vendor setup is blocked.</strong> {NO_FORECAST_START_MSG}
                    </div>
                    <button onClick={() => setViewingStep(1)} style={btnText}>&larr; Go to Step 1</button>
                </div>
            );
        }
        if (step6SubStep === 're-entry') return renderStep6ReEntry();
        if (step6SubStep === 'resume') return renderStep6Resume();
        if (step6SubStep === '6a') return renderStep6a();
        if (step6SubStep === '6b') return renderStep6b();
        if (step6SubStep === '6c') return renderStep6c();
        if (step6SubStep === S6_PREPAID) return renderStep6Prepaid();
        if (step6SubStep === S6_FORECAST) return renderStep6Forecast();
        return renderStep6a();
    };

    // ── Step 7: Revenue Module Selection ─────────────────────
    // Reads rev_* keys from is_actuals (written by Step 4 import) and lets the
    // user map each historical revenue GL line to a forecast module (AE Quota /
    // Company Quota / Misc Revenue / Implementation / Skip).
    // 9b: the Instance column is GONE — module_instance is ENGINE-VESTIGIAL for
    // all four types (every reader resolves instance 1; its only consumer was
    // this screen's preview tree). Deleting the affordance deletes the trap:
    // you cannot join an instance that does not exist. The DB column is
    // UNTOUCHED (the save writes 1; a DROP COLUMN is a later migration — §12).
    const loadStep7Data = useCallback(async (companyId) => {
        try {
            setStep7Loading(true);
            setStep7Error('');
            const [defrevRes, mapRes, miscRes, implRes, glmRes, resvRes] = await Promise.all([
                getDefrevData(companyId),
                getRevenueModuleMappings(companyId).catch(() => ({ data: [] })),
                /* W-016: the misc and implementation LINES, so an AE / CQ mapping onto a line one of them already targets is shown
                   unavailable -- the server refuses it (revenueTargetClaims); the ONE client derivation is miscTargets.js. */
                getMiscRevenueLines(companyId).catch(() => ({ data: [] })),
                getImplementationLines(companyId).catch(() => ({ data: [] })),
                /* W-020: the OWNER of each revenue line's name. No .catch, deliberately: a failed fetch fails the step loudly
                   (step7Error) -- an empty list would show every line as its key and the save would write the keys over good names. */
                getGLLineMappings(companyId),
                /* W-081 part 2: the Step-7 reservations. A failed read -> null (the choice is disabled; the save keeps the stored set). */
                getDrLineReservations(companyId).then(r => r.data).catch(() => null),
            ]);
            setStep7ResvLoaded(!!resvRes);
            setStep7DrReservations(new Set(Object.keys((resvRes && resvRes.reservations) || {})));
            {
                const claims = {};
                for (const [key, list] of revenueTargetClaimants({ miscLines: miscRes?.data || [], implementationLines: implRes?.data || [] })) {
                    claims[key] = list[0].kind === 'misc' ? 'a misc revenue line' : 'an implementation revenue line';
                }
                setStep7LineClaims(claims);
                // Batch 2 (item 16): the revenue lines an ENABLED implementation line is routed onto (its own synthetic is not a line here)
                setStep7ImplTargets(new Set((implRes?.data || []).filter(l => l && l.enabled && l.target_gl_line_key && l.target_gl_line_key !== implSyntheticKey(l.id)).map(l => l.target_gl_line_key)));
                /* W-081: the implementation lines' deferred-revenue links (the builder's predicate: enabled, deferred, a REAL account). */
                setStep7ImplDrLinks(implDeferredLinks(implRes?.data));   // W-138: the ONE derivation (utils/drCarried.js), the pre-flight reads it too
            }
            const isActuals = defrevRes?.data?.is_actuals || {};
            const savedMappings = mapRes?.data || [];
            const savedByKey = {};
            for (const m of savedMappings) {
                savedByKey[m.gl_line_key] = m;
            }
            // Extract rev_* keys from is_actuals
            const revKeys = Object.keys(isActuals).filter(k => k.startsWith('rev_'));
            const bsMeta = defrevRes?.data?.bs_actuals_meta || {};
            // C-1: deferred-revenue BS leaves the DR-module picker offers (role-filtered; excludes the tax decoy).
            const drOptions = deferredRevenueOptionsFromMeta(bsMeta);
            setStep7DrOptions(drOptions);
            /* W-020: a revenue line's name comes from its OWNER, gl_line_mappings.display_name -- the operator's own name for the
               line (the label of record, utils/leafLabels.js), matched by its stored key. It used to be REBUILT FROM THE KEY (a
               title-cased rev_<slug>), which cannot hold punctuation, "&", mixed case or a trailing number: "Discounts/Refunds Given"
               became "Discountsrefunds Given", and the save wrote that into revenue_module_mappings.display_name (its only writer).
               A line no gl_line_mappings row owns shows its KEY, as a key -- never a name rebuilt from one. */
            const glOwnerByKey = indexGlByLeafKey(glmRes?.data || []);
            const lines = revKeys.map(k => {
                const months = isActuals[k] || {};
                // Most recent month available (sorted ascending, take last).
                const monthKeysAsc = Object.keys(months).sort();
                const mostRecentMonth = monthKeysAsc.length ? monthKeysAsc[monthKeysAsc.length - 1] : null;
                const mostRecentAmount = mostRecentMonth ? (Number(months[mostRecentMonth]) || 0) : 0;
                const saved = savedByKey[k];
                const display = ownedLabelFor(glOwnerByKey, k) || k;
                let moduleType, moduleName;
                if (saved) {
                    moduleType = saved.module_type || 'skip';
                    moduleName = saved.module_name || shortenModuleName(display);
                } else {
                    // W-087 F3: no saved answer -> BLANK and REQUIRED (was a name-keyword guess); handleStep7Save names it.
                    moduleType = '';
                    moduleName = shortenModuleName(display);
                }
                // C-1: a deferred-revenue module carries its STORED bs_line_key, or '' -- never a guess (W-010, 2026-09-24:
                // the name-overlap pre-fill rendered exactly like an operator choice). A blank key renders red + "Required",
                // and handleStep7Save refuses it until the operator picks a line or "Synthetic (forecast-only)".
                const bsLineKey = saved ? (saved.bs_line_key || '') : '';
                return {
                    gl_line_key: k,
                    display_name: display,
                    most_recent_month: mostRecentMonth,
                    most_recent_amount: r2(mostRecentAmount),
                    /* ★ G7 — "does this line carry history?", asked of the WHOLE series.
                     * ⛔ NOT most_recent_amount !== 0, the value one line above: measured, the
                     * two disagree on 8 of 23 live rows. rev_other_revenue (4 non-zero months)
                     * and rev_billable_expense_income (6) both END at zero on every tenant, so
                     * a last-month test would wave through two lines that hold real dollars.
                     * Uses the pre-flight's own predicate — the same one P3 splits BLOCKING
                     * from WARNING on — so the two cannot drift. */
                    has_history: nonzeroSeries(months),
                    module_type: moduleType,
                    module_name: moduleName,
                    bs_line_key: bsLineKey,
                };
            }).sort((a, b) => b.most_recent_amount - a.most_recent_amount);
            setStep7Lines(lines);
        } catch (err) {
            console.error('Failed to load Step 7 data:', err);
            setStep7Error('Failed to load revenue data: ' + (err?.message || 'Unknown error'));
        } finally { setStep7Loading(false); }
    }, []);

    useEffect(() => {
        if (viewingStep === 7 && selectedCompanyId && obState && step7Lines.length === 0 && !step7Loading && !step7Error) {
            loadStep7Data(selectedCompanyId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewingStep, selectedCompanyId, obState]);

    const updateStep7Line = (idx, patch) => {
        setStep7Lines(prev => {
            const next = [...prev];
            next[idx] = { ...next[idx], ...patch };
            return next;
        });
    };

    // 9b: which OTHER line (if any) already owns a singleton module type. Used to
    // DISABLE that type in this row's dropdown (invalid state unrepresentable —
    // the Step-5c precedent) and to name the owner in the tooltip.
    const singletonTakenBy = (type, idx) => {
        if (!SINGLETON_MODULE_TYPES.includes(type)) return null;
        const owner = step7Lines.find((l, i) => i !== idx && (l.module_type || 'skip') === type);
        return owner ? owner.display_name : null;
    };

    // Changing a line's module_type. (9b: the join-by-default instance smart-default
    // is GONE with the Instance column; each line's module_name defaults from its own
    // display name — per-line modules (misc/impl) never share names, and singletons
    // can't have another row of the same type at all.)
    // W-016: an AE / CQ option is unavailable on a line a misc or implementation LINE already targets (the server refuses it).
    const lineClaimBy = (type, line) => (SINGLETON_MODULE_TYPES.includes(type) && line && step7LineClaims[line.gl_line_key]) || null;

    const handleStep7TypeChange = (idx, newType) => {
        // Belt: the option is disabled in the UI; refuse a taken singleton anyway.
        if (singletonTakenBy(newType, idx) || lineClaimBy(newType, step7Lines[idx])) return;
        setStep7Lines(prev => {
            const next = [...prev];
            const cur = { ...next[idx], module_type: newType };
            if (newType !== 'skip' && !(cur.module_name && String(cur.module_name).trim())) {
                cur.module_name = shortenModuleName(cur.display_name);
            }
            // C-1: keep bs_line_key in sync with the (new) module type. Switching TO a DR
            // module keeps whatever the operator chose -- blank unless chosen, never a guess (W-010); switching to a non-DR type clears it
            // (the save nulls non-DR anyway — this just avoids a stale key lingering in the UI).
            if (!DR_BS_LINE_MODULE_TYPES.includes(newType)) cur.bs_line_key = '';
            next[idx] = cur;
            return next;
        });
    };

    const handleStep7Save = async () => {
        setStep7Error('');
        // W-087 F3: every revenue line needs an ANSWER (Skip is one). A blank module type is refused, naming each line.
        const unanswered = step7Lines.filter(l => !l.module_type);
        if (unanswered.length) {
            setStep7Error('Choose how each revenue line is forecast (a module, or Skip): ' + unanswered.map(l => l.display_name).join(', '));
            return;
        }
        const nonSkip = step7Lines.filter(l => l.module_type && l.module_type !== 'skip');
        if (!nonSkip.length) {
            setStep7Error('At least one revenue line must be mapped to a forecast module.');
            return;
        }
        // C-1: fail loud — a deferred-revenue module with no Balance Sheet line silently
        // drops its BS wiring. Block save until every DR module names a leaf (or picks Synthetic).
        const drMissing = step7Lines.filter(l => DR_BS_LINE_MODULE_TYPES.includes(l.module_type) && !l.bs_line_key);
        if (drMissing.length) {
            setStep7Error('Select a Balance Sheet (deferred-revenue) line for: ' + drMissing.map(l => l.display_name).join(', '));
            return;
        }
        /* W-010 (2026-09-24): TWO DEFERRED-REVENUE MODULES MAY NOT SHARE ONE BALANCE-SHEET LINE. buildBSLines gives a line ONE
         * module link, so a second module on it books only one module's balance there and the other's vanishes -- measured on
         * cid=36 (in memory): liabilities -8,388,215.11, cash -8,613,976.74, with the balance check at 0 and P6 silent. Refused
         * here, naming both streams and the line; the server refuses the same resulting state as the backstop. */
        const drByLine = new Map();
        step7Lines.filter(l => DR_BS_LINE_MODULE_TYPES.includes(l.module_type) && l.bs_line_key).forEach(l => {
            if (!drByLine.has(l.bs_line_key)) drByLine.set(l.bs_line_key, []);
            drByLine.get(l.bs_line_key).push(l);
        });
        /* W-081: an implementation line linked to an account holds it too -- an AE / CQ stream on the same account is refused. */
        step7ImplDrLinks.forEach(x => {
            if (!drByLine.has(x.key)) drByLine.set(x.key, []);
            drByLine.get(x.key).push({ display_name: 'the implementation line "' + x.name + '"', bs_line_key: x.key });
        });
        /* W-081 part 2: an account RESERVED for implementation revenue is a stream on that line -- an AE / CQ module on it is refused. */
        step7DrReservations.forEach(k => {
            if ((drByLine.get(k) || []).some(x => x.bs_line_key === k && !DR_BS_LINE_MODULE_TYPES.includes(x.module_type))) return;
            if (!drByLine.has(k)) drByLine.set(k, []);
            drByLine.get(k).push({ display_name: 'the reservation for implementation revenue', bs_line_key: k });
        });
        const drShared = [...drByLine.entries()].filter(([, ls]) => ls.length > 1);
        if (drShared.length) {
            const drLineLabel = k => (step7DrOptions.find(o => o.id === k) || {}).label || k;
            setStep7Error('Two revenue streams cannot share one Balance Sheet (deferred-revenue) line: ' +
                drShared.map(([k, ls]) => ls.map(l => l.display_name).join(' and ') + ' both use ' + drLineLabel(k)).join('; ') +
                '. Pick a different line for one of them.');
            return;
        }
        /* 2026-09-24 -- A DEFERRED-REVENUE LINE NO REVENUE STREAM POINTS AT. A balance-sheet line typed deferred revenue at
         * Step 4 gets its forecast balance ONLY from the DR module that links it here (buildBSLines' module link); with none,
         * it books 0 from the first forecast month, silently -- the balance check stays at 0 and no pre-flight check sees it
         * (P6 asks the reverse question: does each DR module name a line that resolves). Refused here, naming the line, with
         * the operator's two ways out. The W-010 idiom; the options are the same role-filtered list the picker offers. */
        /* W-138 (2026-10-05): the ONE "carried" rule (utils/drCarried.js) -- an AE / CQ row's line, an implementation line's link
           (W-081), a reservation for implementation revenue (W-081 part 2). The pre-flight's P8 blocks go-live on the same rule,
           for the case this save cannot see (a Step-4 re-import after Step 7 that adds a deferred-revenue account). */
        const drUnlinked = uncarriedDeferredRevenueAccounts({ accounts: step7DrOptions, streams: step7Lines, implLinks: step7ImplDrLinks, reservations: step7DrReservations });
        if (drUnlinked.length) {
            const one = drUnlinked.length === 1;
            setStep7Error('Every deferred-revenue line on the balance sheet needs a revenue stream: ' +
                drUnlinked.map(o => o.label).join(', ') + (one ? ' is' : ' are') + ' linked to none, so ' + (one ? 'it' : 'they') +
                ' would carry no forecast balance after the close month. Link ' + (one ? 'it' : 'each one') + ' to the ' +
                REV_MODULE_LABEL.ae_quota + ' or ' + REV_MODULE_LABEL.company_quota + ' stream above (its Balance Sheet line), mark it below for implementation revenue (linked on the Deferred Revenue page after onboarding), or, if ' +
                (one ? 'it is' : 'one is') + ' not driven by a revenue stream, go back to Step 4 and change ' + (one ? 'its' : "that line's") + ' type.');
            return;
        }
        /* ★ G7 — REFUSE `implementation` ON A LINE THAT CARRIES HISTORY.
         * MODULE_TYPE_TO_IS_CONFIG (buildISLines.js) wires ae_quota, company_quota and
         * misc_revenue. There is NO `implementation` entry, so a historical revenue line
         * mapped to it is SPLIT: the line itself forecasts $0 forever while the
         * implementation forecast lands on a separate synthetic rev_impl_<id>. Total
         * revenue is not lost — which is exactly why nothing catches it — but the
         * operator's model of what they configured is wrong, silently.
         * ⛔ THE CHEAP HALF ON PURPOSE: refusing costs four lines; giving implementation a
         * real emission path needs a subsection, a subtotal home and its own A/B, and is a
         * different build.
         * ★ THE PREDICATE IS "ANY NON-ZERO MONTH", NOT "THE LAST MONTH IS NON-ZERO" — see
         * the has_history note in the loader; the two disagree on 8 of 23 live rows.
         * ⓘ AN ALL-ZERO LINE IS DELIBERATELY ALLOWED THROUGH. It is P3's WARNING case, not
         * its BLOCKING one: there are no dollars to strand, so refusing it would report a
         * condition nobody can act on — the §12.5 shape-10 defect (a check that fires on
         * correct data). Measured: the only such line on any tenant is
         * rev_discountsrefunds_given (0 of 27 months non-zero, cid 8/36/40).
         * ⓘ 0 rmm rows on any tenant carry module_type 'implementation' today, so this
         * refuses a state nobody is in — a trap being closed, not a number being fixed. */
        const implWithHistory = step7Lines.filter(l => l.module_type === 'implementation' && l.has_history);
        if (implWithHistory.length) {
            setStep7Error(`${REV_MODULE_LABEL.implementation} has no income-statement destination, so a line that already carries history would forecast $0 while the implementation forecast appeared on a separate line: ${implWithHistory.map(l => l.display_name).join(', ')}. Choose ${REV_MODULE_LABEL.misc_revenue} to forecast the line itself — that is what every existing model does — or Skip it and configure Implementation on its own page.`);
            return;
        }
        // 9b belt: the dropdowns already make a duplicate singleton unrepresentable; this
        // catches pre-existing bad state loaded from the DB (and mirrors the server 400).
        for (const t of SINGLETON_MODULE_TYPES) {
            const rows = step7Lines.filter(l => (l.module_type || 'skip') === t);
            if (rows.length > 1) {
                setStep7Error(`${REV_MODULE_LABEL[t]} can forecast only one revenue line (it is one-per-company). Keep one of: ${rows.map(l => l.display_name).join(', ')} — and set the others to Skip; the forecast total will be correct.`);
                return;
            }
        }
        try {
            setStep7Saving(true);
            const payload = step7Lines.map(l => ({
                gl_line_key: l.gl_line_key,
                display_name: l.display_name,
                module_type: l.module_type || 'skip',
                // 9b: the Instance UI is gone; the DB column survives until its DROP
                // migration, so the save pins every row to 1 (all live rows are 1).
                module_instance: 1,
                module_name: l.module_type === 'skip' ? null : (l.module_name || shortenModuleName(l.display_name)),
                // Only DR modules carry a bs_line_key; everything else is explicitly null.
                bs_line_key: DR_BS_LINE_MODULE_TYPES.includes(l.module_type) ? (l.bs_line_key || null) : null,
            }));
            await saveRevenueModuleMappings(selectedCompanyId, payload,
                step7ResvLoaded ? { dr_reservations: [...step7DrReservations].filter(k => step7DrOptions.some(o => o.id === k)) } : undefined);   // W-081 part 2
            const existingSteps = (obState?.steps) || {};
            const newSteps = { ...existingSteps, revenue_modules: { completed: true } };
            await updateOnboardingState(selectedCompanyId, {
                // cursor is a high-water mark — never moves backward (see the Step-2 note)
                current_step: Math.max(obState?.current_step || 8, 8),
                steps: newSteps,
                close_month: obState?.close_month,
                forecast_start: obState?.forecast_start,
                historical_start: obState?.historical_start,
            });
            await loadWizardState(selectedCompanyId);
            setViewingStep(8);
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Unknown error';
            setStep7Error('Failed to save revenue mappings: ' + msg);
            console.error(err);
        } finally { setStep7Saving(false); }
    };

    const renderStep7 = () => {
        if (step7Loading) return <div style={{ padding: 32, color: '#999', fontFamily: FONT }}>Loading...</div>;
        // 9b: group the preview by module TYPE (the Instance grouping key is gone).
        // Singletons have at most one line by construction; per-line types (misc /
        // implementation) list each line — each is its own module.
        const groups = new Map();
        const skipLines = [];
        for (const l of step7Lines) {
            if (!l.module_type) continue;   // W-087 F3: an unanswered line is not "skipped" -- it joins the preview once answered
            const t = l.module_type || 'skip';
            if (t === 'skip') { skipLines.push(l); continue; }
            if (!groups.has(t)) groups.set(t, { type: t, name: '', lines: [] });
            groups.get(t).lines.push(l);
        }
        // Resolve a single name per group: first non-empty module_name among its lines.
        for (const g of groups.values()) {
            const withName = g.lines.find(l => l.module_name && String(l.module_name).trim());
            g.name = withName ? withName.module_name : (g.lines[0] ? shortenModuleName(g.lines[0].display_name) : '');
        }
        const groupEntries = [...groups.values()].sort((a, b) => {
            const la = REV_MODULE_LABEL[a.type] || a.type;
            const lb = REV_MODULE_LABEL[b.type] || b.type;
            return la.localeCompare(lb);
        });

        const th = { ...thStyle, background: '#fff', borderBottom: '2px solid #E0E0E0' };

        return (
            <div>
                <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 18, marginBottom: 6, marginTop: 0 }}>Revenue Modules</h3>
                <p style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 14, margin: '0 0 4px' }}>
                    Map your historical revenue lines to forecast modules
                </p>
                <p style={{ fontFamily: FONT, color: '#666', fontSize: 13, margin: '0 0 20px' }}>
                    Each revenue line needs a forecast module for future projections. Account Executive (AE) Quota and Company Quota forecast one line each; Misc Revenue and Implementation can take any number of lines.
                </p>
                {step7Lines.length === 0 ? (
                    <div style={{ padding: 32, color: '#888', fontFamily: FONT, background: '#F9FAFB', borderRadius: 6, textAlign: 'center' }}>
                        No revenue lines found in historical data. Complete Step 4 (Review Financials) first.
                    </div>
                ) : (
                    <>
                        <div style={{ overflowX: 'auto', border: '1px solid #E0E0E0', borderRadius: 6 }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT, fontSize: 13 }}>
                                <thead>
                                    <tr>
                                        <th style={{ ...th, textAlign: 'left', minWidth: 220 }}>Historical Revenue Line</th>
                                        <th style={{ ...th, textAlign: 'right', minWidth: 140 }}>Most Recent Month</th>
                                        <th style={{ ...th, textAlign: 'left', minWidth: 200 }}>Forecast Module</th>
                                        <th style={{ ...th, textAlign: 'left', minWidth: 220 }}>Module Name</th>
                                        <th style={{ ...th, textAlign: 'left', minWidth: 220 }}>Deferred Revenue Line</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {step7Lines.map((line, idx) => {
                                        const isSkip = (line.module_type || 'skip') === 'skip';
                                        return (
                                            <tr key={line.gl_line_key} style={{ borderTop: '1px solid #F0F0F0' }}>
                                                <td style={{ ...tdStyle, fontFamily: FONT, color: '#1A1C2E', fontWeight: 500 }}>
                                                    {line.display_name}
                                                    <div style={{ fontSize: 11, color: '#999', fontFamily: MONO, marginTop: 2 }}>{line.gl_line_key}</div>
                                                </td>
                                                <td style={{ ...tdStyle, textAlign: 'right' }}>
                                                    <div style={{ fontFamily: MONO, color: BLUE }}>{fmtMoney(line.most_recent_amount)}</div>
                                                    {line.most_recent_month && (
                                                        <div style={{ fontFamily: FONT, fontSize: 11, color: '#999', marginTop: 2 }}>{formatMonthLabel(line.most_recent_month, "shortFull")}</div>
                                                    )}
                                                </td>
                                                <td style={tdStyle}>
                                                    <select value={line.module_type || ''}
                                                        onChange={(e) => handleStep7TypeChange(idx, e.target.value)}
                                                        style={{ padding: '6px 8px', border: `1px solid ${line.module_type ? '#D0D5DD' : '#E53935'}`, borderRadius: 4, fontFamily: FONT, fontSize: 13, background: '#fff', minWidth: 180 }}>
                                                        <option value="">{"\u2014 choose a module \u2014"}</option>
                                                        {REV_MODULE_OPTIONS.map(opt => {
                                                            // 9b: a singleton type already owned by another line is DISABLED
                                                            // here — the invalid two-row state is unrepresentable. The title
                                                            // carries the WHY + the map-one-Skip-the-rest workaround.
                                                            const takenBy = singletonTakenBy(opt.value, idx);
                                                            const claimedBy = lineClaimBy(opt.value, line);   // W-016
                                                            return (
                                                                <option key={opt.value} value={opt.value} disabled={!!takenBy || !!claimedBy}
                                                                    title={takenBy ? singletonTooltip(opt.value, takenBy) : (claimedBy ? 'This line is already forecast by ' + claimedBy + ' -- two revenue streams on one line would replace each other.' : undefined)}>
                                                                    {(opt.value === 'skip' && line.has_history && step7ImplTargets.has(line.gl_line_key)) ? STEP7_SKIP_IMPL_LABEL : opt.label}{takenBy ? ' — in use (one per company)' : ''}
                                                                    {claimedBy && !takenBy ? ' (already forecast by ' + claimedBy + ')' : ''}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                    {!line.module_type && (
                                                        <div data-step7-mark="required-module" style={{ fontSize: 10, color: '#E53935', fontFamily: FONT, marginTop: 2, lineHeight: 1.3 }}>
                                                            Required &mdash; choose how this revenue line is forecast
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={tdStyle}>
                                                    {isSkip ? (
                                                        <span style={{ color: '#CCC', fontSize: 13 }}>&mdash;</span>
                                                    ) : (
                                                        <input type="text" value={line.module_name || ''}
                                                            onChange={(e) => updateStep7Line(idx, { module_name: e.target.value })}
                                                            placeholder="e.g. Enterprise ACV"
                                                            style={{ width: '100%', padding: '6px 8px', border: '1px solid #D0D5DD', borderRadius: 4, fontFamily: FONT, fontSize: 13 }} />
                                                    )}
                                                </td>
                                                <td style={tdStyle}>
                                                    {DR_BS_LINE_MODULE_TYPES.includes(line.module_type) ? (() => {
                                                        const synthKey = `deferred_rev_${line.module_type}`;
                                                        const known = new Set([...step7DrOptions.map(o => o.id), synthKey]);
                                                        return (
                                                            <div>
                                                                <select value={line.bs_line_key || ''}
                                                                    onChange={(e) => updateStep7Line(idx, { bs_line_key: e.target.value })}
                                                                    style={{ width: '100%', minWidth: 200, padding: '6px 8px', borderRadius: 4, fontFamily: FONT, fontSize: 13, background: '#fff', border: `1px solid ${line.bs_line_key ? '#D0D5DD' : '#E53935'}` }}>
                                                                    <option value="">&mdash; Select &mdash;</option>
                                                                    {step7DrOptions.map(o => (
                                                                        (() => { const by = step7ImplDrLinks.find(x => x.key === o.id); /* W-081 */
                                                                        const resv = step7DrReservations.has(o.id) && line.bs_line_key !== o.id;   /* W-081 part 2 */ return (
                                                                        <option key={o.id} value={o.id} disabled={!!by || resv}>{o.label + (by ? ' (used by the implementation line "' + by.name + '")' : resv ? ' (reserved for implementation revenue)' : '')}</option>); })()
                                                                    ))}
                                                                    <option value={synthKey}>Synthetic (forecast-only)</option>
                                                                    {line.bs_line_key && !known.has(line.bs_line_key) && (
                                                                        <option value={line.bs_line_key}>{line.bs_line_key}</option>
                                                                    )}
                                                                </select>
                                                                {!line.bs_line_key && (
                                                                    <div style={{ fontSize: 10, color: '#E53935', fontFamily: FONT, marginTop: 2, lineHeight: 1.3 }}>
                                                                        Required &mdash; where deferred revenue rolls on the Balance Sheet.
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })() : (
                                                        <span style={{ color: '#CCC', fontSize: 13 }}>&mdash;</span>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        <div style={{ fontFamily: FONT, fontSize: 12, color: '#777', marginTop: 10, lineHeight: 1.5 }}>
                            Account Executive (AE) Quota and Company Quota are one-per-company: AE Quota is driven by your sales rep
                            roster and Company Quota by your company quota settings, so each forecasts one revenue
                            line. To forecast several historical lines together, map one to the module and set the
                            others to Skip &mdash; the forecast total will be correct.
                        </div>
                        {/* W-081 part 2: a deferred-revenue account no AE / CQ module above uses (and no implementation line links yet) can be
                            RESERVED for implementation revenue -- implementation lines are set up after onboarding, on the Deferred Revenue
                            page; Step 7 then accepts the account, and go-live is blocked (pre-flight P20) until a deferred implementation
                            line links it. */}
                        {(() => {
                            const aeCqKeys = new Set(step7Lines.filter(l => DR_BS_LINE_MODULE_TYPES.includes(l.module_type) && l.bs_line_key).map(l => l.bs_line_key));
                            const implKeys = new Set(step7ImplDrLinks.map(x => x.key));
                            // a RESERVED account stays listed even when a module now uses it, so a clash the save refuses can be unmarked here
                            const candidates = step7DrOptions.filter(o => step7DrReservations.has(o.id) || (!aeCqKeys.has(o.id) && !implKeys.has(o.id)));
                            if (!candidates.length) return null;
                            return (
                                <div data-w081="step7-reserve" style={{ marginTop: 16, padding: '12px 16px', border: '1px solid #E0E0E0', borderRadius: 6, fontFamily: FONT }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1A1C2E', marginBottom: 4 }}>Deferred-revenue accounts no module above uses</div>
                                    <div style={{ fontSize: 12, color: '#777', lineHeight: 1.5 }}>
                                        Every deferred-revenue account needs a revenue stream. If one carries implementation fees, mark it: implementation
                                        lines are set up after onboarding, and going live is blocked until one is linked to it.
                                    </div>
                                    {!step7ResvLoaded && <div style={{ fontSize: 12, color: '#E53935', marginTop: 6 }}>The saved reservations could not be loaded &mdash; reload this step before changing them.</div>}
                                    {candidates.map(o => {
                                        const on = step7DrReservations.has(o.id);
                                        return (
                                            <label key={o.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#333', marginTop: 8, cursor: step7ResvLoaded ? 'pointer' : 'not-allowed' }}>
                                                <input type="checkbox" data-w081="reserve" data-key={o.id} checked={on} disabled={!step7ResvLoaded}
                                                    onChange={(e) => { const v = e.target.checked; setStep7DrReservations(prev => { const n = new Set(prev); if (v) n.add(o.id); else n.delete(o.id); return n; }); setStep7Error(''); }} />
                                                <span><strong>{o.label}</strong> &mdash; For implementation revenue &mdash; link it on the Deferred Revenue page
                                                    {on && <span style={{ display: 'block', fontSize: 12, color: '#777', marginTop: 2 }}>
                                                        After onboarding, open Deferred Revenue &rarr; Implementation, add a deferred implementation line and choose this
                                                        account as its Deferred Revenue Line. Going live is blocked until you do.
                                                    </span>}
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            );
                        })()}
                        <div style={{ marginTop: 16, padding: '16px 20px', background: '#F9FAFB', border: '1px solid #E0E0E0', borderRadius: 6 }}>
                            <div style={{ fontFamily: FONT, fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Module Preview</div>
                            {groupEntries.length === 0 && skipLines.length === 0 ? (
                                <div style={{ color: '#888', fontSize: 13, fontFamily: FONT }}>No modules configured.</div>
                            ) : (
                                <div style={{ fontFamily: FONT }}>
                                    {groupEntries.map(g => (
                                        <div key={`tree-${g.type}`} style={{ marginBottom: 14 }}>
                                            <div style={{ fontSize: 14, fontWeight: 600, color: '#1A1C2E' }}>
                                                <span style={{ color: '#1A73E8', marginRight: 6 }}>&#9656;</span>
                                                {REV_MODULE_LABEL[g.type] || g.type} &mdash; {g.name || <span style={{ fontStyle: 'italic', color: '#999' }}>Unnamed</span>}
                                            </div>
                                            {g.lines.map(ln => (
                                                <div key={ln.gl_line_key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 13, color: '#444', paddingLeft: 28, marginTop: 4 }}>
                                                    <span>&bull; {ln.display_name}</span>
                                                    <span style={{ fontFamily: MONO, color: BLUE, marginLeft: 12 }}>{fmtMoney(ln.most_recent_amount)}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                    {skipLines.length > 0 && (
                                        <div style={{ marginTop: groupEntries.length ? 10 : 0, color: '#888' }}>
                                            <div style={{ fontSize: 14, fontWeight: 600 }}>
                                                <span style={{ marginRight: 6 }}>&#9656;</span>
                                                Skip / Not Forecasted
                                            </div>
                                            {skipLines.map(ln => (
                                                <div key={`skip-${ln.gl_line_key}`} style={{ fontSize: 13, paddingLeft: 28, marginTop: 4 }}>
                                                    &bull; {ln.display_name}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </>
                )}
                {step7Error && <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginTop: 12 }}>{step7Error}</div>}
                <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                    <button onClick={() => handleBack(7)} style={btnText} disabled={step7Saving}>&larr; Back</button>
                    <button onClick={handleStep7Save} disabled={step7Saving || step7Lines.length === 0} style={btnPrimary}>
                        {step7Saving ? 'Saving...' : 'Save & Continue \u2192'}
                    </button>
                </div>
            </div>
        );
    };

    // ── Step 8: Fixed & Intangible Assets ────────────────────
    // Final onboarding step. Lets user either upload a D&A schedule Excel file
    // OR manually enter fixed/intangible assets. On successful import, marks
    // companies.is_onboarded = true and shows the completion screen.
    const loadStep8Data = useCallback(async (companyId) => {
        try {
            setStep8Loading(true);
            setStep8ImportError('');
            const [statusRes, defrevRes] = await Promise.all([
                getOnboardingAssetsStatus(companyId),
                getDefrevData(companyId).catch(() => ({ data: {} })),
            ]);
            const s = statusRes.data || {};
            setStep8ExistingFixed(s.fixed_count || 0);
            setStep8ExistingIntang(s.intangible_count || 0);

            // BS-line options for each asset row = the tenant's fixed_asset / intangible_asset
            // BS leaves (shared helper, by role — contra lines excluded automatically). key = the
            // BS leaf id (the asset's bs_line_id). NO Semitek fallback: an empty list means the BS
            // chart has no asset lines yet (operator must complete Step 4 first) — the import
            // validates a real bs_line_id per asset rather than defaulting a guess.
            const _abl = assetBsLineOptionsFromMeta(defrevRes?.data?.bs_actuals_meta || {});
            const faOptions = _abl.fa.map(o => ({ key: o.id, label: o.label }));
            const iaOptions = _abl.ia.map(o => ({ key: o.id, label: o.label }));
            setStep8FaOptions(faOptions);
            setStep8IaOptions(iaOptions);

            const hasExisting = (s.fixed_count || 0) > 0 || (s.intangible_count || 0) > 0;
            // If already complete AND user re-visits step 8 from the step indicator,
            // go straight to re-entry. Otherwise start at home.
            setStep8SubStep(hasExisting ? 're-entry' : 'home');
        } catch (err) {
            console.error('Failed to load Step 8 data:', err);
            setStep8SubStep('home');
        } finally { setStep8Loading(false); }
    }, []);

    useEffect(() => {
        if (viewingStep === 8 && selectedCompanyId && obState && step8SubStep === null && !step8Loading) {
            loadStep8Data(selectedCompanyId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewingStep, selectedCompanyId, obState, step8SubStep, step8Loading]);

    const handleStep8Parse = async () => {
        if (!step8File) return;
        setStep8ParseError(''); setStep8Parsing(true);
        try {
            const buf = await step8File.arrayBuffer();
            // AST-2: hand the parser the tenant's real BS asset-line keys + THE slugifier
            // (normalizeBSKey — one implementation, passed in, not copied), so a canonical
            // sheet resolves + validates bs_line per row and fails loud on an unknown line.
            const faKeys = step8FaOptions.map(o => o.key);
            const iaKeys = step8IaOptions.map(o => o.key);
            const result = parseAssetExcel(buf, { faKeys, iaKeys, normalizeKey: normalizeBSKey });

            // asset_type already holds the validated bs_line_id (or '' on a bad bs_line, which carries a matching error
            // flag). W-063: nothing is ever guessed -- a sheet with no bs_line header is not read, and B7 blocks it below.
            const fixed = result.fixed_assets || [];
            const intang = result.intangible_assets || [];

            // ★ FAIL LOUD at parse: an unrecognized bs_line / type / missing BS line is an
            // error flag — surface it and do NOT advance, rather than silently picking a line.
            /* W-068 pass 3 -- THE UPLOAD CHECK joins the parser's own errors. CLAUDE.md section 12 STANDING HAZARD (a): this screen
             * blocks on `type === 'error'`. That predicate is KEPT EXACTLY AS IT WAS for the parser's errors, and the
             * check's problems are added by their SEVERITY beside it -- so the existing error block still works and no
             * parser flag had to change its `type`. A parser error that says the same thing as a problem about the same
             * row and column (type, bs_line) is shown once (mergeSpecProblems). */
            const flags8 = mergeSpecProblems(result.flags, checkAgainstSpec('asset', result));
            const errs = flags8.filter(f => f.type === 'error' || f.severity === BLOCKING);
            if (errs.length) {
                setStep8ParseError([blockingHeading(errs.length), ...errs.map(e => e.message)].join('  \u2022  '));
                setStep8Fixed(fixed);
                setStep8Intang(intang);
                setStep8Flags(flags8);
                return;
            }
            setStep8Fixed(fixed);
            setStep8Intang(intang);
            setStep8Flags(flags8);
            setStep8SubStep('review');
        } catch (err) {
            console.error('[Step 8 Parse]', err);
            setStep8ParseError('Failed to parse file: ' + (err?.message || 'Unknown error'));
        } finally { setStep8Parsing(false); }
    };

    /* W-063 (rule 92): a new row starts with NO balance-sheet line -- the operator chooses one ("Required -- choose a
     * line"). It used to be born on the first line of the list, shown exactly like a decision. */
    const blankAsset = () => {
        return {
            name: '',
            asset_type: '',
            cost: 0,
            purchase_date: '',
            disposal_date: '',
            useful_life_years: '',
            method: 'SL',
        };
    };

    const handleStep8AddFixed = () => {
        setStep8Fixed(prev => [...prev, blankAsset('fixed')]);
        if (step8SubStep !== 'review') setStep8SubStep('review');
    };

    const handleStep8AddIntang = () => {
        setStep8Intang(prev => [...prev, blankAsset('intangible')]);
        if (step8SubStep !== 'review') setStep8SubStep('review');
    };

    const updateStep8Fixed = (idx, patch) => setStep8Fixed(prev => {
        const next = [...prev]; next[idx] = { ...next[idx], ...patch }; return next;
    });
    const updateStep8Intang = (idx, patch) => setStep8Intang(prev => {
        const next = [...prev]; next[idx] = { ...next[idx], ...patch }; return next;
    });
    const removeStep8Fixed = (idx) => setStep8Fixed(prev => prev.filter((_, i) => i !== idx));
    const removeStep8Intang = (idx) => setStep8Intang(prev => prev.filter((_, i) => i !== idx));

    const handleStep8ReImport = () => {
        setStep8File(null); setStep8Parsing(false); setStep8ParseError('');
        setStep8Flags([]); setStep8Fixed([]); setStep8Intang([]);
        setStep8SubStep('home');
    };

    const handleStep8TemplateDownload = () => {
        const companyName = selectedCompany?.name || 'Your Company Name';
        downloadAssetBlank(companyName);
    };

    // Blank-template downloads for the historical imports (Steps 2/5/6). Each reads the
    // tenant's period from obState so the ISO month columns match its historical window.
    const tplName = () => selectedCompany?.name || 'Your Company Name';
    const handleDownloadISBlank = () => downloadISBlank(tplName(), obState?.historical_start, obState?.close_month);
    const handleDownloadBSBlank = () => downloadBSBlank(tplName(), obState?.historical_start, obState?.close_month);
    // S1 (2026-11-02): the Pre-Launch starter templates -- the generic lines, every amount 0 (utils/preLaunchStarter.js).
    const handleDownloadISStarter = () => downloadISStarter(tplName(), obState?.historical_start, obState?.close_month);
    const handleDownloadBSStarter = () => downloadBSStarter(tplName(), obState?.historical_start, obState?.close_month);
    const handleDownloadVendorBlank = () => downloadVendorBlank(tplName(), obState?.historical_start, obState?.close_month);
    const handleDownloadEmployeeBlank = () => downloadEmployeeBlank(tplName());

    // Validates date format strictly YYYY-MM-DD.
    const validYMD = (s) => typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.trim());

    // Normalize any date string from the asset tables to YYYY-MM-DD before it
    // hits the server. The DateInputMDY component commits YMD on blur, but if
    // the user clicks Import without blurring the cell the stored value can
    // still be MM-DD-YYYY. This is the defensive safety net.
    const toYMD = (dateStr) => {
        if (!dateStr) return null;
        const clean = String(dateStr).trim();
        if (!clean) return null;
        if (/^\d{4}-\d{2}-\d{2}$/.test(clean)) return clean;
        const parts = clean.split('-');
        if (parts.length === 3 && parts[2].length === 4) {
            const mm = parts[0].padStart(2, '0');
            const dd = parts[1].padStart(2, '0');
            return `${parts[2]}-${mm}-${dd}`;
        }
        return clean;
    };

    // W-063: a row with no balance-sheet line cannot be imported -- the buttons say so before the handler has to.
    const step8LineMissing = [...step8Fixed, ...step8Intang].some(a => !a.asset_type);

    const handleStep8Import = async () => {
        setStep8ImportError('');
        const allAssets = [...step8Fixed, ...step8Intang];
        // S2 (2026-11-02): with the "no assets yet" tick the import runs with two empty lists (the server accepts it and sets is_onboarded).
        if (!allAssets.length && !(step8NoAssetsConfirmed && !step8ExistingFixed && !step8ExistingIntang)) { setStep8ImportError('Add at least one asset before importing.'); return; }
        const checkLife = (n) => {
            const v = Number(n);
            return Number.isInteger(v) && v >= 1 && v <= 50;
        };
        // Validate against the normalized YMD so a user who clicks Import
        // without tabbing out of a date cell still passes if the text is a
        // valid MM-DD-YYYY.
        if (!step8FaOptions.length && step8Fixed.length) { setStep8ImportError(`No fixed-asset Balance Sheet lines are configured — complete Step 4 (BS chart) first.`); return; }
        if (!step8IaOptions.length && step8Intang.length) { setStep8ImportError(`No intangible-asset Balance Sheet lines are configured — complete Step 4 (BS chart) first.`); return; }
        for (const a of step8Fixed) {
            if (!a.name) { setStep8ImportError(`Fixed asset is missing a name.`); return; }
            if (!a.asset_type) { setStep8ImportError(`Fixed asset "${a.name}" has no Balance Sheet line selected.`); return; }
            const pd = toYMD(a.purchase_date);
            if (!validYMD(pd || '')) { setStep8ImportError(`Fixed asset "${a.name}" has an invalid purchase date (expected MM-DD-YYYY).`); return; }
            if (!checkLife(a.useful_life_years)) { setStep8ImportError(`Fixed asset "${a.name}" needs a useful life between 1 and 50 years.`); return; }
        }
        for (const a of step8Intang) {
            if (!a.name) { setStep8ImportError(`Intangible asset is missing a name.`); return; }
            if (!a.asset_type) { setStep8ImportError(`Intangible asset "${a.name}" has no Balance Sheet line selected.`); return; }
            const pd = toYMD(a.purchase_date);
            if (!validYMD(pd || '')) { setStep8ImportError(`Intangible asset "${a.name}" has an invalid purchase date (expected MM-DD-YYYY).`); return; }
            if (!checkLife(a.useful_life_years)) { setStep8ImportError(`Intangible asset "${a.name}" needs a useful life between 1 and 50 years.`); return; }
        }
        try {
            setStep8Importing(true);
            const normalizeAsset = (a) => ({
                name: a.name,
                // The row's chosen BS line (step8*Options key = the BS leaf id). Sent as bs_line_id;
                // the server validates it is a role-correct BS leaf and rejects the batch otherwise.
                bs_line_id: a.asset_type || '',
                cost: Number(a.cost) || 0,
                purchase_date: toYMD(a.purchase_date),
                disposal_date: toYMD(a.disposal_date),
                useful_life_years: parseInt(a.useful_life_years, 10),
                method: a.method || 'SL',
                /* The payload is hand-built field by field, which is exactly the
                   shape that dropped this value for as long as the importer has
                   existed: the parser can emit a field forever and it never arrives.
                   Meaningful for fixed assets only -- the intangible INSERT does not
                   name the column, so an IA row's value is discarded server-side. */
                salvage_value: Number(a.salvage_value) || 0,
            });
            const payload = {
                fixed_assets: step8Fixed.map(normalizeAsset),
                intangible_assets: step8Intang.map(normalizeAsset),
            };
            console.log('[Step8Import] first fixed asset:', JSON.stringify(payload.fixed_assets[0]));
            console.log('[Step8Import] first intangible asset:', JSON.stringify(payload.intangible_assets[0]));
            await importOnboardingAssets(selectedCompanyId, payload);
            companiesChanged();   // W-027: the import sets is_onboarded -- the shell must see it
            await loadWizardState(selectedCompanyId);
            // In edit mode, return to the re-entry summary with refreshed
            // counts; otherwise this is the fresh-import flow → completion.
            if (step8EditMode) {
                try {
                    const st = await getOnboardingAssetsStatus(selectedCompanyId);
                    setStep8ExistingFixed(st.data?.fixed_count || 0);
                    setStep8ExistingIntang(st.data?.intangible_count || 0);
                } catch { /* ignore */ }
                setStep8EditMode(null);
                setStep8SubStep('re-entry');
                setViewingStep(8);
            } else {
                setStep8SubStep('complete');
                setViewingStep(8);
            }
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || 'Unknown error';
            setStep8ImportError('Failed to import assets: ' + msg);
            console.error(err);
        } finally { setStep8Importing(false); }
    };

    // Load current assets from DB into editable state and jump to the edit
    // sub-step. Used by the re-entry summary's "Edit Fixed / Intangible"
    // links. Same pattern Steps 5 and 6 use for their re-entry edit flows.
    const handleStep8Edit = async (which) => {
        try {
            setStep8Loading(true);
            setStep8ImportError('');
            const [fRes, iRes, defrevRes] = await Promise.all([
                getFixedAssets(selectedCompanyId).catch(() => ({ data: [] })),
                getIntangibleAssets(selectedCompanyId).catch(() => ({ data: [] })),
                getDefrevData(selectedCompanyId).catch(() => ({ data: {} })),
            ]);
            // pg returns DATE as an ISO datetime; keep only the YYYY-MM-DD
            // portion. DateInputMDY flips that into MM-DD-YYYY for display.
            const toYmdOnly = (d) => {
                if (!d) return '';
                const s = String(d);
                return s.includes('T') ? s.split('T')[0] : s;
            };
            const fixed = (fRes.data || []).map(r => ({
                name: r.description || '',
                asset_type: r.bs_line_id || '',
                cost: Number(r.cost) || 0,
                purchase_date: toYmdOnly(r.purchase_date),
                disposal_date: '',
                useful_life_years: Number(r.useful_life_years) || '',
                method: r.depreciation_method || 'SL',
                _dbId: r.id,
            }));
            const intang = (iRes.data || []).map(r => ({
                name: r.description || '',
                asset_type: r.bs_line_id || '',
                cost: Number(r.cost) || 0,
                purchase_date: toYmdOnly(r.purchase_date),
                disposal_date: '',
                useful_life_years: Number(r.useful_life_years) || '',
                method: r.amortization_method || 'SL',
                _dbId: r.id,
            }));
            // BS-line options (shared helper, by role — same as the fresh-import loader). key = BS
            // leaf id (the asset's bs_line_id). No Semitek fallback.
            const _abl = assetBsLineOptionsFromMeta(defrevRes?.data?.bs_actuals_meta || {});
            const faOptions = _abl.fa.map(o => ({ key: o.id, label: o.label }));
            const iaOptions = _abl.ia.map(o => ({ key: o.id, label: o.label }));
            setStep8FaOptions(faOptions);
            setStep8IaOptions(iaOptions);
            setStep8Fixed(fixed);
            setStep8Intang(intang);
            setStep8EditMode(which);
            setStep8SubStep(which === 'fixed' ? 'edit-fixed' : 'edit-intang');
        } catch (err) {
            console.error('[Step 8 Edit Load]', err);
            setStep8ImportError('Failed to load assets: ' + (err?.message || 'Unknown error'));
        } finally { setStep8Loading(false); }
    };

    const handleStep8BackToSummary = () => {
        setStep8EditMode(null);
        setStep8ImportError('');
        setStep8SubStep('re-entry');
    };

    const Step8DropZone = () => {
        const [dragOver, setDragOver] = useState(false);
        return (
            <div onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)}
                onDrop={e => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files[0]) setStep8File(e.dataTransfer.files[0]); }}
                onClick={() => step8FileRef.current?.click()}
                style={{ border: `2px dashed ${dragOver ? '#1A73E8' : '#D0D5DD'}`, borderRadius: 8,
                    padding: '28px 20px', textAlign: 'center', cursor: 'pointer',
                    background: dragOver ? '#F0F6FF' : '#FAFAFA', transition: 'all 0.15s' }}>
                <input ref={step8FileRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                    onChange={e => { if (e.target.files[0]) setStep8File(e.target.files[0]); }} />
                {step8File ? (
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1A1C2E', fontFamily: FONT }}>{step8File.name}</div>
                        <div style={{ fontSize: 12, color: '#888', fontFamily: FONT, marginTop: 4 }}>Click to change</div>
                    </div>
                ) : (
                    <div>
                        <div style={{ fontSize: 14, color: '#666', fontFamily: FONT }}>Upload your D&amp;A Schedule (.xlsx)</div>
                        <div style={{ fontSize: 11, color: '#999', fontFamily: FONT, marginTop: 4 }}>Expects Depreciation and Amortization sections</div>
                    </div>
                )}
            </div>
        );
    };

    /* THE D&A START CONVENTION AT STEP 8 (2026-10-13) -- a company-wide setting (utils/daStartConvention.js owns its labels,
       its stored key and its payload): depreciation / amortization starts in the month an asset is placed in service, or
       the month after. EDITABLE here (the wizard is the Finledom administrator's), on the review screen and on the
       "already imported" screen. It saves on change, by its own PUT that names only its one key -- not with the import,
       so it does not wait for (or depend on) the asset upload. It changes no asset row and no upload column. */
    const step8DaStartValue = normalizeDaStart(
        step8DaStart && step8DaStart.cid === selectedCompanyId ? step8DaStart.value : daStartFromOnboardingState(obState));
    const handleStep8DaStart = async (value) => {
        const next = normalizeDaStart(value);
        if (next === step8DaStartValue || step8DaStartSaving) return;
        setStep8DaStartSaving(true);
        setStep8DaStartError('');
        try {
            await updateOnboardingState(selectedCompanyId, daStartSavePayload(next));
            setStep8DaStart({ cid: selectedCompanyId, value: next });
        } catch (err) {
            setStep8DaStartError('Not saved: ' + (err?.response?.data?.error || err?.message || 'unknown error'));
        } finally { setStep8DaStartSaving(false); }
    };
    const renderStep8DaStart = () => (
        <div data-step8-da-start="row" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 14px', background: '#F8F9FB', border: '1px solid #E0E3EB', borderRadius: 6, marginBottom: 16, fontSize: 13, fontFamily: FONT, color: '#1A1C2E' }}>
            <span style={{ fontWeight: 600 }}>{DA_START_FIELD_LABEL}:</span>
            <select data-step8-da-start="select" value={step8DaStartValue} disabled={step8DaStartSaving}
                onChange={e => handleStep8DaStart(e.target.value)}
                style={{ padding: '5px 8px', border: '1px solid #C8D0E0', borderRadius: 4, fontSize: 13, fontFamily: FONT, color: '#1A1C2E', background: '#fff' }}>
                {DA_START_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
            <span style={{ color: '#666', fontSize: 12 }}>
                Applies to every asset of this company, amortization included. Saved when changed. The cost of an asset is still added to the balance sheet in its purchase month.
            </span>
            {step8DaStartSaving && <span style={{ color: '#888', fontSize: 12 }}>Saving...</span>}
            {step8DaStartError && <span data-step8-da-start="error" style={{ color: '#E53935', fontSize: 12 }}>{step8DaStartError}</span>}
        </div>
    );

    const renderStep8ReEntry = () => {
        const statLabel = { fontSize: 11, color: '#6B7094', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, marginBottom: 4 };
        const statValue = { fontSize: 22, fontFamily: MONO, fontWeight: 600, color: '#1A1C2E' };
        return (
            <div>
                <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 18, marginBottom: 16, marginTop: 0 }}>Fixed &amp; Intangible Assets</h3>
                <div style={{ padding: '20px 24px', background: '#F5F8FF', border: '1px solid #D6E4FF', borderRadius: 8, marginBottom: 20 }}>
                    <div style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 14, marginBottom: 16 }}>
                        Assets already imported for this company.
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 24, marginBottom: 16 }}>
                        <div>
                            <div style={statLabel}>Fixed assets</div>
                            <div style={statValue}>{step8ExistingFixed}</div>
                        </div>
                        <div>
                            <div style={statLabel}>Intangible assets</div>
                            <div style={statValue}>{step8ExistingIntang}</div>
                        </div>
                    </div>
                    <div style={{ borderTop: '1px solid rgba(26,115,232,0.15)', paddingTop: 14, display: 'flex', gap: 16, fontSize: 13, fontFamily: FONT, flexWrap: 'wrap' }}>
                        <button onClick={() => handleStep8Edit('fixed')} disabled={step8Loading}
                            style={{ ...btnText, padding: 0, opacity: step8Loading ? 0.5 : 1 }}>
                            Edit Fixed Assets
                        </button>
                        <span style={{ color: '#C8D0E0' }}>|</span>
                        <button onClick={() => handleStep8Edit('intangible')} disabled={step8Loading}
                            style={{ ...btnText, padding: 0, opacity: step8Loading ? 0.5 : 1 }}>
                            Edit Intangible Assets
                        </button>
                    </div>
                </div>
                {step8ImportError && <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginBottom: 12 }}>{step8ImportError}</div>}
                {renderStep8DaStart()}
                <div style={{ display: 'flex', gap: 12 }}>
                    <button onClick={() => handleBack(8)} style={btnText}>&larr; Back</button>
                    <button onClick={handleStep8ReImport} style={btnSecondary}>Re-import Assets</button>
                    <button onClick={() => setStep8SubStep('complete')} style={btnPrimary}>View Completion &rarr;</button>
                </div>
            </div>
        );
    };

    const renderStep8EditFixed = () => (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12, marginTop: 0 }}>
                <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 18, margin: 0 }}>Edit Fixed Assets</h3>
                <button onClick={handleStep8AddFixed} style={btnText}>+ Add Fixed Asset</button>
            </div>
            <p style={{ fontFamily: FONT, color: '#666', fontSize: 13, margin: '0 0 16px' }}>
                Edits save back to the live model. Disposal dates are captured for context but not yet persisted to the engine.
            </p>
            {renderAssetTable(step8Fixed, 'fixed', updateStep8Fixed, removeStep8Fixed, step8FaOptions)}
            {step8ImportError && <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginTop: 12 }}>{step8ImportError}</div>}
            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                <button onClick={handleStep8BackToSummary} style={btnText} disabled={step8Importing}>&larr; Back to Summary</button>
                <button onClick={handleStep8Import} disabled={step8Importing || step8LineMissing} data-step8-save="edit"
                    style={{ ...btnPrimary, opacity: (step8Importing || step8LineMissing) ? 0.5 : 1 }}>
                    {step8Importing ? 'Saving...' : 'Save Changes'}
                </button>
            </div>
        </div>
    );

    const renderStep8EditIntang = () => (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12, marginTop: 0 }}>
                <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 18, margin: 0 }}>Edit Intangible Assets</h3>
                <button onClick={handleStep8AddIntang} style={btnText}>+ Add Intangible Asset</button>
            </div>
            <p style={{ fontFamily: FONT, color: '#666', fontSize: 13, margin: '0 0 16px' }}>
                Edits save back to the live model. Disposal dates are captured for context but not yet persisted to the engine.
            </p>
            {renderAssetTable(step8Intang, 'intangible', updateStep8Intang, removeStep8Intang, step8IaOptions)}
            {step8ImportError && <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginTop: 12 }}>{step8ImportError}</div>}
            <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
                <button onClick={handleStep8BackToSummary} style={btnText} disabled={step8Importing}>&larr; Back to Summary</button>
                <button onClick={handleStep8Import} disabled={step8Importing || step8LineMissing} data-step8-save="edit"
                    style={{ ...btnPrimary, opacity: (step8Importing || step8LineMissing) ? 0.5 : 1 }}>
                    {step8Importing ? 'Saving...' : 'Save Changes'}
                </button>
            </div>
        </div>
    );

    const renderStep8Home = () => {
        const optionCard = { flex: 1, minWidth: 0, padding: '20px 20px 24px', border: '1px solid #E0E0E0', borderRadius: 8, background: '#fff' };
        return (
            <div>
                <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 18, marginBottom: 8, marginTop: 0 }}>Fixed &amp; Intangible Assets</h3>
                <p style={{ fontFamily: FONT, color: '#666', fontSize: 13, margin: '0 0 20px' }}>
                    Import your existing assets either by uploading a D&amp;A schedule or adding them manually.
                </p>
                <div style={{ display: 'flex', gap: 20, marginBottom: 16, flexWrap: 'wrap' }}>
                    <div style={optionCard}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#1A73E8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Option A</div>
                        <div style={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, color: '#1A1C2E', marginBottom: 12 }}>Upload D&amp;A Schedule</div>
                        <Step8DropZone />
                        {step8ParseError && <div style={{ color: '#E53935', fontSize: 12, fontFamily: FONT, marginTop: 10 }}>{step8ParseError}</div>}
                        <div style={{ marginTop: 14 }}>
                            <button onClick={handleStep8Parse} disabled={!step8File || step8Parsing}
                                style={{ ...btnPrimary, opacity: (!step8File || step8Parsing) ? 0.5 : 1 }}>
                                {step8Parsing ? 'Parsing...' : 'Parse File \u2192'}
                            </button>
                        </div>
                    </div>
                    <div style={optionCard}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#27AE60', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>Option B</div>
                        <div style={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, color: '#1A1C2E', marginBottom: 12 }}>Manual Entry</div>
                        <div style={{ fontFamily: FONT, fontSize: 13, color: '#666', marginBottom: 16 }}>
                            Add assets one at a time. You can edit everything on the review screen.
                        </div>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            <button onClick={handleStep8AddFixed} style={btnSecondary}>+ Add Fixed Asset</button>
                            <button onClick={handleStep8AddIntang} style={btnSecondary}>+ Add Intangible Asset</button>
                        </div>
                    </div>
                </div>
                {/* S2 (2026-11-02): a company with no assets can finish Step 8 -- the same confirm pattern as Step 6's no-prepaid tick. */}
                {/* only while NO asset is stored -- the import REPLACES the stored assets, so a Re-import must not offer it */}
                {!(step8Fixed.length || step8Intang.length) && !step8ExistingFixed && !step8ExistingIntang && (
                    <div data-step8-no-assets="" style={{ padding: 12, marginBottom: 20, background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 6, fontFamily: FONT, fontSize: 13, color: '#7A5B00' }}>
                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
                            <input type="checkbox" checked={step8NoAssetsConfirmed} onChange={e => setStep8NoAssetsConfirmed(e.target.checked)} style={{ marginTop: 2 }} />
                            <span>This company has no fixed or intangible assets yet. (Assets can be added later on the Assets page.)</span>
                        </label>
                        <div style={{ marginTop: 10 }}>
                            <button onClick={handleStep8Import} disabled={!step8NoAssetsConfirmed || step8Importing} data-step8-save="no-assets"
                                style={{ ...btnPrimary, opacity: (!step8NoAssetsConfirmed || step8Importing) ? 0.5 : 1 }}>{step8Importing ? 'Saving...' : 'Continue without assets \u2192'}</button>
                        </div>
                        {step8ImportError && <div style={{ color: '#E53935', fontSize: 12, marginTop: 8 }}>{step8ImportError}</div>}
                    </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: '#FAFAFA', border: '1px dashed #D0D5DD', borderRadius: 6, marginBottom: 20 }}>
                    <div style={{ fontFamily: FONT, fontSize: 13, color: '#555' }}>No D&amp;A schedule yet? Download our template to get started.</div>
                    <button onClick={handleStep8TemplateDownload} style={btnText}>Download Template</button>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                    <button onClick={() => handleBack(8)} style={btnText}>&larr; Back</button>
                </div>
            </div>
        );
    };

    // Shared table cell input styles
    const assetInput = { padding: '5px 7px', border: '1px solid #D0D5DD', borderRadius: 4, fontFamily: FONT, fontSize: 12, width: '100%', boxSizing: 'border-box' };
    const assetMonoInput = { ...assetInput, fontFamily: MONO, textAlign: 'right' };
    const assetDateInput = { ...assetInput, fontFamily: MONO };

    const renderAssetTable = (rows, section, updateFn, removeFn, typeOptions) => {
        if (!rows.length) {
            return (
                <div style={{ padding: 16, color: '#888', fontFamily: FONT, fontSize: 13, background: '#FAFAFA', borderRadius: 6, textAlign: 'center' }}>
                    No {section} assets yet.
                </div>
            );
        }
        const lifeInputStyle = { ...assetInput, fontFamily: MONO, textAlign: 'center' };
        // Same split-table sticky header pattern Steps 5 and 6 use. Identical
        // colgroup on header + body tables so columns line up exactly.
        const COLS = (
            <colgroup>
                {/* 8 columns since the Salvage add -- the widths are rebalanced, not
                    appended to. This colgroup is shared by BOTH tables of the
                    split-table sticky-header pattern, so a header cell added without a
                    matching col here silently misaligns the body against the header. */}
                <col style={{ width: '18%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '7%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '11%' }} />
                <col style={{ width: '40px' }} />
            </colgroup>
        );
        // W-063: the method list from its ONE owner (utils/depreciationMethods) -- the D&A page's list and labels.
        const METHOD_OPTIONS = DEP_METHODS.map(m => ({ value: m.id, label: m.label }));
        const headerThStyle = { padding: '8px 10px', fontSize: 11, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px' };
        const tableStyle = { width: '100%', borderCollapse: 'collapse', fontFamily: FONT, fontSize: 12, tableLayout: 'fixed' };
        return (
            <div style={{ border: '1px solid #E0E0E0', borderRadius: 6, overflow: 'hidden' }}>
                <table style={tableStyle}>
                    {COLS}
                    <thead>
                        <tr style={{ background: '#fff', borderBottom: '2px solid #E0E0E0' }}>
                            <th style={{ ...headerThStyle, textAlign: 'left' }}>Name</th>
                            <th style={{ ...headerThStyle, textAlign: 'left' }}>Asset Type</th>
                            <th style={{ ...headerThStyle, textAlign: 'right' }}>Cost</th>
                            <th style={{ ...headerThStyle, textAlign: 'right' }}>Salvage</th>
                            <th style={{ ...headerThStyle, textAlign: 'left' }}>Purchase Date</th>
                            <th style={{ ...headerThStyle, textAlign: 'center' }}>Life (yrs)</th>
                            <th style={{ ...headerThStyle, textAlign: 'left' }}>Method</th>
                            <th style={{ ...headerThStyle, textAlign: 'left' }}>Disposal Date</th>
                            <th style={{ padding: '8px 4px' }}></th>
                        </tr>
                    </thead>
                </table>
                <div style={{ maxHeight: 400, overflowY: 'auto' }}>
                    <table style={tableStyle}>
                        {COLS}
                        <tbody>
                            {rows.map((a, idx) => (
                                <tr key={idx} style={{ borderTop: idx === 0 ? 'none' : '1px solid #F0F0F0' }}>
                                    <td style={{ padding: '6px 10px' }}>
                                        <input type="text" value={a.name || ''}
                                            onChange={e => updateFn(idx, { name: e.target.value })}
                                            style={assetInput} placeholder="Asset name" />
                                    </td>
                                    <td style={{ padding: '6px 10px' }}>
                                        {/* W-063 (rule 92): an empty value shows as "choose a line" -- it used to DISPLAY the first
                                            option while the row held nothing. The Step-3 idiom: a blank required pick reads Required. */}
                                        <select value={a.asset_type || ''}
                                            onChange={e => updateFn(idx, { asset_type: e.target.value })}
                                            style={{ ...assetInput, background: '#fff' }}>
                                            <option value="">{'\u2014 choose a line \u2014'}</option>
                                            {typeOptions.map(opt => <option key={opt.key} value={opt.key}>{opt.label}</option>)}
                                        </select>
                                        {!a.asset_type && (
                                            <div data-step8-mark="required" style={{ fontSize: 11, color: '#B45309', fontWeight: 600, marginTop: 3 }}>
                                                Required &mdash; choose a line
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ padding: '6px 10px' }}>
                                        <NumInput value={a.cost} onChange={v => updateFn(idx, { cost: v })} decimals={2} style={assetMonoInput} />
                                    </td>
                                    <td style={{ padding: '6px 10px' }}>
                                        {/* THE VISIBLE DEFAULT. `?? 0` renders a real 0 for a row whose sheet had no
                                            salvage column, so the operator SEES the value being imported and can change
                                            it. That is the whole point: before this, 0 was not a choice they made, it
                                            was an outcome they were never offered. Optional-with-a-visible-default,
                                            not a blocking flag -- a $0 salvage is legitimate and near-universal. */}
                                        <NumInput value={a.salvage_value ?? 0} onChange={v => updateFn(idx, { salvage_value: v })} decimals={2} style={assetMonoInput} />
                                    </td>
                                    <td style={{ padding: '6px 10px' }}>
                                        <DateInputMDY value={a.purchase_date || ''}
                                            onChange={v => updateFn(idx, { purchase_date: v })}
                                            style={assetDateInput} placeholder="MM-DD-YYYY" />
                                    </td>
                                    <td style={{ padding: '6px 10px' }}>
                                        <input type="text" inputMode="numeric" maxLength={2}
                                            value={a.useful_life_years === '' || a.useful_life_years == null ? '' : String(a.useful_life_years)}
                                            placeholder="e.g. 3"
                                            onFocus={e => { const el = e.target; setTimeout(() => el.select(), 0); }}
                                            onChange={e => {
                                                const cleaned = e.target.value.replace(/[^0-9]/g, '');
                                                if (!cleaned) { updateFn(idx, { useful_life_years: '' }); return; }
                                                const n = parseInt(cleaned, 10);
                                                updateFn(idx, { useful_life_years: isNaN(n) ? '' : n });
                                            }}
                                            style={lifeInputStyle} />
                                    </td>
                                    <td style={{ padding: '6px 10px' }}>
                                        <select value={a.method || 'SL'}
                                            onChange={e => updateFn(idx, { method: e.target.value })}
                                            style={{ ...assetInput, background: '#fff' }}>
                                            {METHOD_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                        </select>
                                    </td>
                                    <td style={{ padding: '6px 10px' }}>
                                        <DateInputMDY value={a.disposal_date || ''}
                                            onChange={v => updateFn(idx, { disposal_date: v })}
                                            style={assetDateInput} placeholder="MM-DD-YYYY" />
                                    </td>
                                    <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                                        <button onClick={() => removeFn(idx)}
                                            style={{ border: 'none', background: 'transparent', color: '#E53935', fontSize: 16, cursor: 'pointer', padding: 4 }}
                                            title="Remove">&times;</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderStep8Review = () => {
        const totalAssets = step8Fixed.length + step8Intang.length;
        return (
            <div>
                <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 18, marginBottom: 6, marginTop: 0 }}>Review Assets</h3>
                <p style={{ fontFamily: FONT, color: '#666', fontSize: 13, margin: '0 0 20px' }}>
                    Review and edit before importing. Disposal dates are captured for context but not yet persisted to the engine.
                </p>
                {renderStep8DaStart()}
                {step8Flags.length > 0 && (
                    <div style={{ padding: '10px 14px', background: '#FFF8E1', border: '1px solid #FFE0A0', borderRadius: 6, marginBottom: 16, fontSize: 12, fontFamily: FONT, color: '#8C6500' }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>Parse notes ({step8Flags.length})</div>
                        {step8Flags.slice(0, 8).map((f, i) => (
                            <div key={i} style={{ marginBottom: 2 }}>&bull; {f.message}</div>
                        ))}
                        {step8Flags.length > 8 && <div style={{ color: '#666' }}>...and {step8Flags.length - 8} more.</div>}
                    </div>
                )}

                <div style={{ marginBottom: 24 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                        <div style={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, color: '#1A1C2E' }}>
                            Fixed Assets <span style={{ color: '#888', fontWeight: 400 }}>({step8Fixed.length})</span>
                        </div>
                        <button onClick={handleStep8AddFixed} style={btnText}>+ Add Fixed Asset</button>
                    </div>
                    {renderAssetTable(step8Fixed, 'fixed', updateStep8Fixed, removeStep8Fixed, step8FaOptions)}
                </div>

                <div style={{ marginBottom: 24 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 10 }}>
                        <div style={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, color: '#1A1C2E' }}>
                            Intangible Assets <span style={{ color: '#888', fontWeight: 400 }}>({step8Intang.length})</span>
                        </div>
                        <button onClick={handleStep8AddIntang} style={btnText}>+ Add Intangible Asset</button>
                    </div>
                    {renderAssetTable(step8Intang, 'intangible', updateStep8Intang, removeStep8Intang, step8IaOptions)}
                </div>

                {step8ImportError && <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginBottom: 12 }}>{step8ImportError}</div>}
                <div style={{ display: 'flex', gap: 12 }}>
                    <button onClick={() => setStep8SubStep('home')} style={btnText} disabled={step8Importing}>&larr; Back</button>
                    <button onClick={handleStep8Import} disabled={step8Importing || totalAssets === 0 || step8LineMissing} data-step8-save="import"
                        style={{ ...btnPrimary, opacity: (step8Importing || totalAssets === 0 || step8LineMissing) ? 0.5 : 1 }}>
                        {step8Importing ? 'Importing...' : 'Import Assets \u2192'}
                    </button>
                </div>
            </div>
        );
    };

    const renderStep8Complete = () => {
        const items = [
            'Company Setup', 'IS & BS Upload', 'GL Mapping', 'Review Financials',
            'Payroll Setup', 'Vendor Setup', 'Revenue Modules', 'Assets',
        ];
        return (
            <div>
                <div style={{ textAlign: 'center', padding: '36px 12px 20px' }}>
                    <div style={{ fontSize: 44, marginBottom: 8 }}>&#127881;</div>
                    <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 24, margin: '0 0 6px' }}>{'Steps 1\u20138 complete'}</h3>
                    <p style={{ fontFamily: FONT, color: '#666', fontSize: 14, margin: 0 }}>
                        <strong style={{ color: '#1A1C2E' }}>{selectedCompany?.name || 'This company'}</strong>{'\u2019s data is in. Next: Step 9 (Module Setup and Company Financials), then Complete Setup \u2014 the go-live check comes after that.'}
                    </p>
                </div>
                <div style={{ maxWidth: 420, margin: '0 auto 24px', padding: '16px 24px', background: '#F6FAF6', border: '1px solid #C8E6C9', borderRadius: 8 }}>
                    {items.map((label, i) => (
                        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0', fontFamily: FONT, fontSize: 13, color: '#1A1C2E' }}>
                            <span style={{ color: '#27AE60', fontSize: 15, fontWeight: 700 }}>&#10003;</span>
                            <span>{label}</span>
                        </div>
                    ))}
                </div>
                <div style={{ maxWidth: 520, margin: '0 auto 24px', padding: '10px 14px', background: '#FFF8E1', border: '1px solid #FFE0A0', borderRadius: 6, fontFamily: FONT, fontSize: 12, color: '#8C6500', textAlign: 'center' }}>
                    Data onboarding is complete — one step remains before go-live: Step 9, where you configure the forecast modules
                    (Module Setup) so the model produces real numbers instead of honest zeros, and then press Complete Setup.
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: 12 }}>
                    <button onClick={() => { setSelectedCompanyId(null); setObState(null); loadCompanies(); }} style={btnSecondary}>&larr; Back to Companies</button>
                    <button onClick={() => setViewingStep(9)} style={btnPrimary}>Continue &rarr; Module Setup</button>
                </div>
            </div>
        );
    };

    const renderStep8 = () => {
        if (step8Loading || step8SubStep === null) return <div style={{ padding: 32, color: '#999', fontFamily: FONT }}>Loading...</div>;
        if (step8SubStep === 're-entry') return renderStep8ReEntry();
        if (step8SubStep === 'home') return renderStep8Home();
        if (step8SubStep === 'review') return renderStep8Review();
        if (step8SubStep === 'edit-fixed') return renderStep8EditFixed();
        if (step8SubStep === 'edit-intang') return renderStep8EditIntang();
        if (step8SubStep === 'complete') return renderStep8Complete();
        return renderStep8Home();
    };

    // ── Step 9: Module Setup (9a-2, shape (b): link-out) ──────────────────────────────
    // A STATUS TABLE over the tenant's wired revenue modules + a BLOCK on Continue.
    // "Configured" comes from the IMPORTED evaluateModuleConfig — the SAME predicate the
    // pre-flight's P8 runs (one implementation, two consumers; never row-existence).
    // ★ Step 9 WRITES NOTHING for settings — the production pages own the forms (DefRev
    // owns the existing book / every P8-blocking field; RevenueBuild owns CQ growth +
    // enabled and misc amounts). The ONLY write here is 9c's misc auto-create (row+route,
    // a true derivation; amounts NEVER seeded — a forecast assumption is a guess).
    const loadStep9Data = useCallback(async (companyId) => {
        setStep9Loading(true);
        setStep9Error('');
        try {
            const [defrevRes, rmmRes, aeRes, cqRes, miscRes, implRes, glmRes, resvRes] = await Promise.all([
                getDefrevData(companyId),
                getRevenueModuleMappings(companyId).catch(() => ({ data: [] })),
                getAEQuotaSettings(companyId),
                getCompanyQuotaSettings(companyId),
                getMiscRevenueLines(companyId).catch(() => ({ data: [] })),
                getImplementationLines(companyId).catch(() => ({ data: [] })),
                /* W-154 / W-123: the owner of each line's name, so a finding names the stream and its account (never a key). */
                getGLLineMappings(companyId).catch(() => ({ data: [] })),
                /* W-138: the Step-7 reservations, for the uncarried-account check below. A failed read -> undefined (not judged). */
                getDrLineReservations(companyId).then(r => r.data).catch(() => undefined),
            ]);
            const d = defrevRes?.data || {};
            const isActuals = d.is_actuals || {};
            const bsActuals = d.bs_actuals || {};
            const rmm = rmmRes?.data || [];
            let miscLines = miscRes?.data || [];
            const cutoff = obState?.close_month || null;
            // ── 9c: auto-create routed misc rows. IDEMPOTENT BY LOOKUP — create only when no
            // line already routes to this rmm row's key (cid=3's id=20 makes this a no-op).
            // monthly_amounts stays {} — the engine emits an honest $0 until the operator
            // enters the assumption on Revenue Build; the status below stays "needs amounts".
            const autoCreated = [];
            /* 2026-11-02 (PantryProof): a create the server REFUSES is said on screen (Module Setup), not only in the console --
               every refusal of a company's Step-7 misc mapping used to leave an empty Misc Revenue card with nothing to explain
               it. A 409 target_already_routed is not collected: another misc line already routes there (the next load finds it). */
            const autoRefused = [];
            for (const r of rmm) {
                if (r.module_type !== 'misc_revenue') continue;
                if (miscLines.some(l => l && l.target_gl_line_key === r.gl_line_key)) continue;
                try {
                    const created = await createMiscRevenueLine(companyId, { target_gl_line_key: r.gl_line_key });
                    if (created?.data) { miscLines = [...miscLines, created.data]; autoCreated.push(r.gl_line_key); }
                } catch (e) {
                    // A 409 target_already_routed = a concurrent create — re-fetch wins next entry.
                    console.warn('misc auto-create skipped for', r.gl_line_key, e?.response?.data || e?.message);
                    const why = e?.response?.data?.error;
                    if (why !== 'target_already_routed') autoRefused.push({ gl_line_key: r.gl_line_key, why: String(why || e?.message || 'the request failed') });
                }
            }
            const aeq = aeRes?.data || {};
            const cq = cqRes?.data || {};
            const bsMeta = d.bs_actuals_meta || {};
            const glByKey = indexGlByLeafKey(glmRes?.data || []);
            const labelOf = k => ownedLabelFor(glByKey, k) || (bsMeta[k] && bsMeta[k].label) || null;
            /* W-138 (2026-10-05): a deferred-revenue account no stream carries -- the ONE rule Step 7's save and the pre-flight's P8
               apply (utils/drCarried.js). Step 9 names it too, because a Step-4 re-import after Step 7 is the case Step 7 cannot
               see. null = the reservations could not be read (not judged here; the go-live check still judges it). */
            const uncarried = resvRes === undefined ? null : uncarriedDeferredRevenueAccounts({
                accounts: deferredRevenueOptionsFromMeta(bsMeta), streams: rmm,
                implLinks: implDeferredLinks(implRes?.data), reservations: Object.keys((resvRes && resvRes.reservations) || {}),
            }).map(o => ({ id: o.id, label: labelOf(o.id) || o.label }));
            const rows = evaluateModuleConfig({
                labelOf,   // W-154 / W-123: findings name the stream and its account
                rmm, isActuals, bsActuals,
                settingsRows: {
                    ae_quota: aeq._row_exists ? aeq : null,
                    company_quota: cq._row_exists ? cq : null,
                },
                moduleSettings: {
                    ae_quota: !!aeq._row_exists,
                    company_quota: !!cq._row_exists,
                    misc_revenue: miscLines.length,
                    implementation: (implRes?.data || []).length,
                },
                miscLines, cutoff,
            });
            // D-D: the copyable prefill suggestions, with provenance — surfaced HERE (the row),
            // typed THERE (the owning page). The two derivable singles only; the AE
            // existing_total_acv grid is operator-authored from the billing system and is
            // not suggestible at all.
            const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
            const withSuggestions = rows.map(row => ({
                ...row,
                suggestedAnchor: num((isActuals[row.gl_line_key] || {})[cutoff]),
                suggestedOpeningBook: row.bs_line_key ? num((bsActuals[row.bs_line_key] || {})[cutoff]) : null,
            }));
            setStep9Rows(withSuggestions);
            setStep9Meta({ cutoff, autoCreated, uncarried,
                autoRefused: autoRefused.map(x => ({ ...x, label: labelOf(x.gl_line_key) || x.gl_line_key })) });
            /* Commission ship 5: the commission row -- the Step-5 gate's rule again (the step bar can pass Step 5's continue once
               Step 5 is complete) and, for an accruing company, P23 in its own words (the real pre-flight). A failed read is said
               and blocks; it is never "nothing to do". */
            try {
                const [cSet, cEmp, cAssum] = await Promise.all([getCompanySettings(companyId), getEmployees(companyId), getBSAssumptions(companyId)]);
                setStep9Comm(commissionAccrualProblems({ companyId, settings: cSet.data, employees: cEmp.data, bsActualsMeta: overlayLeafLabels(bsMeta, glmRes?.data || []), bsAssumptions: cAssum.data }));
            } catch (e) {
                setStep9Comm({ error: (e && e.response && e.response.data && e.response.data.error) || (e && e.message) || 'Unknown error' });
            }
        } catch (err) {
            console.error('Failed to load Step 9 data:', err);
            setStep9Error('Failed to load module status: ' + (err?.message || 'Unknown error'));
        } finally { setStep9Loading(false); }
    }, [obState]);

    useEffect(() => {
        if (viewingStep === 9 && selectedCompanyId && obState && step9Rows === null && !step9Loading && !step9Error) {
            loadStep9Data(selectedCompanyId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewingStep, selectedCompanyId, obState, step9Rows]);   // U19 (2026-10-30): + step9Rows -- "Re-check" sets it to null and must re-run this (diffs/step9-recheck-hang.txt)

    // Build 1 (item 4): load the Company Financials sub-section data (settings row +
    // derived prepaid opening). The opening month is utils/companyWindow.js openingMonthOf -- the month BEFORE
    // historical_start (a January start: the prior December; a Pre-Launch company: the month before its opening balance sheet).
    const loadCompanyFinData = useCallback(async (cid) => {
        setCfLoading(true); setCfError('');
        try {
            /* ★ glMappings is fetched HERE and nowhere else on Step 9. MEASURED: this
             * Promise.all had no GL fetch — `step6Glm` is Step-6-scoped state — so the
             * sweep-interest picker had no vocabulary to offer. The call already exists at
             * five other AdminPage sites; this is a sixth caller, not a new mechanism. */
            const [sRes, dRes, oRes, gRes] = await Promise.all([
                getCompanySettings(cid),
                getDefrevData(cid).catch(() => ({ data: {} })),
                getOnboardingState(cid).catch(() => ({ data: {} })),
                getGLLineMappings(cid).catch(() => ({ data: [] })),
            ]);
            const s = sRes.data || {};
            const histStart = (oRes.data && oRes.data.historical_start) || null;
            const openingMonth = (histStart && /^\d{4}-\d{2}$/.test(histStart)) ? openingMonthOf(histStart) : null;   // N-d1 (2026-11-03): the one owner -- the same month as the inline it replaces
            const bsActuals = (dRes.data && dRes.data.bs_actuals) || {};
            const pe = bsActuals.prepaid_expenses || {};
            const hasBsActuals = Object.keys(bsActuals).length > 0;
            const derivedPrepaid = (openingMonth && pe[openingMonth] != null) ? Number(pe[openingMonth]) : null;
            // N-d1: a Pre-Launch company's row also shows its close-month prepaid (its opening balance sheet), so the month is not misread
            const closeM = (oRes.data && oRes.data.close_month) || null;
            const cfPreLaunch = isOneMonthStart(histStart, closeM);
            const closePrepaid = (closeM && pe[closeM] != null) ? Number(pe[closeM]) : null;
            setCfData({
                comp_cycle_basis: s.comp_cycle_basis || 'calendar_period_end',
                bonus_payout_delay_months: s.bonus_payout_delay_months != null ? Number(s.bonus_payout_delay_months) : 0,
                min_cash: s.min_cash != null ? Number(s.min_cash) : null,
                sweep_rate: s.sweep_rate != null ? Number(s.sweep_rate) : null,
                sweep_mround: s.sweep_mround || 'none',
                commission_delay_months: s.commission_delay_months != null ? Number(s.commission_delay_months) : 0,
                prepaid_start_balance: s.prepaid_start_balance != null ? Number(s.prepaid_start_balance) : null,
                /* The stored sweep-interest answer. NULL = never answered (blocks Step 9);
                   'oi_interest' = the operator chose to keep a separate forecast line;
                   any other key = the historical IS leaf the forecast interest books onto.
                   ⛔ No `||` fallback to an inferred value: a default applied here would BE
                   the silent answer this whole build exists to remove. */
                sweep_interest_is_line_key: s.sweep_interest_is_line_key || null,
                /* D&A routing, Part 2 -- the same three states, one column per stream. No || fallback
                   to an inferred value: NULL is never-answered and blocks Step 9. */
                depreciation_is_line_key: s.depreciation_is_line_key || null,
                amortization_is_line_key: s.amortization_is_line_key || null,
                glMappings: gRes.data || [],
                derivedPrepaid, openingMonth, hasBsActuals, cfPreLaunch, closeMonth: closeM, closePrepaid,
            });
            /* Reset the pending pick so the seeding effect re-runs for THIS tenant — without
               this, switching companies would carry the previous tenant's suggestion. */
            setCfSweepIntPick(undefined);
            setCfDaPick({});
        } catch (err) {
            setCfError('Failed to load company financials: ' + (err?.response?.data?.error || err?.message || 'Unknown error'));
        } finally { setCfLoading(false); }
    }, []);

    useEffect(() => {
        if (viewingStep === 9 && selectedCompanyId && cfData === null && !cfLoading && !cfError) {
            loadCompanyFinData(selectedCompanyId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewingStep, selectedCompanyId, cfData]);

    // Save one settings field via the PUT (COALESCE → sending one field never clobbers
    // the others) and update local cfData. Empty min_cash/sweep_rate are NEVER sent
    // (they must stay null → block); only a typed value (incl. 0) persists.
    const saveCf = async (patch) => {
        try {
            await updateCompanySettings(selectedCompanyId, patch);
            setCfData(prev => ({ ...prev, ...patch }));
            setCfError('');
        } catch (err) {
            setCfError('Failed to save: ' + (err?.response?.data?.error || err?.message || 'Unknown error'));
        }
    };
    /* ═══ SWEEP-INTEREST TARGET — candidates, pre-selection, and the confirm gate ═══
     *
     * THE OPTIONS are the tenant's own OIE leaves, read from the SHARED taxonomy
     * (buildISLineTaxonomy, already imported above and already used by Step 6) — never a
     * third list. ⓘ Filter on the GL row's own is_category/subsection: the ASSEMBLED
     * skeleton buildISLinesForCompany returns carries no `subsection` field at all, so a
     * filter over built lines silently returns zero (measured).
     *
     * THE PRE-SELECTION is the role='oi_interest' leaf, and ONLY when there is exactly one.
     * Two lines named "Interest Income" and "Interest Expense" both take role='oi_interest'
     * from inferIsMeta's NAME test, and nothing can rank them — so >=2 pre-selects NOTHING
     * and names both (a find()-first there would be a silent coin flip on a real accounting
     * decision). 0 candidates leaves only the keep-separate option. */
    const cfIsTaxonomy = useMemo(() => buildISLineTaxonomy(cfData?.glMappings || []), [cfData]);
    const cfOieOptions = useMemo(() => (cfIsTaxonomy.ordered || [])
        .filter(e => e.section === 'Other'), [cfIsTaxonomy]);
    const cfInterestCandidates = useMemo(() => cfOieOptions.filter(e => e.role === 'oi_interest'), [cfOieOptions]);
    /* Seed the pending pick ONCE per tenant load: a stored answer wins; otherwise the
       single-candidate suggestion; otherwise no pre-selection. */
    useEffect(() => {
        if (!cfData || cfSweepIntPick !== undefined) return;
        if (cfData.sweep_interest_is_line_key) { setCfSweepIntPick(cfData.sweep_interest_is_line_key); return; }
        setCfSweepIntPick(cfInterestCandidates.length === 1 ? cfInterestCandidates[0].id : '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cfData, cfInterestCandidates]);
    const cfSweepIntConfirmed = !!(cfData && cfData.sweep_interest_is_line_key);

    // The Company-Financials gate: min_cash + sweep_rate block advance (Option B), and
    // — Build: the sweep-interest seam — the sweep-interest target must be CONFIRMED.
    // Prepaid (validation-only), commission (0 legit), mround ('none' default), and
    // fundraising (unverifiable) never block.
    /* ★ CONFIRM-TO-PROCEED ON EVERY TENANT, INCLUDING THE NO-INTEREST-LINE CASE. The
     * pre-fill makes the common case one click, but the click is never skipped: a tenant
     * with no historical interest line still has to say "keep a separate forecast line",
     * because "we never asked" and "they chose the separate row" are different facts and
     * only one of them is an answer. This is why the column's NULL means never-answered and
     * the 'oi_interest' sentinel means chosen. */
    /* D&A FORECAST TARGETS (D&A routing, Part 2) -- one per stream. THE OPTIONS are
     * daTargetOptions: this tenant's IS rows in subsection 'ooe' carrying the stream's STORED
     * role, keyed leaf_key ?? is_line_key -- the client half of the deployed server guard's
     * predicate, so the picker cannot offer a key the server refuses. THE PRE-SELECTION is the
     * single candidate by stored role, and ONLY when there is exactly one; no name test and
     * none of the suggest helpers. The third state, 'Keep a separate forecast line', stores the
     * stream's sentinel (the synthetic's own id). Once answered the column cannot be cleared
     * back to NULL from here: the settings PUT is COALESCE-on-omit. */
    const cfDaOptions = useMemo(() => Object.fromEntries(DA_STREAMS.map(st =>
        [st.column, daTargetOptions(cfData?.glMappings, st.role)])), [cfData]);
    useEffect(() => {
        if (!cfData) return;
        setCfDaPick(prev => {
            const next = { ...prev }; let changed = false;
            DA_STREAMS.forEach(st => {
                if (next[st.column] !== undefined) return;
                const opts = cfDaOptions[st.column] || [];
                next[st.column] = cfData[st.column] || (opts.length === 1 ? opts[0].id : '');
                changed = true;
            });
            return changed ? next : prev;
        });
    }, [cfData, cfDaOptions]);
    const cfDaConfirmed = !!cfData && DA_STREAMS.every(st => !!cfData[st.column]);
    const cfPass = !!cfData && cfData.min_cash != null && cfData.sweep_rate != null && cfSweepIntConfirmed && cfDaConfirmed;

    // A row passes when the shared predicate says configured/not_applicable, or the operator
    // explicitly confirmed a needs_input state ("$0 is intended") for THIS session.
    const step9RowPasses = (row) =>
        row.status === 'configured' || row.status === 'not_applicable' ||
        (row.status === 'needs_input' && !!step9Confirmed[row.gl_line_key]);

    const handleStep9Complete = async () => {
        if (!step9Rows || !step9Rows.every(step9RowPasses)) return;
        if (step9Meta?.uncarried?.length) return;   // W-138: a deferred-revenue account no stream carries
        if (!step9Comm || step9Comm.error || step9Comm.missing || step9Comm.p23) return;   // commission ship 5: the commission row
        if (!cfPass) return; // Build 1 (item 4): Company Financials — min_cash + sweep_rate must be set
        try {
            setStep9Saving(true);
            const existingSteps = (obState?.steps) || {};
            const newSteps = { ...existingSteps, module_setup: { completed: true } };
            await updateOnboardingState(selectedCompanyId, {
                // cursor is a high-water mark — never moves backward (see the Step-2 note).
                // Step 9 is the LAST step, so this holds at 9 rather than advancing.
                current_step: Math.max(obState?.current_step || 9, 9), steps: newSteps,
                close_month: obState?.close_month,
                forecast_start: obState?.forecast_start,
                historical_start: obState?.historical_start,
            });
            await loadWizardState(selectedCompanyId);
            setViewingStep(9);
        } catch (err) {
            setStep9Error('Failed to save completion: ' + (err?.response?.data?.error || err?.message || 'Unknown error'));
        } finally { setStep9Saving(false); }
    };

    const renderStep9 = () => {
        if (step9Loading || step9Rows === null) {
            return (
                <div style={{ padding: 32, color: '#999', fontFamily: FONT }}>
                    {step9Error ? <span style={{ color: '#E53935' }}>{step9Error}</span> : 'Checking module configuration...'}
                </div>
            );
        }
        const drPass = !(step9Meta?.uncarried?.length);   // W-138
        const commPass = !!step9Comm && !step9Comm.error && !step9Comm.missing && !step9Comm.p23;   // commission ship 5
        const allPass = step9Rows.every(step9RowPasses) && drPass;
        const done = !!obState?.steps?.module_setup?.completed && allPass;
        const CHIP = {
            configured: { bg: '#E8F5E9', fg: '#1B5E20', label: '✓ Configured' },
            blocking: { bg: '#FDECEA', fg: '#B71C1C', label: '✗ Needs setup' },
            needs_input: { bg: '#FFF8E1', fg: '#8C6500', label: '⚠ Needs input' },
            not_applicable: { bg: '#F0F0F0', fg: '#777', label: '—' },
        };
        // Owner links (STEP 0c): every blocking field is DefRev-owned for both singleton
        // types; RevenueBuild owns CQ growth/enabled + misc amounts. ★ The 9a-2 live bug:
        // these were plain <a href> — they navigated but landed on the NAV's selected company,
        // not the wizard's tenant (the two company states are independent; the switcher
        // amendment made the tenant SELECTABLE, nothing SELECTED it). Now each button goes
        // through onConfigureCompany: select the tenant, THEN navigate — one click, the right
        // company's page. The nav company STAYS switched afterwards (deliberate: the operator
        // is working "in" that tenant; the top bar shows which). Wizard position is
        // DB-persisted, and the Step-9 indicator chip is slug-unlocked, so the return trip is
        // Admin → the company → the Module Setup chip.
        /* ★ G6c — CARRY THE TAB. These links used to pass a PATH ONLY, so an operator sent
         * to configure Company Quota landed on the page's DEFAULT tab (ae_quota) and had to
         * find the right one. The destination slug is passed by the CALL SITE because the two
         * pages spell the same modules differently (RevenueBuild "misc_rev"/"impl" vs DefRev
         * "implementation"), so there is no shared vocabulary to derive it from — each page
         * validates ?tab= against its own list and ignores anything else.
         * ★ G6d — AND THE FALL-THROUGH NO LONGER NAVIGATES. It used to run
         *   window.location.href = path  when the tenant was NOT selectable, which sent the
         * operator to that page for whatever tenant the NAV happened to hold — i.e. it
         * silently showed another company's data. Refusing with a named message is the whole
         * point: a dead button that explains itself beats a live button that lies. */
        /* W-027: onConfigureCompany is async -- it re-reads the shell's list before refusing a company it
           does not yet know, so it is awaited. */
        const goConfigure = async (path, tab) => {
            const url = tab ? `${path}?tab=${encodeURIComponent(tab)}` : path;
            if (onConfigureCompany && await onConfigureCompany(selectedCompanyId, url)) return;
            setStep9Error(`${selectedCompany?.name || 'This company'} cannot be opened yet — it is not selectable until onboarding is complete. Finish the wizard, then configure its modules from here.`);
        };
        const linkBtnStyle = { color: '#1A73E8', fontSize: 12, fontFamily: FONT, fontWeight: 600, background: 'none', border: 'none', padding: 0, cursor: 'pointer' };
        return (
            <div>
                <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 18, marginBottom: 6, marginTop: 0 }}>Module Setup</h3>
                <p style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 14, margin: '0 0 4px' }}>
                    Each forecast module needs its settings before it can produce a forecast.
                </p>
                <p style={{ fontFamily: FONT, color: '#666', fontSize: 13, margin: '0 0 16px' }}>
                    Click <strong>Configure</strong> to open the module's page for <strong>{selectedCompany?.name}</strong>,
                    enter the values (each row below tells you what's missing), then come back and <strong>Re-check</strong>.
                </p>
                {step9Meta?.autoCreated?.length > 0 && (
                    <div style={{ marginBottom: 12, padding: '8px 12px', background: '#EEF4FF', border: '1px solid #C9DCFF', borderRadius: 6, fontFamily: FONT, fontSize: 12, color: '#1A4DA0' }}>
                        Created {step9Meta.autoCreated.length} routed Misc Revenue line{step9Meta.autoCreated.length > 1 ? 's' : ''} ({step9Meta.autoCreated.join(', ')}) —
                        the row and its routing are derived from Step 7; the monthly amounts are yours to enter on Revenue Build.
                    </div>
                )}
                {step9Meta?.autoRefused?.length > 0 && (
                    <div data-misc-auto-refused="1" style={{ marginBottom: 12, padding: '8px 12px', background: '#FDECEA', border: '1px solid #F5C2BD', borderRadius: 6, fontFamily: FONT, fontSize: 12, color: '#9B2C21' }}>
                        {step9Meta.autoRefused.map(x => (
                            <div key={x.gl_line_key} style={{ marginBottom: 2 }}>
                                Could not create the Misc Revenue line for <strong>{x.label}</strong>. The server said: {x.why}
                            </div>
                        ))}
                        <div style={{ marginTop: 4, color: '#6B2018' }}>Nothing was saved for {step9Meta.autoRefused.length === 1 ? 'this line' : 'these lines'}. Re-check tries again.</div>
                    </div>
                )}
                <div style={{ overflowX: 'auto', border: '1px solid #E0E0E0', borderRadius: 6 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT, fontSize: 13 }}>
                        <thead><tr>
                            <th style={{ ...thStyle, background: '#fff', textAlign: 'left' }}>Module</th>
                            <th style={{ ...thStyle, background: '#fff', textAlign: 'left' }}>Target line</th>
                            <th style={{ ...thStyle, background: '#fff', textAlign: 'left' }}>Status</th>
                            <th style={{ ...thStyle, background: '#fff', textAlign: 'left', minWidth: 320 }}>What's missing / suggested values</th>
                            <th style={{ ...thStyle, background: '#fff', textAlign: 'left' }}>Configure on</th>
                        </tr></thead>
                        <tbody>
                            {step9Rows.map(row => {
                                const chip = CHIP[row.status] || CHIP.not_applicable;
                                const offenders = [
                                    ...(row.existence ? [{ what: `no ${REV_MODULE_LABEL[row.module_type] || row.module_type} settings yet` }] : []),
                                    ...row.shape, ...row.book,
                                    ...(row.openingArr || []), ...(row.openingBook || []),   // W-154 (b) / W-123: blocking
                                    ...(row.arrEstimate ? [row.arrEstimate] : []),            // W-154 (a): needs input
                                    ...(row.miscAmounts ? [row.miscAmounts] : []),
                                ];
                                const isSingleton = row.module_type === 'ae_quota' || row.module_type === 'company_quota';
                                return (
                                    <tr key={row.gl_line_key} style={{ borderTop: '1px solid #F0F0F0', verticalAlign: 'top' }}>
                                        <td style={{ ...tdStyle, fontWeight: 600 }}>{REV_MODULE_LABEL[row.module_type] || row.module_type}<div style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>{row.module_name}</div></td>
                                        <td style={tdStyle}><span style={{ fontFamily: MONO, fontSize: 12 }}>{row.gl_line_key}</span></td>
                                        <td style={tdStyle}>
                                            <span style={{ background: chip.bg, color: chip.fg, borderRadius: 10, padding: '3px 10px', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>{chip.label}</span>
                                            {row.enabledInfo && <div style={{ fontSize: 11, color: '#8C6500', marginTop: 4 }}>{row.enabledInfo}</div>}
                                        </td>
                                        <td style={tdStyle}>
                                            {offenders.length === 0 ? (
                                                <span style={{ color: '#27AE60', fontSize: 12 }}>All checks pass.</span>
                                            ) : (
                                                <div>
                                                    {offenders.map((o, i) => (
                                                        <div key={i} style={{ fontSize: 12, color: '#444', marginBottom: 4 }}>
                                                            &bull; {o.what}{o.detail ? <span style={{ color: '#888' }}> — {o.detail}</span> : null}
                                                        </div>
                                                    ))}
                                                    {isSingleton && (row.shape.length > 0 || row.existence || (row.openingBook || []).length > 0 || (row.openingArr || []).length > 0) && (
                                                        <div style={{ fontSize: 11, color: '#1A4DA0', background: '#F4F8FF', borderRadius: 4, padding: '6px 8px', marginTop: 4 }}>
                                                            Suggested (copy into Deferred Revenue): start mode <strong>Existing</strong>
                                                            {step9Meta?.cutoff ? <> &middot; GAAP anchor [{step9Meta.cutoff}]: <strong>{fmtMoney(row.suggestedAnchor)}</strong> <span style={{ color: '#7A93BD' }}>(= the line's own close-month actual)</span></> : null}
                                                            {row.suggestedOpeningBook ? <> &middot; opening BS balance: <strong>{fmtMoney(row.suggestedOpeningBook)}</strong> <span style={{ color: '#7A93BD' }}>(= {row.bs_line_key} at close)</span></> : null}
                                                        </div>
                                                    )}
                                                    {row.status === 'needs_input' && (
                                                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#8C6500', marginTop: 6, cursor: 'pointer' }}>
                                                            <input type="checkbox" checked={!!step9Confirmed[row.gl_line_key]}
                                                                onChange={(e) => setStep9Confirmed(prev => ({ ...prev, [row.gl_line_key]: e.target.checked }))} />
                                                            This is intentional (e.g. $0 is correct) &mdash; proceed
                                                        </label>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                        <td style={tdStyle}>
                                            {isSingleton ? (
                                                <div>
                                                    <button onClick={() => goConfigure('/defrev', row.module_type)} style={linkBtnStyle}>Configure: Deferred Revenue &rarr;</button>
                                                    <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>existing book, anchors, opening balance</div>
                                                    {row.module_type === 'company_quota' && (
                                                        <div style={{ marginTop: 6 }}>
                                                            <button onClick={() => goConfigure('/revenue', 'company_quota')} style={linkBtnStyle}>Configure: Revenue Build &rarr;</button>
                                                            <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>growth settings, enable toggle</div>
                                                        </div>
                                                    )}
                                                </div>
                                            ) : row.module_type === 'misc_revenue' ? (
                                                <div>
                                                    <button onClick={() => goConfigure('/revenue', 'misc_rev')} style={linkBtnStyle}>Configure: Revenue Build &rarr;</button>
                                                    <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>monthly amounts (Misc Rev tab)</div>
                                                </div>
                                            ) : (
                                                <div>
                                                    <button onClick={() => goConfigure('/revenue', 'impl')} style={linkBtnStyle}>Configure: Revenue Build &rarr;</button>
                                                    <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>implementation lines</div>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {step9Rows.length === 0 && (
                                <tr><td colSpan={5} style={{ ...tdStyle, color: '#888', textAlign: 'center', padding: 24 }}>No forecast modules wired at Step 7 — nothing to configure.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* W-138 (2026-10-05): a deferred-revenue account no revenue stream carries -- blocks Complete, named. */}
                {step9Meta?.uncarried?.length > 0 && (
                    <div data-step9-uncarried="1" style={{ marginTop: 12, padding: '10px 12px', background: '#FDECEA', border: '1px solid #F5C6C0', borderRadius: 6, fontFamily: FONT, fontSize: 13, color: '#B71C1C' }}>
                        <strong>{step9Meta.uncarried.length === 1 ? 'A deferred-revenue account no revenue stream carries' : 'Deferred-revenue accounts no revenue stream carries'}</strong>
                        {step9Meta.uncarried.map(o => (
                            <div key={o.id} style={{ marginTop: 4 }}>&bull; {o.label} is a deferred-revenue account no revenue stream carries &mdash; link it at Step 7 or on Deferred Revenue</div>
                        ))}
                        <div style={{ marginTop: 6, fontSize: 12, color: '#7A1512' }}>With no stream it books $0 from the first forecast month and its whole close balance moves cash. (Typically a Step-4 re-import after Step 7 added it.)</div>
                    </div>
                )}
                {step9Meta && step9Meta.uncarried === null && (
                    <div style={{ marginTop: 12, fontSize: 12, color: '#8C6500', fontFamily: FONT }}>The Step-7 reservations could not be read, so deferred-revenue accounts were not checked here (the go-live check still checks them).</div>
                )}
                {/* Commission ship 5: the commission answer -- missing (a commissioned rep, no answer) or, for an accruing company,
                    P23's own words. Blocks Complete, named. Silent otherwise. */}
                {step9Comm && step9Comm.error && (
                    <div data-step9-commission="error" style={{ marginTop: 12, fontSize: 13, color: '#B71C1C', fontFamily: FONT }}>The commission answer could not be read: {step9Comm.error}. Re-check before completing.</div>
                )}
                {step9Comm && step9Comm.missing && (
                    <div data-step9-commission="missing" style={{ marginTop: 12, padding: '10px 12px', background: '#FDECEA', border: '1px solid #F5C6C0', borderRadius: 6, fontFamily: FONT, fontSize: 13, color: '#B71C1C' }}>
                        <strong>Commissions</strong>
                        <div style={{ marginTop: 4 }}>{step9Comm.missing}</div>
                        <div style={{ marginTop: 6, fontSize: 12, color: '#7A1512' }}>Answer it at Step 5 (Payroll Setup), on the Employees already imported screen.</div>
                    </div>
                )}
                {step9Comm && step9Comm.p23 && (
                    <div data-step9-commission="p23" style={{ marginTop: 12, padding: '10px 12px', background: '#FDECEA', border: '1px solid #F5C6C0', borderRadius: 6, fontFamily: FONT, fontSize: 13, color: '#B71C1C' }}>
                        <strong>{step9Comm.p23.name}</strong>
                        {step9Comm.p23.offenders.map((o, i) => <div key={i} data-step9-p23-arm={o.arm} style={{ marginTop: 4 }}>&bull; {o.what}</div>)}
                        <div style={{ marginTop: 6, fontSize: 12, color: '#7A1512' }}>{step9Comm.p23.fix}</div>
                    </div>
                )}
                {/* ── Company Financials (Build 1 / item 4) ─────────────────────────── */}
                <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 18, margin: '28px 0 6px' }}>Company Financials</h3>
                <p style={{ fontFamily: FONT, color: '#666', fontSize: 13, margin: '0 0 16px' }}>
                    Company-level settings that feed the forecast. The cash sweep floor and interest rate
                    are required — enter this company's own numbers (there is no default; typing 0 is a
                    deliberate choice).
                </p>
                {cfData && <CompCycleControl bonusDelay={cfData.bonus_payout_delay_months} lag={cfData.commission_delay_months} onChange={saveCf} style={{ marginBottom: 20 }} />}
                {(cfLoading || cfData === null) ? (
                    <div style={{ padding: 20, color: '#999', fontFamily: FONT, fontSize: 13 }}>
                        {cfError ? <span style={{ color: '#E53935' }}>{cfError}</span> : 'Loading company financials…'}
                    </div>
                ) : (() => {
                    const chipCfg = (v) => v != null
                        ? { bg: '#E8F5E9', fg: '#1B5E20', label: '✓ Set' }
                        : { bg: '#FDECEA', fg: '#B71C1C', label: '✗ Needs setup' };
                    const infoChip = { bg: '#F0F0F0', fg: '#777', label: '—' };
                    const inp = { width: 150, fontSize: 12, padding: '6px 8px', borderRadius: 4, border: '1px solid #D1D5DB', textAlign: 'right', fontFamily: MONO };
                    const chipEl = (c) => <span style={{ background: c.bg, color: c.fg, borderRadius: 10, padding: '3px 10px', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>{c.label}</span>;
                    /* ★ `goConfigureCf` LIVED HERE AND IS GONE. It was a near-copy of goConfigure
                     * (defined ~130 lines above, in scope here) with a single call site. When G6d
                     * replaced goConfigure's silent `window.location.href = path` fall-through with a
                     * named refusal, THIS COPY KEPT IT — so the Fundraising link could still open that
                     * page for whatever tenant the NAV happened to hold, which is the defect G6d exists
                     * to remove. A census that greps the NAME finds one wire; the second was called
                     * something else. Both call sites now use the ONE goConfigure. */
                    const linkBtn = { color: '#1A73E8', fontSize: 12, fontFamily: FONT, fontWeight: 600, background: 'none', border: 'none', padding: 0, cursor: 'pointer' };
                    const mcChip = chipCfg(cfData.min_cash), srChip = chipCfg(cfData.sweep_rate);
                    // prepaid status: pending Step-4 data / needs-confirm / set
                    const prepaidSet = cfData.prepaid_start_balance != null;
                    return (
                    <div style={{ overflowX: 'auto', border: '1px solid #E0E0E0', borderRadius: 6 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT, fontSize: 13 }}>
                            <thead><tr>
                                <th style={{ ...thStyle, background: '#fff', textAlign: 'left' }}>Setting</th>
                                <th style={{ ...thStyle, background: '#fff', textAlign: 'left' }}>Value</th>
                                <th style={{ ...thStyle, background: '#fff', textAlign: 'left' }}>Status</th>
                                <th style={{ ...thStyle, background: '#fff', textAlign: 'left', minWidth: 300 }}>What's missing / notes</th>
                            </tr></thead>
                            <tbody>
                                {/* Cash sweep floor — EMPTY REQUIRED, blocks */}
                                <tr style={{ borderTop: '1px solid #F0F0F0', verticalAlign: 'top' }}>
                                    <td style={{ ...tdStyle, fontWeight: 600 }}>Cash sweep floor<div style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>minimum operating cash</div></td>
                                    <td style={tdStyle}><span style={{ color: '#6B7280', fontSize: 11 }}>$ </span>
                                        <input type="text" inputMode="decimal" style={inp} placeholder="required — enter amount"
                                            defaultValue={cfData.min_cash == null ? '' : Number(cfData.min_cash).toLocaleString('en-US')}
                                            key={'cf-mc-' + String(cfData.min_cash)}
                                            onBlur={e => { const raw = e.target.value.replace(/,/g, '').trim(); if (raw === '') return; const v = parseFloat(raw); if (Number.isFinite(v)) saveCf({ min_cash: v }); }} /></td>
                                    <td style={tdStyle}>{chipEl(mcChip)}</td>
                                    <td style={tdStyle}>{cfData.min_cash == null
                                        ? <span style={{ fontSize: 12, color: '#B71C1C' }}>Not set. Enter this company's minimum operating cash balance (the floor the sweep keeps in checking each month).</span>
                                        : <span style={{ fontSize: 12, color: '#27AE60' }}>Set.</span>}</td>
                                </tr>
                                {/* Sweep interest rate — EMPTY REQUIRED, blocks */}
                                <tr style={{ borderTop: '1px solid #F0F0F0', verticalAlign: 'top' }}>
                                    <td style={{ ...tdStyle, fontWeight: 600 }}>Sweep interest rate<div style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>decimal, e.g. 0.0475 = 4.75%</div></td>
                                    <td style={tdStyle}>
                                        <input type="text" inputMode="decimal" style={inp} placeholder="required — e.g. 0.0475"
                                            defaultValue={cfData.sweep_rate == null ? '' : String(cfData.sweep_rate)}
                                            key={'cf-sr-' + String(cfData.sweep_rate)}
                                            onBlur={e => {
                                                const raw = e.target.value.trim();
                                                if (raw === '') return;          // blank = unchanged (still blocks; empty-required)
                                                const v = parseFloat(raw);
                                                if (!Number.isFinite(v)) return;
                                                /* ★ THE RANGE CHECK. A rate is a DECIMAL here: 0.0475 = 4.75%. Typing
                                                 * 4.75 used to store 4.75 and model a 475% return — measured once at
                                                 * ~$186M of phantom interest, $656.9M of cash, and a balance sheet that
                                                 * still checked to $0.00 in all 36 months. The only mechanical tell was
                                                 * the iteration count. Nothing else in the model catches it.
                                                 * ⛔ REFUSE, DO NOT COERCE. Auto-dividing by 100 would be guessing what
                                                 * they meant (§E1); the operator retypes and stays in control.
                                                 * ⓘ 0 IS ACCEPTED — "no interest" is a real answer, and Option-B's
                                                 * empty-required design exists precisely so that typing 0 is a
                                                 * deliberate choice while leaving it blank is not. */
                                                if (v < 0 || v > 1) {
                                                    setCfError(`Sweep interest rate must be a decimal between 0 and 1 — enter 0.0475 for 4.75%. You typed ${raw}, which would model ${(v * 100).toFixed(2)}% interest.`);
                                                    return;
                                                }
                                                saveCf({ sweep_rate: v });
                                            }} /></td>
                                    <td style={tdStyle}>{chipEl(srChip)}</td>
                                    <td style={tdStyle}>{cfData.sweep_rate == null
                                        ? <span style={{ fontSize: 12, color: '#B71C1C' }}>Not set. Enter the annual interest rate earned on the swept money-market balance, as a decimal (4.75% → 0.0475).</span>
                                        : <span style={{ fontSize: 12, color: '#27AE60' }}>Set ({(Number(cfData.sweep_rate) * 100).toFixed(2)}%).</span>}</td>
                                </tr>
                                {/* Sweep rounding — default 'none', not blocking */}
                                <tr style={{ borderTop: '1px solid #F0F0F0', verticalAlign: 'top' }}>
                                    <td style={{ ...tdStyle, fontWeight: 600 }}>Sweep rounding</td>
                                    <td style={tdStyle}>
                                        <select value={cfData.sweep_mround || 'none'} onChange={e => saveCf({ sweep_mround: e.target.value })}
                                            style={{ fontSize: 12, padding: '6px 8px', borderRadius: 4, border: '1px solid #D1D5DB', cursor: 'pointer' }}>
                                            {['none', '1000', '5000', '10000', '25000', '50000', '100000'].map(o => <option key={o} value={o}>{o === 'none' ? 'None (exact)' : '$' + Number(o).toLocaleString('en-US')}</option>)}
                                        </select></td>
                                    <td style={tdStyle}>{chipEl({ bg: '#E8F5E9', fg: '#1B5E20', label: '✓ Set' })}</td>
                                    <td style={tdStyle}><span style={{ fontSize: 12, color: '#888' }}>Rounds the monthly sweep to the nearest increment. Most companies use None.</span></td>
                                </tr>
                                {/* Sweep interest → IS line — CONFIRM-TO-PROCEED, blocks */}
                                <tr style={{ borderTop: '1px solid #F0F0F0', verticalAlign: 'top' }}>
                                    <td style={{ ...tdStyle, fontWeight: 600 }}>Sweep interest books to<div style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>income-statement line</div></td>
                                    <td style={tdStyle}>
                                        <select value={cfSweepIntPick === undefined ? '' : cfSweepIntPick}
                                            onChange={e => setCfSweepIntPick(e.target.value)}
                                            style={{ fontSize: 12, padding: '6px 8px', borderRadius: 4, border: '1px solid #D1D5DB', cursor: 'pointer', maxWidth: 260 }}>
                                            <option value="">— select a line —</option>
                                            {cfOieOptions.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                                            <option value="oi_interest">Keep a separate forecast line</option>
                                        </select>
                                        {cfSweepIntPick && cfSweepIntPick !== cfData.sweep_interest_is_line_key && (
                                            <div style={{ marginTop: 6 }}>
                                                <button onClick={() => saveCf({ sweep_interest_is_line_key: cfSweepIntPick })} style={btnSecondary}>Confirm</button>
                                            </div>
                                        )}
                                    </td>
                                    <td style={tdStyle}>{chipEl(cfSweepIntConfirmed
                                        ? { bg: '#E8F5E9', fg: '#1B5E20', label: '✓ Confirmed' }
                                        : { bg: '#FDECEA', fg: '#B71C1C', label: '✗ Needs confirming' })}</td>
                                    <td style={tdStyle}>
                                        {cfSweepIntConfirmed ? (
                                            <span style={{ fontSize: 12, color: '#27AE60' }}>
                                                {cfData.sweep_interest_is_line_key === 'oi_interest'
                                                    ? 'Confirmed — the forecast interest keeps its own row ("Interest (Income) / Expense").'
                                                    : 'Confirmed — the forecast interest books onto "' + ((cfOieOptions.find(o => o.id === cfData.sweep_interest_is_line_key) || {}).label || cfData.sweep_interest_is_line_key) + '", the same row as its history.'}
                                            </span>
                                        ) : cfInterestCandidates.length === 1 ? (
                                            <span style={{ fontSize: 12, color: '#B71C1C' }}>
                                                Not confirmed. The cash sweep earns interest every forecast month; this is the line it books to.
                                                We suggest <strong>{cfInterestCandidates[0].label}</strong> — your existing interest line, so history and forecast
                                                share one row instead of two. Confirm to continue.
                                            </span>
                                        ) : cfInterestCandidates.length > 1 ? (
                                            <span style={{ fontSize: 12, color: '#B71C1C' }}>
                                                Not confirmed — and we will not choose for you: <strong>{cfInterestCandidates.map(c => c.label).join(' and ')}</strong>{' '}
                                                both look like interest lines. Pick the one the money-market interest belongs on.
                                            </span>
                                        ) : (
                                            <span style={{ fontSize: 12, color: '#B71C1C' }}>
                                                Not confirmed. No interest line was found in your chart of accounts — choose an Other-income line, or
                                                <strong> Keep a separate forecast line</strong> to give the sweep interest its own row.
                                            </span>
                                        )}
                                    </td>
                                </tr>
                                {/* D&A forecast -> IS line (D&A routing, Part 2) -- CONFIRM-TO-PROCEED, blocks; one row per stream */}
                                {DA_STREAMS.map(st => {
                                    const opts = cfDaOptions[st.column] || [];
                                    const pick = cfDaPick[st.column];
                                    const stored = cfData[st.column];
                                    const noun = st.role === 'depreciation' ? 'Depreciation' : 'Amortization';
                                    return (
                                        <tr key={st.column} style={{ borderTop: '1px solid #F0F0F0', verticalAlign: 'top' }}>
                                            <td style={{ ...tdStyle, fontWeight: 600 }}>{noun} forecast books to<div style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>income-statement line</div></td>
                                            <td style={tdStyle}>
                                                <select value={pick === undefined ? '' : pick}
                                                    onChange={e => { const v = e.target.value; setCfDaPick(p => ({ ...p, [st.column]: v })); }}
                                                    style={{ fontSize: 12, padding: '6px 8px', borderRadius: 4, border: '1px solid #D1D5DB', cursor: 'pointer', maxWidth: 260 }}>
                                                    <option value="">{'\u2014 select a line \u2014'}</option>
                                                    {opts.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                                                    <option value={st.synthetic}>Keep a separate forecast line</option>
                                                </select>
                                                {pick && pick !== stored && (
                                                    <div style={{ marginTop: 6 }}>
                                                        <button onClick={() => saveCf({ [st.column]: pick })} style={btnSecondary}>Confirm</button>
                                                    </div>
                                                )}
                                            </td>
                                            <td style={tdStyle}>{chipEl(stored
                                                ? { bg: '#E8F5E9', fg: '#1B5E20', label: '\u2713 Confirmed' }
                                                : { bg: '#FDECEA', fg: '#B71C1C', label: '\u2717 Needs confirming' })}</td>
                                            <td style={tdStyle}>
                                                {stored ? (
                                                    <span style={{ fontSize: 12, color: '#27AE60' }}>
                                                        {stored === st.synthetic
                                                            ? 'Confirmed \u2014 the forecast ' + st.role + ' keeps its own row ("' + noun + '"), separate from its history.'
                                                            : 'Confirmed \u2014 the forecast ' + st.role + ' books onto "' + ((opts.find(o => o.id === stored) || {}).label || stored) + '", the same row as its history.'}
                                                        {' It can be changed later, but not cleared back to unanswered.'}
                                                    </span>
                                                ) : opts.length === 1 ? (
                                                    <span style={{ fontSize: 12, color: '#B71C1C' }}>
                                                        Not confirmed. We suggest <strong>{opts[0].label}</strong> {'\u2014'} your existing {st.role} line, so history and forecast
                                                        share one row instead of two. Confirm to continue.
                                                    </span>
                                                ) : opts.length > 1 ? (
                                                    <span style={{ fontSize: 12, color: '#B71C1C' }}>
                                                        Not confirmed {'\u2014'} and we will not choose for you: <strong>{opts.map(o => o.label).join(' and ')}</strong>{' '}
                                                        all carry the {st.role} role. Pick the one the forecast belongs on.
                                                    </span>
                                                ) : (
                                                    <span style={{ fontSize: 12, color: '#B71C1C' }}>
                                                        Not confirmed. No {st.role} line was found in your chart of accounts {'\u2014'} choose
                                                        <strong> Keep a separate forecast line</strong> to give the forecast {st.role} its own row.
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                {/* Commission payment lag moved to the Compensation Cycle card above (item #1 part 2). */}
                                {/* Prepaid start balance — derive-and-confirm, soft (never blocks) */}
                                <tr style={{ borderTop: '1px solid #F0F0F0', verticalAlign: 'top' }}>
                                    <td style={{ ...tdStyle, fontWeight: 600 }}>Prepaid opening balance<div style={{ fontSize: 11, color: '#999', fontWeight: 400 }}>at model start</div></td>
                                    <td style={tdStyle}>{prepaidSet
                                        ? <span style={{ fontFamily: MONO, fontSize: 12 }}>${Number(cfData.prepaid_start_balance).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                        : <span style={{ color: '#999', fontSize: 12 }}>—</span>}</td>
                                    <td style={tdStyle}>{chipEl(prepaidSet ? { bg: '#E8F5E9', fg: '#1B5E20', label: '✓ Set' } : infoChip)}</td>
                                    <td style={tdStyle}>
                                        {!cfData.hasBsActuals ? (
                                            <span style={{ fontSize: 12, color: '#888' }}>Pending Step 4 data — the opening balance is read from your imported balance sheet. Complete Step 4, then re-check.</span>
                                        ) : cfData.derivedPrepaid != null ? (
                                            <div>
                                                {cfData.cfPreLaunch ? (() => { const $2 = n => '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); const cl = formatMonthLabel(cfData.closeMonth, 'shortFull'); return (
                                                    <span data-cf-prepaid-prelaunch="" style={{ fontSize: 12, color: '#444' }}>{'Pre-Launch: the model starts at your opening balance sheet (' + cl + '), so the prepaid balance entering it is '}<strong>{$2(cfData.derivedPrepaid)}</strong>{'. ' + (cfData.closePrepaid ? 'Your ' + cl + ' prepaid of ' + $2(cfData.closePrepaid) + ' is reached through the prepaid contracts dated ' + cl + '.' : 'Your ' + cl + ' balance sheet carries no prepaid.')}</span>); })() : (
                                                <span style={{ fontSize: 12, color: '#444' }}>Derived from your balance sheet: <strong>${Number(cfData.derivedPrepaid).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong> <span style={{ color: '#888' }}>(prepaid expenses at {cfData.openingMonth})</span>.</span>)}
                                                {/* Batch 2 (2026-11-03, item 15, Brian): "Use this value" is offered ONLY when the prepaid schedule ties (the engine's
                                                    own prepaidTies, read by PrepaidTieWarning); otherwise only the warning shows. SubBase: the saved 0.00 is right and
                                                    the schedule does not tie, so the button is gone there. */}
                                                {((!prepaidSet || Number(cfData.prepaid_start_balance) !== Number(cfData.derivedPrepaid)) || Number(cfData.closePrepaid || 0) !== 0) && (
                                                    <PrepaidTieWarning companyId={selectedCompanyId} companyName={selectedCompany?.name} runKey={String(cfData.prepaid_start_balance ?? '')}
                                                        warn={Number(cfData.closePrepaid || 0) !== 0}
                                                        whenTies={(!prepaidSet || Number(cfData.prepaid_start_balance) !== Number(cfData.derivedPrepaid)) ? (
                                                    <div data-prepaid-use-value="" style={{ marginTop: 6 }}>
                                                        <button onClick={() => saveCf({ prepaid_start_balance: Number(cfData.derivedPrepaid) })} style={{ ...linkBtn }}>Use this value →</button>
                                                        <span style={{ fontSize: 11, color: '#999' }}> (optional — validation only; the forecast does not depend on it)</span>
                                                    </div>) : null} />
                                                )}
                                                {/* Batch 1b (2026-11-03, N-d2): the WARNING (never a block) when the close-month prepaid is nonzero and the engine's
                                                    own tie verdict (runFullModel prepaidTies) says the stored contracts do not roll to it from the saved
                                                    opening balance -- since Batch 2 the ONE mount above shows it (warn), re-checked after a save (runKey). */}
                                            </div>
                                        ) : (
                                            <span style={{ fontSize: 12, color: '#888' }}>No opening prepaid balance found in the balance sheet — leave blank if this company had no prepaids at model start.</span>
                                        )}
                                    </td>
                                </tr>
                                {/* ★ G4 — BALANCE SHEET ASSUMPTIONS. INFO-only, never blocks.
                                    The drivers behind every forecast BS line (DSO / DPO / formula /
                                    module_link, plus the per-month values grid) live in a panel on /bs
                                    that ships COLLAPSED, is on no nav, and — until this link — nothing
                                    in the wizard pointed at. The operator could not find it.
                                    ⛔ NOT BLOCKING: Step 9 can see that bs_assumptions ROWS exist (the
                                    Step-4 seed writes them) but not whether their drivers are RIGHT, and
                                    a gate that judges data it cannot read is the shape §12.5 files as a
                                    check firing on correct data. This points; it does not police. */}
                                <tr style={{ borderTop: '1px solid #F0F0F0', verticalAlign: 'top' }}>
                                    <td style={{ ...tdStyle, fontWeight: 600 }}>Balance sheet assumptions</td>
                                    <td style={tdStyle}><span style={{ color: '#999', fontSize: 12 }}>on the Balance Sheet</span></td>
                                    <td style={tdStyle}>{chipEl(infoChip)}</td>
                                    <td style={tdStyle}>
                                        <span style={{ fontSize: 12, color: '#888' }}>How each balance sheet line is forecast — receivable days (DSO), payable days (DPO), held constant, or driven by a module. Seeded at Step 4; review them before going live. </span>
                                        <button onClick={() => goConfigure('/bs', 'assumptions')} style={linkBtn}>Open Forecast Assumptions →</button>
                                    </td>
                                </tr>
                                {/* Fundraising — INFO-only, never blocks */}
                                <tr style={{ borderTop: '1px solid #F0F0F0', verticalAlign: 'top' }}>
                                    <td style={{ ...tdStyle, fontWeight: 600 }}>Fundraising rounds</td>
                                    <td style={tdStyle}><span style={{ color: '#999', fontSize: 12 }}>optional</span></td>
                                    <td style={tdStyle}>{chipEl(infoChip)}</td>
                                    <td style={tdStyle}>
                                        <span style={{ fontSize: 12, color: '#888' }}>If this company has raised equity, add the rounds on the Fundraising page. </span>
                                        <button onClick={() => goConfigure('/fundraising')} style={linkBtn}>Open Fundraising →</button>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    );
                })()}
                {cfError && !cfLoading && cfData && <div style={{ color: '#E53935', fontSize: 12, fontFamily: FONT, marginTop: 8 }}>{cfError}</div>}

                {done && (
                    <div style={{ marginTop: 16, padding: '14px 18px', background: '#F6FAF6', border: '1px solid #C8E6C9', borderRadius: 8, fontFamily: FONT, fontSize: 13, color: '#1B5E20', textAlign: 'center' }}>
                        &#127881; <strong>Module setup complete.</strong> Every forecast module is configured —
                        the model can produce a full forecast. Final validation and go-live are next.
                    </div>
                )}
                {step9Error && <div style={{ color: '#E53935', fontSize: 13, fontFamily: FONT, marginTop: 12 }}>{step9Error}</div>}

                {/* ── Go Live (Session 9d) ──────────────────────────────────────────
                    Sits AFTER Company Financials because it is the last thing on the
                    wizard: everything above it is what the pre-flight measures.
                    ✅ ARMED (landing 3): the button really flips is_engine_ready.
                    The panel mounts its OWN ModelProvider (AdminPage is outside one) and
                    reads the verdict that provider already computes — one pre-flight, not two.
                    is_engine_ready / engine_ready_at / engine_ready_by ride along on
                    SELECT * FROM companies, so the ALREADY-LIVE state needs no new fetch. */}
                {/* Batch 1 (2026-11-03, N-c, Brian: YES): the path is Complete Setup -> the pre-flight -> Set live. The panel shows once
                    Step 9 is complete (`done`), or for a company already live (as before). */}
                {(done || selectedCompany?.is_engine_ready === true) ? <GoLivePanel
                    companyId={selectedCompanyId}
                    companyName={selectedCompany?.name}
                    isEngineReady={selectedCompany?.is_engine_ready === true}
                    engineReadyAt={selectedCompany?.engine_ready_at}
                    engineReadyBy={selectedCompany?.engine_ready_by}
                    onWentLive={companiesChanged}
                /> : (
                    <div data-golive-wait="" style={{ marginTop: 16, padding: '12px 16px', background: '#F5F6FA', border: '1px solid #E2E5F0', borderRadius: 8, fontFamily: FONT, fontSize: 13, color: '#4B5563' }}>
                        <strong>Go live</strong> comes after <strong>Complete Setup</strong> (below): once Step 9 is complete, the go-live check runs here and the button to set the company live appears.
                    </div>
                )}

                <div style={{ display: 'flex', gap: 12, marginTop: 20, alignItems: 'center' }}>
                    <button onClick={() => setViewingStep(8)} style={btnText} disabled={step9Saving}>&larr; Back</button>
                    <button onClick={() => { setStep9Rows(null); setStep9Error(''); setCfData(null); setCfError(''); }} style={btnSecondary} disabled={step9Saving || step9Loading}>&#8635; Re-check</button>
                    {/* U17 (2026-10-30): once Step 9 is complete the button is a STATUS, not a control -- a click only re-saved the flag it reported
                       (diffs/step9-done-button-check.txt). The way out is "← All Companies" at the top of the wizard. */}
                    {done ? <span data-step9-complete="1" style={{ fontSize: 13, fontWeight: 700, color: '#27AE60', padding: '8px 4px' }}>Step 9 complete ✓</span> : (
                    <button onClick={handleStep9Complete} disabled={step9Saving || !allPass || !cfPass || !commPass} style={{ ...btnPrimary, opacity: (allPass && cfPass && commPass) ? 1 : 0.5 }}
                        title={(allPass && cfPass && commPass) ? undefined : !commPass ? 'The commission answer (named above the Company Financials).' : (!drPass ? 'Every deferred-revenue account needs a revenue stream (named above the Company Financials).' : !allPass ? 'Every wired module must pass its configuration check first.' : 'Under Company Financials: enter the cash sweep floor and interest rate, and confirm the IS line the sweep interest books to.')}>
                        {step9Saving ? 'Saving...' : 'Complete Setup →'}
                    </button>)}
                </div>
            </div>
        );
    };

    // ── Placeholder for steps 5-7 ────────────────────────────
    const renderPlaceholderStep = (stepNum) => {
        const step = WIZARD_STEPS.find(s => s.num === stepNum);
        return (
            <div>
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#999' }}>
                    <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 18, marginBottom: 12 }}>{step.label}</h3>
                    <p style={{ fontFamily: FONT, fontSize: 14 }}>Coming Soon &mdash; This step will be built in a future session.</p>
                </div>
                <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                    <button onClick={() => handleBack(stepNum)} style={btnText}>&larr; Back</button>
                </div>
            </div>
        );
    };

    const renderCurrentStep = () => {
        if (wizardLoading) return <div style={{ padding: 32, color: '#999', fontFamily: FONT }}>Loading...</div>;
        if (viewingStep === 1 || editing || !step1Complete) return renderStep1();
        if (viewingStep === 2) return renderStep2();
        if (viewingStep === 3) return renderStep3();
        if (viewingStep === 4) return renderStep4();
        if (viewingStep === 5) return renderStep5();
        if (viewingStep === 6) return renderStep6();
        if (viewingStep === 7) return renderStep7();
        if (viewingStep === 8) return renderStep8();
        // 9a-2: the appended Module Setup step. A stored current_step=9 (the server's
        // assets-import write) now lands HERE — "advance to Module Setup", no longer a
        // fall-through to Step 1.
        if (viewingStep === 9) return renderStep9();
        return renderStep1();
    };

    const renderWizard = () => (
        <div>
            <div style={{ marginBottom: 16 }}>
                <button onClick={() => { setSelectedCompanyId(null); setObState(null); setEditing(false); setError(''); setCfData(null); loadCompanies(); }}
                    style={{ ...btnText, fontSize: 13 }}>&larr; All Companies</button>
            </div>
            <h2 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 20, margin: '0 0 24px' }}>
                {selectedCompany?.name || 'Company'}
            </h2>
            {renderStepIndicator()}
            {step5SutaFlushErrors.length > 0 && (
                <div style={{ background: 'rgba(212,133,31,0.08)', border: '1px solid rgba(212,133,31,0.3)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ fontSize: 12, color: '#8A5A12', fontFamily: FONT, lineHeight: 1.5 }}>
                        {step5SutaFlushErrors.length} state SUTA rate{step5SutaFlushErrors.length !== 1 ? 's' : ''} couldn't be saved ({step5SutaFlushErrors.map(f => f.state).join(', ')}) — the roster imported fine; set these in Tax Config. Any unset state books $0 SUTA and shows in the Model-Health panel.
                    </div>
                    <button onClick={() => setStep5SutaFlushErrors([])} style={{ ...btnText, fontSize: 12, flexShrink: 0 }}>Dismiss</button>
                </div>
            )}
            {/* ═══ ITEM 2 — THE PREPAID CLOBBER GATE (R3, DEFAULT = KEEP) ═══
                Rendered at the TOP LEVEL, beside step6PrepaidNotice, because the six
                buttons that trigger the import live on TWO different sub-screens (the
                prepaid editor and the forecast grid) and the operator must see this
                wherever they clicked. It is a BLOCK, not a notice: handleStep6Import
                has already returned without posting, so the DB is untouched while this
                is on screen. */}
            {step6Clobber && step6Clobber.affected.length > 0 && (
                <div style={{ background: 'rgba(229,57,53,0.06)', border: '1px solid rgba(229,57,53,0.35)', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
                    <div style={{ fontSize: 13, fontFamily: FONT, color: '#1A1C2E', fontWeight: 600, marginBottom: 6 }}>
                        {step6Clobber.affected.length} prepaid contract{step6Clobber.affected.length !== 1 ? 's have' : ' has'} a forecast schedule this workbook does not contain
                    </div>
                    <div style={{ fontSize: 12, fontFamily: FONT, color: '#555', lineHeight: 1.6, marginBottom: 10 }}>
                        These schedules were entered in the app, not in the spreadsheet. Importing rebuilds the
                        prepaid table from the file, so they will be replaced unless you keep them. Closed months
                        are not affected either way &mdash; only forecast months after {obState?.close_month || 'the close month'}.
                    </div>
                    <div style={{ maxHeight: 190, overflowY: 'auto', border: '1px solid rgba(229,57,53,0.2)', borderRadius: 6, background: '#fff', marginBottom: 10 }}>
                        {step6Clobber.affected.map((a, i) => (
                            <div key={a.key + '|' + i} style={{ padding: '7px 10px', borderBottom: i < step6Clobber.affected.length - 1 ? '1px solid #F0F0F0' : 'none', fontSize: 12, fontFamily: FONT, color: '#333' }}>
                                <strong>{a.vendor_name}</strong>
                                {a.account ? <span style={{ color: '#888' }}> &middot; {a.account}</span> : null}
                                <span style={{ color: '#888' }}> &middot; paid {a.start_month} &middot; ${Number(a.prepaid_amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                <div style={{ color: '#8A1F1B', marginTop: 2 }}>
                                    {a.kind === 'orphan'
                                        ? 'not in this workbook at all — the whole contract would be dropped. '
                                        : ''}
                                    at risk: {a.months.join(', ')} (${Number(a.dollars).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                                </div>
                            </div>
                        ))}
                    </div>
                    {/* ★ TWO-WAY, NOT A DISMISS. §E1: the answer is the operator's, and it is
                        stated rather than inferred. KEEP is pre-selected because it is the
                        non-destructive arm — but the acknowledgement below is what unblocks,
                        so a default is never mistaken for a decision. */}
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 13, fontFamily: FONT, color: '#1A1C2E', marginBottom: 4 }}>
                        <input type="radio" name="step6Clobber" checked={step6ClobberChoice === 'keep'}
                            onChange={() => setStep6ClobberChoice('keep')} style={{ marginTop: 3 }} />
                        <span><strong>Keep the schedules I entered</strong> &mdash; the workbook supplies history, these contracts keep their forecast.</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 13, fontFamily: FONT, color: '#1A1C2E', marginBottom: 10 }}>
                        <input type="radio" name="step6Clobber" checked={step6ClobberChoice === 'replace'}
                            onChange={() => setStep6ClobberChoice('replace')} style={{ marginTop: 3 }} />
                        <span><strong>Replace them with the workbook</strong> &mdash; discard the entries above.</span>
                    </label>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <button
                            onClick={() => { setStep6ClobberAck(step6Clobber.fingerprint); setStep6Clobber(null); }}
                            style={btnPrimary}>
                            {step6ClobberChoice === 'keep' ? 'Keep these schedules — continue' : 'Replace these schedules — continue'}
                        </button>
                        <span style={{ fontSize: 12, fontFamily: FONT, color: '#888' }}>
                            Nothing has been saved yet. Choose, then press the save button again.
                        </span>
                    </div>
                </div>
            )}
            {/* ★ PRESERVED-ROUTE NOTICE — informational, never blocking. It renders only after
                an import that actually preserved something, so a normal import looks exactly as
                it did before. Deliberately NO Keep/Replace control: the value being preserved is
                one the operator set on the Vendor Dashboard, and that is where they change it. */}
            {step6RouteNotice && step6RouteNotice.names.length > 0 && (
                <div style={{ background: 'rgba(42,109,176,0.08)', border: '1px solid rgba(42,109,176,0.3)', borderRadius: 8, padding: '10px 14px', marginBottom: 16 }}>
                    <div style={{ fontSize: 12, color: '#1F4E79', fontFamily: FONT, lineHeight: 1.6 }}>
                        <strong>{step6RouteNotice.names.length} vendor{step6RouteNotice.names.length === 1 ? '' : 's'} kept their manually-set income-statement line</strong>
                        {' '}and {step6RouteNotice.names.length === 1 ? 'was' : 'were'} not overwritten by this import:{' '}
                        {step6RouteNotice.names.slice(0, 5).join(', ')}
                        {step6RouteNotice.names.length > 5 ? `, +${step6RouteNotice.names.length - 5} more` : ''}.
                        {' '}To change one, edit it on the Vendor Dashboard.
                    </div>
                </div>
            )}
            {step6PrepaidNotice && (step6PrepaidNotice.irregular.length > 0 || step6PrepaidNotice.conferences.length > 0 || (step6PrepaidNotice.lagAmbiguous || []).length > 0) && (
                <div style={{ background: 'rgba(212,133,31,0.08)', border: '1px solid rgba(212,133,31,0.3)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ fontSize: 12, color: '#8A5A12', fontFamily: FONT, lineHeight: 1.6 }}>
                        {(step6PrepaidNotice.lagAmbiguous || []).length > 0 && (
                            <div style={{ marginBottom: (step6PrepaidNotice.conferences.length || step6PrepaidNotice.irregular.length) ? 8 : 0 }}>
                                <strong>{step6PrepaidNotice.lagAmbiguous.length} prepaid renewal{step6PrepaidNotice.lagAmbiguous.length !== 1 ? 's have' : ' has'} an ambiguous amortization start</strong> — earlier cycles of the same vendor/account amortize on different lags, so the forecast was generated amortizing from the payment month (lag 0). Confirm the amortization start in the Prepaid tab:
                                <div style={{ marginTop: 4, color: '#6B4E1A' }}>
                                    {step6PrepaidNotice.lagAmbiguous.map(c => `${c.vendor_name} (${c.account || '—'}, paid ${c.start_month}; prior lags ${c.sibLags.join('/')})`).join(' · ')}
                                </div>
                            </div>
                        )}
                        {step6PrepaidNotice.conferences.length > 0 && (
                            <div style={{ marginBottom: step6PrepaidNotice.irregular.length ? 8 : 0 }}>
                                <strong>{step6PrepaidNotice.conferences.length} conference{step6PrepaidNotice.conferences.length !== 1 ? 's' : ''} need a forecast event month.</strong> These are booked entirely in the event month (not amortized), and the template doesn't carry a forward event — open each in the Prepaid tab and set its <strong>Event Month</strong> if the event falls in the forecast:
                                <div style={{ marginTop: 4, color: '#6B4E1A' }}>
                                    {step6PrepaidNotice.conferences.map((c, i) => `${c.name} ($${Number(c.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}, paid ${c.paid})`).join(' · ')}
                                </div>
                            </div>
                        )}
                        {step6PrepaidNotice.irregular.length > 0 && (
                            <div>
                                <strong>{step6PrepaidNotice.irregular.length} prepaid contract{step6PrepaidNotice.irregular.length !== 1 ? 's' : ''} had an irregular booking history</strong> — the forecast was NOT auto-generated (the historical amortization isn't a clean straight-line, so guessing forward would be wrong). Set the schedule in the Prepaid tab:
                                <div style={{ marginTop: 4, color: '#6B4E1A' }}>
                                    {step6PrepaidNotice.irregular.map(c => `${c.vendor_name} (${c.account || '—'}, start ${c.start_month})`).join(' · ')}
                                </div>
                            </div>
                        )}
                    </div>
                    <button onClick={() => setStep6PrepaidNotice(null)} style={{ ...btnText, fontSize: 12, flexShrink: 0 }}>Dismiss</button>
                </div>
            )}
            {step6UnlinkedNotice && (
                <div data-unlinked-notice="1" style={{ background: 'rgba(192,57,43,0.06)', border: '1px solid rgba(192,57,43,0.3)', borderRadius: 8, padding: '10px 14px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ fontSize: 12, color: '#8B2C24', fontFamily: FONT, lineHeight: 1.6 }}>
                        {(step6UnlinkedNotice.unlinked || []).map((u, k) => (
                            <div key={'u' + k}>{u.vendor_name} {u.start_month || ''}: linked to no vendor {'\u2014'} its forecast cost will not reach the income statement (P27 blocks go-live) {'\u2014'} link it on the Vendor Dashboard after Step 8.</div>
                        ))}
                        {(step6UnlinkedNotice.unresolvedLinks || []).map((u, k) => (
                            <div key={'r' + k}>{u.vendor_name} {u.start_month || ''}: its renewal link could not be resolved {'\u2014'} it was saved as a stand-alone contract.</div>
                        ))}
                    </div>
                    <button onClick={() => setStep6UnlinkedNotice(null)} style={{ ...btnText, fontSize: 12, flexShrink: 0 }}>Dismiss</button>
                </div>
            )}
            {renderCurrentStep()}
        </div>
    );

    // ── Company list ─────────────────────────────────────────
    const renderCompanyList = () => (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div>
                    <h2 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 20, margin: 0 }}>Company Management</h2>
                    <p style={{ fontFamily: FONT, color: '#888', fontSize: 13, margin: '4px 0 0' }}>Manage company onboarding and settings</p>
                </div>
                <button onClick={() => setShowNewCompanyModal(true)} style={btnPrimary}>+ New Company</button>
            </div>
            {companiesLoading ? (
                <div style={{ padding: 32, color: '#999', fontFamily: FONT, textAlign: 'center' }}>Loading...</div>
            ) : companies.length === 0 ? (
                <div style={{ padding: 32, color: '#999', fontFamily: FONT, textAlign: 'center' }}>No companies yet. Click "+ New Company" to get started.</div>
            ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT, fontSize: 14 }}>
                    <thead>
                        <tr style={{ borderBottom: '2px solid #E0E0E0', textAlign: 'left' }}>
                            <th style={thStyle}>Company Name</th>
                            <th style={thStyle}>Status</th>
                            <th style={thStyle}>Historical Start</th>
                            <th style={thStyle}>Close Month</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {companies.map(co => {
                            const live = co.is_onboarded;
                            const ob = onboardingStates[co.id];
                            return (
                                <tr key={co.id} style={{ borderBottom: '1px solid #F0F0F0' }}>
                                    <td style={tdStyle}>{co.name}</td>
                                    <td style={tdStyle}>
                                        <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                                            background: live ? '#E8F5E9' : '#FFF3E0', color: live ? '#2E7D32' : '#E65100' }}>
                                            {live ? 'Live' : 'Onboarding'}
                                        </span>
                                    </td>
                                    <td style={{ ...tdStyle, fontFamily: MONO, fontSize: 13 }}>{ob?.historical_start || '\u2014'}</td>
                                    <td style={{ ...tdStyle, fontFamily: MONO, fontSize: 13 }}>{ob?.close_month || '\u2014'}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                                        <button onClick={() => openCompany(co.id)} style={btnSmall}>
                                            {live ? 'Edit Onboarding' : 'Open'}
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );

    /* ═══════════════ CHART OF ACCOUNTS (C4) ═══════════════
     * The live-tenant rename surface. A rename here is a LABEL-ONLY edit: since Build 1
     * pinned each leaf's identity in gl_line_mappings.leaf_key, nothing joined to the leaf
     * can be stranded, so this screen needs no migration, no progress state and no scary
     * confirm — just an edit and a Confirm. */
    const [coaCid, setCoaCid] = useState(null);
    const [coaRows, setCoaRows] = useState(null);
    const [coaLoading, setCoaLoading] = useState(false);
    const [coaError, setCoaError] = useState('');
    const [coaEdit, setCoaEdit] = useState({});        // glm id -> pending label
    const [coaNotice, setCoaNotice] = useState(null);  // { id, notices[] } from the last save
    const [coaUses, setCoaUses] = useState({});        // leaf_key -> [informational strings]
    const [coaOrder, setCoaOrder] = useState({});       // leaf id -> its INDEX in the built statement (the render order)

    const loadCoa = useCallback(async (cid) => {
        setCoaLoading(true); setCoaError(''); setCoaNotice(null); setCoaEdit({});
        try {
            const [gRes, faRes, iaRes, baRes, rmRes, sRes, dRes, dmRes, aeRes, cqRes,
                   vRes, pcRes, mrRes, ilRes] = await Promise.all([
                getGLLineMappings(cid),
                getFixedAssets(cid).catch(() => ({ data: [] })),
                getIntangibleAssets(cid).catch(() => ({ data: [] })),
                getBSAssumptions(cid).catch(() => ({ data: {} })),
                getRevenueModuleMappings(cid).catch(() => ({ data: [] })),
                getCompanySettings(cid).catch(() => ({ data: {} })),
                getDefrevData(cid).catch(() => ({ data: {} })),
                /* the three remaining builder inputs — the tab orders its rows by the
                   STATEMENTS' OWN emission order, so it needs what the statements need. */
                getDepartmentMappings(cid).catch(() => ({ data: [] })),
                getAEQuotaSettings(cid).catch(() => ({ data: {} })),
                getCompanyQuotaSettings(cid).catch(() => ({ data: {} })),
                /* ★ THE THREE LARGEST POINTER CLASSES, none of which the first cut showed:
                   vendors.is_line (916 rows across the four tenants — by far the biggest),
                   prepaid_contracts.is_line (668, stamped by today's backfill), and the
                   misc-revenue Path-A route. Measured: 45 of 53 pointed-at lines were
                   reading "—". */
                getVendors(cid).catch(() => ({ data: [] })),
                getPrepaidContracts(cid).catch(() => ({ data: [] })),
                getMiscRevenueLines(cid).catch(() => ({ data: [] })),
                /* W-081: implementation lines -- a routed IS target, and (deferred) the deferred-revenue account they are linked to. */
                getImplementationLines(cid).catch(() => ({ data: [] })),
            ]);
            setCoaRows(gRes.data || []);
            /* ★★ STATEMENT ORDER — BY RUNNING THE BUILDERS, NOT BY RE-APPLYING THEIR SORT KEYS.
             * The first cut of this sorted IS rows by (subsection walk, sort_order) and BS rows
             * by bs_actuals_meta.display_order — the fields the builders themselves sort on. A
             * gate caught it failing on BOTH statements, and the reasons are worth keeping:
             *   IS — a revenue row's glm carries subsection=NULL and sort_order=0; the builder
             *        supplies `subsection: revenue` at emission (dynRevLeaves), so the stored
             *        fields cannot place the revenue block at all.
             *   BS — first_republic_bank has display_order 100 and wells_fargo_4165 has 110, yet
             *        buildBSLines emits Wells Fargo FIRST; display_order is a band, not the
             *        emission order.
             * ⇒ §6: the only order that cannot drift from the statements is the statements' own.
             * This runs the SAME two builders the IS and BS pages run and keys each row by its
             * leaf's INDEX in the result. */
            const _rmm = rmRes.data || [];
            const _ctx = {
                aeQuotaEnabled: moduleIsWired(_rmm, 'ae_quota') && (aeRes.data?.enabled !== false),
                companyQuotaEnabled: moduleIsWired(_rmm, 'company_quota') && (cqRes.data?.enabled === true),
                hasFixedAssets: (faRes.data || []).length > 0,
                hasIntangibleAssets: (iaRes.data || []).length > 0,
            };
            const _meta = (dRes.data && dRes.data.bs_actuals_meta) || {};
            const _order = {};
            /* hoisted: the BS skeleton is needed twice — for the row ORDER below and for the
               role='sweep_target' lookup in the pointer map. Built once. */
            let _bsLines = [];
            try {
                const _is = buildISLinesForCompany(cid, gRes.data || [], _rmm, dmRes.data || [], _ctx,
                    (dRes.data && dRes.data.is_actuals) || {});
                _is.filter(l => !l.type && l.id).forEach((l, i) => { _order[l.id] = i; });
                if (Object.keys(_meta).length) {
                    _bsLines = buildBSLinesForCompany(cid, _meta, _rmm, _ctx);
                    _bsLines.filter(l => !l.type && l.id).forEach((l, i) => { _order[l.id] = i; });
                }
            } catch (e) {
                /* A tenant mid-onboarding can have a chart the builders refuse to assemble.
                   Order is presentation: fall back to the fetch order rather than blanking
                   the screen, and say so rather than failing silently. */
                console.warn('[chart-of-accounts] could not build the statement order; falling back to fetch order:', e?.message);
            }
            setCoaOrder(_order);
            /* ★ THE BLAST-RADIUS COUNTS ARE INFORMATIONAL, NOT A WARNING. The rename is
             * harmless, so gating on these would be dishonest — but the operator is about
             * to change what a line is CALLED on statements other people read, and these
             * are cheap to compute from the join columns. Show, do not gate. */
            /* ★★ EVERY POINTER, RESOLVED BY THE STORED KEY OR THE ROLE THE ENGINE USES —
             * ⛔ NEVER by matching the line's NAME. Matching by name is precisely the defect
             * the rename work exists to kill, and a warning column that resolved by name would
             * go blank at exactly the moment it was needed (a rename).
             * A census of every table.column that stores a leaf key found the first cut showed
             * 8 of 53 pointed-at lines; the rest read "—" while being load-bearing. */
            const uses = {};
            const add = (k, s) => { if (!k) return; (uses[k] = uses[k] || []).push(s); };
            const countBy = (arr, f) => arr.reduce((m, x) => { const k = f(x); if (k) m[k] = (m[k] || 0) + 1; return m; }, {});
            const plural = (n, w) => `${n} ${w}${n > 1 ? 's' : ''}`;
            Object.entries(countBy(faRes.data || [], a => a.bs_line_id)).forEach(([k, n]) => add(k, plural(n, 'fixed asset')));
            Object.entries(countBy(iaRes.data || [], a => a.bs_line_id)).forEach(([k, n]) => add(k, plural(n, 'intangible asset')));
            /* vendors.is_line — the largest class by an order of magnitude, and the one an
               operator most needs warned about: renaming a line 31 vendors book against. */
            Object.entries(countBy((vRes.data || []).filter(v => v.is_active !== false), v => v.is_line))
                .forEach(([k, n]) => add(k, plural(n, 'vendor')));
            /* prepaid_contracts.is_line — the slug stamped by today's backfill. */
            Object.entries(countBy(pcRes.data || [], p => p.is_line || p.isLine))
                .forEach(([k, n]) => add(k, plural(n, 'prepaid contract')));
            const ba = baRes.data || {};
            Object.entries(ba).forEach(([lineId, a]) => {
                if (!a) return;
                /* being a driver's BASE (something else is computed FROM this line) … */
                if (a.base_is_line_id) add(a.base_is_line_id, `the ${String(a.driver || '').toUpperCase() || 'BS'} driver for ${lineId}`);
                /* … and being a driven balance ITSELF, which is a different relationship and
                   was missing entirely. `constant` is the default and says nothing, so it is
                   not surfaced — a column that flags every line flags nothing. */
                if (a.driver && a.driver !== 'constant') add(lineId, `a ${a.driver}-driven balance`);
            });
            (rmRes.data || []).forEach(r => {
                if (r.module_type && r.module_type !== 'skip') add(r.gl_line_key, `the ${r.module_type} revenue module`);
                if (r.bs_line_key) add(r.bs_line_key, 'a deferred-revenue balance');
            });
            (mrRes.data || []).forEach(m => { if (m.target_gl_line_key) add(m.target_gl_line_key, 'a misc-revenue line'); });
            /* W-081: an implementation line points at its routed revenue line and at the deferred-revenue account it is linked to. */
            (ilRes.data || []).forEach(l => {
                if (l.target_gl_line_key && l.target_gl_line_key !== implSyntheticKey(l.id)) add(l.target_gl_line_key, 'an implementation line');
                const k = implDrLinkedKey(l.id, l.bs_line_key);
                if (k) add(k, "an implementation line's deferred revenue");
            });
            if (sRes.data && sRes.data.sweep_interest_is_line_key) add(sRes.data.sweep_interest_is_line_key, 'the sweep-interest target');
            /* D&A routing, Part 2: a routed D&A stream points at its historical leaf. The sentinel
               names the synthetic row, which is not a chart-of-accounts line, so it adds nothing. */
            DA_STREAMS.forEach(st => {
                const k = sRes.data && sRes.data[st.column];
                if (k && k !== st.synthetic) add(k, 'the ' + st.role + ' forecast target');
            });
            /* ★ THE CASH SWEEP TARGET — resolved BY ROLE off the built skeleton, exactly as
               ModelProvider does it (bsLines.find(l => l.role === 'sweep_target')?.id). It is
               not stored as a pointer anywhere, so a column that only read columns would miss
               the single most load-bearing line on the balance sheet. */
            const _sweepTarget = (_bsLines.find(l => l.role === 'sweep_target') || {}).id || null;
            if (_sweepTarget) add(_sweepTarget, 'the cash sweep target');
            setCoaUses(uses);
        } catch (err) {
            setCoaError('Failed to load the chart of accounts: ' + (err?.response?.data?.error || err?.message || 'Unknown error'));
        } finally { setCoaLoading(false); }
    }, []);

    const saveCoaLabel = async (row) => {
        const next = (coaEdit[row.id] || '').trim();
        if (!next) return;
        try {
            const res = await renameGLLineLabel(coaCid, row.id, next);
            setCoaRows(prev => (prev || []).map(r => (r.id === row.id ? { ...r, display_name: next } : r)));
            setCoaEdit(prev => { const p = { ...prev }; delete p[row.id]; return p; });
            setCoaNotice({ id: row.id, notices: (res.data && res.data.notices) || [], cache: res.data && res.data.cache_refreshed });
            setCoaError('');
        } catch (err) {
            setCoaError('Rename failed: ' + (err?.response?.data?.error || err?.message || 'Unknown error'));
        }
    };

    const renderChartOfAccounts = () => {
        if (!coaCid) {
            return (
                <div style={{ padding: 24 }}>
                    <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 18, marginBottom: 6 }}>Chart of Accounts</h3>
                    <p style={{ fontFamily: FONT, color: '#666', fontSize: 13, maxWidth: 760 }}>
                        Rename the lines on this company&rsquo;s statements. Renaming changes the LABEL only &mdash;
                        every schedule, driver and asset stays attached to the same line, so nothing needs re-pointing.
                    </p>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT, fontSize: 13, marginTop: 16 }}>
                        <thead><tr>
                            <th style={{ ...thStyle, textAlign: 'left' }}>Company</th>
                            <th style={{ ...thStyle, textAlign: 'right' }}></th>
                        </tr></thead>
                        <tbody>
                            {companies.map(co => (
                                <tr key={co.id} style={{ borderBottom: '1px solid #F0F0F0' }}>
                                    <td style={tdStyle}>{co.name}</td>
                                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                                        <button style={btnSmall} onClick={() => { setCoaCid(co.id); loadCoa(co.id); }}>Open</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            );
        }
        const co = companies.find(c => c.id === coaCid);
        /* ★ RENAMABLE ⟺ the row owns a statement leaf (it has is_line_key or leaf_key).
         * That excludes, by construction rather than by a list: the alwaysPresent synthetics
         * (oi_interest, totals, headers — they live in lineRoles.js, not in tenant data),
         * total_clearing (a rollup with no glm row), and the per-department payroll leaves,
         * which are keyed off department_mappings.display_group and are renamed at Step 5c. */
        const rows = (coaRows || []).filter(ownsLeaf);
        const skipped = (coaRows || []).length - rows.length;
        /* ★ STATEMENT ORDER — REUSED FROM THE BUILDERS' OWN SORT KEYS, NOT REINVENTED.
         * The tab used to list rows in gl_line_mappings array order, which matches nothing
         * the operator sees. The statements order their leaves like this:
         *   IS  — the subsection WALK is fixed in code (buildISLines.js:480-499:
         *         revenue · cogs · sm · [sw · pf · tme] · ga · rd · ooe · oie) and within a
         *         subsection the leaves sort on `sort_order`
         *         (buildISLines.js:316: sort((a,b) => (a.sort_order||0)-(b.sort_order||0)))
         *   BS  — buildBSLines.js:188: sort((a,b) => (a.display_order||0)-(b.display_order||0)),
         *         with display_order stored per leaf in bs_actuals_meta
         * Both keys are already on data this screen loads, so the order is read from the
         * SAME fields the builders read rather than from a second rule that could drift.
         * ⓘ A row whose subsection is absent sorts to the end rather than to the top — an
         * unplaced line should not lead the chart of accounts. */
        /* Each row's position IS its leaf's position on the statement (see loadCoa). A leaf
         * the builders did not emit sorts to the end rather than to the top — an unplaced
         * line should not lead the chart of accounts — and `id` breaks any remaining tie so
         * the order is stable across renders. */
        const stmtOrder = (r) => {
            const i = coaOrder[leafKeyFor(r)];
            return Number.isFinite(i) ? i : Number.MAX_SAFE_INTEGER;
        };
        const byStatement = (a, b) => stmtOrder(a) - stmtOrder(b) || (a.id - b.id);
        const bs = rows.filter(r => r.statement_type === 'BS').sort(byStatement);
        const is = rows.filter(r => r.statement_type !== 'BS').sort(byStatement);
        const section = (title, list) => (
            <>
                <tr><td colSpan={4} style={{ ...tdStyle, fontWeight: 700, background: '#FAFAFA', color: '#1A1C2E' }}>{title}</td></tr>
                {list.map(r => {
                    const key = leafKeyFor(r);
                    const pending = coaEdit[r.id];
                    const dirty = pending !== undefined && pending.trim() && pending.trim() !== (r.display_name || '');
                    const uses = coaUses[key] || [];
                    return (
                        <tr key={r.id} style={{ borderTop: '1px solid #F0F0F0', verticalAlign: 'top' }}>
                            <td style={tdStyle}>
                                <div>{r.gl_name}</div>
                                {/* ★ THE CATEGORY IS NOT DECORATION: six gl_names on cid=8 appear under
                                    more than one gl_category ("Travel", "Benefits", "Salaries" …), so the
                                    renamable unit is the ROW, never the name. */}
                                <div style={{ fontSize: 11, color: '#999' }}>{r.gl_category || '\u2014'}</div>
                            </td>
                            <td style={tdStyle}>
                                <ViewOnly canEdit={canEditModel(user)}><input type="text" value={pending !== undefined ? pending : (r.display_name || '')}
                                    onChange={e => setCoaEdit(prev => ({ ...prev, [r.id]: e.target.value }))}
                                    style={{ padding: '4px 8px', borderRadius: 4, border: '1px solid #D0D5DD', fontSize: 13, fontFamily: FONT, width: '100%', boxSizing: 'border-box' }} /></ViewOnly>
                                {dirty && (
                                    <div style={{ marginTop: 6 }}>
                                        <ViewOnly canEdit={canEditModel(user)}><button style={btnSecondary} onClick={() => saveCoaLabel(r)}>Confirm</button></ViewOnly>
                                        <button style={{ ...btnText, marginLeft: 8 }}
                                            onClick={() => setCoaEdit(prev => { const p = { ...prev }; delete p[r.id]; return p; })}>Cancel</button>
                                    </div>
                                )}
                                {coaNotice && coaNotice.id === r.id && (
                                    <div style={{ marginTop: 6, fontSize: 12, color: coaNotice.notices.length ? '#B26B00' : '#27AE60' }}>
                                        {coaNotice.notices.length ? coaNotice.notices.join(' ') : 'Renamed.'}
                                    </div>
                                )}
                            </td>
                            <td style={{ ...tdStyle, fontFamily: MONO, fontSize: 11, color: '#777' }}>{key}</td>
                            <td style={{ ...tdStyle, fontSize: 12, color: '#666' }}>
                                {uses.length ? ('Used by ' + uses.join(' \u00b7 ') + '.') : <span style={{ color: '#BBB' }}>&mdash;</span>}
                            </td>
                        </tr>
                    );
                })}
            </>
        );
        return (
            <div style={{ padding: 24 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                    <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 18, margin: 0 }}>Chart of Accounts &mdash; {co?.name}</h3>
                    <button style={btnText} onClick={() => { setCoaCid(null); setCoaRows(null); }}>&larr; All companies</button>
                </div>
                <p style={{ fontFamily: FONT, color: '#666', fontSize: 13, margin: '8px 0 16px', maxWidth: 820 }}>
                    Renaming changes the LABEL only. The line keeps its identity, so its history, its balance-sheet
                    driver, and any assets or modules pointing at it stay attached &mdash; nothing needs re-pointing afterwards.
                </p>
                {coaError && <div style={{ color: '#B71C1C', fontSize: 13, marginBottom: 12 }}>{coaError}</div>}
                {coaLoading || coaRows === null ? (
                    <div style={{ padding: 20, color: '#999', fontFamily: FONT, fontSize: 13 }}>Loading&hellip;</div>
                ) : (
                    <div style={{ maxHeight: 'calc(100vh - 320px)', minHeight: 240, overflow: 'auto',
                        border: '1px solid #E0E0E0', borderRadius: 6 }}>
                        {/* ★ THE STICKY HEADER NEEDS A BOUNDED SCROLL CONTAINER — that was what
                          * was missing, not the sticky declaration (which has been on the thead
                          * since dea4231 and never worked).
                          * THE CAUSE: `overflowX:'auto'` alone. Per CSS, a non-`visible` overflow
                          * on one axis computes the other axis to `auto` too — so this wrapper WAS
                          * already the nearest scrolling ancestor on both axes, but it had NO
                          * bounded height, so it never scrolled vertically. The PAGE scrolled
                          * instead, and `top:0` pinned the header to a container that was not
                          * moving: it simply travelled up and off with everything else.
                          * ⇒ THE FIX IS THE CONTAINER, NOT THE HEADER. Capping the height makes the
                          * table body scroll INSIDE this div, under a header now pinned to something
                          * that actually scrolls. `overflow:'auto'` states both axes explicitly
                          * rather than leaning on the computed-value rule above.
                          * ⓘ Nothing clips it: the table is this div's only child, so there is no
                          * `overflow:hidden` between the scroll container and the pinned row. */}
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: FONT, fontSize: 13 }}>
                            {/* Sticky header — the same pattern as the Step-3 grid
                                (AdminPage thead position:'sticky'). The background is not
                                decoration: without it the scrolled rows show through. */}
                            <thead style={{ position: 'sticky', top: 0, zIndex: 3, background: '#fff' }}><tr>
                                <th style={{ ...thStyle, textAlign: 'left' }}>GL line / category</th>
                                <th style={{ ...thStyle, textAlign: 'left', minWidth: 280 }}>Label on the statement</th>
                                <th style={{ ...thStyle, textAlign: 'left' }}>Line id</th>
                                <th style={{ ...thStyle, textAlign: 'left', minWidth: 260 }}>What points at it</th>
                            </tr></thead>
                            <tbody>
                                {section('Balance sheet', bs)}
                                {section('Income statement', is)}
                            </tbody>
                        </table>
                    </div>
                )}
                <p style={{ fontFamily: FONT, color: '#999', fontSize: 12, marginTop: 12 }}>
                    {rows.length} renamable line{rows.length === 1 ? '' : 's'}.
                    {skipped ? ` ${skipped} row${skipped === 1 ? '' : 's'} own no statement line (skipped, or handled by the payroll engine) and are not listed.` : ''}
                </p>
            </div>
        );
    };

    const renderOnboardingContent = () => selectedCompanyId ? renderWizard() : renderCompanyList();

    const renderTabContent = () => {
        // W-035: a tab this user may not see is never rendered -- a tenant user never reaches the wizard.
        if (!visibleTabs.some(t => t.key === activeTab)) return renderChartOfAccounts();
        if (activeTab === 'onboarding') return renderOnboardingContent();
        if (activeTab === 'chart-of-accounts') return renderChartOfAccounts();
        if (activeTab === 'health-check') return <HealthCheckPanel company={navCompany || null} />;
        // W-053 step 3a: the Add User tab -- a tenant admin manages their own company; the super admin the switcher's company.
        if (activeTab === 'add-user') {
            const usersCompanyId = superAdmin ? (navCompany ? navCompany.id : null) : user && user.company_id;
            if (!usersCompanyId) return <div style={{ padding: 8, fontSize: 13, color: '#666' }}>Select a company in the switcher to manage its users.</div>;
            return <UserManagementPanel key={usersCompanyId} companyId={usersCompanyId} companyName={navCompany ? navCompany.name : null} user={user} onSelfChanged={onUserChanged} />;
        }
        const tab = visibleTabs.find(t => t.key === activeTab);
        return (
            <div style={{ textAlign: 'center', padding: '80px 0', color: '#999' }}>
                <h3 style={{ fontFamily: FONT, color: '#1A1C2E', fontSize: 18, marginBottom: 12 }}>{tab?.label}</h3>
                <p style={{ fontFamily: FONT, fontSize: 14 }}>Coming Soon</p>
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', minHeight: 'calc(100vh - 49px)', background: '#F5F6FA', fontFamily: FONT }}>
            <div style={{ width: 200, background: '#fff', borderRight: '1px solid #E0E0E0', flexShrink: 0, paddingTop: 16 }}>
                {visibleTabs.map(tab => (
                    <div key={tab.key} onClick={() => setActiveTab(tab.key)}
                        style={{ padding: '10px 16px', cursor: 'pointer', fontSize: 14, fontFamily: FONT,
                            color: activeTab === tab.key ? '#1A73E8' : '#1A1C2E',
                            background: activeTab === tab.key ? '#E8F0FE' : 'transparent',
                            borderLeft: activeTab === tab.key ? '3px solid #1A73E8' : '3px solid transparent',
                            fontWeight: activeTab === tab.key ? 600 : 400, transition: 'all 0.15s' }}>
                        {tab.label}
                    </div>
                ))}
            </div>
            <div style={{ flex: 1, minWidth: 0, padding: 32 }}>
                <div style={{ background: '#fff', borderRadius: 8, padding: 32,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)', border: '1px solid #E0E0E0',
                    minWidth: 0, maxWidth: '100%' }}>
                    {renderTabContent()}
                </div>
            </div>
            {superAdmin && showNewCompanyModal && <NewCompanyModal onClose={() => setShowNewCompanyModal(false)} onCreate={handleNewCompanyCreated} />}
        </div>
    );
}

// ── Shared styles ────────────────────────────────────────────
const btnPrimary = {
    padding: '10px 24px', borderRadius: 6, border: 'none',
    background: '#1A73E8', color: '#fff', fontSize: 14,
    fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans',-apple-system,sans-serif",
};
const btnSecondary = {
    padding: '10px 24px', borderRadius: 6, border: '1px solid #D0D5DD',
    background: '#fff', color: '#1A1C2E', fontSize: 14,
    fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Sans',-apple-system,sans-serif",
};
const btnText = {
    padding: '6px 0', border: 'none', background: 'none',
    color: '#1A73E8', fontSize: 14, fontWeight: 500, cursor: 'pointer',
    fontFamily: "'DM Sans',-apple-system,sans-serif",
};
const btnSmall = {
    padding: '6px 16px', borderRadius: 4, border: '1px solid #D0D5DD',
    background: '#fff', color: '#1A1C2E', fontSize: 13,
    fontWeight: 500, cursor: 'pointer', fontFamily: "'DM Sans',-apple-system,sans-serif",
};
const thStyle = {
    padding: '10px 12px', fontSize: 12, fontWeight: 600, color: '#888',
    textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'left',
};
const tdStyle = { padding: '12px 12px', color: '#1A1C2E' };
const stickyTh = { ...thStyle, background: '#fff', borderBottom: '2px solid #E0E0E0' };
